import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type MouseEvent, type ReactNode } from "react";
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  Ban,
  Boxes,
  CalendarDays,
  CalendarRange,
  ClipboardList,
  Gauge,
  LayoutList,
  PackageCheck,
  PackageOpen,
  Settings2,
  StickyNote,
  UserRound,
  ArrowUp,
  Barcode,
  Bell,
  Bluetooth,
  Bold,
  Building2,
  CheckCircle2,
  Camera,
  Check,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  CircleSlash,
  Copy,
  CreditCard,
  DollarSign,
  Download,
  Edit3,
  Eye,
  EyeOff,
  ExternalLink,
  FileDown,
  FileText,
  Film,
  GraduationCap,
  GripVertical,
  Heading,
  History,
  Image,
  ImagePlus,
  KeyRound,
  LayoutTemplate,
  LifeBuoy,
  List,
  ListChecks,
  Loader2,
  MessageCircle,
  Lock,
  LogOut,
  MapPin,
  MoreHorizontal,
  Moon,
  MoveRight,
  PackageSearch,
  Plus,
  Printer,
  QrCode,
  RotateCcw,
  Save,
  Search,
  Send,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Star,
  Sun,
  Trash2,
  Underline,
  Upload,
  X as CloseIcon,
  ZoomIn,
  ZoomOut,
  UserCircle,
  UserPlus,
  Wrench
} from "lucide-react";
import { AppLayout, type ViewId } from "./components/AppLayout";
import { EmptyState, MetricCard, PageHeader, PrimaryButton, SecondaryButton, StatusPill } from "./components/ui";
import {
  Avatar,
  EquipmentAvailabilityBoard,
  EquipmentUsagePanel,
  ExternalServicePdfModal,
  ExternalServiceRulesPanel,
  ExternalServicesCalendar,
  MyEquipmentPanel,
  PickupConferenceModal,
  ServiceStatusStepper,
  SheetModal,
  ToggleRow,
  type PickupConferenceEntry
} from "./components/externalServices";
import {
  dateKey,
  externalServiceItemTone,
  externalServiceStatusTone,
  findAssetConflict,
  formatExternalServiceItemStatus,
  formatExternalServiceStatus,
  formatServicePeriod,
  resolveExternalServiceRules,
  serviceColor,
  serviceSpan
} from "./domain/externalServices";
import { Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { buildDepreciationChartData, calculateLinearMonthlyDepreciation, findDepreciationRuleForAsset, formatCurrency } from "./domain/depreciation";
import type { User } from "firebase/auth";
import { useAuth } from "./hooks/useAuth";
import { createAccountWithEmail, sendPasswordReset, signInWithEmail, signInWithGoogle, signOutUser } from "./services/auth";
import { applyServiceWorkerUpdate, PWA_UPDATE_AVAILABLE_EVENT, SERVICE_WORKER_VERSION } from "./services/pwa";
import { getCurrentTheme, useTheme } from "./services/theme";
import { captureCurrentScreen } from "./services/screenCapture";
import { SYNC_QUEUE_CHANGED_EVENT, countPendingSyncPhotos, isOnline, processPendingSync, subscribeOnlineStatus } from "./services/offlineSync";
import { assetReportFields, exportAssetsCsv, exportAssetsPdf, type AssetReportField, type AssetReportGroupBy } from "./services/reports";
import { companyCnpjExists, defaultCompanyOperationalSettings, saveCompany, saveCompanyOperationalSettings, saveExternalServiceRules, subscribeCompanies, type CompanyOperationalSettings, type CompanyRecord, type SaveCompanyInput } from "./services/companies";
import { addAssetPhotos, createAsset, importAssetsFromCsv, markAssetLabelLost, markAssetsLabelIssued, subscribeAssetMaintenance, subscribeAssets, subscribeAssetStatusHistory, updateAsset, updateAssetPhotos, updateAssetsDepreciationRule, updateAssetStatus, type AssetRecord, type AssetStatusInput, type ImportAssetInput } from "./services/assets";
import { compressImageFiles } from "./services/imageCompression";
import { downloadAssetLabelPdf, downloadAssetLabelPng, printAssetLabel } from "./services/labels";
import { saveGlobalLabelTemplate, subscribeGlobalLabelTemplates } from "./services/labelTemplates";
import {
  contractMergeFieldHelp,
  generateAndSaveContract,
  saveContractTemplate,
  setContractTemplateActive,
  subscribeContractTemplates,
  subscribeGeneratedContracts,
  uploadSignedContract
} from "./services/contracts";
import { markNotificationRead, subscribeMyNotifications } from "./services/notifications";
import {
  createTrainingArticle,
  createTrainingCategory,
  generateTrainingArticleId,
  reorderTrainingArticles,
  reorderTrainingCategories,
  sanitizeTrainingHtml,
  setTrainingCategoryActive,
  trainingImageFigureHtml,
  trainingVideoFigureHtml,
  subscribeTrainingArticles,
  subscribeTrainingCategories,
  updateTrainingArticle,
  updateTrainingCategory,
  uploadTrainingImage,
  uploadTrainingVideo
} from "./services/training";
import { DndContext, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent, type DraggableAttributes, type DraggableSyntheticListeners } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { isPushConfigured, registerPushNotifications } from "./services/pushNotifications";
import {
  ensureCurrentUserProfile,
  createSelfUserProfile,
  findUnassignedUsers,
  permissionsForRole,
  saveUserProfile,
  subscribeCurrentUserProfile,
  subscribeCompanyUsers,
  subscribeManagedUsers,
  subscribeUserAccessProfile,
  updateSelfUserAccount,
  updateSelfUserPhoto,
  type ManagedUser,
  type SaveUserInput,
  type UserAccessProfile,
  type UserCompanyAssignmentInput
} from "./services/users";
import {
  saveCompanyLocation,
  saveCompanyBrand,
  saveGlobalReference,
  subscribeAssetTypes,
  subscribeCompanyBrands,
  subscribeCompanyLocations,
  subscribeGlobalReferences,
  type AssetTypeOption
} from "./services/referenceData";
import { createAssetGroup, subscribeAssetGroups, updateAssetGroupMembers } from "./services/assetGroups";
import { createChecklistTemplate, fetchChecklistTemplatesOnce, setChecklistTemplateActive, subscribeChecklistTemplates, updateChecklistTemplate } from "./services/checklists";
import { createAssetInspection, generateInspectionId, queueOfflineInspectionPhoto, subscribeAssetInspections, uploadInspectionPhotos } from "./services/inspections";
import { createAssetDamage, generateDamageId, markAssetDamageEscalatedToMaintenance, queueOfflineDamagePhoto, resolveAssetDamage, subscribeAssetDamages } from "./services/damages";
import { checklistContextForInspection, resolveChecklistTemplatesForAsset, validateChecklistAnswers } from "./domain/checklists";
import {
  defaultSystemSettings,
  saveImageCompressionSettings,
  savePaymentSettings,
  saveSupportAgents,
  saveSupportAssistantSettings,
  subscribeSystemSettings,
  type PaymentSettings,
  type SupportAssistantSettings,
  type SystemSettings
} from "./services/settings";
import {
  claimSupportTicket,
  createSupportKnowledgeEntry,
  createSupportTicket,
  deleteSupportTicket,
  isSupportTicketOpen,
  isSupportTicketUnread,
  markSupportTicketRead,
  rateSupportTicket,
  sendSupportTicketMessage,
  setSupportKnowledgeEntryActive,
  subscribeAllSupportTickets,
  subscribeMySupportTickets,
  subscribeSupportKnowledgeEntries,
  subscribeSupportTicketMessages,
  updateSupportKnowledgeEntry,
  updateSupportTicketStatus
} from "./services/support";
import { saveDepreciationRule, subscribeDepreciationRules } from "./services/depreciationRules";
import { saveAssetAudit, saveAuditorRelease, subscribeAssetAuditHistory, subscribeAuditorReleases, subscribeAuditorUserAccess, type SaveAuditorReleaseInput } from "./services/audit";
import {
  acceptResponsibilityTransfer,
  approveResponsibilityTransfer,
  createResponsibilityTransferRequest,
  directTransferResponsibility,
  rejectResponsibilityTransfer,
  subscribeAssetResponsibilityHistory,
  subscribeResponsibilityTransfers,
  type ResponsibilityHistoryRecord,
  type ResponsibilityTransferRequest
} from "./services/responsibilityTransfers";
import { approveCompanyTransfer, createCompanyTransferRequest, rejectCompanyTransfer, subscribeCompanyTransfers } from "./services/companyTransfers";
import {
  addExternalServiceItem,
  addExternalServiceParticipant,
  releaseExternalServiceItem,
  archiveExternalService,
  createExternalService,
  exportExternalServicePdf,
  markExternalServiceItemReturned,
  markExternalServiceStartedIfPending,
  subscribeCompanyReservations,
  reassignExternalServiceItem,
  resolveExternalServiceItemDivergence,
  restoreExternalService,
  startExternalServiceReturn,
  subscribeExternalServiceEvents,
  subscribeExternalServiceItems,
  subscribeExternalServiceParticipants,
  subscribeExternalServices,
  updateExternalServiceDetails,
  updateExternalServiceItemStatus,
  updateExternalServiceItemNotes,
  updateExternalServiceStatus,
  type ExternalServiceParticipantInput
} from "./services/externalServices";
import { isAssetReturnOverdue, isItemReturnOverdue, periodsOverlap } from "./domain/reservations";
import { createMercadoPagoPreference } from "./services/payments";
import { calculateServiceEstimate, createServiceRequest, releaseServiceManually, saveServiceCatalogItem, sendServiceQuote, subscribeServiceCatalog, subscribeServiceRequests, updateServiceRequestStatus, type ServiceRequestRecord } from "./services/commercial";
import logoImobiFull from "../assets/logo imobi.png";
import {
  seedBranches,
  seedCompanies,
  seedCompany,
  seedAssets,
  seedDepreciationRules,
  seedBrands,
  seedDepartments,
  seedInventoryChecks,
  seedLabelTemplates,
  seedLocations,
  seedResponsibles,
  seedServiceCatalog
} from "./data/seeds";
import { canAccess } from "./domain/permissions";
import type { AppNotification, Asset, AssetAuditRecord, AssetReservation, ExternalServiceRules, AssetDamage, AssetDynamicField, AssetDynamicFieldType, AssetGroup, AssetInspection, AssetMaintenanceRecord, AssetStatusHistoryRecord, AuditValuationMethod, AuditorAccessRelease, ChecklistAnswer, ChecklistAnswerType, ChecklistContext, ChecklistQuestion, ChecklistTemplate, ChecklistTemplateScope, Company, CompanyAccess, CompanyRole, CompanyTransferRequest, ContractTemplate, DamageCategory, GeneratedContract, GeneratedContractStatus, DepreciationMethod, DepreciationRule, ExternalService, ExternalServiceEvent, ExternalServiceItem, ExternalServiceItemStatus, ExternalServiceParticipant, GlobalRole, InspectionContext, InventoryCheck, LabelElement, LabelFieldId, LabelTemplate, ServiceCatalogItem, SupportKnowledgeEntry, SupportMessageAuthorRole, SupportTicket, SupportTicketMessage, SupportTicketStatus, SupportTicketType, TrainingArticle, TrainingArticleStatus, TrainingCategory, UserProfile } from "./types/imobi";

type AccessContext = {
  globalRole: GlobalRole;
  companyRole?: CompanyRole;
  accessibleCompanies: CompanyRecord[];
  scopedCompanies: CompanyRecord[];
  selectedCompany: CompanyRecord | null;
  scopedAssets: AssetRecord[];
  scopedInventoryChecks: InventoryCheck[];
  auditorReleasedCompanyIds: string[];
  summaryLabel: string;
  companyScopeLabel: string;
};

type CompanyFormPayload = {
  id?: string;
  cnpj: string;
  cnpjRaw: string;
  legalName: string;
  tradeName: string;
  city: string;
  state: string;
  zipCode: string;
  phone?: string;
  address: string;
  responsibleName?: string;
  responsibleContact?: string;
  status: Company["status"];
  isAuditorCompany?: boolean;
  logoFile?: File | null;
};

type CsvAssetDraft = ImportAssetInput & {
  rowNumber: number;
  raw: Record<string, string>;
};

type CsvColumn = {
  index: number;
  header: string;
  normalized: string;
};

type CsvParsedRow = {
  rowNumber: number;
  cells: string[];
};

type CsvImportField = keyof Pick<
  ImportAssetInput,
  | "patrimonyNumber"
  | "description"
  | "assetTypeId"
  | "physicalLocation"
  | "department"
  | "responsible"
  | "conservationState"
  | "status"
  | "serialNumber"
  | "brand"
  | "model"
  | "vehiclePlate"
  | "chassis"
  | "renavam"
  | "manufactureYear"
  | "modelYear"
  | "mileageKm"
  | "builtAreaM2"
  | "dimensions"
  | "acquisitionValue"
  | "purchaseDate"
  | "inactiveReason"
>;

type CsvImportMapping = Partial<Record<CsvImportField, number>>;

type CsvAssetIssue = {
  rowNumber: number;
  type: "duplicate" | "missing";
  message: string;
  draft: CsvAssetDraft;
  existingAsset?: AssetRecord;
};

type CsvDuplicateAction = "replace" | "ignore";

type UnsavedChangesPromptProps = {
  title?: string;
  description?: string;
  isSaving?: boolean;
  onSave: () => void;
  onDiscard: () => void;
  onCancel: () => void;
};

const csvImportFieldOptions: Array<{ id: CsvImportField; label: string; required?: boolean }> = [
  { id: "patrimonyNumber", label: "Número de patrimônio", required: true },
  { id: "description", label: "Descrição completa", required: true },
  { id: "assetTypeId", label: "Tipo de imobilizado", required: true },
  { id: "physicalLocation", label: "Local físico", required: true },
  { id: "department", label: "Departamento" },
  { id: "responsible", label: "Responsável" },
  { id: "conservationState", label: "Estado de conservação" },
  { id: "status", label: "Status" },
  { id: "serialNumber", label: "Número de série" },
  { id: "brand", label: "Marca" },
  { id: "model", label: "Modelo" },
  { id: "vehiclePlate", label: "Placa" },
  { id: "chassis", label: "Chassi" },
  { id: "renavam", label: "Renavam" },
  { id: "manufactureYear", label: "Ano de fabricação" },
  { id: "modelYear", label: "Ano modelo" },
  { id: "mileageKm", label: "Quilometragem" },
  { id: "builtAreaM2", label: "Área m²" },
  { id: "dimensions", label: "Dimensões" },
  { id: "acquisitionValue", label: "Valor de aquisição" },
  { id: "purchaseDate", label: "Data de aquisição" },
  { id: "inactiveReason", label: "Motivo de inativação" }
];

const csvImportFieldAliases: Record<CsvImportField, string[]> = {
  patrimonyNumber: ["Patrimônio", "Patrimônio", "Número de patrimônio", "Número de patrimônio", "Código imobilizado", "código", "patrimonyNumber"],
  description: ["Descricao", "Descrição", "Descricao completa", "description"],
  assetTypeId: ["Tipo", "Tipo de imobilizado", "Categoria", "assetTypeId"],
  physicalLocation: ["Local", "Local fisico", "Local físico", "Endereco", "Endereço", "physicalLocation"],
  department: ["Departamento", "Setor", "department"],
  responsible: ["Responsável", "Responsável", "responsible"],
  conservationState: ["Conservação", "Conservação", "Estado de conservação", "Estado de conservação", "conservationState"],
  status: ["Status", "Situacao", "Situação"],
  serialNumber: ["Serie", "Série", "Número de série", "Número de série", "Serial", "serialNumber"],
  brand: ["Marca", "brand"],
  model: ["Modelo", "model"],
  vehiclePlate: ["Placa", "vehiclePlate"],
  chassis: ["Chassi", "chassis"],
  renavam: ["Renavam", "renavam"],
  manufactureYear: ["Ano fabricacao", "Ano fabricação", "Ano de fabricacao", "Ano de fabricação", "manufactureYear"],
  modelYear: ["Ano modelo", "modelYear"],
  mileageKm: ["Quilometragem", "Km", "mileageKm"],
  builtAreaM2: ["Area m2", "Área m²", "M2", "builtAreaM2"],
  dimensions: ["Dimensoes", "Dimensões", "dimensions"],
  acquisitionValue: ["Valor aquisicao", "Valor aquisição", "Valor de aquisicao", "Valor de aquisição", "acquisitionValue"],
  purchaseDate: ["Data aquisicao", "Data aquisição", "Data de aquisicao", "Data de aquisição", "purchaseDate"],
  inactiveReason: ["Motivo inativacao", "Motivo inativação", "Motivo de inativacao", "Motivo de inativação", "inactiveReason"]
};

const defaultAccessProfile: UserAccessProfile = {
  globalRole: "USER",
  accessibleCompanyIds: [],
  manageableCompanyIds: [],
  auditorCompanyIds: [],
  companyRoles: {},
  companyPermissions: {}
};

const brazilStates = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO"
];

function onlyDigits(value: string) {
  return value.replace(/\D/g, "");
}

function normalizeCnpj(value: string) {
  return value
    .toLocaleUpperCase("pt-BR")
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 14);
}

function formatCnpjInput(value: string) {
  const chars = normalizeCnpj(value);

  if (chars.length <= 2) {
    return chars;
  }

  if (chars.length <= 5) {
    return `${chars.slice(0, 2)}.${chars.slice(2)}`;
  }

  if (chars.length <= 8) {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  }

  if (chars.length <= 12) {
    return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8)}`;
  }

  return `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8, 12)}-${chars.slice(12)}`;
}

function formatPhoneInput(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 6) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  }

  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }

  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function formatCpfInput(value: string) {
  const digits = onlyDigits(value).slice(0, 11);

  if (digits.length <= 3) {
    return digits;
  }

  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}.${digits.slice(3)}`;
  }

  if (digits.length <= 9) {
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

function formatDateInput(value: string) {
  const digits = onlyDigits(value).slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function formatZipCodeInput(value: string) {
  const digits = onlyDigits(value).slice(0, 8);

  if (digits.length <= 2) {
    return digits;
  }

  if (digits.length <= 5) {
    return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  }

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}-${digits.slice(5)}`;
}

function forceUppercaseInput(event: ChangeEvent<HTMLInputElement>) {
  event.currentTarget.value = event.currentTarget.value.toLocaleUpperCase("pt-BR");
}

function isValidCnpj(value: string) {
  const cnpj = normalizeCnpj(value);
  return cnpj.length === 14 && !/^([A-Z0-9])\1+$/.test(cnpj);
}

function formatAuditDate(value?: string) {
  if (!value) {
    return "-";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short"
  }).format(new Date(value));
}

function formatDateOnly(value?: string) {
  if (!value) {
    return "-";
  }

  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" }).format(date);
}

function formatAssetAge(value?: string) {
  if (!value) {
    return "-";
  }

  const start = new Date(`${value}T00:00:00`);
  const today = new Date();
  if (Number.isNaN(start.getTime()) || start > today) {
    return "-";
  }

  let years = today.getFullYear() - start.getFullYear();
  let months = today.getMonth() - start.getMonth();
  if (today.getDate() < start.getDate()) {
    months -= 1;
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const parts = [];
  if (years > 0) {
    parts.push(`${years} ano${years === 1 ? "" : "s"}`);
  }
  parts.push(`${months} ${months === 1 ? "mês" : "meses"}`);
  return parts.join(" e ");
}

function parseCurrencyNumber(value: string) {
  const cleaned = value.replace(/[^\d,.-]/g, "").trim();
  const normalized = cleaned.includes(",")
    ? cleaned.replace(/\./g, "").replace(",", ".")
    : cleaned;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeCsvHeader(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function parseCsvLine(line: string, delimiter: string) {
  const cells: string[] = [];
  let current = "";
  let insideQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const next = line[index + 1];
    if (char === "\"" && next === "\"") {
      current += "\"";
      index += 1;
      continue;
    }
    if (char === "\"") {
      insideQuotes = !insideQuotes;
      continue;
    }
    if (char === delimiter && !insideQuotes) {
      cells.push(current.trim());
      current = "";
      continue;
    }
    current += char;
  }

  cells.push(current.trim());
  return cells;
}

function parseCsvText(text: string) {
  const parsed = parseCsvTextDetailed(text);
  return parsed.rows.map((row) => {
    return parsed.columns.reduce<Record<string, string>>((record, column) => {
      record[column.normalized] = row.cells[column.index] ?? "";
      return record;
    }, { __rowNumber: String(row.rowNumber) });
  });
}

function parseCsvTextDetailed(text: string): { columns: CsvColumn[]; rows: CsvParsedRow[] } {
  const normalizedText = text.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const lines = normalizedText.split("\n").filter((line) => line.trim());
  if (lines.length < 2) {
    return { columns: [], rows: [] };
  }

  const delimiter = (lines[0].match(/;/g)?.length ?? 0) >= (lines[0].match(/,/g)?.length ?? 0) ? ";" : ",";
  const headers = parseCsvLine(lines[0], delimiter);
  const columns = headers.map((header, index) => ({ index, header: header || `Coluna ${index + 1}`, normalized: normalizeCsvHeader(header) }));
  const rows = lines.slice(1).map((line, index) => {
    const cells = parseCsvLine(line, delimiter);
    return { rowNumber: index + 2, cells };
  });
  return { columns, rows };
}

function pickCsvValue(row: Record<string, string>, aliases: string[]) {
  for (const alias of aliases) {
    const value = row[normalizeCsvHeader(alias)];
    if (value) {
      return value.trim();
    }
  }
  return "";
}

function parseCsvNumber(value: string) {
  const parsed = parseCurrencyNumber(value.replace(/\s*km$/i, ""));
  return typeof parsed === "number" ? parsed : undefined;
}

function parseCsvDate(value: string) {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "-") {
    return "";
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  const match = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) {
    return "";
  }
  return `${match[3]}-${match[2].padStart(2, "0")}-${match[1].padStart(2, "0")}`;
}

function normalizeAssetStatus(value: string): Asset["status"] {
  const normalized = normalizeCsvHeader(value);
  if (["inativo", "baixado", "vendido", "perdido"].includes(normalized)) {
    return normalized as Asset["status"];
  }
  if (normalized === "emmanutenção") {
    return "em_manutencao";
  }
  if (normalized === "emauditoria") {
    return "em_auditoria";
  }
  return "ativo";
}

function normalizeConservationState(value: string): Asset["conservationState"] {
  const normalized = normalizeCsvHeader(value);
  if (["novo", "otimo", "bom", "regular", "ruim", "sucata"].includes(normalized)) {
    return normalized as Asset["conservationState"];
  }
  return "bom";
}

function csvRowToAssetDraft(row: Record<string, string>): CsvAssetDraft {
  const rowNumber = Number(row.__rowNumber) || 0;
  return {
    rowNumber,
    raw: row,
    patrimonyNumber: pickCsvValue(row, ["Patrimônio", "Patrimônio", "Número de patrimônio", "Número de patrimônio", "patrimonyNumber"]),
    description: pickCsvValue(row, ["Descrição", "Descricao", "description"]),
    assetTypeId: pickCsvValue(row, ["Tipo", "Tipo de imobilizado", "assetTypeId"]),
    physicalLocation: pickCsvValue(row, ["Local", "Local físico", "Local fisico", "physicalLocation"]),
    department: pickCsvValue(row, ["Departamento", "department"]),
    responsible: pickCsvValue(row, ["Responsável", "Responsável", "responsible"]),
    conservationState: normalizeConservationState(pickCsvValue(row, ["Conservação", "Conservação", "conservationState"])),
    status: normalizeAssetStatus(pickCsvValue(row, ["Status"])),
    serialNumber: pickCsvValue(row, ["Número de série", "Número de série", "Serial", "serialNumber"]),
    brand: pickCsvValue(row, ["Marca", "brand"]),
    model: pickCsvValue(row, ["Modelo", "model"]),
    vehiclePlate: pickCsvValue(row, ["Placa", "vehiclePlate"]),
    chassis: pickCsvValue(row, ["Chassi", "chassis"]),
    renavam: pickCsvValue(row, ["Renavam", "renavam"]),
    manufactureYear: parseCsvNumber(pickCsvValue(row, ["Ano fabricação", "Ano fabricacao", "manufactureYear"])),
    modelYear: parseCsvNumber(pickCsvValue(row, ["Ano modelo", "modelYear"])),
    mileageKm: parseCsvNumber(pickCsvValue(row, ["Quilometragem", "mileageKm"])),
    builtAreaM2: parseCsvNumber(pickCsvValue(row, ["Área m²", "Area m2", "M2", "builtAreaM2"])),
    dimensions: pickCsvValue(row, ["Dimensões", "Dimensoes", "dimensions"]),
    acquisitionValue: parseCsvNumber(pickCsvValue(row, ["Valor de aquisição", "Valor de aquisicao", "acquisitionValue"])),
    purchaseDate: parseCsvDate(pickCsvValue(row, ["Data de aquisição", "Data de aquisicao", "purchaseDate"])),
    inactiveReason: pickCsvValue(row, ["Motivo de inativação", "Motivo de inativacao", "inactiveReason"]),
    customFields: {}
  };
}

function inferCsvImportMapping(columns: CsvColumn[]): CsvImportMapping {
  return csvImportFieldOptions.reduce<CsvImportMapping>((mapping, field) => {
    const aliases = csvImportFieldAliases[field.id].map(normalizeCsvHeader);
    const exactMatch = columns.find((column) => aliases.includes(column.normalized));
    const partialMatch = exactMatch ?? columns.find((column) => aliases.some((alias) => column.normalized.includes(alias) || alias.includes(column.normalized)));
    if (partialMatch) {
      mapping[field.id] = partialMatch.index;
    }
    return mapping;
  }, {});
}

function getCsvMappedValue(row: CsvParsedRow, mapping: CsvImportMapping, field: CsvImportField) {
  const columnIndex = mapping[field];
  if (typeof columnIndex !== "number" || columnIndex < 0) {
    return "";
  }

  return row.cells[columnIndex]?.trim() ?? "";
}

function csvRowsToAssetDrafts(rows: CsvParsedRow[], mapping: CsvImportMapping): CsvAssetDraft[] {
  return rows.map((row) => {
    const raw = Object.fromEntries(Object.entries(mapping).map(([field, columnIndex]) => [field, typeof columnIndex === "number" ? row.cells[columnIndex] ?? "" : ""]));
    return {
      rowNumber: row.rowNumber,
      raw,
      patrimonyNumber: getCsvMappedValue(row, mapping, "patrimonyNumber"),
      description: getCsvMappedValue(row, mapping, "description"),
      assetTypeId: getCsvMappedValue(row, mapping, "assetTypeId"),
      physicalLocation: getCsvMappedValue(row, mapping, "physicalLocation"),
      department: getCsvMappedValue(row, mapping, "department"),
      responsible: getCsvMappedValue(row, mapping, "responsible"),
      conservationState: normalizeConservationState(getCsvMappedValue(row, mapping, "conservationState")),
      status: normalizeAssetStatus(getCsvMappedValue(row, mapping, "status")),
      serialNumber: getCsvMappedValue(row, mapping, "serialNumber"),
      brand: getCsvMappedValue(row, mapping, "brand"),
      model: getCsvMappedValue(row, mapping, "model"),
      vehiclePlate: getCsvMappedValue(row, mapping, "vehiclePlate"),
      chassis: getCsvMappedValue(row, mapping, "chassis"),
      renavam: getCsvMappedValue(row, mapping, "renavam"),
      manufactureYear: parseCsvNumber(getCsvMappedValue(row, mapping, "manufactureYear")),
      modelYear: parseCsvNumber(getCsvMappedValue(row, mapping, "modelYear")),
      mileageKm: parseCsvNumber(getCsvMappedValue(row, mapping, "mileageKm")),
      builtAreaM2: parseCsvNumber(getCsvMappedValue(row, mapping, "builtAreaM2")),
      dimensions: getCsvMappedValue(row, mapping, "dimensions"),
      acquisitionValue: parseCsvNumber(getCsvMappedValue(row, mapping, "acquisitionValue")),
      purchaseDate: parseCsvDate(getCsvMappedValue(row, mapping, "purchaseDate")),
      inactiveReason: getCsvMappedValue(row, mapping, "inactiveReason"),
      customFields: {}
    };
  });
}

function validateCsvAssetDrafts(drafts: CsvAssetDraft[], existingAssets: AssetRecord[], companyId: string): CsvAssetIssue[] {
  const existingByPatrimony = new Map(existingAssets
    .filter((asset) => asset.companyId === companyId)
    .map((asset) => [asset.patrimonyNumber.trim().toLowerCase(), asset])
  );
  const seenRows = new Map<string, number>();
  const issues: CsvAssetIssue[] = [];

  drafts.forEach((draft) => {
    const normalizedPatrimony = draft.patrimonyNumber.trim().toLowerCase();
    if (!draft.patrimonyNumber.trim()) {
      issues.push({ rowNumber: draft.rowNumber, type: "missing", message: "Número de patrimônio ausente.", draft });
    } else if (existingByPatrimony.has(normalizedPatrimony)) {
      issues.push({ rowNumber: draft.rowNumber, type: "duplicate", message: "Número de patrimônio ja cadastrado no sistema.", draft, existingAsset: existingByPatrimony.get(normalizedPatrimony) });
    } else if (seenRows.has(normalizedPatrimony)) {
      issues.push({ rowNumber: draft.rowNumber, type: "duplicate", message: `Numero repetido no proprio CSV. Primeira ocorrencia na linha ${seenRows.get(normalizedPatrimony)}.`, draft });
    }

    seenRows.set(normalizedPatrimony, draft.rowNumber);

    if (!draft.description.trim()) {
      issues.push({ rowNumber: draft.rowNumber, type: "missing", message: "Descricao obrigatoria ausente.", draft });
    }
    if (!draft.assetTypeId.trim()) {
      issues.push({ rowNumber: draft.rowNumber, type: "missing", message: "Tipo de imobilizado ausente.", draft });
    }
    if (!draft.physicalLocation.trim()) {
      issues.push({ rowNumber: draft.rowNumber, type: "missing", message: "Local fisico ausente.", draft });
    }
  });

  return issues;
}

function formatCurrencyMask(value: string) {
  const digits = value.replace(/\D/g, "");
  if (!digits) {
    return "";
  }

  return formatCurrency(Number(digits) / 100);
}

function matchesCompanySearch(company: Company & { cnpjRaw?: string }, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  const rawQuery = normalizeCnpj(query);

  if (!normalizedQuery && !rawQuery) {
    return true;
  }

  const searchableText = [company.legalName, company.tradeName, company.cnpj, company.city, company.state]
    .join(" ")
    .toLowerCase();
  const rawCnpj = company.cnpjRaw || normalizeCnpj(company.cnpj);

  return searchableText.includes(normalizedQuery) || (rawQuery.length > 0 && rawCnpj.includes(rawQuery));
}

function isSelectableCompany(company: Company) {
  return company.status === "ativa";
}

function isTrialActive(trialUntil?: string) {
  if (!trialUntil) {
    return false;
  }

  const expiresAt = new Date(`${trialUntil}T23:59:59`);
  return Number.isFinite(expiresAt.getTime()) && expiresAt >= new Date();
}

function isCompanyPlanCurrentlyActive(company: CompanyRecord | null | undefined) {
  if (!company) {
    return false;
  }

  if (company.settings?.trialUntil) {
    return isTrialActive(company.settings.trialUntil);
  }

  return Boolean(company.settings?.activePlanId);
}

function hasUsableCompanyPlan(company: CompanyRecord | null | undefined, requests: ServiceRequestRecord[]) {
  if (!company) {
    return false;
  }

  if (isCompanyPlanCurrentlyActive(company)) {
    return true;
  }

  if (company.settings?.trialUntil && !isTrialActive(company.settings.trialUntil)) {
    return false;
  }

  return requests.some((request) => (
    request.companyId === company.id
    && request.serviceCategory === "licenca_mensal"
    && ["aprovado", "liberado", "em_execucao", "concluido"].includes(request.status)
  ));
}

function daysUntilDate(dateValue?: string) {
  if (!dateValue) {
    return null;
  }

  const target = new Date(`${dateValue}T23:59:59`);
  if (!Number.isFinite(target.getTime())) {
    return null;
  }

  return Math.ceil((target.getTime() - Date.now()) / 86_400_000);
}

function App() {
  const [activeView, setActiveView] = useState<ViewId>("dashboard");
  const [companies, setCompanies] = useState<CompanyRecord[]>(seedCompanies);
  const [assets, setAssets] = useState<AssetRecord[]>([]);
  const [accessProfile, setAccessProfile] = useState(defaultAccessProfile);
  const [isAccessLoading, setIsAccessLoading] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [assetDetailBackView, setAssetDetailBackView] = useState<ViewId>("assets");
  const [updateRegistration, setUpdateRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [systemSettings, setSystemSettings] = useState<SystemSettings>(defaultSystemSettings);
  const [depreciationRules, setDepreciationRules] = useState<DepreciationRule[]>(seedDepreciationRules);
  const [auditorReleasedCompanyIds, setAuditorReleasedCompanyIds] = useState<string[]>([]);
  const [auditorReleases, setAuditorReleases] = useState<AuditorAccessRelease[]>([]);
  const [companyTransfers, setCompanyTransfers] = useState<CompanyTransferRequest[]>([]);
  const [externalServices, setExternalServices] = useState<ExternalService[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [pendingNotificationServiceId, setPendingNotificationServiceId] = useState<string | null>(null);
  const [trainingCategories, setTrainingCategories] = useState<TrainingCategory[]>([]);
  const [trainingArticles, setTrainingArticles] = useState<TrainingArticle[]>([]);
  const [pendingTrainingArticleId, setPendingTrainingArticleId] = useState<string | null>(null);
  const [mySupportTickets, setMySupportTickets] = useState<SupportTicket[]>([]);
  const [allSupportTickets, setAllSupportTickets] = useState<SupportTicket[]>([]);
  const [supportKnowledgeEntries, setSupportKnowledgeEntries] = useState<SupportKnowledgeEntry[]>([]);
  const [serviceCatalog, setServiceCatalog] = useState<ServiceCatalogItem[]>(seedServiceCatalog);
  const [serviceRequests, setServiceRequests] = useState<ServiceRequestRecord[]>([]);
  const [currentUserProfile, setCurrentUserProfile] = useState<UserProfile | null>(null);
  const [commercialUsers, setCommercialUsers] = useState<ManagedUser[]>([]);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [profileLoadError, setProfileLoadError] = useState("");
  const { user, loading } = useAuth();
  const accessContext = useMemo(
    () => buildAccessContext(accessProfile, selectedCompanyId, companies, assets, auditorReleasedCompanyIds),
    [accessProfile, assets, auditorReleasedCompanyIds, companies, selectedCompanyId]
  );
  const selectableCompanies = useMemo(
    () => accessContext.accessibleCompanies.filter(isSelectableCompany),
    [accessContext.accessibleCompanies]
  );
  const selectedAsset = selectedAssetId ? accessContext.scopedAssets.find((asset) => asset.id === selectedAssetId) ?? null : null;
  const assetCompanyIds = useMemo(
    () => companies
      .filter((company) => accessProfile.globalRole === "MASTER" || accessProfile.globalRole === "ADMIN" || accessProfile.accessibleCompanyIds.includes(company.id) || auditorReleasedCompanyIds.includes(company.id))
      .map((company) => company.id),
    [accessProfile, auditorReleasedCompanyIds, companies]
  );
  const canSeeAuditMenu = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || accessContext.companyRole === "AUDITOR"
    || accessContext.auditorReleasedCompanyIds.length > 0;
  const canSeeCompanyTransferMenu = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || Object.values(accessProfile.companyRoles).includes("GERENTE");
  const canSeeCompaniesMenu = accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN";
  const isGlobalAdmin = accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN";
  const selectedCompanyNeedsPlan = Boolean(accessContext.selectedCompany && !isGlobalAdmin && !hasUsableCompanyPlan(accessContext.selectedCompany, serviceRequests));
  const canManageSelectedCompanyPlan = isGlobalAdmin || accessContext.companyRole === "GERENTE";
  const shouldBlockCurrentCompanyByPlan = selectedCompanyNeedsPlan && !canManageSelectedCompanyPlan;
  const canSeeServicesMenu = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || Object.values(accessProfile.companyRoles).includes("GERENTE");
  const selectedCompanyExternalServicesEnabled = Boolean(accessContext.selectedCompany?.settings?.externalServicesEnabled);
  const selectedCompanyAllowsExternalServicesByPlan = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || hasUsableCompanyPlan(accessContext.selectedCompany, serviceRequests);
  const canSeeExternalServicesMenu = Boolean(accessContext.selectedCompany)
    && selectedCompanyExternalServicesEnabled
    && selectedCompanyAllowsExternalServicesByPlan
    && canAccess(
      accessContext.selectedCompany ? accessProfile.companyPermissions[accessContext.selectedCompany.id] : undefined,
      "canViewExternalServices",
      accessContext.globalRole
    );
  const isTrainingManager = accessContext.globalRole === "MASTER";
  const canSeeTrainingMenu = isTrainingManager || Boolean(accessContext.selectedCompany?.settings?.trainingEnabled);
  const isSupportAgent = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || systemSettings.supportAgentUserIds.includes(user?.uid ?? "");
  const publishedTrainingArticles = useMemo(
    () => trainingArticles.filter((article) => article.status === "publicado"),
    [trainingArticles]
  );
  const contextualTrainingArticles = useMemo(
    () => canSeeTrainingMenu ? publishedTrainingArticles.filter((article) => article.helpViewIds.includes(activeView)) : [],
    [activeView, canSeeTrainingMenu, publishedTrainingArticles]
  );
  const [isHelpPickerOpen, setIsHelpPickerOpen] = useState(false);
  const [isSupportWidgetOpen, setIsSupportWidgetOpen] = useState(false);
  const [pendingSupportTab, setPendingSupportTab] = useState<SupportPageTab | null>(null);
  // Replies from the team, tickets waiting for a rating and (for agents) new user messages.
  const supportUnreadCount = mySupportTickets.filter((ticket) => isSupportTicketUnread(ticket, "reporter") || (ticket.status === "resolvido" && !ticket.rating)).length
    + (isSupportAgent ? allSupportTickets.filter((ticket) => isSupportTicketUnread(ticket, "agent")).length : 0);

  function handleOpenContextualHelp() {
    if (contextualTrainingArticles.length === 1) {
      setPendingTrainingArticleId(contextualTrainingArticles[0].id);
      setActiveView("training");
      return;
    }
    setIsHelpPickerOpen(true);
  }

  const pendingTransferCount = companyTransfers.filter((transfer) => transfer.status === "aguardando_master").length;
  const totals = useMemo(() => {
    const acquisition = accessContext.scopedAssets.reduce((sum, asset) => sum + (asset.acquisitionValue ?? 0), 0);
    const book = accessContext.scopedAssets.reduce((sum, asset) => sum + calculateLinearMonthlyDepreciation(asset, findDepreciationRuleForAsset(asset, depreciationRules)).currentBookValue, 0);
    const audited = accessContext.scopedAssets.filter((asset) => asset.auditStatus === "auditado").length;

    return {
      activeAssets: accessContext.scopedAssets.filter((asset) => asset.status === "ativo").length,
      acquisition,
      book,
      auditedPercent: accessContext.scopedAssets.length ? Math.round((audited / accessContext.scopedAssets.length) * 100) : 0
    };
  }, [accessContext.scopedAssets, depreciationRules]);

  useEffect(() => {
    if (selectedAssetId && !accessContext.scopedAssets.some((asset) => asset.id === selectedAssetId)) {
      setSelectedAssetId(null);
    }
  }, [accessContext.scopedAssets, selectedAssetId]);

  useEffect(() => {
    function handleUpdateAvailable(event: Event) {
      const updateEvent = event as CustomEvent<ServiceWorkerRegistration>;
      setUpdateRegistration(updateEvent.detail);
    }

    window.addEventListener(PWA_UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
    return () => window.removeEventListener(PWA_UPDATE_AVAILABLE_EVENT, handleUpdateAvailable);
  }, []);

  useEffect(() => {
    if (!user) {
      return () => undefined;
    }

    void processPendingSync(user);
    const unsubscribeOnlineStatus = subscribeOnlineStatus((online) => {
      if (online) {
        void processPendingSync(user);
      }
    });
    return unsubscribeOnlineStatus;
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCurrentUserProfile(null);
      setIsProfileLoading(false);
      setProfileLoadError("");
      setAuditorReleasedCompanyIds([]);
      return () => undefined;
    }

    let unsubscribe: (() => void) | undefined;
    let disposed = false;
    setIsProfileLoading(true);
    setProfileLoadError("");

    void ensureCurrentUserProfile(user)
      .then(() => {
        if (disposed) {
          return;
        }

        unsubscribe = subscribeCurrentUserProfile(
          user.uid,
          (profile) => {
            setCurrentUserProfile(profile);
            setProfileLoadError(profile ? "" : "Cadastro do usuário não encontrado.");
            setIsProfileLoading(false);
          },
          (message) => {
            console.warn("Não foi possível carregar o cadastro do usuário.", message);
            setProfileLoadError(message || "Não foi possível carregar o cadastro do usuário.");
            setIsProfileLoading(false);
          }
        );
      })
      .catch((error) => {
        console.warn("Não foi possível garantir o cadastro inicial do usuário.", error);
        setProfileLoadError(error instanceof Error ? error.message : "Não foi possível garantir o cadastro inicial do usuário.");
        setIsProfileLoading(false);
      });

    return () => {
      disposed = true;
      unsubscribe?.();
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setAuditorReleasedCompanyIds([]);
      return () => undefined;
    }

    return subscribeAuditorUserAccess(
      user.uid,
      setAuditorReleasedCompanyIds,
      (message) => console.warn("Não foi possível carregar liberacoes de auditoria.", message)
    );
  }, [user]);

  useEffect(() => subscribeAuditorReleases(
    setAuditorReleases,
    (message) => console.warn("Não foi possível carregar empresas liberadas para auditoria.", message)
  ), []);

  useEffect(() => {
    if (!user || !accessContext.scopedCompanies.length) {
      setCompanyTransfers([]);
      return () => undefined;
    }

    return subscribeCompanyTransfers(
      accessContext.scopedCompanies.map((company) => company.id),
      setCompanyTransfers,
      (message) => console.warn("Não foi possível carregar transferencias entre empresas.", message)
    );
  }, [accessContext.scopedCompanies, user]);

  useEffect(() => {
    if (!user || !accessContext.selectedCompany || !accessContext.selectedCompany.settings?.externalServicesEnabled) {
      setExternalServices([]);
      return () => undefined;
    }

    return subscribeExternalServices(
      accessContext.selectedCompany.id,
      setExternalServices,
      (message) => {
        console.warn("Não foi possível carregar serviços externos.", message);
        setExternalServices([]);
      }
    );
  }, [accessContext.selectedCompany, user]);

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      return () => undefined;
    }

    return subscribeMyNotifications(
      accessContext.scopedCompanies.map((company) => company.id),
      user.uid,
      setNotifications,
      (message) => console.warn("Não foi possível carregar notificações.", message)
    );
  }, [accessContext.scopedCompanies, user]);

  useEffect(() => {
    if (!user || !isPushConfigured() || typeof Notification === "undefined" || Notification.permission !== "default") {
      return;
    }

    void registerPushNotifications(user).catch((error) => {
      console.warn("Não foi possível solicitar permissão de notificações automaticamente.", error);
    });
  }, [user]);

  useEffect(() => {
    if (!user) {
      setTrainingCategories([]);
      setTrainingArticles([]);
      return () => undefined;
    }

    const unsubscribeCategories = subscribeTrainingCategories(
      setTrainingCategories,
      (message) => console.warn("Não foi possível carregar as categorias de treinamento.", message)
    );
    const unsubscribeArticles = subscribeTrainingArticles(
      setTrainingArticles,
      (message) => console.warn("Não foi possível carregar os treinamentos.", message)
    );
    return () => {
      unsubscribeCategories();
      unsubscribeArticles();
    };
  }, [user]);

  useEffect(() => {
    if (!user) {
      setMySupportTickets([]);
      return () => undefined;
    }

    return subscribeMySupportTickets(
      user.uid,
      setMySupportTickets,
      (message) => console.warn("Não foi possível carregar seus chamados de suporte.", message)
    );
  }, [user]);

  useEffect(() => {
    const isSupportStaff = accessContext.globalRole === "MASTER"
      || accessContext.globalRole === "ADMIN"
      || systemSettings.supportAgentUserIds.includes(user?.uid ?? "");

    if (!user || !isSupportStaff) {
      setAllSupportTickets([]);
      return () => undefined;
    }

    return subscribeAllSupportTickets(
      setAllSupportTickets,
      (message) => console.warn("Não foi possível carregar os chamados de suporte.", message)
    );
  }, [user, accessContext.globalRole, systemSettings.supportAgentUserIds]);

  useEffect(() => {
    if (!user) {
      setSupportKnowledgeEntries([]);
      return () => undefined;
    }

    return subscribeSupportKnowledgeEntries(
      setSupportKnowledgeEntries,
      (message) => console.warn("Não foi possível carregar a base de conhecimento da IA.", message)
    );
  }, [user]);

  async function handleMarkNotificationRead(notification: AppNotification) {
    if (!user) {
      return;
    }

    try {
      await markNotificationRead(notification.companyId, notification.id, user);
    } catch (error) {
      console.warn("Não foi possível marcar a notificação como lida.", error);
    }
  }

  function handleOpenNotification(notification: AppNotification) {
    if (notification.externalServiceId) {
      setSelectedCompanyId(notification.companyId);
      setPendingNotificationServiceId(notification.externalServiceId);
      setActiveView("external-services");
    }
  }

  useEffect(() => {
    if (!user) {
      setServiceCatalog(seedServiceCatalog);
      return () => undefined;
    }

    return subscribeServiceCatalog(
      (items) => {
        const merged = [...items];
        seedServiceCatalog.forEach((seed) => {
          if (!merged.some((item) => item.id === seed.id)) {
            merged.push(seed);
          }
        });
        setServiceCatalog(merged.length ? merged : seedServiceCatalog);
      },
      (message) => {
        console.warn("Não foi possível carregar catalogo de servicos.", message);
        setServiceCatalog(seedServiceCatalog);
      }
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setServiceRequests([]);
      return () => undefined;
    }

    return subscribeServiceRequests(
      { userId: user.uid, admin: accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN" },
      setServiceRequests,
      (message) => {
        console.warn("Não foi possível carregar solicitacoes de servico.", message);
        setServiceRequests([]);
      }
    );
  }, [accessContext.globalRole, user]);

  useEffect(() => {
    if (!user) {
      setSystemSettings(defaultSystemSettings);
      return () => undefined;
    }

    return subscribeSystemSettings(
      setSystemSettings,
      (message) => console.warn("Não foi possível carregar configuracoes do sistema.", message)
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setDepreciationRules(seedDepreciationRules);
      return () => undefined;
    }

    return subscribeDepreciationRules(
      (rules) => setDepreciationRules(rules.length ? rules : seedDepreciationRules),
      (message) => {
        console.warn("Não foi possível carregar regras de depreciação.", message);
        setDepreciationRules(seedDepreciationRules);
      }
    );
  }, [user]);

  useEffect(() => {
    if (!user) {
      setCompanies(seedCompanies);
      return () => undefined;
    }

    if (isAccessLoading) {
      setCompanies([]);
      return () => undefined;
    }

    return subscribeCompanies(
      {
        globalRole: accessProfile.globalRole,
        companyIds: Array.from(new Set([...accessProfile.accessibleCompanyIds, ...auditorReleasedCompanyIds]))
      },
      (firebaseCompanies) => {
        const canSeeAllCompanies = accessProfile.globalRole === "MASTER" || accessProfile.globalRole === "ADMIN";
        setCompanies(firebaseCompanies.length ? firebaseCompanies : canSeeAllCompanies ? seedCompanies : []);
      },
      (message) => {
        console.warn("Não foi possível carregar empresas do Firebase.", message);
        setCompanies([]);
      }
    );
  }, [accessProfile.accessibleCompanyIds, accessProfile.globalRole, auditorReleasedCompanyIds, isAccessLoading, user]);
useEffect(() => {
    if (!user || !assetCompanyIds.length) {
      setAssets([]);
      return () => undefined;
    }

    return subscribeAssets(
      assetCompanyIds,
      setAssets,
      (message) => {
        console.warn("Não foi possivel carregar bens do Firebase.", message);
        setAssets([]);
      }
    );
  }, [assetCompanyIds, user]);

  useEffect(() => {
    if (!user) {
      setAccessProfile(defaultAccessProfile);
      setIsAccessLoading(false);
      return () => undefined;
    }

    setIsAccessLoading(true);

    return subscribeUserAccessProfile(
      user.uid,
      (profile) => {
        setAccessProfile(profile);
        setIsAccessLoading(false);
      },
      (message) => {
        console.warn("Não foi possível carregar o perfil do usuário.", message);
        setIsAccessLoading(false);
      }
    );
  }, [user]);

  useEffect(() => {
    const canLoadManagedUsers = accessProfile.globalRole === "MASTER"
      || accessProfile.globalRole === "ADMIN"
      || accessProfile.manageableCompanyIds.length > 0;

    if (!user || !canLoadManagedUsers) {
      setCommercialUsers([]);
      return () => undefined;
    }

    return subscribeManagedUsers(
      accessProfile,
      setCommercialUsers,
      (message) => {
        console.warn("Não foi possível carregar usuários para faturamento.", message);
        setCommercialUsers([]);
      }
    );
  }, [accessProfile, user]);

  useEffect(() => {
    if (!user) {
      setSelectedCompanyId(null);
      return;
    }

    const accessibleCompanies = companies.filter((company) => (
      (accessProfile.globalRole === "MASTER" || accessProfile.globalRole === "ADMIN" || accessProfile.accessibleCompanyIds.includes(company.id) || auditorReleasedCompanyIds.includes(company.id))
      && isSelectableCompany(company)
    ));
    setSelectedCompanyId(accessibleCompanies.length === 1 ? accessibleCompanies[0].id : null);
  }, [accessProfile, auditorReleasedCompanyIds, companies, user]);

  useEffect(() => {
    if (selectedCompanyId && !selectableCompanies.some((company) => company.id === selectedCompanyId)) {
      setSelectedCompanyId(null);
    }
  }, [selectableCompanies, selectedCompanyId]);

  useEffect(() => {
    if (selectedCompanyNeedsPlan && canManageSelectedCompanyPlan && activeView !== "service-request") {
      setActiveView("service-request");
    }
  }, [activeView, canManageSelectedCompanyPlan, selectedCompanyNeedsPlan]);

  async function handleSaveCompany(payload: CompanyFormPayload) {
    if (!user) {
      throw new Error("Usuário autenticado não encontrado.");
    }

    const saveInput: SaveCompanyInput = {
      ...payload,
      cnpj: formatCnpjInput(payload.cnpj),
      cnpjRaw: payload.cnpjRaw
    };

    const savedCompanyId = await saveCompany(saveInput, user);

    setCompanies((currentCompanies) => {
      const currentCompany = currentCompanies.find((company) => company.id === savedCompanyId);

      if (!currentCompany) {
        return currentCompanies;
      }

      return currentCompanies.map((company) => company.id === savedCompanyId ? { ...company, ...payload, cnpj: saveInput.cnpj } : company);
    });
  }

  async function handleSaveUser(payload: SaveUserInput, previousAssignments: CompanyAccess[]) {
    if (!user) {
      throw new Error("Usuário autenticado não encontrado.");
    }

    const isUserCountedInCompanyLimit = payload.globalRole !== "MASTER" && payload.globalRole !== "ADMIN";
    if (isUserCountedInCompanyLimit) {
      const activeAssignments = payload.assignments.filter((assignment) => assignment.status === "ativo");
      activeAssignments.forEach((assignment) => {
        const company = companies.find((item) => item.id === assignment.companyId);
        const plan = company?.settings?.activePlanId ? serviceCatalog.find((item) => item.id === company.settings?.activePlanId) : undefined;
        const usersLimit = company?.settings?.maxUsers || plan?.maxUsers || 0;

        if (!usersLimit) {
          return;
        }

        const activeUsersCount = commercialUsers.filter((profile) => (
          profile.id !== payload.id
          && profile.globalRole !== "MASTER"
          && profile.globalRole !== "ADMIN"
          && profile.companyAccess.some((access) => access.companyId === assignment.companyId && access.status === "ativo")
        )).length;

        if (activeUsersCount + 1 > usersLimit) {
          throw new Error(`A empresa ${company?.tradeName ?? assignment.companyId} atingiu o limite de ${usersLimit} usuário(s) do plano atual.`);
        }
      });
    }

    await saveUserProfile(payload, user, accessProfile, previousAssignments);
  }

  const updatePrompt = updateRegistration ? (
    <UpdateAvailablePrompt
      onDismiss={() => setUpdateRegistration(null)}
      onUpdate={() => applyServiceWorkerUpdate(updateRegistration)}
    />
  ) : null;

  const renderView = () => {
    switch (activeView) {
      case "login":
        return <LoginPage onEnter={() => setActiveView("dashboard")} />;
      case "company-select":
        return (
          <CompanySelectPage
            companies={selectableCompanies}
            selectedCompanyId={selectedCompanyId}
            onSelect={setSelectedCompanyId}
            onEnter={() => setActiveView("dashboard")}
          />
        );
      case "assets":
        return (
          <AssetsPage
            accessContext={accessContext}
            accessProfile={accessProfile}
            currentUser={user}
            onTransferResponsibles={() => setActiveView("responsibles")}
            onCreate={() => setActiveView("asset-form")}
            onOpen={(asset) => {
              setSelectedAssetId(asset.id);
              setAssetDetailBackView("assets");
              setActiveView("asset-detail");
            }}
          />
        );
      case "asset-form":
        return (
          <AssetFormPage
            accessContext={accessContext}
            currentUser={user}
            imageCompression={systemSettings.imageCompression}
            onSaved={(assetId, shouldGenerateLabel) => {
              setSelectedAssetId(assetId);
              setActiveView(shouldGenerateLabel ? "label-print" : "assets");
            }}
            onBack={() => setActiveView("assets")}
          />
        );
      case "asset-detail":
        return selectedAsset ? (
          <AssetDetailPage
            asset={selectedAsset}
            accessContext={accessContext}
            accessProfile={accessProfile}
            depreciationRules={depreciationRules}
            currentUser={user}
            onBack={() => setActiveView(assetDetailBackView)}
            onPhotos={() => setActiveView("photos")}
          />
        ) : <EmptyAssetSelectionPage onBack={() => setActiveView("assets")} />;
      case "photos":
        return selectedAsset ? <PhotosPage asset={selectedAsset} currentUser={user} imageCompression={systemSettings.imageCompression} onBack={() => setActiveView("asset-detail")} /> : <EmptyAssetSelectionPage onBack={() => setActiveView("assets")} />;
      case "companies":
        if (!canSeeCompaniesMenu) {
          return <AccessDeniedPage title="Empresas" description="Somente ADMIN e MASTER podem cadastrar ou editar empresas." />;
        }
        return <CompaniesPage accessContext={accessContext} onSelectCompany={setSelectedCompanyId} onSaveCompany={handleSaveCompany} />;
      case "users":
        return <UsersPage accessContext={accessContext} accessProfile={accessProfile} onSaveUser={handleSaveUser} />;
      case "responsibles":
        return (
          <ResponsiblesPageV2
            accessContext={accessContext}
            accessProfile={accessProfile}
            currentUser={user}
            currentUserId={user?.uid ?? ""}
            currentUserName={user?.displayName ?? user?.email ?? "Usuário"}
            currentUserEmail={user?.email ?? ""}
          />
        );
      case "company-transfers":
        return <CompanyTransfersPage accessContext={accessContext} currentUser={user} transfers={companyTransfers} canOpen={canSeeCompanyTransferMenu} />;
      case "external-services":
        if (!canSeeExternalServicesMenu) {
          return <AccessDeniedPage title="Serviços externos" description="Ative o controle de serviços externos nas configurações da empresa e confira suas permissões de acesso." />;
        }
        return (
          <ExternalServicesPage
            accessContext={accessContext}
            accessProfile={accessProfile}
            currentUser={user}
            currentUserName={currentUserProfile?.fullName ?? currentUserProfile?.displayName ?? user?.displayName ?? user?.email ?? "Usuário"}
            companyUsers={commercialUsers}
            services={externalServices}
            initialServiceId={pendingNotificationServiceId}
            onInitialServiceConsumed={() => setPendingNotificationServiceId(null)}
            onOpenAsset={(assetId) => {
              setSelectedAssetId(assetId);
              setAssetDetailBackView("external-services");
              setActiveView("asset-detail");
            }}
          />
        );
      case "depreciation":
        return <DepreciationPage currentUser={user} accessContext={accessContext} rules={depreciationRules} />;
      case "reports":
        return <ReportsPage accessContext={accessContext} accessProfile={accessProfile} depreciationRules={depreciationRules} currentUser={user} currentUserName={currentUserProfile?.fullName ?? currentUserProfile?.displayName ?? user?.displayName ?? user?.email ?? "Usuário"} />;
      case "audit":
        return <AuditPage accessContext={accessContext} currentUser={user} releases={auditorReleases} onSaveRelease={async (input) => {
          if (!user) {
            throw new Error("Usuário autenticado nao encontrado.");
          }
          await saveAuditorRelease(input, user);
        }} />;
      case "logs":
        return <LogsPage />;
      case "settings":
        return (
          <SettingsPage
            currentUser={user}
            currentProfile={currentUserProfile}
            settings={systemSettings}
            accessContext={accessContext}
            accessProfile={accessProfile}
            onOpenLabelTemplates={() => setActiveView("label-templates")}
            onOpenContractTemplates={() => setActiveView("contract-templates")}
            onSignOut={() => void signOutUser()}
          />
        );
      case "service-request":
        if (!canSeeServicesMenu) {
          return <AccessDeniedPage title="Serviços" description="Solicitações de serviço ficam disponíveis para GERENTE, ADMIN e MASTER." />;
        }
        return (
          <ServiceRequestPageV2
            accessContext={accessContext}
            currentUser={user}
            serviceCatalog={serviceCatalog}
            serviceRequests={serviceRequests}
            planRequiredMessage={selectedCompanyNeedsPlan && accessContext.selectedCompany ? `Para usar o sistema, a empresa ${accessContext.selectedCompany.tradeName} precisa de um plano ativo.` : ""}
          />
        );
      case "commercial-panel":
        if (accessContext.globalRole !== "MASTER") {
          return <AccessDeniedPage title="Faturamento" description="O painel financeiro do sistema fica disponivel apenas para MASTER." />;
        }
        return <CommercialPanelPage currentUser={user} companies={companies} assets={assets} users={commercialUsers} serviceCatalog={serviceCatalog} serviceRequests={serviceRequests} paymentSettings={systemSettings.payment} />;
      case "service-catalog":
        return <ServiceCatalogPage serviceCatalog={serviceCatalog} />;
      case "feature-releases":
        return <FeatureReleasesPage />;
      case "inventory":
        return <InventoryPage accessContext={accessContext} onScan={() => setActiveView("mobile-scan")} />;
      case "mobile-scan":
        return <MobileScanPage accessContext={accessContext} />;
      case "inventory-report":
        return <InventoryReportPage />;
      case "label-templates":
        return <LabelTemplatesPage accessContext={accessContext} currentUser={user} />;
      case "contract-templates":
        if (accessContext.globalRole !== "MASTER") {
          return <AccessDeniedPage title="Modelos de contrato" description="A gestão de modelos de contrato fica disponível apenas para MASTER." />;
        }
        return <ContractTemplatesPage currentUser={user} companies={companies} serviceCatalog={serviceCatalog} imobiLogoUrl={logoImobiFull} />;
      case "label-print":
        return <LabelsManagementPage accessContext={accessContext} currentUser={user} preselectedAsset={selectedAsset} />;
      case "training":
        if (!canSeeTrainingMenu) {
          return <AccessDeniedPage title="Treinamento" description="Este módulo ainda não foi liberado para a sua empresa." />;
        }
        return (
          <TrainingPage
            categories={trainingCategories}
            articles={trainingArticles}
            isManager={isTrainingManager}
            currentUser={user}
            initialArticleId={pendingTrainingArticleId}
            onInitialArticleConsumed={() => setPendingTrainingArticleId(null)}
          />
        );
      case "support":
        return (
          <SupportPage
            currentUser={user}
            company={accessContext.selectedCompany}
            trainingArticles={publishedTrainingArticles}
            knowledgeEntries={supportKnowledgeEntries}
            assistantSettings={systemSettings.supportAssistant}
            myTickets={mySupportTickets}
            allTickets={allSupportTickets}
            isSupportAgent={isSupportAgent}
            isMaster={accessContext.globalRole === "MASTER"}
            supportAgentUserIds={systemSettings.supportAgentUserIds}
            companyUsers={commercialUsers}
            initialTab={pendingSupportTab}
            onInitialTabConsumed={() => setPendingSupportTab(null)}
          />
        );
      default:
        return (
          <DashboardPage
            totals={totals}
            accessContext={accessContext}
            serviceCatalog={serviceCatalog}
            serviceRequests={serviceRequests}
            managedUsers={commercialUsers}
            externalServices={externalServices}
            userName={user?.displayName ?? user?.email ?? "Usuário"}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="font-semibold text-ink">Carregando IMOBI</p>
          <p className="mt-2 text-sm text-slate-500">Verificando sessão autenticada.</p>
        </div>
        {updatePrompt}
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <LoginPage onEnter={() => setActiveView("dashboard")} />
        {updatePrompt}
      </>
    );
  }

  if (profileLoadError && !isProfileLoading && !currentUserProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-lg rounded-lg border border-amber-200 bg-white p-6 text-center shadow-sm">
          <AlertTriangle className="mx-auto h-10 w-10 text-amber-600" />
          <p className="mt-4 font-semibold text-ink">Não foi possível carregar seu cadastro</p>
          <p className="mt-2 text-sm text-slate-500">{profileLoadError}</p>
          <div className="mt-6 flex justify-center">
            <SecondaryButton onClick={() => void signOutUser()}>
              <LogOut size={16} />
              Sair
            </SecondaryButton>
          </div>
        </div>
        {updatePrompt}
      </div>
    );
  }

  if (isAccessLoading || isProfileLoading || !currentUserProfile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
          <p className="font-semibold text-ink">Carregando cadastro</p>
          <p className="mt-2 text-sm text-slate-500">Verificando seu perfil e empresas liberadas.</p>
        </div>
        {updatePrompt}
      </div>
    );
  }

  if (currentUserProfile.status === "bloqueado") {
    return (
      <>
        <BlockedUserPage userName={currentUserProfile.fullName ?? currentUserProfile.displayName ?? user.email ?? "Usuário"} onSignOut={() => void signOutUser()} />
        {updatePrompt}
      </>
    );
  }

  if (profileNeedsCompletion(currentUserProfile)) {
    return (
      <>
        <CompleteRegistrationPage user={user} profile={currentUserProfile} onDone={() => setActiveView("dashboard")} />
        {updatePrompt}
      </>
    );
  }

  if (accessProfile.globalRole === "USER" && selectableCompanies.length === 0) {
    return (
      <>
        <NoCompanyAccessPage userName={user.displayName ?? user.email ?? "Usuário"} />
        {updatePrompt}
      </>
    );
  }

  if (shouldBlockCurrentCompanyByPlan && accessContext.selectedCompany) {
    return (
      <>
        <PlanRequiredPage
          company={accessContext.selectedCompany}
          userName={currentUserProfile.fullName ?? currentUserProfile.displayName ?? user.displayName ?? user.email ?? "Usuário"}
          onSignOut={() => void signOutUser()}
        />
        {updatePrompt}
      </>
    );
  }

  return (
    <AppLayout
      activeView={activeView}
      onNavigate={setActiveView}
      globalRole={accessContext.globalRole}
      companyRole={accessContext.companyRole}
      pendingTransferCount={pendingTransferCount}
      canSeeTransfers={canSeeCompanyTransferMenu}
      canSeeAudit={canSeeAuditMenu}
      canSeeCompanies={canSeeCompaniesMenu}
      canSeeServices={canSeeServicesMenu}
      canSeeExternalServices={canSeeExternalServicesMenu}
      canSeeTraining={canSeeTrainingMenu}
      helpButton={
        canSeeTrainingMenu && contextualTrainingArticles.length ? (
          <button
            type="button"
            onClick={handleOpenContextualHelp}
            aria-label="Ajuda desta tela"
            className="focus-ring inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-700 text-lg font-bold text-white shadow-panel hover:bg-brand-800"
          >
            ?
          </button>
        ) : undefined
      }
      supportButton={
        <button
          type="button"
          onClick={() => setIsSupportWidgetOpen((current) => !current)}
          aria-label="Abrir suporte"
          className="focus-ring relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-ink text-white shadow-panel hover:bg-slate-800"
        >
          <MessageCircle size={20} />
          {supportUnreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white ring-2 ring-white">{supportUnreadCount}</span>
          ) : null}
        </button>
      }
      topBar={
        <AppCompanyHeader
          companies={selectableCompanies}
          selectedCompanyId={selectedCompanyId}
          currentUser={user}
          currentProfile={currentUserProfile}
          globalRole={accessContext.globalRole}
          companyRole={accessContext.companyRole}
          notifications={notifications}
          onCompanyChange={setSelectedCompanyId}
          onMarkNotificationRead={(notification) => void handleMarkNotificationRead(notification)}
          onOpenNotification={handleOpenNotification}
          onSignOut={() => void signOutUser()}
        />
      }
      mobileBellSlot={
        <NotificationBellMenu
          currentUser={user}
          notifications={notifications}
          onMarkRead={(notification) => void handleMarkNotificationRead(notification)}
          onOpen={handleOpenNotification}
          variant="mobile-nav"
        />
      }
      mobileUserSlot={
        <UserNavButton
          userName={currentUserProfile.fullName || currentUserProfile.displayName || user.displayName || user.email || "Usuário"}
          roleLabel={accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN" ? accessContext.globalRole : accessContext.companyRole === "GERENTE" ? "GERENTE" : "USUARIO"}
          photoUrl={currentUserProfile.photoUrl || user.photoURL}
          email={user.email}
          onSignOut={() => void signOutUser()}
        />
      }
      onSignOut={() => void signOutUser()}
    >
      {renderView()}
      <ConnectionStatusBanner />
      {updatePrompt}
      {isHelpPickerOpen ? (
        <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm" onClick={() => setIsHelpPickerOpen(false)}>
          <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-4 shadow-panel" onClick={(event) => event.stopPropagation()}>
            <h2 className="font-semibold text-ink">Ajuda desta tela</h2>
            <div className="mt-3 space-y-2">
              {contextualTrainingArticles.map((article) => (
                <button
                  key={article.id}
                  type="button"
                  onClick={() => {
                    setPendingTrainingArticleId(article.id);
                    setActiveView("training");
                    setIsHelpPickerOpen(false);
                  }}
                  className="focus-ring block w-full rounded-md border border-slate-200 px-3 py-2 text-left text-sm font-medium text-ink hover:border-brand-300 hover:bg-brand-50/40"
                >
                  {article.title}
                </button>
              ))}
            </div>
            <div className="mt-4 flex justify-end"><SecondaryButton onClick={() => setIsHelpPickerOpen(false)}>Fechar</SecondaryButton></div>
          </div>
        </div>
      ) : null}
      {isSupportWidgetOpen ? (
        <SupportFloatingWidget
          currentUser={user}
          company={accessContext.selectedCompany}
          contextArticles={contextualTrainingArticles}
          trainingArticles={publishedTrainingArticles}
          knowledgeEntries={supportKnowledgeEntries}
          assistantSettings={systemSettings.supportAssistant}
          unreadTicketCount={supportUnreadCount}
          currentViewId={activeView}
          currentViewLabel={trainingHelpViewOptions.find((option) => option.id === activeView)?.label ?? supportViewLabels[activeView] ?? activeView}
          onClose={() => setIsSupportWidgetOpen(false)}
          onOpenFullSupport={(tab) => {
            setPendingSupportTab(tab ?? null);
            setActiveView("support");
            setIsSupportWidgetOpen(false);
          }}
        />
      ) : null}
    </AppLayout>
  );
}

function buildAccessContext(
  accessProfile: UserAccessProfile,
  selectedCompanyId: string | null,
  companies: CompanyRecord[],
  assets: AssetRecord[],
  auditorReleasedCompanyIds: string[] = []
): AccessContext {
  const accessibleCompanies = companies.filter((company) => accessProfile.globalRole === "MASTER" || accessProfile.globalRole === "ADMIN" || accessProfile.accessibleCompanyIds.includes(company.id) || auditorReleasedCompanyIds.includes(company.id));
  const selectedCompany = accessibleCompanies.find((company) => company.id === selectedCompanyId) ?? null;
  const scopedCompanies = selectedCompany ? [selectedCompany] : accessibleCompanies;
  const scopedCompanyIds = scopedCompanies.map((company) => company.id);
  const scopedAssets = assets.filter((asset) => scopedCompanyIds.includes(asset.companyId));
  const scopedInventoryChecks = seedInventoryChecks.filter((item) => scopedCompanyIds.includes(item.companyId));
  const isSystemAdmin = accessProfile.globalRole === "MASTER" || accessProfile.globalRole === "ADMIN";
  const selectedCompanyRole = selectedCompany ? accessProfile.companyRoles[selectedCompany.id] ?? (auditorReleasedCompanyIds.includes(selectedCompany.id) ? "AUDITOR" : undefined) : accessProfile.companyRole;
  const roleLabel = selectedCompanyRole ?? accessProfile.companyRole ?? accessProfile.globalRole;
  const summaryLabel = selectedCompany
    ? `${selectedCompany.tradeName} - visão operacional por empresa`
    : isSystemAdmin
      ? "Resumo global do sistema para MASTER/ADMIN"
      : `Resumo das empresas liberadas para ${roleLabel}`;
  const companyScopeLabel = selectedCompany
    ? selectedCompany.tradeName
    : accessibleCompanies.length === 1
      ? accessibleCompanies[0].tradeName
      : isSystemAdmin
        ? "Todas as empresas"
        : "Empresas liberadas";

  return {
    globalRole: accessProfile.globalRole,
    companyRole: selectedCompanyRole ?? accessProfile.companyRole,
    accessibleCompanies,
    scopedCompanies,
    selectedCompany,
    scopedAssets,
    scopedInventoryChecks,
    auditorReleasedCompanyIds,
    summaryLabel,
    companyScopeLabel
  };
}

function ConnectionStatusBanner() {
  const [online, setOnline] = useState(isOnline());
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => subscribeOnlineStatus(setOnline), []);

  useEffect(() => {
    function refreshPendingCount() {
      void countPendingSyncPhotos().then(setPendingCount);
    }

    refreshPendingCount();
    window.addEventListener(SYNC_QUEUE_CHANGED_EVENT, refreshPendingCount);
    return () => window.removeEventListener(SYNC_QUEUE_CHANGED_EVENT, refreshPendingCount);
  }, []);

  if (online && !pendingCount) {
    return null;
  }

  return (
    <div className="fixed inset-x-3 top-3 z-40 mx-auto max-w-sm rounded-lg border border-amber-200 bg-amber-50 p-3 text-left shadow-panel lg:inset-x-auto lg:right-4 lg:left-auto">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
        <span className={`h-2 w-2 rounded-full ${online ? "bg-emerald-500" : "bg-rose-500"}`} />
        {online ? "Online" : "Offline"}
      </div>
      {!online ? <p className="mt-1 text-xs text-amber-800">Você está trabalhando offline. As alterações serão sincronizadas quando a conexão retornar.</p> : null}
      {pendingCount > 0 ? <p className="mt-1 text-xs text-amber-800">{pendingCount} foto(s) aguardando sincronização.</p> : null}
    </div>
  );
}

function UpdateAvailablePrompt({ onDismiss, onUpdate }: { onDismiss: () => void; onUpdate: () => void }) {
  return (
    <div className="fixed inset-x-4 bottom-24 z-50 mx-auto max-w-md rounded-lg border border-brand-200 bg-white p-4 text-left shadow-panel lg:bottom-6 lg:right-6 lg:left-auto">
      <p className="text-sm font-semibold text-ink">Nova atualização disponível</p>
      <p className="mt-1 text-sm text-slate-500">Uma nova versão do IMOBI já pode ser carregada neste dispositivo.</p>
      <div className="mt-3 flex justify-end gap-2">
        <SecondaryButton onClick={onDismiss}>Depois</SecondaryButton>
        <PrimaryButton onClick={onUpdate}>
          <RotateCcw size={16} />
          Atualizar agora
        </PrimaryButton>
      </div>
    </div>
  );
}

type AssetTypeKind = "generic" | "land" | "vehicle" | "building";

function getAssetTypeKind(assetTypeName: string): AssetTypeKind {
  const normalized = normalizeSearch(assetTypeName);
  if (normalized.includes("terreno")) {
    return "land";
  }
  if (["carro", "veiculo", "motocicleta", "caminhao"].some((term) => normalized.includes(term))) {
    return "vehicle";
  }
  if (["construcao", "edificacao", "predio", "imovel"].some((term) => normalized.includes(term))) {
    return "building";
  }
  return "generic";
}

function canEditAssetRecord(accessContext: AccessContext, accessProfile: UserAccessProfile, asset: Asset) {
  const permissions = accessProfile.companyPermissions[asset.companyId];
  return accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || accessProfile.companyRoles[asset.companyId] === "GERENTE"
    || canAccess(permissions, "canEditAssets", accessContext.globalRole);
}

function ProcessingOverlay({ title, description }: { title: string; description: string }) {
  return (
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-950/55 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-lg border border-white/20 bg-white p-6 text-center shadow-panel">
        <Loader2 className="mx-auto h-10 w-10 animate-spin text-brand-700" />
        <h2 className="mt-4 text-lg font-semibold text-ink">{title}</h2>
        <p className="mt-2 text-sm text-slate-500">{description}</p>
      </div>
    </div>
  );
}

function profileNeedsCompletion(profile: UserProfile) {
  return !profile.cpfRaw || !profile.birthDate || !(profile.fullName ?? profile.displayName).trim();
}

function BlockedUserPage({ userName, onSignOut }: { userName: string; onSignOut: () => void }) {
  return (
    <section className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
        <img src={logoImobiFull} alt="IMOBI" className="mx-auto mb-6 h-16 w-auto object-contain" />
        <StatusPill tone="red">Usuário bloqueado</StatusPill>
        <h1 className="mt-4 text-2xl font-semibold text-ink">Acesso bloqueado</h1>
        <p className="mt-3 text-sm text-slate-500">
          {userName}, sua conta esta bloqueada no IMOBI. Entre em contato com um administrador para revisar o acesso.
        </p>
        <div className="mt-6 flex justify-center">
          <SecondaryButton onClick={onSignOut}>
            <LogOut size={16} />
            Sair
          </SecondaryButton>
        </div>
      </div>
    </section>
  );
}

function NoCompanyAccessPage({ userName }: { userName: string }) {
  return (
    <section className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm">
        <img src={logoImobiFull} alt="IMOBI" className="mx-auto mb-6 h-16 w-auto object-contain" />
        <StatusPill tone="yellow">Acesso pendente</StatusPill>
        <h1 className="mt-4 text-2xl font-semibold text-ink">Nenhuma empresa liberada</h1>
        <p className="mt-3 text-sm text-slate-500">
          {userName}, sua conta foi criada como usuário padrão, mas ainda não possui empresas liberadas.
          Solicite acesso a um administrador da empresa para começar a usar o IMOBI.
        </p>
        <div className="mt-6 flex justify-center">
          <SecondaryButton onClick={() => void signOutUser()}>
            <LogOut size={16} />
            Sair
          </SecondaryButton>
        </div>
      </div>
    </section>
  );
}

function PlanRequiredPage({ company, userName, onSignOut }: { company: CompanyRecord; userName: string; onSignOut: () => void }) {
  return (
    <section className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-xl rounded-lg border border-amber-200 bg-white p-6 text-center shadow-sm">
        <img src={logoImobiFull} alt="IMOBI" className="mx-auto mb-6 h-16 w-auto object-contain" />
        <StatusPill tone="yellow">Plano necessario</StatusPill>
        <h1 className="mt-4 text-2xl font-semibold text-ink">Empresa sem plano ativo</h1>
        <p className="mt-3 text-sm text-slate-600">
          {userName}, a empresa <span className="font-semibold text-ink">{company.tradeName}</span> nao possui um plano ativo no IMOBI.
          Para usar o sistema, e necessario que um GERENTE da empresa contrate um plano ou entre em contato com o suporte da IMOBI para verificar a liberação.
        </p>
        <div className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3 text-left text-sm text-slate-600">
          <p className="font-semibold text-ink">{company.legalName}</p>
          <p className="mt-1">CNPJ {company.cnpj}</p>
          <p className="mt-1">Status: {company.status}</p>
        </div>
        <div className="mt-6 flex justify-center">
          <SecondaryButton onClick={onSignOut}>
            <LogOut size={16} />
            Sair
          </SecondaryButton>
        </div>
      </div>
    </section>
  );
}

function CompleteRegistrationPage({ user, profile, onDone }: { user: User; profile: UserProfile; onDone: () => void }) {
  const [fullName, setFullName] = useState((profile.fullName ?? profile.displayName ?? user.displayName ?? "").toLocaleUpperCase("pt-BR"));
  const [cpf, setCpf] = useState(profile.cpf ?? "");
  const [birthDate, setBirthDate] = useState(profile.birthDate ?? "");
  const [phone, setPhone] = useState(profile.phone ?? "");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const email = (profile.email || user.email || "").toLocaleLowerCase("pt-BR");

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const normalizedFullName = fullName.trim().toLocaleUpperCase("pt-BR");
    if (!normalizedFullName) {
      setMessage("Informe seu nome completo.");
      return;
    }

    if (onlyDigits(cpf).length !== 11) {
      setMessage("CPF incompleto. Use o formato 000.000.000-00.");
      return;
    }

    if (onlyDigits(birthDate).length !== 8) {
      setMessage("Data de nascimento incompleta. Use dd/mm/aaaa.");
      return;
    }

    setIsSubmitting(true);
    try {
      await createSelfUserProfile(user, {
        cpf,
        cpfRaw: onlyDigits(cpf),
        fullName: normalizedFullName,
        birthDate,
        email,
        phone
      });
      onDone();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível concluir o cadastro.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <div className="text-center">
          <img src={user.photoURL ?? logoImobiFull} alt="Perfil" className="mx-auto mb-4 h-16 w-16 rounded-full object-cover" />
          <StatusPill tone="blue">Concluir cadastro</StatusPill>
          <h1 className="mt-4 text-2xl font-semibold text-ink">Complete seus dados</h1>
          <p className="mt-2 text-sm text-slate-500">Recebemos seu login com Google. Confirme os dados abaixo para finalizar sua conta no IMOBI.</p>
        </div>
        <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
          <input className="focus-ring rounded-md border border-slate-300 px-4 py-3 uppercase" placeholder="Nome completo" value={fullName} onChange={(event) => setFullName(event.target.value.toLocaleUpperCase("pt-BR"))} />
          <input className="focus-ring rounded-md border border-slate-300 px-4 py-3 bg-slate-50 text-slate-600" value={email} readOnly />
          <div className="grid gap-4 sm:grid-cols-2">
            <input className="focus-ring rounded-md border border-slate-300 px-4 py-3" placeholder="CPF" inputMode="numeric" maxLength={14} value={cpf} onChange={(event) => setCpf(formatCpfInput(event.target.value))} />
            <input className="focus-ring rounded-md border border-slate-300 px-4 py-3" placeholder="Nascimento" inputMode="numeric" maxLength={10} value={birthDate} onChange={(event) => setBirthDate(formatDateInput(event.target.value))} />
          </div>
          <input className="focus-ring rounded-md border border-slate-300 px-4 py-3" placeholder="Telefone" inputMode="numeric" maxLength={15} value={phone} onChange={(event) => setPhone(formatPhoneInput(event.target.value))} />
          <div className="flex flex-wrap justify-center gap-2">
            <PrimaryButton type="submit" disabled={isSubmitting}>{isSubmitting ? "Salvando..." : "Concluir cadastro"}</PrimaryButton>
            <SecondaryButton onClick={() => void signOutUser()}><LogOut size={16} /> Sair</SecondaryButton>
          </div>
        </form>
        {message ? <p className="mt-4 text-center text-sm text-slate-500">{message}</p> : null}
      </div>
    </section>
  );
}

function LoginPage({ onEnter }: { onEnter: () => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [cpf, setCpf] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [phone, setPhone] = useState("");
  const [mode, setMode] = useState<"login" | "create">("login");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const overviewCards = [
    { title: "Controle patrimonial", description: "Cadastre bens, fotos, valores, responsáveis, status e histórico em uma base organizada por empresa.", icon: <Building2 size={20} /> },
    { title: "Auditoria e inventário", description: "Faça conferências por QR Code ou código de barras, registre divergências e acompanhe auditorias.", icon: <ShieldCheck size={20} /> },
    { title: "Relatórios e etiquetas", description: "Gere PDFs, CSVs e etiquetas patrimoniais com QR Code para uso em campo e controle interno.", icon: <FileDown size={20} /> },
    { title: "PWA para campo", description: "Use no computador ou celular, com estrutura preparada para operação responsiva e rotinas offline.", icon: <QrCode size={20} /> }
  ];

  function switchMode(nextMode: "login" | "create") {
    setMode(nextMode);
    setMessage("");
  }

  function finishAuthenticatedFlow() {
    onEnter();
  }

  async function handleGoogleLogin() {
    setIsSubmitting(true);
    try {
      await signInWithGoogle();
      finishAuthenticatedFlow();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Falha ao entrar com Google.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleEmailSubmit(event?: FormEvent<HTMLFormElement>) {
    event?.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      if (mode === "create") {
        const normalizedEmail = email.trim().toLocaleLowerCase("pt-BR");
        const normalizedFullName = fullName.trim().toLocaleUpperCase("pt-BR");
        if (!normalizedFullName) throw new Error("Informe seu nome completo.");
        if (onlyDigits(cpf).length !== 11) throw new Error("CPF incompleto. Use o formato 000.000.000-00.");
        if (onlyDigits(birthDate).length !== 8) throw new Error("Data de nascimento incompleta. Use dd/mm/aaaa.");
        const loggedUser = await createAccountWithEmail(normalizedEmail, password);
        await createSelfUserProfile(loggedUser, { cpf, cpfRaw: onlyDigits(cpf), fullName: normalizedFullName, birthDate, email: normalizedEmail, phone });
        finishAuthenticatedFlow();
      } else {
        await signInWithEmail(email, password);
        finishAuthenticatedFlow();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : mode === "create" ? "Falha ao cadastrar usuário." : "Falha no login por e-mail.");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handlePasswordReset() {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setMessage("Informe seu e-mail para receber o link de redefinição de senha.");
      return;
    }
    setIsSubmitting(true);
    try {
      await sendPasswordReset(normalizedEmail);
      setMessage("Enviamos um link de redefinição de senha para o e-mail informado.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível enviar o e-mail de redefinição.");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <section className="min-h-screen bg-slate-50 text-ink">
      <header className="fixed inset-x-0 top-0 z-40 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 lg:px-8">
          <div className="flex min-w-0 items-center gap-3">
            <img src={logoImobiFull} alt="IMOBI" className="h-10 w-auto object-contain" />
            <span className="hidden text-xs font-semibold uppercase tracking-wide text-slate-500 sm:inline">Controle de bens imobilizados</span>
          </div>
        </div>
      </header>

      <div className="mx-auto grid min-h-screen max-w-7xl gap-8 px-4 pb-10 pt-24 lg:grid-cols-[minmax(0,1fr)_440px] lg:px-8 lg:pb-12 lg:pt-28">
        <main className="flex flex-col justify-center">
          <div className="mb-8 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <img src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&w=1400&q=80" alt="Mesa de trabalho com documentos patrimoniais" className="h-56 w-full object-cover sm:h-72 lg:h-80" />
          </div>
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-700">IMOBI v0.1.70</p>
            <h1 className="mt-4 max-w-2xl text-4xl font-semibold leading-tight text-ink sm:text-5xl">Gestão patrimonial simples para empresas que precisam enxergar seus bens.</h1>
            <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">O IMOBI centraliza cadastro, fotos, responsáveis, valores, auditorias, inventários e relatórios de bens imobilizados em uma experiência responsiva para escritório e campo.</p>
          </div>

          <div className="mt-8 grid gap-3 sm:grid-cols-2">
            {overviewCards.map((card) => (
              <article key={card.title} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start gap-3">
                  <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">{card.icon}</span>
                  <div>
                    <h2 className="text-sm font-semibold text-ink">{card.title}</h2>
                    <p className="mt-1 text-sm leading-6 text-slate-600">{card.description}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-3">
            <div><p className="text-2xl font-semibold text-ink">Multiempresa</p><p className="mt-1 text-sm text-slate-500">Permissões por usuário e empresa.</p></div>
            <div><p className="text-2xl font-semibold text-ink">QR Code</p><p className="mt-1 text-sm text-slate-500">Leitura, etiquetas e inventário em campo.</p></div>
            <div><p className="text-2xl font-semibold text-ink">PDF/CSV</p><p className="mt-1 text-sm text-slate-500">Relatórios prontos para análise.</p></div>
          </div>
        </main>

        <aside id="login-panel" className="scroll-mt-24 lg:sticky lg:top-24 lg:self-start">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-panel sm:p-6">
            <div className="text-center">
              <img src={logoImobiFull} alt="IMOBI" className="mx-auto mb-5 h-16 w-auto object-contain" />
              <p className="text-sm font-semibold text-brand-700">Acesso ao sistema</p>
              <h2 className="mt-2 text-2xl font-semibold text-ink">{mode === "login" ? "Entrar na conta" : "Cadastro de usuário"}</h2>
              <p className="mt-2 text-sm text-slate-500">{mode === "login" ? "Use seu e-mail, senha ou conta Google para continuar." : "Crie sua conta e aguarde a liberação de acesso à empresa."}</p>
            </div>

            <form className="mt-6 space-y-4" onSubmit={handleEmailSubmit}>
              {mode === "create" ? (
                <div className="grid gap-4 text-left">
                  <input className="focus-ring w-full rounded-md border border-slate-300 px-4 py-3 uppercase" placeholder="Nome completo" value={fullName} onChange={(event) => setFullName(event.target.value.toLocaleUpperCase("pt-BR"))} required />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <input className="focus-ring w-full rounded-md border border-slate-300 px-4 py-3" placeholder="CPF" inputMode="numeric" maxLength={14} value={cpf} onChange={(event) => setCpf(formatCpfInput(event.target.value))} required />
                    <input className="focus-ring w-full rounded-md border border-slate-300 px-4 py-3" placeholder="Nascimento" inputMode="numeric" maxLength={10} value={birthDate} onChange={(event) => setBirthDate(formatDateInput(event.target.value))} required />
                  </div>
                  <input className="focus-ring w-full rounded-md border border-slate-300 px-4 py-3" placeholder="Telefone" inputMode="numeric" maxLength={15} value={phone} onChange={(event) => setPhone(formatPhoneInput(event.target.value))} />
                </div>
              ) : null}
              <input className="focus-ring w-full rounded-md border border-slate-300 px-4 py-3 text-left" placeholder="E-mail" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />
              <div className="relative">
                <input className="focus-ring w-full rounded-md border border-slate-300 px-4 py-3 pr-12 text-left" placeholder="Senha" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} />
                <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100" aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                <PrimaryButton type="submit" disabled={isSubmitting}>{isSubmitting ? mode === "login" ? "Entrando..." : "Cadastrando..." : mode === "login" ? "Entrar" : "Cadastrar"}</PrimaryButton>
                <SecondaryButton onClick={() => switchMode(mode === "login" ? "create" : "login")}>{mode === "login" ? "Criar conta" : "Já tenho conta"}</SecondaryButton>
              </div>
              {mode === "login" ? (
                <button type="button" onClick={handleGoogleLogin} disabled={isSubmitting} className="focus-ring flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-70"><GoogleIcon />Entrar com Google</button>
              ) : null}
              <button type="button" onClick={handlePasswordReset} disabled={isSubmitting} className={(mode === "login" ? "mx-auto block" : "hidden") + " text-xs font-medium text-slate-500 underline-offset-4 hover:text-brand-700 hover:underline disabled:cursor-not-allowed disabled:opacity-60"}>Esqueci minha senha!</button>
            </form>
            {message ? <p className="mt-5 rounded-md border border-slate-200 bg-slate-50 p-3 text-center text-sm text-slate-600">{message}</p> : null}
          </div>
        </aside>
      </div>
    </section>
  );
}

function GoogleIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1c-.22-.66-.35-1.36-.35-2.1s.13-1.44.35-2.1V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  );
}

function CompanySelectPage({
  companies,
  selectedCompanyId,
  onSelect,
  onEnter
}: {
  companies: Company[];
  selectedCompanyId: string | null;
  onSelect: (companyId: string | null) => void;
  onEnter: () => void;
}) {
  return (
    <>
      <PageHeader title="Seleção de empresa" subtitle="Escolha um contexto apenas quando quiser operar uma empresa específica." />
      <div className="grid gap-3 p-4 lg:grid-cols-3 lg:p-6">
        {companies.length > 1 ? (
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              onEnter();
            }}
            className={`rounded-lg border p-5 text-left shadow-sm ${
              selectedCompanyId === null ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white hover:border-brand-500"
            }`}
          >
            <p className="font-semibold text-ink">Todas as empresas liberadas</p>
            <p className="mt-1 text-sm text-slate-500">Dashboard consolidado sem empresa ativa.</p>
            <StatusPill tone="blue">resumo</StatusPill>
          </button>
        ) : null}
        {companies.map((company) => (
          <button
            key={company.id}
            type="button"
            onClick={() => {
              onSelect(company.id);
              onEnter();
            }}
            className={`rounded-lg border p-5 text-left shadow-sm ${
              selectedCompanyId === company.id ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white hover:border-brand-500"
            }`}
          >
            <p className="font-semibold text-ink">{company.tradeName}</p>
            <p className="mt-1 text-sm text-slate-500">{company.legalName}</p>
            <div className="mt-3">
              <StatusPill tone={company.status === "ativa" ? "green" : company.status === "bloqueada" ? "red" : "neutral"}>{company.status}</StatusPill>
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

function AppCompanyHeader({
  companies,
  selectedCompanyId,
  currentUser,
  currentProfile,
  globalRole,
  companyRole,
  notifications,
  onCompanyChange,
  onMarkNotificationRead,
  onOpenNotification,
  onSignOut
}: {
  companies: Company[];
  selectedCompanyId: string | null;
  currentUser: User;
  currentProfile: UserProfile;
  globalRole: GlobalRole;
  companyRole?: CompanyRole;
  notifications: AppNotification[];
  onCompanyChange: (companyId: string | null) => void;
  onMarkNotificationRead: (notification: AppNotification) => void;
  onOpenNotification: (notification: AppNotification) => void;
  onSignOut: () => void;
}) {
  const roleLabel = globalRole === "MASTER" || globalRole === "ADMIN" ? globalRole : companyRole === "GERENTE" ? "GERENTE" : "USUARIO";
  const userName = currentProfile.fullName || currentProfile.displayName || currentUser.displayName || currentUser.email || "Usuário";
  const photoUrl = currentProfile.photoUrl || currentUser.photoURL;

  return (
    <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <CompanySwitcher companies={companies} selectedCompanyId={selectedCompanyId} onChange={onCompanyChange} />
      <div className="hidden items-center gap-3 lg:flex">
        <ThemeToggleButton />
        <NotificationBellMenu currentUser={currentUser} notifications={notifications} onMarkRead={onMarkNotificationRead} onOpen={onOpenNotification} />
        <div className="flex min-w-0 items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
          {photoUrl ? (
            <img src={photoUrl} alt={userName} className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-slate-200" />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
              <UserCircle size={18} />
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-ink">{userName}</p>
            <p className="text-xs font-medium text-brand-700">{roleLabel}</p>
          </div>
        </div>
        <SecondaryButton onClick={onSignOut}><LogOut size={16} /> Sair</SecondaryButton>
      </div>
    </div>
  );
}

function ThemeToggleButton() {
  const [theme, setTheme] = useTheme();
  const isDark = theme === "dark";
  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? "light" : "dark")}
      className="focus-ring inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
      aria-label={isDark ? "Usar tema claro" : "Usar tema escuro"}
      title={isDark ? "Usar tema claro" : "Usar tema escuro"}
    >
      {isDark ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );
}

function ThemeSelector() {
  const [theme, setTheme] = useTheme();
  const options: Array<{ id: "light" | "dark"; label: string; icon: ReactNode }> = [
    { id: "light", label: "Claro", icon: <Sun size={16} /> },
    { id: "dark", label: "Escuro", icon: <Moon size={16} /> }
  ];
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tema</p>
      <div className="mt-2 grid grid-cols-2 gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
        {options.map((option) => (
          <button
            key={option.id}
            type="button"
            onClick={() => setTheme(option.id)}
            aria-pressed={theme === option.id}
            className={`focus-ring inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${theme === option.id ? "bg-white text-ink shadow-sm" : "text-slate-500 hover:text-ink"}`}
          >
            {option.icon} {option.label}
          </button>
        ))}
      </div>
      <p className="mt-1 text-[11px] text-slate-500">Fica salvo neste dispositivo.</p>
    </div>
  );
}

function NotificationBellMenu({
  currentUser,
  notifications,
  onMarkRead,
  onOpen,
  variant = "header"
}: {
  currentUser: User;
  notifications: AppNotification[];
  onMarkRead: (notification: AppNotification) => void;
  onOpen: (notification: AppNotification) => void;
  variant?: "header" | "mobile-nav";
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [pushStatus, setPushStatus] = useState<"idle" | "requesting" | "active" | "error">("idle");
  const [pushMessage, setPushMessage] = useState("");
  const unreadCount = notifications.filter((notification) => !notification.read).length;
  const canOfferPush = isPushConfigured() && typeof Notification !== "undefined" && Notification.permission !== "granted";

  async function handleActivatePush() {
    setPushStatus("requesting");
    setPushMessage("");
    try {
      await registerPushNotifications(currentUser);
      setPushStatus("active");
      setPushMessage("Notificações push ativadas neste dispositivo.");
    } catch (error) {
      setPushStatus("error");
      setPushMessage(error instanceof Error ? error.message : "Não foi possível ativar notificações push.");
    }
  }

  const panel = isOpen ? (
    <>
      <div className="fixed inset-0 z-[85]" onClick={() => setIsOpen(false)} />
      <div
        className={
          variant === "mobile-nav"
            ? "fixed inset-x-3 bottom-20 z-[90] max-h-[60vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-panel"
            : "fixed inset-x-3 top-16 z-[90] max-h-[70vh] overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-panel sm:absolute sm:inset-x-auto sm:top-auto sm:right-0 sm:mt-2 sm:w-80"
        }
      >
        <div className="flex items-center justify-between gap-2 px-2 py-1">
          <p className="text-xs font-semibold uppercase text-slate-500">Notificações</p>
          {canOfferPush && pushStatus !== "active" ? (
            <button
              type="button"
              onClick={() => void handleActivatePush()}
              disabled={pushStatus === "requesting"}
              className="text-[11px] font-semibold text-brand-700 hover:underline disabled:opacity-60"
            >
              {pushStatus === "requesting" ? "Ativando..." : "Ativar push"}
            </button>
          ) : null}
        </div>
        {pushMessage ? <p className="px-2 pb-1 text-[11px] text-slate-500">{pushMessage}</p> : null}
        {notifications.slice(0, 30).map((notification) => (
          <button
            key={notification.id}
            type="button"
            onClick={() => {
              if (!notification.read) {
                onMarkRead(notification);
              }
              onOpen(notification);
              setIsOpen(false);
            }}
            className={`mt-1 block w-full rounded-md p-2 text-left text-sm ${notification.read ? "text-slate-500" : "bg-brand-50 text-ink"}`}
          >
            <p className="font-medium">{notification.title}</p>
            <p className="mt-0.5 text-xs text-slate-500">{notification.body}</p>
            <p className="mt-1 text-[11px] text-slate-400">{formatAuditDate(notification.createdAt)}</p>
          </button>
        ))}
        {!notifications.length ? <p className="p-3 text-sm text-slate-500">Nenhuma notificação por aqui.</p> : null}
      </div>
    </>
  ) : null;

  if (variant === "mobile-nav") {
    return (
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen((current) => !current)}
          className={`relative flex flex-col items-center gap-1 rounded-md px-1 py-1 text-[11px] font-medium ${isOpen ? "text-brand-700" : "text-slate-500"}`}
          aria-label="Notificações"
        >
          <Bell size={18} />
          {unreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-bold text-white">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          ) : null}
          Avisos
        </button>
        {panel}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen((current) => !current)}
        className="focus-ring relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        aria-label="Notificações"
      >
        <Bell size={18} />
        {unreadCount > 0 ? (
          <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        ) : null}
      </button>
      {panel}
    </div>
  );
}

function UserNavButton({
  userName,
  roleLabel,
  photoUrl,
  email,
  onSignOut
}: {
  userName: string;
  roleLabel: string;
  photoUrl?: string | null;
  email?: string | null;
  onSignOut: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const firstName = userName.trim().split(/\s+/)[0] || userName;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex flex-col items-center gap-1 rounded-md px-1 py-1 text-[11px] font-medium text-slate-500"
      >
        {photoUrl ? (
          <img src={photoUrl} alt={userName} className="h-[18px] w-[18px] shrink-0 rounded-full object-cover ring-1 ring-slate-200" />
        ) : (
          <UserCircle size={18} />
        )}
        <span className="max-w-[4.5rem] truncate">{firstName}</span>
      </button>
      {isOpen ? (
        <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm" onClick={() => setIsOpen(false)}>
          <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-4 shadow-panel" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-center gap-3">
              {photoUrl ? (
                <img src={photoUrl} alt={userName} className="h-14 w-14 shrink-0 rounded-full object-cover ring-1 ring-slate-200" />
              ) : (
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-700">
                  <UserCircle size={28} />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate font-semibold text-ink">{userName}</p>
                <p className="text-xs font-medium text-brand-700">{roleLabel}</p>
                {email ? <p className="truncate text-xs text-slate-500">{email}</p> : null}
              </div>
            </div>
            <div className="mt-4 border-t border-slate-200 pt-3">
              <ThemeSelector />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <SecondaryButton onClick={() => setIsOpen(false)}>Fechar</SecondaryButton>
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onSignOut();
                }}
                className="focus-ring inline-flex items-center justify-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-rose-500"
              >
                <LogOut size={16} /> Sair
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

function DashboardPage({
  totals,
  accessContext,
  serviceCatalog,
  serviceRequests,
  managedUsers,
  externalServices,
  userName
}: {
  totals: { activeAssets: number; acquisition: number; book: number; auditedPercent: number };
  accessContext: AccessContext;
  serviceCatalog: ServiceCatalogItem[];
  serviceRequests: ServiceRequestRecord[];
  managedUsers: ManagedUser[];
  externalServices: ExternalService[];
  userName: string;
}) {
  const isCompanyScoped = Boolean(accessContext.selectedCompany);
  const selectedCompanySettings = accessContext.selectedCompany?.settings ?? defaultCompanyOperationalSettings;
  const selectedCompanyPlan = selectedCompanySettings.activePlanId
    ? serviceCatalog.find((service) => service.id === selectedCompanySettings.activePlanId)
    : undefined;
  const planName = selectedCompanySettings.activePlanName || selectedCompanyPlan?.name || (isTrialActive(selectedCompanySettings.trialUntil) ? "Teste ativo" : "Sem plano ativo");
  const assetsLimit = selectedCompanySettings.maxAssets || selectedCompanyPlan?.maxAssets || 0;
  const usersLimit = selectedCompanySettings.maxUsers || selectedCompanyPlan?.maxUsers || 0;
  const activeCompanyUsersCount = accessContext.selectedCompany
    ? managedUsers.filter((profile) => (
      profile.globalRole !== "MASTER"
      && profile.globalRole !== "ADMIN"
      && profile.companyAccess.some((access) => access.companyId === accessContext.selectedCompany?.id && access.status === "ativo")
    )).length
    : 0;
  const trialDaysLeft = daysUntilDate(selectedCompanySettings.trialUntil);
  const showPlanExpiryAlert = isCompanyScoped && trialDaysLeft !== null && trialDaysLeft >= 0 && trialDaysLeft < 30;
  const isPlanCriticalExpiry = showPlanExpiryAlert && trialDaysLeft !== null && trialDaysLeft < 7;
  const activeInventory = accessContext.scopedInventoryChecks[0] ?? null;
  const companyMetricLabel = isCompanyScoped ? "Empresa ativa" : "Empresas ativas";
  const activeCompaniesCount = accessContext.scopedCompanies.filter((company) => company.status === "ativa").length;
  const assetTotalSuffix = isCompanyScoped ? "na empresa" : "no escopo";
  const assetsWithoutPhotos = accessContext.scopedAssets.filter((asset) => (asset.photosCount ?? 0) === 0).length;
  const assetsPendingAudit = accessContext.scopedAssets.filter((asset) => asset.auditStatus !== "auditado").length;
  const labelsToIssue = accessContext.scopedAssets.filter((asset) => asset.status === "ativo").length;
  const inventoryProgress = activeInventory?.totals.expected ? (activeInventory.totals.verified / activeInventory.totals.expected) * 100 : 0;
  const externalServicesEnabled = accessContext.selectedCompany?.settings?.externalServicesEnabled === true;
  const activeExternalServicesCount = externalServices.filter((service) => ["aberto", "aguardando_confirmacao", "em_andamento", "em_retorno"].includes(service.status)).length;
  const servicesWithPendingConfirmationCount = externalServices.filter((service) => service.status === "aguardando_confirmacao").length;
  const overdueReturnsCount = accessContext.scopedAssets.filter((asset) => isAssetReturnOverdue(asset)).length;
  const reservedAssetsCount = accessContext.scopedAssets.filter((asset) => asset.externalServiceStatus === "reservado").length;
  const assetsInMaintenanceCount = accessContext.scopedAssets.filter((asset) => asset.status === "em_manutencao").length;
  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={accessContext.summaryLabel}
      />
      <div className="space-y-6 p-4 lg:p-6">
        <p className="text-sm text-slate-500">Sessao autenticada: <span className="font-medium text-ink">{userName}</span> - Escopo: <span className="font-medium text-ink">{accessContext.companyScopeLabel}</span></p>
        {showPlanExpiryAlert ? (
          <section className={`rounded-lg border p-4 shadow-sm ${isPlanCriticalExpiry ? "border-rose-200 bg-rose-50" : "border-amber-200 bg-amber-50"}`}>
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <p className={`font-semibold ${isPlanCriticalExpiry ? "text-rose-950" : "text-amber-950"}`}>{isPlanCriticalExpiry ? "Plano em vencimento crítico" : "Plano perto do vencimento"}</p>
                <p className={`mt-1 text-sm ${isPlanCriticalExpiry ? "text-rose-800" : "text-amber-800"}`}>
                  O acesso da empresa {accessContext.selectedCompany?.tradeName} vence em {trialDaysLeft === 0 ? "hoje" : `${trialDaysLeft} dia(s)`}. Verifique a renovação para evitar bloqueio de uso.
                </p>
              </div>
              <StatusPill tone={isPlanCriticalExpiry ? "red" : "yellow"}>{isPlanCriticalExpiry ? "Menos de 7 dias" : "Menos de 30 dias"}</StatusPill>
            </div>
          </section>
        ) : null}
        {isCompanyScoped ? (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Plano e limites da empresa</p>
                <h2 className="mt-1 text-lg font-semibold text-ink">{planName}</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {selectedCompanySettings.trialUntil ? `Validade/teste ate ${formatDateOnly(selectedCompanySettings.trialUntil)}.` : "Sem data de vencimento configurada no cadastro comercial."}
                </p>
              </div>
              <StatusPill tone={hasUsableCompanyPlan(accessContext.selectedCompany, serviceRequests) ? "green" : "yellow"}>
                {hasUsableCompanyPlan(accessContext.selectedCompany, serviceRequests) ? "Plano ativo" : "Plano pendente"}
              </StatusPill>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div className="rounded-md bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Bens cadastrados</p>
                <p className="mt-1 text-xl font-semibold text-ink">{accessContext.scopedAssets.length} de {assetsLimit || "sem limite"}</p>
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Bens ativos</p>
                <p className="mt-1 text-xl font-semibold text-ink">{totals.activeAssets} de {assetsLimit || "sem limite"}</p>
              </div>
              <div className="rounded-md bg-slate-50 p-3">
                <p className="text-xs font-semibold uppercase text-slate-500">Usuários permitidos</p>
                <p className="mt-1 text-xl font-semibold text-ink">
                  {managedUsers.length ? `${activeCompanyUsersCount} de ${usersLimit || "sem limite"}` : `0 de ${usersLimit || "sem limite"}`}
                </p>
                <p className="mt-1 text-xs text-slate-500">MASTER e ADMIN nao contam no limite.</p>
              </div>
            </div>
          </section>
        ) : null}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard label={accessContext.selectedCompany ? "Empresa selecionada" : "Empresas no escopo"} value={String(accessContext.selectedCompany ? 1 : accessContext.accessibleCompanies.length)} hint={accessContext.companyScopeLabel} />
          <MetricCard label={`Bens ativos ${assetTotalSuffix}`} value={String(totals.activeAssets)} />
          <MetricCard label={`Valor contábil ${assetTotalSuffix}`} value={formatCurrency(totals.book)} />
          <MetricCard label="Balanços em andamento" value={String(accessContext.scopedInventoryChecks.filter((item) => item.status === "em_andamento").length)} />
        </div>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard label={`Valor de aquisição ${assetTotalSuffix}`} value={formatCurrency(totals.acquisition)} />
          <MetricCard label={`Percentual auditado ${assetTotalSuffix}`} value={`${totals.auditedPercent}%`} />
          <MetricCard label={companyMetricLabel} value={String(activeCompaniesCount)} />
          <MetricCard label="Pendências de revisão" value={String(accessContext.scopedAssets.filter((asset) => asset.auditStatus === "pendente_revisao").length)} />
        </div>
        <div className="grid gap-4 xl:grid-cols-[1.2fr_0.8fr]">
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold text-ink">Alertas operacionais {isCompanyScoped ? "da empresa" : "do escopo"}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <AlertCard icon={<Camera size={18} />} label="Sem foto suficiente" value={`${assetsWithoutPhotos} bens`} />
              <AlertCard icon={<ShieldCheck size={18} />} label="Pendentes de auditoria" value={`${assetsPendingAudit} bens`} />
              <AlertCard icon={<Printer size={18} />} label="Etiquetas a emitir" value={`${labelsToIssue} bens`} />
            </div>
          </section>
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold text-ink">Balanço em andamento {isCompanyScoped ? "da empresa" : "do escopo"}</h2>
            {activeInventory ? (
              <div className="mt-4">
                <div className="h-3 rounded-full bg-slate-100">
                  <div className="h-3 rounded-full bg-brand-600" style={{ width: `${inventoryProgress}%` }} />
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  {activeInventory.totals.verified} de {activeInventory.totals.expected} itens verificados
                </p>
              </div>
            ) : (
              <p className="mt-4 text-sm text-slate-500">Nenhum balanço em andamento para {accessContext.companyScopeLabel}.</p>
            )}
          </section>
        </div>
        {externalServicesEnabled ? (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold text-ink">Serviços externos da empresa</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard label="Serviços em andamento" value={String(activeExternalServicesCount)} />
              <MetricCard label="Devolução atrasada" value={String(overdueReturnsCount)} hint={overdueReturnsCount ? "Verifique os bens fora do prazo" : undefined} />
              <MetricCard label="Equipamentos reservados" value={String(reservedAssetsCount)} />
              <MetricCard label="Aguardando confirmação" value={String(servicesWithPendingConfirmationCount)} hint="Retirada ou devolução pendente" />
            </div>
          </section>
        ) : null}
        {assetsInMaintenanceCount > 0 ? (
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold text-ink">Manutenção {isCompanyScoped ? "da empresa" : "do escopo"}</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-3">
              <AlertCard icon={<Wrench size={18} />} label="Itens em manutenção" value={`${assetsInMaintenanceCount} bens`} />
            </div>
          </section>
        ) : null}
      </div>
    </>
  );
}

function CompanySwitcher({
  companies,
  selectedCompanyId,
  onChange
}: {
  companies: Company[];
  selectedCompanyId: string | null;
  onChange: (companyId: string | null) => void;
}) {
  const selectedCompany = companies.find((company) => company.id === selectedCompanyId) ?? null;
  const [query, setQuery] = useState(selectedCompany ? `${selectedCompany.tradeName} - ${selectedCompany.cnpj}` : "");
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const filteredCompanies = query.trim()
    ? companies.filter((company) => matchesCompanySearch(company, query))
    : companies;

  useEffect(() => {
    setQuery(selectedCompany ? `${selectedCompany.tradeName} - ${selectedCompany.cnpj}` : "");
  }, [selectedCompany]);

  return (
    <div className="relative w-full min-w-0 lg:w-[28rem]">
      <label className="sr-only" htmlFor="company-switcher">Selecionar empresa</label>
      <div className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        {selectedCompany?.logoUrl ? (
          <img src={selectedCompany.logoUrl} alt={selectedCompany.tradeName} className="h-7 w-7 shrink-0 rounded-md object-contain ring-1 ring-slate-200" />
        ) : (
          <Building2 size={16} className="shrink-0 text-brand-700" />
        )}
        <div className="min-w-0 flex-1">
          {!isResultsOpen ? (
            <button
              type="button"
              onClick={() => setIsResultsOpen(true)}
              className="block w-full min-w-0 text-left"
            >
              <span className="block truncate text-sm font-semibold text-ink">{selectedCompany ? selectedCompany.tradeName : "Todas as empresas"}</span>
              <span className="block truncate text-xs text-slate-500">{selectedCompany ? `CNPJ ${selectedCompany.cnpj}` : "Resumo consolidado"}</span>
            </button>
          ) : (
            <input
              id="company-switcher"
              className="min-w-0 bg-transparent text-sm font-medium text-ink outline-none"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setIsResultsOpen(true);
              }}
              onFocus={(event) => {
                event.currentTarget.select();
                setIsResultsOpen(true);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  setIsResultsOpen(true);
                }
                if (event.key === "Escape") {
                  setIsResultsOpen(false);
                }
              }}
              placeholder="Digite nome ou CNPJ"
            />
          )}
        </div>
        <button
          type="button"
          onClick={() => setIsResultsOpen((current) => !current)}
          aria-label="Listar empresas"
          className="focus-ring inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-white hover:text-brand-700"
        >
          <Search size={16} />
        </button>
      </div>
      {isResultsOpen ? (
        <div className="absolute right-0 z-40 mt-2 max-h-80 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white p-2 shadow-panel">
          {companies.length > 1 ? (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setQuery("");
                setIsResultsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-slate-50"
            >
              <Building2 size={18} className="text-brand-700" />
              <div>
                <p className="text-sm font-semibold text-ink">Todas as empresas</p>
                <p className="text-xs text-slate-500">Resumo consolidado</p>
              </div>
            </button>
          ) : null}
          {filteredCompanies.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => {
                onChange(company.id);
                setQuery(`${company.tradeName} - ${company.cnpj}`);
                setIsResultsOpen(false);
              }}
              className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-left hover:bg-slate-50"
            >
              {company.logoUrl ? (
                <img src={company.logoUrl} alt={company.tradeName} className="h-9 w-9 shrink-0 rounded-md object-contain ring-1 ring-slate-200" />
              ) : (
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-700">
                  <Building2 size={18} />
                </div>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-ink">{company.tradeName}</p>
                <p className="truncate text-xs text-slate-500">{company.cnpj} - {company.legalName}</p>
              </div>
            </button>
          ))}
          {!filteredCompanies.length ? <p className="px-3 py-2 text-sm text-slate-500">Nenhuma empresa encontrada.</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function AlertCard({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex items-center gap-2 text-brand-700">{icon}<span className="text-sm font-medium">{label}</span></div>
      <p className="mt-2 text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

function UnsavedChangesPrompt({
  title = "Descartar alterações?",
  description = "Existem alterações abertas que ainda não foram salvas.",
  isSaving = false,
  onSave,
  onDiscard,
  onCancel
}: UnsavedChangesPromptProps) {
  return (
    <div className="fixed inset-0 z-[110] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start gap-3">
          <span className="rounded-md bg-amber-50 p-2 text-amber-700"><AlertTriangle size={20} /></span>
          <div>
            <h2 className="font-semibold text-ink">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <SecondaryButton onClick={onCancel} disabled={isSaving}>Continuar editando</SecondaryButton>
          <SecondaryButton onClick={onDiscard} disabled={isSaving}><CircleSlash size={16} /> Descartar</SecondaryButton>
          <PrimaryButton onClick={onSave} disabled={isSaving}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}


function AssetsPage({
  accessContext,
  accessProfile,
  currentUser,
  onOpen,
  onTransferResponsibles,
  onCreate,
  compact = false,
  hideHeader = false
}: {
  accessContext: AccessContext;
  accessProfile: UserAccessProfile;
  currentUser: User | null;
  onOpen: (asset: AssetRecord) => void;
  onTransferResponsibles: () => void;
  onCreate: () => void;
  compact?: boolean;
  hideHeader?: boolean;
}) {
  const [search, setSearch] = useState("");
  const [responsibleFilter, setResponsibleFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState<Asset["status"] | "todos">("todos");
  const [gallery, setGallery] = useState<{ photos: string[]; index: number; title: string } | null>(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const selectedCompany = accessContext.selectedCompany;
  const selectedCompanyPermissions = selectedCompany ? accessProfile.companyPermissions[selectedCompany.id] : undefined;
  const canCreateAsset = Boolean(
    selectedCompany
    && (
      accessContext.globalRole === "MASTER"
      || accessContext.globalRole === "ADMIN"
      || accessContext.companyRole === "GERENTE"
      || canAccess(selectedCompanyPermissions, "canCreateAssets", accessContext.globalRole)
      || canAccess(selectedCompanyPermissions, "canEditAssets", accessContext.globalRole)
    )
  );
  const responsibles = Array.from(new Set(accessContext.scopedAssets.map((asset) => asset.responsible).filter(Boolean) as string[])).sort();
  const filteredAssets = accessContext.scopedAssets.filter((asset) => {
    const normalizedSearch = normalizeSearch(search);
    const searchable = normalizeSearch([asset.patrimonyNumber, asset.description, asset.serialNumber, asset.internalCode, asset.physicalLocation, asset.responsible].join(" "));

    return (!normalizedSearch || searchable.includes(normalizedSearch))
      && (responsibleFilter === "todos" || asset.responsible === responsibleFilter)
      && (statusFilter === "todos" || asset.status === statusFilter);
  });

  if (!selectedCompany && !compact) {
    return (
      <>
        <PageHeader title="Bens" subtitle="Selecione a empresa no cabeçalho fixo para listar e cadastrar bens imobilizados." />
        <div className="p-4 lg:p-6">
          <EmptyState title="Empresa obrigatoria" description="O cadastro de bens opera sempre dentro de uma empresa selecionada." />
        </div>
      </>
    );
  }

  return (
    <>
      {!hideHeader ? (
        <PageHeader
          title={compact ? "Bens do relatorio" : "Bens"}
          subtitle={selectedCompany ? `${selectedCompany.tradeName} - busca por patrimonio, descricao, série ou QR Code.` : "Listagem de bens do escopo atual."}
          action={
            <div className="flex flex-wrap gap-2">
              {!compact ? <SecondaryButton onClick={onTransferResponsibles}><MoveRight size={16} /> Responsáveis</SecondaryButton> : null}
              {!compact && canCreateAsset ? <SecondaryButton onClick={() => setIsImportModalOpen(true)}><Upload size={16} /> Importar CSV</SecondaryButton> : null}
              {!compact && canCreateAsset ? <PrimaryButton onClick={onCreate}><Plus size={16} /> Cadastrar bem</PrimaryButton> : null}
            </div>
          }
        />
      ) : null}
      <div className="p-4 lg:p-6">
        <div className="mb-4 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_180px_160px]">
          <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input className="min-w-0 flex-1 outline-none" placeholder="Buscar por patrimonio, descricao ou série" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={responsibleFilter} onChange={(event) => setResponsibleFilter(event.target.value)}>
            <option value="todos">Responsável: todos</option>
            {responsibles.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as Asset["status"] | "todos")}>
            <option value="todos">Status: todos</option>
            <option value="ativo">Ativos</option>
            <option value="inativo">Inativos</option>
            <option value="baixado">Baixados</option>
            <option value="vendido">Vendidos</option>
            <option value="perdido">Perdidos</option>
            <option value="em_manutencao">Em manutenção</option>
            <option value="em_auditoria">Em auditoria</option>
          </select>
        </div>
        {filteredAssets.length ? (
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {filteredAssets.map((asset) => (
              <button key={asset.id} type="button" onClick={() => onOpen(asset)} className="grid w-full gap-3 border-b border-slate-100 p-3 text-left last:border-b-0 hover:bg-slate-50 md:grid-cols-[132px_1fr_130px_130px_120px] md:items-center">
                <AssetPhotoPager asset={asset} onOpenGallery={(photos, index) => setGallery({ photos, index, title: `${asset.patrimonyNumber} - ${asset.description}` })} />
                <div className="min-w-0">
                  <p className="truncate font-semibold text-ink">{asset.patrimonyNumber} - {asset.description}</p>
                  <p className="mt-1 truncate text-sm text-slate-500">{asset.physicalLocation || "Sem local informado"} - {asset.responsible ?? "Sem responsavel"}</p>
                  <p className="mt-1 truncate text-xs text-slate-500">{[asset.brand, asset.model, asset.serialNumber ? `Serie ${asset.serialNumber}` : ""].filter(Boolean).join(" - ") || "Sem detalhes tecnicos"}</p>
                </div>
                <StatusPill tone={asset.status === "ativo" ? "green" : asset.status === "em_auditoria" ? "blue" : asset.status === "em_manutencao" ? "yellow" : "neutral"}>{asset.status}</StatusPill>
                <span className="text-sm text-slate-600">{formatCurrency(asset.acquisitionValue ?? 0)}</span>
                <AuditStatus status={asset.auditStatus} />
              </button>
            ))}
          </div>
        ) : (
          <EmptyState title="Nenhum bem encontrado" description="Ajuste os filtros ou cadastre o primeiro bem da empresa selecionada." />
        )}
      </div>
      {gallery ? <AssetListPhotoLightbox gallery={gallery} onChangeIndex={(index) => setGallery((current) => current ? { ...current, index } : current)} onClose={() => setGallery(null)} /> : null}
      {isImportModalOpen && selectedCompany ? (
        <CsvAssetImportModal
          accessContext={accessContext}
          currentUser={currentUser}
          defaultCompanyId={selectedCompany.id}
          onClose={() => setIsImportModalOpen(false)}
        />
      ) : null}
    </>
  );
}

function CsvAssetImportModal({
  accessContext,
  currentUser,
  defaultCompanyId,
  onClose
}: {
  accessContext: AccessContext;
  currentUser: User | null;
  defaultCompanyId: string;
  onClose: () => void;
}) {
  const [importCompanyId, setImportCompanyId] = useState(defaultCompanyId);
  const [columns, setColumns] = useState<CsvColumn[]>([]);
  const [rows, setRows] = useState<CsvParsedRow[]>([]);
  const [mapping, setMapping] = useState<CsvImportMapping>({});
  const [csvDrafts, setCsvDrafts] = useState<CsvAssetDraft[]>([]);
  const [selectedIssueKey, setSelectedIssueKey] = useState("");
  const [selectedRowNumber, setSelectedRowNumber] = useState<number | null>(null);
  const [duplicateActions, setDuplicateActions] = useState<Record<number, CsvDuplicateAction>>({});
  const [isImportingCsv, setIsImportingCsv] = useState(false);
  const [importMessage, setImportMessage] = useState("");
  const rowRefs = useRef<Record<number, HTMLTableRowElement | null>>({});
  const importCompany = accessContext.accessibleCompanies.find((company) => company.id === importCompanyId) ?? accessContext.selectedCompany;
  const importIssues = useMemo(() => importCompany
    ? validateCsvAssetDrafts(csvDrafts, accessContext.scopedAssets, importCompany.id)
    : [], [accessContext.scopedAssets, csvDrafts, importCompany]);
  const selectedImportIssue = importIssues.find((issue) => `${issue.rowNumber}-${issue.type}-${issue.message}` === selectedIssueKey) ?? importIssues[0];
  const missingIssues = importIssues.filter((issue) => duplicateActions[issue.rowNumber] !== "ignore" && issue.type === "missing");
  const unresolvedDuplicateIssues = importIssues.filter((issue) => issue.type === "duplicate" && !duplicateActions[issue.rowNumber]);
  const canImportCsv = Boolean(currentUser && importCompany && csvDrafts.length && !missingIssues.length && !unresolvedDuplicateIssues.length);
  const validRowsCount = Math.max(csvDrafts.length - new Set(importIssues.filter((issue) => duplicateActions[issue.rowNumber] !== "ignore").map((issue) => issue.rowNumber)).size, 0);
  const ignoredRowsCount = Object.values(duplicateActions).filter((action) => action === "ignore").length;
  const replaceRowsCount = Object.values(duplicateActions).filter((action) => action === "replace").length;

  function rebuildDrafts(nextRows: CsvParsedRow[], nextMapping: CsvImportMapping) {
    setCsvDrafts(csvRowsToAssetDrafts(nextRows, nextMapping));
    setSelectedIssueKey("");
    setSelectedRowNumber(null);
    setDuplicateActions({});
  }

  async function handleImportCsvFile(file: File | null) {
    if (!file) {
      return;
    }

    setImportMessage("Lendo arquivo CSV...");
    const text = await file.text();
    const parsed = parseCsvTextDetailed(text);
    const inferredMapping = inferCsvImportMapping(parsed.columns);
    setColumns(parsed.columns);
    setRows(parsed.rows);
    setMapping(inferredMapping);
    rebuildDrafts(parsed.rows, inferredMapping);
    setImportMessage(`${parsed.rows.length} linha(s) carregada(s). Confira o mapeamento das colunas antes de cadastrar o lote.`);
  }

  function updateColumnDestination(columnIndex: number, fieldValue: string) {
    const nextMapping = { ...mapping };
    Object.entries(nextMapping).forEach(([field, mappedColumnIndex]) => {
      if (mappedColumnIndex === columnIndex || field === fieldValue) {
        delete nextMapping[field as CsvImportField];
      }
    });

    if (fieldValue) {
      nextMapping[fieldValue as CsvImportField] = columnIndex;
    }

    setMapping(nextMapping);
    rebuildDrafts(rows, nextMapping);
  }

  function updateCsvDraft(rowNumber: number, patch: Partial<ImportAssetInput>) {
    setCsvDrafts((current) => current.map((draft) => draft.rowNumber === rowNumber ? { ...draft, ...patch } : draft));
  }

  function openIssue(issue: CsvAssetIssue) {
    const key = `${issue.rowNumber}-${issue.type}-${issue.message}`;
    setSelectedIssueKey(key);
    setSelectedRowNumber(issue.rowNumber);
    rowRefs.current[issue.rowNumber]?.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  function duplicateIssueForRow(rowNumber: number) {
    return importIssues.find((issue) => issue.rowNumber === rowNumber && issue.type === "duplicate");
  }

  function issueCountForRow(rowNumber: number) {
    return importIssues.filter((issue) => issue.rowNumber === rowNumber && duplicateActions[rowNumber] !== "ignore").length;
  }

  async function handleConfirmCsvImport() {
    if (!currentUser || !importCompany) {
      setImportMessage("Usuário ou empresa nao encontrado para importacao.");
      return;
    }

    if (!canImportCsv) {
      setImportMessage("Resolva as pendencias antes de cadastrar o lote.");
      return;
    }

    const duplicateByRow = new Map(importIssues.filter((issue) => issue.type === "duplicate").map((issue) => [issue.rowNumber, issue]));
    const rowsToCreate = csvDrafts.filter((draft) => !duplicateByRow.has(draft.rowNumber));
    const rowsToReplace = csvDrafts.filter((draft) => duplicateActions[draft.rowNumber] === "replace" && duplicateByRow.get(draft.rowNumber)?.existingAsset);

    setIsImportingCsv(true);
    setImportMessage("Cadastrando lote no banco de dados...");
    try {
      const branchId = seedBranches.find((branch) => branch.companyId === importCompany.id)?.id ?? "matriz";
      const createdCount = rowsToCreate.length
        ? await importAssetsFromCsv(importCompany.id, branchId, rowsToCreate, accessContext.scopedAssets, currentUser)
        : 0;

      for (const draft of rowsToReplace) {
        const existingAsset = duplicateByRow.get(draft.rowNumber)?.existingAsset;
        if (!existingAsset) {
          continue;
        }

        await updateAsset(importCompany.id, existingAsset.id, {
          patrimonyNumber: existingAsset.patrimonyNumber,
          description: draft.description,
          shortDescription: draft.description,
          assetTypeId: draft.assetTypeId,
          physicalLocation: draft.physicalLocation,
          department: draft.department,
          responsible: draft.responsible || importCompany.tradeName,
          responsibleUserId: existingAsset.responsibleUserId,
          conservationState: draft.conservationState,
          status: draft.status ?? "ativo",
          inactiveReason: draft.inactiveReason,
          serialNumber: draft.serialNumber,
          brand: draft.brand,
          model: draft.model,
          vehiclePlate: draft.vehiclePlate,
          chassis: draft.chassis,
          renavam: draft.renavam,
          manufactureYear: draft.manufactureYear,
          modelYear: draft.modelYear,
          mileageKm: draft.mileageKm,
          builtAreaM2: draft.builtAreaM2,
          dimensions: draft.dimensions,
          acquisitionValue: draft.acquisitionValue,
          purchaseDate: draft.purchaseDate,
          customFields: draft.customFields,
          canEditPatrimony: false
        }, currentUser);
      }

      setImportMessage(`${createdCount} bem(ns) cadastrado(s), ${rowsToReplace.length} substituido(s) e ${ignoredRowsCount} ignorado(s).`);
      setRows([]);
      setColumns([]);
      setCsvDrafts([]);
      setMapping({});
      setDuplicateActions({});
    } catch (error) {
      setImportMessage(error instanceof Error ? error.message : "Não foi possível importar o CSV.");
    } finally {
      setIsImportingCsv(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-6xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Importar bens por CSV</h2>
            <p className="mt-1 text-sm text-slate-500">Selecione o arquivo, confira as colunas identificadas e cadastre o lote somente depois da validação.</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>

        <div className="mt-4 grid gap-4 xl:grid-cols-[320px_1fr]">
          <aside className="space-y-3">
            <label className="text-sm font-medium text-slate-700">Empresa destino
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={importCompany?.id ?? ""} onChange={(event) => { setImportCompanyId(event.target.value); setSelectedIssueKey(""); }}>
                {accessContext.accessibleCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}
              </select>
            </label>
            <label className="block rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600">
              <span className="flex items-center gap-2 font-semibold text-ink"><Upload size={16} /> Selecionar CSV</span>
              <input type="file" accept=".csv,text/csv" className="mt-3 block w-full text-sm" onChange={(event) => void handleImportCsvFile(event.target.files?.[0] ?? null)} />
              <span className="mt-2 block text-xs">O sistema tenta reconhecer as colunas automaticamente, mas voce pode ajustar tudo manualmente.</span>
            </label>
            <div className="grid gap-2 rounded-md border border-slate-200 bg-white p-3 text-sm">
              <p><strong>{rows.length}</strong> linha(s) lida(s)</p>
              <p><strong>{columns.length}</strong> coluna(s) encontrada(s)</p>
              <p><strong>{importIssues.length}</strong> pendencia(s)</p>
              <p><strong>{validRowsCount}</strong> linha(s) sem pendencia</p>
              <p><strong>{replaceRowsCount}</strong> substituicao(oes), <strong>{ignoredRowsCount}</strong> ignorada(s)</p>
            </div>
            {importMessage ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{importMessage}</p> : null}
          </aside>

          <div className="space-y-4">
            {columns.length ? (
              <section className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-ink">Mapeamento das colunas</h3>
                    <p className="text-sm text-slate-500">Escolha para onde cada coluna do CSV deve ir no cadastro do bem.</p>
                  </div>
                  <StatusPill tone={mapping.patrimonyNumber !== undefined ? "green" : "yellow"}>{mapping.patrimonyNumber !== undefined ? "Patrimônio identificado" : "Mapeie o patrimonio"}</StatusPill>
                </div>
                <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {columns.map((column) => {
                    const selectedField = Object.entries(mapping).find(([, columnIndex]) => columnIndex === column.index)?.[0] ?? "";
                    return (
                      <label key={`${column.index}-${column.header}`} className="rounded-md border border-slate-200 bg-slate-50 p-2 text-sm font-medium text-slate-700">
                        <span className="block truncate">{column.header}</span>
                        <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 bg-white px-2 py-2" value={selectedField} onChange={(event) => updateColumnDestination(column.index, event.target.value)}>
                          <option value="">Ignorar coluna</option>
                          {csvImportFieldOptions.map((field) => <option key={field.id} value={field.id}>{field.label}{field.required ? " *" : ""}</option>)}
                        </select>
                      </label>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {importIssues.length ? (
              <section className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertTriangle size={16} /> Avisos para revisar antes de cadastrar</div>
                <div className="mt-3 grid max-h-48 gap-2 overflow-y-auto md:grid-cols-2">
                  {importIssues.map((issue) => {
                    const key = `${issue.rowNumber}-${issue.type}-${issue.message}`;
                    return (
                      <button key={key} type="button" onClick={() => openIssue(issue)} className={`rounded-md border p-3 text-left text-sm ${selectedIssueKey === key ? "border-amber-600 bg-white" : "border-amber-200 bg-amber-50"}`}>
                        <span className="block font-semibold text-amber-950">Linha {issue.rowNumber}</span>
                        <span className="block text-amber-800">{issue.message}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ) : rows.length ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-medium text-emerald-800">Arquivo validado. Nenhuma pendencia encontrada.</div>
            ) : (
              <EmptyState title="Nenhum CSV carregado" description="Selecione um arquivo para iniciar a leitura das colunas." />
            )}

            {selectedImportIssue ? (
              <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-semibold text-ink">Detalhes da linha {selectedImportIssue.rowNumber}</h3>
                  <StatusPill tone={selectedImportIssue.type === "duplicate" ? "red" : "yellow"}>{selectedImportIssue.type === "duplicate" ? "Duplicidade" : "Campo faltante"}</StatusPill>
                </div>
                {selectedImportIssue.type === "duplicate" ? (
                  <div className="mt-3 space-y-3">
                    {selectedImportIssue.existingAsset ? (
                      <div className="grid gap-3 md:grid-cols-2">
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                          <p className="text-sm font-semibold text-ink">CSV</p>
                          <AssetImportCompareData draft={selectedImportIssue.draft} />
                        </div>
                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                          <p className="text-sm font-semibold text-ink">Sistema</p>
                          <AssetImportCompareData asset={selectedImportIssue.existingAsset} />
                        </div>
                      </div>
                    ) : null}
                    <div className="flex flex-wrap gap-2">
                      {selectedImportIssue.existingAsset ? <PrimaryButton onClick={() => setDuplicateActions((current) => ({ ...current, [selectedImportIssue.rowNumber]: "replace" }))}><Check size={16} /> Substituir existente</PrimaryButton> : null}
                      <SecondaryButton onClick={() => setDuplicateActions((current) => ({ ...current, [selectedImportIssue.rowNumber]: "ignore" }))}><CircleSlash size={16} /> Ignorar linha</SecondaryButton>
                      {duplicateActions[selectedImportIssue.rowNumber] ? <StatusPill tone="blue">Acao: {duplicateActions[selectedImportIssue.rowNumber] === "replace" ? "substituir" : "ignorar"}</StatusPill> : null}
                    </div>
                  </div>
                ) : null}

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <label className="text-sm font-medium text-slate-700">Patrimônio<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedImportIssue.draft.patrimonyNumber} onChange={(event) => updateCsvDraft(selectedImportIssue.rowNumber, { patrimonyNumber: event.target.value })} /></label>
                  <label className="text-sm font-medium text-slate-700">Tipo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedImportIssue.draft.assetTypeId} onChange={(event) => updateCsvDraft(selectedImportIssue.rowNumber, { assetTypeId: event.target.value })} /></label>
                  <label className="text-sm font-medium text-slate-700 md:col-span-2">Descrição<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedImportIssue.draft.description} onChange={(event) => updateCsvDraft(selectedImportIssue.rowNumber, { description: event.target.value })} /></label>
                  <label className="text-sm font-medium text-slate-700">Local<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedImportIssue.draft.physicalLocation} onChange={(event) => updateCsvDraft(selectedImportIssue.rowNumber, { physicalLocation: event.target.value })} /></label>
                  <label className="text-sm font-medium text-slate-700">Responsável<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedImportIssue.draft.responsible ?? ""} onChange={(event) => updateCsvDraft(selectedImportIssue.rowNumber, { responsible: event.target.value })} /></label>
                </div>
              </section>
            ) : null}

            {csvDrafts.length ? (
              <section className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-3 py-2">
                  <h3 className="font-semibold text-ink">Prévia do lote</h3>
                </div>
                <div className="max-h-72 overflow-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-500">
                      <tr>
                        <th className="px-3 py-2">Linha</th>
                        <th className="px-3 py-2">Patrimônio</th>
                        <th className="px-3 py-2">Descrição</th>
                        <th className="px-3 py-2">Local</th>
                        <th className="px-3 py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvDrafts.map((draft) => {
                        const duplicateIssue = duplicateIssueForRow(draft.rowNumber);
                        const issueCount = issueCountForRow(draft.rowNumber);
                        return (
                          <tr
                            key={draft.rowNumber}
                            ref={(node) => { rowRefs.current[draft.rowNumber] = node; }}
                            className={`${selectedRowNumber === draft.rowNumber ? "bg-brand-50" : issueCount ? "bg-amber-50" : "bg-white"} border-b border-slate-100`}
                          >
                            <td className="px-3 py-2 font-semibold">{draft.rowNumber}</td>
                            <td className="px-3 py-2">{draft.patrimonyNumber || "-"}</td>
                            <td className="max-w-80 truncate px-3 py-2">{draft.description || "-"}</td>
                            <td className="px-3 py-2">{draft.physicalLocation || "-"}</td>
                            <td className="px-3 py-2">
                              {duplicateIssue ? <button type="button" className="text-sm font-semibold text-amber-700 underline" onClick={() => openIssue(duplicateIssue)}>Ver aviso</button> : issueCount ? <span className="font-semibold text-amber-700">{issueCount} aviso(s)</span> : <span className="text-emerald-700">Ok</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            ) : null}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
          <p className="text-sm text-slate-500">Duplicidades precisam ser substituidas ou ignoradas. Campos obrigatorios podem ser corrigidos no detalhe da linha.</p>
          <div className="flex flex-wrap gap-2">
            <SecondaryButton onClick={onClose}>Fechar</SecondaryButton>
            <PrimaryButton disabled={!canImportCsv || isImportingCsv} onClick={() => void handleConfirmCsvImport()}><Upload size={16} /> Cadastrar Lote</PrimaryButton>
          </div>
        </div>
      </div>
      {isImportingCsv ? <ProcessingOverlay title="Cadastrando lote" description="Gravando novos bens e aplicando substituicoes aprovadas." /> : null}
    </div>
  );
}

function AssetImportCompareData({ draft, asset }: { draft?: CsvAssetDraft; asset?: AssetRecord }) {
  const data = draft ?? asset;
  if (!data) {
    return null;
  }

  return (
    <div className="mt-2 space-y-1 text-sm text-slate-700">
      <p>Patrimônio: <strong>{data.patrimonyNumber || "-"}</strong></p>
      <p>Descrição: {data.description || "-"}</p>
      <p>Local: {data.physicalLocation || "-"}</p>
      <p>Responsável: {data.responsible || "-"}</p>
      <p>Marca/modelo: {[data.brand, data.model].filter(Boolean).join(" - ") || "-"}</p>
      <p>Série: {data.serialNumber || "-"}</p>
      <p>Valor: {formatCurrency(data.acquisitionValue ?? 0)}</p>
    </div>
  );
}

function AssetPhotoPager({ asset, onOpenGallery }: { asset: AssetRecord; onOpenGallery?: (photos: string[], index: number) => void }) {
  const photos = asset.photoUrls.length ? asset.photoUrls : asset.mainPhotoUrl ? [asset.mainPhotoUrl] : [];
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentPhoto = photos[currentIndex] ?? "";

  function showPrevious(event: MouseEvent<HTMLSpanElement>) {
    event.stopPropagation();
    setCurrentIndex((index) => (index === 0 ? photos.length - 1 : index - 1));
  }

  function showNext(event: MouseEvent<HTMLSpanElement>) {
    event.stopPropagation();
    setCurrentIndex((index) => (index + 1) % photos.length);
  }

  function openGallery(event: MouseEvent<HTMLSpanElement>) {
    event.stopPropagation();
    if (photos.length && onOpenGallery) {
      onOpenGallery(photos, currentIndex);
    }
  }

  return (
    <span role="button" tabIndex={0} onClick={openGallery} className="relative block h-24 w-32 overflow-hidden rounded-md bg-slate-100 ring-1 ring-slate-200">
      {currentPhoto ? (
        <img src={currentPhoto} alt={asset.description || "Foto do bem"} className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center text-xs font-medium text-slate-500">Sem foto</span>
      )}
      {photos.length > 1 ? (
        <>
          <span role="button" tabIndex={0} aria-label="Foto anterior" onClick={showPrevious} className="absolute left-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md bg-white/90 text-slate-700 shadow-sm">
            <ChevronLeft size={16} />
          </span>
          <span role="button" tabIndex={0} aria-label="Proxima foto" onClick={showNext} className="absolute right-1 top-1/2 inline-flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md bg-white/90 text-slate-700 shadow-sm">
            <ChevronRight size={16} />
          </span>
          <span className="absolute bottom-1 right-1 rounded bg-ink/80 px-1.5 py-0.5 text-[11px] font-semibold text-white">{currentIndex + 1}/{photos.length}</span>
        </>
      ) : null}
    </span>
  );
}

function EmptyAssetSelectionPage({ onBack }: { onBack: () => void }) {
  return (
    <>
      <PageHeader title="Bem não encontrado" subtitle="Nenhum bem foi selecionado no escopo atual." action={<SecondaryButton onClick={onBack}><RotateCcw size={16} /> Voltar</SecondaryButton>} />
      <div className="p-4 lg:p-6">
        <EmptyState title="Selecione um bem" description="Abra um item pela listagem de bens para visualizar os detalhes." />
      </div>
    </>
  );
}

function AssetListPhotoLightbox({
  gallery,
  onChangeIndex,
  onClose
}: {
  gallery: { photos: string[]; index: number; title: string };
  onChangeIndex: (index: number) => void;
  onClose: () => void;
}) {
  const [zoom, setZoom] = useState(1);
  const activePhoto = gallery.photos[gallery.index];

  function previous() {
    onChangeIndex(gallery.index === 0 ? gallery.photos.length - 1 : gallery.index - 1);
    setZoom(1);
  }

  function next() {
    onChangeIndex((gallery.index + 1) % gallery.photos.length);
    setZoom(1);
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-slate-950 text-white">
      <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{gallery.title}</p>
          <p className="text-xs text-white/60">{gallery.index + 1}/{gallery.photos.length}</p>
        </div>
        <div className="flex gap-2">
          <SecondaryButton onClick={() => setZoom((current) => Math.min(current + 0.25, 3))}><ZoomIn size={16} /> Zoom</SecondaryButton>
          <SecondaryButton onClick={onClose}>Fechar</SecondaryButton>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-auto p-4">
        {gallery.photos.length > 1 ? <button type="button" onClick={previous} className="absolute left-4 z-10 rounded-full bg-white/10 p-3 hover:bg-white/20"><ChevronLeft size={28} /></button> : null}
        {activePhoto ? <img src={activePhoto} alt={gallery.title} className="max-h-full max-w-full rounded-md object-contain transition-transform" style={{ transform: `scale(${zoom})` }} /> : null}
        {gallery.photos.length > 1 ? <button type="button" onClick={next} className="absolute right-4 z-10 rounded-full bg-white/10 p-3 hover:bg-white/20"><ChevronRight size={28} /></button> : null}
      </div>
    </div>
  );
}

function AccessDeniedPage({ title, description }: { title: string; description: string }) {
  return (
    <>
      <PageHeader title={title} subtitle="Acesso restrito por perfil." />
      <div className="p-4 lg:p-6">
        <EmptyState title="Sem permissão" description={description} />
      </div>
    </>
  );
}

function AssetFormPageLegacy({ accessContext, onSaved, onBack }: { accessContext: AccessContext; onSaved: () => void; onBack: () => void }) {
  const [locations, setLocations] = useState(seedLocations);
  const [brands, setBrands] = useState(seedBrands);
  const [departments, setDepartments] = useState(seedDepartments);
  const [responsibles, setResponsibles] = useState(seedResponsibles);

  if (!accessContext.selectedCompany) {
    return (
      <>
        <PageHeader title="Cadastro de bem" subtitle="Selecione uma empresa antes de cadastrar bens." action={<SecondaryButton onClick={onBack}><RotateCcw size={16} /> Voltar</SecondaryButton>} />
        <div className="p-4 lg:p-6">
          <EmptyState title="Empresa obrigatoria" description="O novo bem precisa ser vinculado a uma empresa ativa." />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Cadastro de bem" subtitle="Formulário inicial otimizado para celular, com foto obrigatória e fila offline preparada." />
      <form className="grid gap-4 p-4 lg:grid-cols-2 lg:p-6">
        {["Número de patrimônio", "Descrição", "Número de série", "Modelo", "Valor de aquisição"].map((label) => (
          <label key={label} className="text-sm font-medium text-slate-700">
            {label}
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" />
          </label>
        ))}
        <ReferenceSelectWithCreate label="Local físico" value="" onChange={() => undefined} options={locations} onCreate={(value) => setLocations((items) => [...items, value])} />
        <ReferenceSelectWithCreate label="Marca" value="" onChange={() => undefined} options={brands} onCreate={(value) => setBrands((items) => [...items, value])} />
        <ReferenceSelectWithCreate label="Departamento/setor" value="" onChange={() => undefined} options={departments} onCreate={(value) => setDepartments((items) => [...items, value])} />
        <ReferenceSelectWithCreate label="Responsável" value="" onChange={() => undefined} options={responsibles} onCreate={(value) => setResponsibles((items) => [...items, value])} />
        <label className="text-sm font-medium text-slate-700">
          Tipo de imobilizado
          <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2">
            {seedDepreciationRules.map((rule) => <option key={rule.id}>{rule.assetTypeName}</option>)}
          </select>
        </label>
        <label className="text-sm font-medium text-slate-700">
          Estado de conservação
          <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"><option>bom</option><option>otimo</option><option>regular</option></select>
        </label>
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 lg:col-span-2">
          <div className="flex flex-wrap gap-3">
            <SecondaryButton><Camera size={16} /> Abrir câmera</SecondaryButton>
            <SecondaryButton><Upload size={16} /> Enviar galeria</SecondaryButton>
          </div>
          <p className="mt-3 text-sm text-slate-500">Pelo menos 1 foto será obrigatória antes de sincronizar o cadastro definitivo.</p>
        </div>
        <div className="lg:col-span-2">
          <PrimaryButton onClick={onSaved}><Save size={16} /> Salvar e gerar etiqueta</PrimaryButton>
        </div>
      </form>
    </>
  );
}

void AssetFormPageLegacy;

function AssetFormPage({
  accessContext,
  currentUser,
  imageCompression,
  onSaved,
  onBack
}: {
  accessContext: AccessContext;
  currentUser: User | null;
  imageCompression: SystemSettings["imageCompression"];
  onSaved: (assetId: string, shouldGenerateLabel: boolean) => void;
  onBack: () => void;
}) {
  const [locations, setLocations] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserProfile[]>([]);
  const [automaticNumbering, setAutomaticNumbering] = useState(true);
  const [patrimonyNumber, setPatrimonyNumber] = useState("");
  const [description, setDescription] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [model, setModel] = useState("");
  const [vehiclePlate, setVehiclePlate] = useState("");
  const [chassis, setChassis] = useState("");
  const [renavam, setRenavam] = useState("");
  const [manufactureYear, setManufactureYear] = useState("");
  const [modelYear, setModelYear] = useState("");
  const [mileageKm, setMileageKm] = useState("");
  const [builtAreaM2, setBuiltAreaM2] = useState("");
  const [dimensions, setDimensions] = useState("");
  const [acquisitionValue, setAcquisitionValue] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [physicalLocation, setPhysicalLocation] = useState("");
  const [brand, setBrand] = useState("");
  const [department, setDepartment] = useState("");
  const [responsible, setResponsible] = useState("");
  const [assetTypeId, setAssetTypeId] = useState("");
  const [customFields, setCustomFields] = useState<Record<string, string | number | boolean | null>>({});
  const [conservationState, setConservationState] = useState<Asset["conservationState"]>("bom");
  const [availableForExternalService, setAvailableForExternalService] = useState(false);
  const [assetGroups, setAssetGroups] = useState<AssetGroup[]>([]);
  const [assetGroup, setAssetGroup] = useState("");
  const [photos, setPhotos] = useState<File[]>([]);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [scanTarget, setScanTarget] = useState<"patrimonyNumber" | "serialNumber" | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const formRef = useRef<HTMLFormElement | null>(null);
  const saveModeRef = useRef<"list" | "label">("list");
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const selectedCompany = accessContext.selectedCompany;
  const externalServicesEnabled = selectedCompany?.settings?.externalServicesEnabled === true;
  const canManageAssetGroups = accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN" || accessContext.companyRole === "GERENTE";

  useEffect(() => subscribeGlobalReferences(
    "departments",
    (items) => setDepartments(items.map((item) => item.name)),
    (message) => console.warn("Não foi possivel carregar departamentos.", message)
  ), []);

  useEffect(() => subscribeAssetTypes(
    setAssetTypes,
    (message) => console.warn("Não foi possivel carregar tipos de imobilizado.", message)
  ), []);

  useEffect(() => {
    if (!selectedCompany) {
      setLocations([]);
      setBrands([]);
      setCompanyUsers([]);
      setAssetGroups([]);
      return () => undefined;
    }

    const unsubscribeLocations = subscribeCompanyLocations(
      selectedCompany.id,
      (items) => setLocations(items.map((item) => item.name)),
      (message) => console.warn("Não foi possivel carregar locais fisicos.", message)
    );
    const unsubscribeBrands = subscribeCompanyBrands(
      selectedCompany.id,
      (items) => setBrands(items.map((item) => item.name)),
      (message) => console.warn("Não foi possível carregar marcas da empresa.", message)
    );
    const unsubscribeUsers = subscribeCompanyUsers(
      selectedCompany.id,
      setCompanyUsers,
      (message) => console.warn("Não foi possivel carregar responsaveis da empresa.", message)
    );
    const unsubscribeAssetGroups = subscribeAssetGroups(
      selectedCompany.id,
      setAssetGroups,
      (message) => console.warn("Não foi possível carregar grupos de bens.", message)
    );

    return () => {
      unsubscribeLocations();
      unsubscribeBrands();
      unsubscribeUsers();
      unsubscribeAssetGroups();
    };
  }, [selectedCompany]);

  const selectedAssetType = assetTypes.find((item) => item.id === assetTypeId);
  const assetTypeKind = getAssetTypeKind(selectedAssetType?.name ?? "");
  const selectedDynamicFields = selectedAssetType?.dynamicFields ?? [];

  const responsibleOptions = useMemo(() => {
    if (!selectedCompany) {
      return [];
    }

    const users = companyUsers.map((profile) => profile.fullName ?? profile.displayName ?? profile.email).filter(Boolean);
    return [selectedCompany.tradeName, ...users];
  }, [companyUsers, selectedCompany]);
  const selectedResponsibleUser = useMemo(
    () => companyUsers.find((profile) => responsibleMatches(responsible, userResponsibleLabels(profile))),
    [companyUsers, responsible]
  );

  useEffect(() => {
    if (selectedCompany && !responsible) {
      const mode = selectedCompany.settings?.defaultResponsibleMode ?? "company";
      if (mode === "company") {
        setResponsible(selectedCompany.tradeName);
      }
    }
  }, [responsible, selectedCompany]);

  const branchId = useMemo(() => {
    if (!selectedCompany) {
      return "";
    }

    return seedBranches.find((branch) => branch.companyId === selectedCompany.id)?.id ?? "matriz";
  }, [selectedCompany]);

  function handleScanResult(value: string) {
    if (scanTarget === "patrimonyNumber") {
      setPatrimonyNumber(value);
    }

    if (scanTarget === "serialNumber") {
      setSerialNumber(value);
    }

    setScanTarget(null);
  }

  async function addPhotos(files: FileList | null) {
    const imageFiles = Array.from(files ?? []).filter((file) => file.type.startsWith("image/"));
    if (!imageFiles.length) {
      return;
    }

    setMessage("Compactando fotos...");
    try {
      const compressedFiles = await compressImageFiles(imageFiles, imageCompression);
      setPhotos((current) => [...current, ...compressedFiles].slice(0, 4));
      setIsDirty(true);
      setMessage("");
    } catch {
      setPhotos((current) => [...current, ...imageFiles].slice(0, 4));
      setIsDirty(true);
      setMessage("Não foi possível compactar uma foto. Ela foi anexada no formato original.");
    }
  }

  function removePhoto(indexToRemove: number) {
    setPhotos((current) => current.filter((_, index) => index !== indexToRemove));
    setIsDirty(true);
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCompany || !currentUser) {
      setMessage("Selecione uma empresa e confirme a sessão autenticada antes de salvar.");
      return;
    }

    if (!description.trim()) {
      setMessage("Informe a descrição completa do bem.");
      return;
    }

    if (!physicalLocation) {
      setMessage("Selecione ou cadastre o local físico do bem.");
      return;
    }

    if (!assetTypeId) {
      setMessage("Selecione o tipo de imobilizado.");
      return;
    }

    if ((selectedCompany.settings?.defaultResponsibleMode ?? "company") === "required" && !responsible) {
      setMessage("Selecione o responsavel do bem conforme a configuracao da empresa.");
      return;
    }

    const missingDynamicField = selectedDynamicFields.find((field) => field.required && !String(customFields[field.id] ?? "").trim());
    if (missingDynamicField) {
      setMessage(`Informe o campo ${missingDynamicField.label}.`);
      return;
    }

    if (!photos.length) {
      setMessage("Inclua pelo menos uma foto do bem antes de salvar.");
      return;
    }

    if (externalServicesEnabled && availableForExternalService && !assetGroup) {
      setMessage("Selecione um grupo de bens para liberar este bem em serviços externos.");
      return;
    }

    setIsSaving(true);
    setMessage("Salvando bem e enviando fotos...");
    try {
      const result = await createAsset({
        companyId: selectedCompany.id,
        branchId,
        patrimonyNumber,
        automaticNumbering,
        description,
        shortDescription: description,
        assetTypeId,
        physicalLocation,
        department,
        responsible: responsible || selectedCompany.tradeName,
        conservationState,
        serialNumber,
        brand,
        model,
        vehiclePlate,
        chassis,
        renavam,
        manufactureYear: manufactureYear ? Number(manufactureYear) : undefined,
        modelYear: modelYear ? Number(modelYear) : undefined,
        mileageKm: parseCurrencyNumber(mileageKm),
        builtAreaM2: parseCurrencyNumber(builtAreaM2),
        dimensions,
        acquisitionValue: parseCurrencyNumber(acquisitionValue),
        purchaseDate,
        availableForExternalService: externalServicesEnabled ? availableForExternalService : false,
        assetGroup: externalServicesEnabled && availableForExternalService ? assetGroup : undefined,
        customFields,
        responsibleUserId: selectedResponsibleUser?.id,
        photos,
        existingAssets: accessContext.scopedAssets
      }, currentUser);

      setMessage(`Bem ${result.patrimonyNumber} cadastrado com sucesso.`);
      setIsDirty(false);
      onSaved(result.assetId, saveModeRef.current === "label");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível cadastrar o bem.");
    } finally {
      setIsSaving(false);
    }
  }

  if (!selectedCompany) {
    return (
      <>
        <PageHeader title="Cadastro de bem" subtitle="Selecione uma empresa antes de cadastrar bens." action={<SecondaryButton onClick={onBack}><RotateCcw size={16} /> Voltar</SecondaryButton>} />
        <div className="p-4 lg:p-6">
          <EmptyState title="Empresa obrigatoria" description="O novo bem precisa ser vinculado a uma empresa ativa." />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Cadastro de bem" subtitle={`${selectedCompany.tradeName} - formulario inicial com foto obrigatoria.`} action={<SecondaryButton onClick={() => isDirty ? setShowUnsavedPrompt(true) : onBack()}><RotateCcw size={16} /> Voltar para lista</SecondaryButton>} />
      <form ref={formRef} className="grid gap-4 p-4 lg:grid-cols-2 lg:p-6" onSubmit={handleSubmit} onChange={() => setIsDirty(true)}>
        <label className="text-sm font-medium text-slate-700 lg:col-span-2">
          Tipo de imobilizado
          <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={assetTypeId} onChange={(event) => setAssetTypeId(event.target.value)} required>
            <option value="">Selecionar tipo para ajustar o cadastro</option>
            {assetTypes.map((rule) => <option key={rule.id} value={rule.id}>{rule.name}</option>)}
          </select>
          {!assetTypes.length ? <span className="mt-1 block text-xs text-slate-500">Nenhum tipo cadastrado. O MASTER deve configurar a tabela global de imobilizados.</span> : null}
        </label>
        <div className="rounded-lg border border-slate-200 bg-white p-4 lg:col-span-2">
          <label className="flex items-start gap-3 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={automaticNumbering}
              onChange={(event) => setAutomaticNumbering(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700"
            />
            <span>
              <span className="block font-semibold text-ink">Numeração automática</span>
              <span className="text-slate-500">O IMOBI lê o último patrimônio numérico da empresa e gera o próximo sequencial disponível.</span>
            </span>
          </label>
        </div>
        <CodeInput label="Número de patrimônio" value={patrimonyNumber} onChange={setPatrimonyNumber} onScan={() => setScanTarget("patrimonyNumber")} disabled={automaticNumbering} placeholder={automaticNumbering ? "Gerado automaticamente ao salvar" : ""} />
        <label className="text-sm font-medium text-slate-700">
          Descrição completa
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={description} onChange={(event) => setDescription(event.target.value.toLocaleUpperCase("pt-BR"))} required />
        </label>
        {assetTypeKind === "generic" ? <CodeInput label="Número de série" value={serialNumber} onChange={setSerialNumber} onScan={() => setScanTarget("serialNumber")} /> : null}
        {assetTypeKind === "generic" ? (
          <label className="text-sm font-medium text-slate-700">
            Modelo
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={model} onChange={(event) => setModel(event.target.value.toLocaleUpperCase("pt-BR"))} />
          </label>
        ) : null}
        {assetTypeKind === "vehicle" ? (
          <>
            <label className="text-sm font-medium text-slate-700">Placa<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={vehiclePlate} onChange={(event) => setVehiclePlate(event.target.value.toLocaleUpperCase("pt-BR"))} /></label>
            <label className="text-sm font-medium text-slate-700">Chassi<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={chassis} onChange={(event) => setChassis(event.target.value.toLocaleUpperCase("pt-BR"))} /></label>
            <label className="text-sm font-medium text-slate-700">Renavam<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={renavam} onChange={(event) => setRenavam(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700">Ano fabricação<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" maxLength={4} value={manufactureYear} onChange={(event) => setManufactureYear(event.target.value.replace(/\D/g, "").slice(0, 4))} /></label>
            <label className="text-sm font-medium text-slate-700">Ano modelo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" maxLength={4} value={modelYear} onChange={(event) => setModelYear(event.target.value.replace(/\D/g, "").slice(0, 4))} /></label>
            <label className="text-sm font-medium text-slate-700">Quilometragem<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={mileageKm} onChange={(event) => setMileageKm(event.target.value)} /></label>
          </>
        ) : null}
        {assetTypeKind === "building" ? (
          <>
            <label className="text-sm font-medium text-slate-700">Área construída m²<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="decimal" value={builtAreaM2} onChange={(event) => setBuiltAreaM2(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700">Endereço / dimensões<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={dimensions} onChange={(event) => setDimensions(event.target.value.toLocaleUpperCase("pt-BR"))} /></label>
          </>
        ) : null}
        <label className="text-sm font-medium text-slate-700">
          Valor de aquisição
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={acquisitionValue} onChange={(event) => setAcquisitionValue(formatCurrencyMask(event.target.value))} placeholder="R$ 0,00" />
        </label>
        <label className="text-sm font-medium text-slate-700">
          Data de aquisição
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="date" value={purchaseDate} onChange={(event) => setPurchaseDate(event.target.value)} />
        </label>
        <ReferenceSelectWithCreate label="Local fisico" value={physicalLocation} onChange={setPhysicalLocation} options={locations} onCreate={(value) => saveCompanyLocation(selectedCompany.id, value)} />
        {assetTypeKind === "generic" || assetTypeKind === "vehicle" ? <ReferenceSelectWithCreate label="Marca" value={brand} onChange={setBrand} options={brands} onCreate={(value) => saveCompanyBrand(selectedCompany.id, value)} /> : null}
        <ReferenceSelectWithCreate label="Departamento/setor" value={department} onChange={setDepartment} options={departments} onCreate={(value) => saveGlobalReference("departments", value)} />
        <ReferenceSelectWithCreate label="Responsável" value={responsible} onChange={setResponsible} options={responsibleOptions} allowCreate={false} />
        <label className="text-sm font-medium text-slate-700">
          Estado de conservação
          <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={conservationState} onChange={(event) => setConservationState(event.target.value as Asset["conservationState"])}>
            <option value="novo">novo</option>
            <option value="otimo">ótimo</option>
            <option value="bom">bom</option>
            <option value="regular">regular</option>
            <option value="ruim">ruim</option>
            <option value="sucata">sucata</option>
          </select>
        </label>
        {selectedDynamicFields.length ? (
          <DynamicFieldsInput fields={selectedDynamicFields} values={customFields} onChange={setCustomFields} />
        ) : null}
        {externalServicesEnabled ? (
          <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700 lg:col-span-2">
            <input
              type="checkbox"
              checked={availableForExternalService}
              onChange={(event) => setAvailableForExternalService(event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700"
            />
            <span>
              <span className="block font-semibold text-ink">Disponível para serviços externos</span>
              <span className="text-slate-500">Permite que este bem seja selecionado em saídas temporárias e atribuído a participantes.</span>
            </span>
          </label>
        ) : null}
        {externalServicesEnabled && availableForExternalService ? (
          <div className="lg:col-span-2">
            <ReferenceSelectWithCreate
              label="Grupo de bens"
              value={assetGroup}
              onChange={setAssetGroup}
              options={assetGroups.filter((group) => group.active).map((group) => group.name)}
              onCreate={canManageAssetGroups ? (name) => { if (currentUser) return createAssetGroup(selectedCompany.id, name, currentUser); } : undefined}
              allowCreate={canManageAssetGroups}
            />
            <p className="mt-1 text-xs text-slate-500">Define quais usuários poderão solicitar este bem em serviços externos, conforme o acesso configurado ao grupo.</p>
          </div>
        ) : null}
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 lg:col-span-2">
          <div className="flex flex-wrap gap-3">
            <SecondaryButton onClick={() => cameraInputRef.current?.click()}><Camera size={16} /> Abrir câmera</SecondaryButton>
            <SecondaryButton onClick={() => galleryInputRef.current?.click()}><Upload size={16} /> Enviar galeria</SecondaryButton>
          </div>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => void addPhotos(event.target.files)} />
          <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(event) => void addPhotos(event.target.files)} />
          <p className="mt-3 text-sm text-slate-500">
            Pelo menos 1 foto e obrigatoria. Compactacao atual: {imageCompression.enabled ? `${imageCompression.maxWidth}x${imageCompression.maxHeight}px, qualidade ${Math.round(imageCompression.quality * 100)}%` : "desativada"}.
          </p>
          {photos.length ? (
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {photos.map((photo, index) => (
                <div key={`${photo.name}-${index}`} className="relative overflow-hidden rounded-md border border-slate-200 bg-slate-50">
                  <img src={URL.createObjectURL(photo)} alt={`Foto ${index + 1}`} className="h-24 w-full object-cover" />
                  <button type="button" onClick={() => removePhoto(index)} className="absolute right-1 top-1 rounded bg-white/90 px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm">
                    Remover
                  </button>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col gap-3 lg:col-span-2">
          <div className="flex flex-col gap-2 sm:flex-row">
            <PrimaryButton type="submit" disabled={isSaving} onClick={() => { saveModeRef.current = "list"; }}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar"}</PrimaryButton>
            <SecondaryButton type="submit" disabled={isSaving} onClick={() => { saveModeRef.current = "label"; }}><Printer size={16} /> Salvar e gerar etiqueta</SecondaryButton>
          </div>
          {message ? <p className="text-sm text-slate-600">{message}</p> : null}
        </div>
      </form>
      {scanTarget ? (
        <CodeScannerModal
          title={scanTarget === "patrimonyNumber" ? "Ler numero de patrimonio" : "Ler numero de série"}
          onClose={() => setScanTarget(null)}
          onScan={handleScanResult}
        />
      ) : null}
      {isSaving ? <ProcessingOverlay title="Salvando bem imobilizado" description="Gravando os dados e enviando as fotos." /> : null}
      {showUnsavedPrompt ? (
        <UnsavedChangesPrompt
          isSaving={isSaving}
          onCancel={() => setShowUnsavedPrompt(false)}
          onDiscard={() => {
            setShowUnsavedPrompt(false);
            setIsDirty(false);
            onBack();
          }}
          onSave={() => {
            saveModeRef.current = "list";
            setShowUnsavedPrompt(false);
            formRef.current?.requestSubmit();
          }}
        />
      ) : null}
    </>
  );
}

function AssetDetailPage({
  asset,
  accessContext,
  accessProfile,
  depreciationRules,
  currentUser,
  onBack,
  onPhotos
}: {
  asset: AssetRecord;
  accessContext: AccessContext;
  accessProfile: UserAccessProfile;
  depreciationRules: DepreciationRule[];
  currentUser: User | null;
  onBack: () => void;
  onPhotos: () => void;
}) {
  const depreciationRule = findDepreciationRuleForAsset(asset, depreciationRules);
  const depreciation = calculateLinearMonthlyDepreciation(asset, depreciationRule);
  const depreciationChartData = buildDepreciationChartData(asset, depreciationRule);
  const [isEditing, setIsEditing] = useState(false);
  const [isAssetEditDirty, setIsAssetEditDirty] = useState(false);
  const [showAssetEditPrompt, setShowAssetEditPrompt] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusHistory, setStatusHistory] = useState<AssetStatusHistoryRecord[]>([]);
  const [maintenanceHistory, setMaintenanceHistory] = useState<AssetMaintenanceRecord[]>([]);
  const assetEditFormRef = useRef<HTMLFormElement | null>(null);
  const canEdit = canEditAssetRecord(accessContext, accessProfile, asset);
  const canTransferResponsible = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || accessProfile.manageableCompanyIds.includes(asset.companyId)
    || accessProfile.companyRoles[asset.companyId] === "GERENTE";
  const assetCompany = accessContext.scopedCompanies.find((company) => company.id === asset.companyId) ?? accessContext.selectedCompany;
  const assetExternalServicesEnabled = assetCompany?.settings?.externalServicesEnabled === true;

  function handleBack() {
    if (isEditing && isAssetEditDirty) {
      setShowAssetEditPrompt(true);
      return;
    }

    onBack();
  }

  useEffect(() => {
    const unsubscribeStatus = subscribeAssetStatusHistory(asset.companyId, asset.id, setStatusHistory, (message) => console.warn("Não foi possível carregar histórico de status.", message));
    const unsubscribeMaintenance = subscribeAssetMaintenance(asset.companyId, asset.id, setMaintenanceHistory, (message) => console.warn("Não foi possível carregar manutencoes.", message));
    return () => {
      unsubscribeStatus();
      unsubscribeMaintenance();
    };
  }, [asset.companyId, asset.id]);

  return (
    <>
      <PageHeader
        title="Detalhes do bem"
        subtitle={`${asset.patrimonyNumber} - ${asset.description}`}
        action={
          <div className="flex flex-wrap gap-2">
            <SecondaryButton onClick={handleBack}><RotateCcw size={16} /> Voltar</SecondaryButton>
            {canEdit && !isEditing ? <SecondaryButton onClick={() => setIsEditing(true)}><Edit3 size={16} /> Editar</SecondaryButton> : null}
            {canEdit && !isEditing ? <SecondaryButton onClick={() => setIsStatusModalOpen(true)}><History size={16} /> Status</SecondaryButton> : null}
            {!isEditing && canTransferResponsible ? <SecondaryButton onClick={() => setIsTransferModalOpen(true)}><MoveRight size={16} /> Trocar responsavel</SecondaryButton> : null}
            {!isEditing ? <SecondaryButton onClick={onPhotos}><Camera size={16} /> Fotos</SecondaryButton> : null}
          </div>
        }
      />
      <div className="grid gap-4 p-4 lg:grid-cols-[360px_1fr] lg:p-6">
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          {asset.mainPhotoUrl ? <button type="button" onClick={onPhotos} className="block w-full"><img src={asset.mainPhotoUrl} alt={asset.description} className="h-64 w-full object-cover" /></button> : <div className="flex h-64 items-center justify-center bg-slate-100 text-slate-500">Sem foto principal</div>}
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              <StatusPill tone={asset.status === "ativo" ? "green" : asset.status === "em_manutencao" ? "yellow" : asset.status === "em_auditoria" ? "blue" : "neutral"}>{formatAssetStatusLabel(asset.status)}</StatusPill>
              <AuditStatus status={asset.auditStatus} />
              {assetExternalServicesEnabled && asset.availableForExternalService ? <StatusPill tone="blue">Serviços externos</StatusPill> : null}
              {assetExternalServicesEnabled && asset.externalServiceStatus && asset.externalServiceStatus !== "disponivel" ? <StatusPill tone={asset.externalServiceStatus === "extraviado" || asset.externalServiceStatus === "divergente" ? "red" : "yellow"}>{formatAssetExternalServiceStatus(asset.externalServiceStatus)}</StatusPill> : null}
              {assetExternalServicesEnabled && isAssetReturnOverdue(asset) ? <StatusPill tone="red">Devolução atrasada</StatusPill> : null}
            </div>
            <p className="mt-3 text-sm text-slate-500">{asset.photoUrls.length} foto(s). Clique na foto para abrir a galeria.</p>
          </div>
        </div>
        {isEditing && canEdit ? (
          <AssetEditPanel
            asset={asset}
            accessContext={accessContext}
            currentUser={currentUser}
            externalFormRef={assetEditFormRef}
            onDirtyChange={setIsAssetEditDirty}
            onDone={() => {
              setIsAssetEditDirty(false);
              setIsEditing(false);
            }}
          />
        ) : (
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <dl className="grid gap-4 md:grid-cols-2">
              <Info label="Local" value={asset.physicalLocation} />
              <Info label="Responsável" value={asset.responsible ?? "-"} />
              <Info label="Marca / modelo" value={[asset.brand, asset.model].filter(Boolean).join(" - ") || "-"} />
              <Info label="Serie" value={asset.serialNumber ?? "-"} />
              <Info label="Placa / chassi" value={[asset.vehiclePlate, asset.chassis].filter(Boolean).join(" - ") || "-"} />
              <Info label="Ano veículo" value={[asset.manufactureYear ? `Fab. ${asset.manufactureYear}` : "", asset.modelYear ? `Mod. ${asset.modelYear}` : ""].filter(Boolean).join(" - ") || "-"} />
              <Info label="Area / KM" value={[asset.builtAreaM2 ? `${asset.builtAreaM2} m2` : "", asset.mileageKm ? `${asset.mileageKm} km` : ""].filter(Boolean).join(" - ") || "-"} />
              {asset.inactiveReason ? <Info label="Motivo inativacao" value={asset.inactiveReason} /> : null}
              <Info label="Conservação" value={asset.conservationState} />
              <Info label="Aquisição" value={formatCurrency(asset.acquisitionValue ?? 0)} />
              <Info label="Data de aquisição" value={formatDateOnly(asset.purchaseDate)} />
              <Info label="Idade do bem" value={formatAssetAge(asset.purchaseDate)} />
              <Info label="Depreciação acumulada" value={formatCurrency(depreciation.accumulatedDepreciation)} />
              <Info label="Valor contabil atual" value={formatCurrency(depreciation.currentBookValue)} />
              <Info label="Auditoria" value={asset.auditorCompanyName ? `Auditada por ${asset.auditorCompanyName}` : "Não auditada"} />
              <Info label="Tabela de depreciação" value={depreciationRule ? depreciationRule.assetTypeName : "Sem tabela vinculada"} />
              <Info label="Vida útil usada" value={depreciation.usefulLifeMonths ? `${Math.round(depreciation.usefulLifeMonths / 12)} anos` : "Não depreciável"} />
              {assetExternalServicesEnabled ? <Info label="Serviços externos" value={asset.availableForExternalService ? formatAssetExternalServiceStatus(asset.externalServiceStatus ?? "disponivel") : "Não liberado"} /> : null}
              {assetExternalServicesEnabled && asset.currentExternalServiceParticipantName ? <Info label="Responsável temporário" value={asset.currentExternalServiceParticipantName} /> : null}
              {assetExternalServicesEnabled && asset.currentExternalServiceExpectedReturnAt ? <Info label="Previsão de retorno" value={`${formatDateOnly(asset.currentExternalServiceExpectedReturnAt)}${isAssetReturnOverdue(asset) ? " (atrasado)" : ""}`} /> : null}
              {(depreciationRule?.dynamicFields ?? []).map((field) => (
                <Info key={field.id} label={field.label} value={formatDynamicFieldValue(asset.customFields?.[field.id], field.unit)} />
              ))}
            </dl>
            <DepreciationChart
              data={depreciationChartData}
              elapsedMonths={depreciation.elapsedMonths}
              currentBookValue={depreciation.currentBookValue}
            />
            <AssetStatusTimeline statusHistory={statusHistory} maintenanceHistory={maintenanceHistory} />
            <AssetDamagesPanel companyId={asset.companyId} asset={asset} canManage={canEdit} currentUser={currentUser} />
            <AssetResponsibilityHistoryPanel companyId={asset.companyId} assetId={asset.id} />
          </div>
        )}
      </div>
      {isStatusModalOpen ? (
        <AssetStatusModal
          asset={asset}
          currentUser={currentUser}
          onClose={() => setIsStatusModalOpen(false)}
        />
      ) : null}
      {isTransferModalOpen ? (
        <AssetResponsibleTransferModal
          asset={asset}
          accessContext={accessContext}
          currentUser={currentUser}
          onClose={() => setIsTransferModalOpen(false)}
        />
      ) : null}
      {showAssetEditPrompt ? (
        <UnsavedChangesPrompt
          isSaving={false}
          onCancel={() => setShowAssetEditPrompt(false)}
          onDiscard={() => {
            setShowAssetEditPrompt(false);
            setIsAssetEditDirty(false);
            setIsEditing(false);
            onBack();
          }}
          onSave={() => {
            setShowAssetEditPrompt(false);
            assetEditFormRef.current?.requestSubmit();
          }}
        />
      ) : null}
    </>
  );
}

function AssetResponsibleTransferModal({
  asset,
  accessContext,
  currentUser,
  onClose
}: {
  asset: AssetRecord;
  accessContext: AccessContext;
  currentUser: User | null;
  onClose: () => void;
}) {
  const [companyUsers, setCompanyUsers] = useState<UserProfile[]>([]);
  const [targetUserId, setTargetUserId] = useState("");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [checklistTemplates, setChecklistTemplates] = useState<ChecklistTemplate[]>([]);
  const [inspectionTemplates, setInspectionTemplates] = useState<ChecklistTemplate[] | null>(null);
  const company = accessContext.accessibleCompanies.find((item) => item.id === asset.companyId)
    ?? accessContext.scopedCompanies.find((item) => item.id === asset.companyId)
    ?? accessContext.selectedCompany;
  const targetUsers = companyUsers.filter((profile) => profile.id !== asset.responsibleUserId);
  const targetUser = targetUsers.find((profile) => profile.id === targetUserId) ?? null;

  useEffect(() => subscribeCompanyUsers(
    asset.companyId,
    setCompanyUsers,
    (errorMessage) => console.warn("Não foi possível carregar responsaveis da empresa.", errorMessage)
  ), [asset.companyId]);

  useEffect(() => (
    subscribeChecklistTemplates(asset.companyId, setChecklistTemplates, () => undefined)
  ), [asset.companyId]);

  async function handleTransfer(condition?: { inspectionId: string; conditionAssessed: Asset["conservationState"] }) {
    if (!currentUser || !targetUser) {
      setMessage("Selecione o novo responsavel para continuar.");
      return;
    }

    if (!condition) {
      const templates = resolveChecklistTemplatesForAsset(checklistTemplates, asset, checklistContextForInspection("transferencia"));
      if (templates.length) {
        setInspectionTemplates(templates);
        return;
      }
    }

    setIsSaving(true);
    setMessage("");
    try {
      await directTransferResponsibility(asset.companyId, [asset], {
        userId: targetUser.id,
        userName: userDisplayName(targetUser),
        userEmail: targetUser.email
      }, currentUser, condition ? { conditionAtTransfer: condition.conditionAssessed, inspectionId: condition.inspectionId } : undefined);
      setMessage("Responsável alterado e histórico registrado.");
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível transferir a responsabilidade.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Trocar responsável</h2>
            <p className="mt-1 text-sm text-slate-500">{asset.patrimonyNumber} - {asset.description}</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 grid gap-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="text-slate-500">Empresa</p>
            <p className="font-semibold text-ink">{company?.tradeName ?? asset.companyId}</p>
          </div>
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
            <p className="text-slate-500">Responsável atual</p>
            <p className="font-semibold text-ink">{asset.responsible || "Sem responsável definido"}</p>
          </div>
          <label className="text-sm font-medium text-slate-700">Novo responsável
            <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={targetUserId} onChange={(event) => setTargetUserId(event.target.value)}>
              <option value="">Selecionar usuário da mesma empresa</option>
              {targetUsers.map((profile) => <option key={profile.id} value={profile.id}>{userDisplayName(profile)} - {profile.email}</option>)}
            </select>
          </label>
          {!targetUsers.length ? <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Nenhum outro usuário está vinculado a esta empresa para receber a responsabilidade.</p> : null}
          {message ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <SecondaryButton onClick={onClose} disabled={isSaving}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleTransfer()} disabled={!targetUser || isSaving}><MoveRight size={16} /> {isSaving ? "Transferindo..." : "Transferir"}</PrimaryButton>
        </div>
      </div>
      {isSaving ? <ProcessingOverlay title="Transferindo responsabilidade" description="Atualizando o bem e registrando o histórico." /> : null}
      {inspectionTemplates ? (
        <AssetInspectionModal
          companyId={asset.companyId}
          asset={asset}
          context="transferencia"
          templates={inspectionTemplates}
          currentUser={currentUser}
          onClose={() => setInspectionTemplates(null)}
          onCompleted={(result) => {
            setInspectionTemplates(null);
            void handleTransfer(result);
          }}
        />
      ) : null}
    </div>
  );
}

function AssetStatusModal({
  asset,
  currentUser,
  onClose,
  preset
}: {
  asset: AssetRecord;
  currentUser: User | null;
  onClose: () => void;
  preset?: { status: Asset["status"]; reason?: string; externalServiceId?: string; externalServiceItemId?: string; externalServiceTitle?: string };
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [status, setStatus] = useState<Asset["status"]>(preset?.status ?? (asset.status === "em_manutencao" ? "ativo" : asset.status));
  const [eventDate, setEventDate] = useState(today);
  const [reason, setReason] = useState(preset?.reason ?? "");
  const [expectedReturnAt, setExpectedReturnAt] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [serviceProvider, setServiceProvider] = useState("");
  const [cost, setCost] = useState("");
  const [saleValue, setSaleValue] = useState("");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const isStartingMaintenance = status === "em_manutencao";
  const isReturningFromMaintenance = asset.status === "em_manutencao" && status === "ativo";
  const isSale = status === "vendido";

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    const payload: AssetStatusInput = {
      status,
      reason,
      eventDate,
      expectedReturnAt,
      resolutionNotes,
      serviceProvider,
      cost: parseCurrencyNumber(cost),
      saleValue: parseCurrencyNumber(saleValue),
      externalServiceId: preset?.externalServiceId,
      externalServiceItemId: preset?.externalServiceItemId,
      externalServiceTitle: preset?.externalServiceTitle
    };

    try {
      setIsSaving(true);
      await updateAssetStatus(asset.companyId, asset, payload, currentUser);
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível alterar o status do bem.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Alterar status do bem</h2>
            <p className="mt-1 text-sm text-slate-500">{asset.patrimonyNumber} - {asset.description}</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        {preset?.externalServiceTitle ? (
          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Vinculado ao serviço externo: <span className="font-semibold">{preset.externalServiceTitle}</span></p>
        ) : null}
        <form className="mt-4 grid gap-3" onSubmit={handleSubmit}>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
              <p className="text-slate-500">Status atual</p>
              <p className="font-semibold text-ink">{formatAssetStatusLabel(asset.status)}</p>
              {asset.maintenanceReason ? <p className="mt-1 text-xs text-slate-500">{asset.maintenanceReason}</p> : null}
            </div>
            <label className="text-sm font-medium text-slate-700">Novo status
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value as Asset["status"])}>
                <option value="ativo">Ativo / voltou para operação</option>
                <option value="em_manutencao">Em manutenção</option>
                <option value="vendido">Vendido</option>
                <option value="inativo">Inativo por defeito ou decisao interna</option>
                <option value="baixado">Baixado</option>
                <option value="perdido">Perdido</option>
                <option value="em_auditoria">Em auditoria</option>
              </select>
            </label>
          </div>
          <label className="text-sm font-medium text-slate-700">Data do evento
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="date" value={eventDate} onChange={(event) => setEventDate(event.target.value)} />
          </label>
          <label className="text-sm font-medium text-slate-700">Motivo
            <textarea className="focus-ring mt-1 min-h-20 w-full rounded-md border border-slate-300 px-3 py-2" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Ex.: vendido, defeito irreparável, enviado para manutenção, retornou revisado..." />
          </label>
          {isStartingMaintenance || isReturningFromMaintenance ? (
            <div className="grid gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 md:grid-cols-2">
              {isStartingMaintenance ? (
                <label className="text-sm font-medium text-slate-700">Previsão de retorno
                  <input className="focus-ring mt-1 w-full rounded-md border border-amber-200 px-3 py-2" type="date" value={expectedReturnAt} onChange={(event) => setExpectedReturnAt(event.target.value)} />
                </label>
              ) : null}
              <label className="text-sm font-medium text-slate-700">Prestador / oficina
                <input className="focus-ring mt-1 w-full rounded-md border border-amber-200 px-3 py-2 uppercase" value={serviceProvider} onChange={(event) => setServiceProvider(event.target.value.toLocaleUpperCase("pt-BR"))} />
              </label>
              <label className="text-sm font-medium text-slate-700">Custo
                <input className="focus-ring mt-1 w-full rounded-md border border-amber-200 px-3 py-2" inputMode="numeric" value={cost} onChange={(event) => setCost(formatCurrencyMask(event.target.value))} placeholder="R$ 0,00" />
              </label>
              {isReturningFromMaintenance ? (
                <label className="text-sm font-medium text-slate-700 md:col-span-2">O que foi feito na manutenção
                  <textarea className="focus-ring mt-1 min-h-20 w-full rounded-md border border-amber-200 px-3 py-2" value={resolutionNotes} onChange={(event) => setResolutionNotes(event.target.value)} placeholder="Ex.: troca de peça, limpeza, revisão, laudo, entregue em operação..." />
                </label>
              ) : null}
            </div>
          ) : null}
          {isSale ? (
            <label className="text-sm font-medium text-slate-700">Valor de venda
              <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={saleValue} onChange={(event) => setSaleValue(formatCurrencyMask(event.target.value))} placeholder="R$ 0,00" />
            </label>
          ) : null}
          {message ? <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-700">{message}</p> : null}
          <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
            <SecondaryButton type="button" onClick={onClose} disabled={isSaving}>Cancelar</SecondaryButton>
            <PrimaryButton type="submit" disabled={isSaving}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar status"}</PrimaryButton>
          </div>
        </form>
      </div>
      {isSaving ? <ProcessingOverlay title="Atualizando status" description="Registrando histórico e manutenção do bem." /> : null}
    </div>
  );
}

function AssetStatusTimeline({
  statusHistory,
  maintenanceHistory
}: {
  statusHistory: AssetStatusHistoryRecord[];
  maintenanceHistory: AssetMaintenanceRecord[];
}) {
  return (
    <section className="mt-6 grid gap-4 lg:grid-cols-2">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h3 className="font-semibold text-ink">Histórico de status</h3>
        <div className="mt-3 space-y-2">
          {statusHistory.slice(0, 6).map((record) => (
            <div key={record.id} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
              <p className="font-semibold text-ink">{formatAssetStatusLabel(record.previousStatus)} {"->"} {formatAssetStatusLabel(record.nextStatus)}</p>
              <p className="text-slate-500">{formatDateOnly(record.eventDate)} - {record.reason}</p>
              {record.saleValue ? <p className="text-slate-500">Venda: {formatCurrency(record.saleValue)}</p> : null}
              {record.resolutionNotes ? <p className="text-slate-500">{record.resolutionNotes}</p> : null}
              {record.externalServiceTitle ? <p className="text-slate-500">Serviço externo: {record.externalServiceTitle}</p> : null}
            </div>
          ))}
          {!statusHistory.length ? <p className="text-sm text-slate-500">Nenhuma alteração de status registrada ainda.</p> : null}
        </div>
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <h3 className="font-semibold text-ink">Manutencoes</h3>
        <div className="mt-3 space-y-2">
          {maintenanceHistory.slice(0, 6).map((record) => (
            <div key={record.id} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold text-ink">{record.reason}</p>
                <StatusPill tone={record.status === "concluida" ? "green" : "yellow"}>{record.status}</StatusPill>
              </div>
              <p className="text-slate-500">Entrada: {formatDateOnly(record.startedAt)}{record.closedAt ? ` - Retorno: ${formatDateOnly(record.closedAt)}` : ""}</p>
              {record.expectedReturnAt ? <p className="text-slate-500">Previsto: {formatDateOnly(record.expectedReturnAt)}</p> : null}
              {record.serviceProvider ? <p className="text-slate-500">Prestador: {record.serviceProvider}</p> : null}
              {record.cost ? <p className="text-slate-500">Custo: {formatCurrency(record.cost)}</p> : null}
              {record.externalServiceId ? <p className="text-slate-500">Origem: encaminhado de um serviço externo</p> : null}
              {record.resolutionNotes ? <p className="text-slate-500">Fechamento: {record.resolutionNotes}</p> : null}
            </div>
          ))}
          {!maintenanceHistory.length ? <p className="text-sm text-slate-500">Nenhuma manutenção registrada ainda.</p> : null}
        </div>
      </div>
    </section>
  );
}

function AssetDamagesPanel({
  companyId,
  asset,
  canManage,
  currentUser
}: {
  companyId: string;
  asset: AssetRecord;
  canManage: boolean;
  currentUser: User | null;
}) {
  const [damages, setDamages] = useState<AssetDamage[]>([]);
  const [message, setMessage] = useState("");
  const [escalatingId, setEscalatingId] = useState<string | null>(null);

  useEffect(() => (
    subscribeAssetDamages(companyId, asset.id, setDamages, () => undefined)
  ), [companyId, asset.id]);

  async function handleResolve(damage: AssetDamage) {
    if (!currentUser) {
      return;
    }

    try {
      await resolveAssetDamage(companyId, asset.id, damage.id, "Avaria resolvida.", currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar a avaria.");
    }
  }

  async function handleEscalate(damage: AssetDamage) {
    if (!currentUser) {
      return;
    }

    setEscalatingId(damage.id);
    try {
      await updateAssetStatus(companyId, asset, {
        status: "em_manutencao",
        reason: `Avaria: ${damage.description}`,
        eventDate: new Date().toISOString().slice(0, 10),
        externalServiceId: damage.externalServiceId,
        externalServiceItemId: damage.externalServiceItemId,
        damageId: damage.id
      }, currentUser);
      await markAssetDamageEscalatedToMaintenance(companyId, asset.id, damage.id, currentUser);
      setMessage("Bem encaminhado para manutenção.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível encaminhar para manutenção.");
    } finally {
      setEscalatingId(null);
    }
  }

  if (!damages.length) {
    return null;
  }

  return (
    <section className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h3 className="font-semibold text-ink">Avarias</h3>
      <div className="mt-3 space-y-2">
        {damages.map((damage) => (
          <div key={damage.id} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex gap-2">
                {damage.photoUrls[0] ? <img src={damage.photoUrls[0]} alt={damage.description} className="h-14 w-14 rounded object-cover" /> : null}
                <div>
                  <p className="font-semibold text-ink">{damage.description}</p>
                  <p className="text-xs text-slate-500">{damageCategoryLabel(damage.category)} - {damage.preexisting ? "Preexistente" : "Nova"} - {formatAuditDate(damage.createdAt)}</p>
                  <p className="text-xs text-slate-500">Identificada por {damage.identifiedByUserName}</p>
                  {damage.resolutionNotes ? <p className="text-xs text-slate-500">{damage.resolutionNotes}</p> : null}
                </div>
              </div>
              <StatusPill tone={damage.status === "resolvida" ? "green" : damage.status === "encaminhada_manutencao" ? "blue" : "yellow"}>{damageStatusLabel(damage.status)}</StatusPill>
            </div>
            {canManage && damage.status === "aberta" ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <SecondaryButton onClick={() => void handleResolve(damage)}>Marcar como resolvida</SecondaryButton>
                <SecondaryButton onClick={() => void handleEscalate(damage)} disabled={escalatingId === damage.id}>Encaminhar para manutenção</SecondaryButton>
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {message ? <p className="mt-2 text-xs text-slate-500">{message}</p> : null}
    </section>
  );
}

function AssetResponsibilityHistoryPanel({ companyId, assetId }: { companyId: string; assetId: string }) {
  const [history, setHistory] = useState<ResponsibilityHistoryRecord[]>([]);

  useEffect(() => (
    subscribeAssetResponsibilityHistory(companyId, assetId, setHistory, () => undefined)
  ), [companyId, assetId]);

  if (!history.length) {
    return null;
  }

  return (
    <section className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <h3 className="font-semibold text-ink">Histórico de responsabilidade</h3>
      <div className="mt-3 space-y-2">
        {history.slice(0, 8).map((record) => (
          <div key={record.id} className="rounded-md border border-slate-200 bg-white p-3 text-sm">
            <p className="font-semibold text-ink">{record.fromUserName || "Sem responsável anterior"} {"->"} {record.toUserName}</p>
            <p className="text-xs text-slate-500">Recebido em {formatAuditDate(record.startedAt)} - autorizado por {record.authorizedByName}</p>
            {record.conditionAtTransfer ? <p className="text-xs text-slate-500">Condição na transferência: {record.conditionAtTransfer}</p> : null}
          </div>
        ))}
      </div>
    </section>
  );
}

function formatAssetStatusLabel(status: Asset["status"]) {
  const labels: Record<Asset["status"], string> = {
    ativo: "Ativo",
    inativo: "Inativo",
    baixado: "Baixado",
    vendido: "Vendido",
    perdido: "Perdido",
    em_manutencao: "Em manutenção",
    em_auditoria: "Em auditoria"
  };
  return labels[status] ?? status;
}

function damageCategoryLabel(category: DamageCategory) {
  const labels: Record<DamageCategory, string> = {
    desgaste_natural: "Desgaste natural",
    avaria: "Avaria",
    mau_uso: "Mau uso",
    manutencao_necessaria: "Manutenção necessária",
    outro: "Outro"
  };
  return labels[category] ?? category;
}

function damageStatusLabel(status: AssetDamage["status"]) {
  const labels: Record<AssetDamage["status"], string> = {
    aberta: "Aberta",
    resolvida: "Resolvida",
    encaminhada_manutencao: "Encaminhada para manutenção"
  };
  return labels[status] ?? status;
}

function formatAssetExternalServiceStatus(status: NonNullable<Asset["externalServiceStatus"]>) {
  const labels: Record<NonNullable<Asset["externalServiceStatus"]>, string> = {
    disponivel: "Disponível",
    reservado: "Reservado",
    em_servico: "Em serviço externo",
    em_retorno: "Em retorno",
    devolvido: "Devolvido",
    divergente: "Com divergência",
    extraviado: "Extraviado"
  };
  return labels[status] ?? status;
}

function AssetEditPanel({
  asset,
  accessContext,
  currentUser,
  externalFormRef,
  onDirtyChange,
  onDone
}: {
  asset: AssetRecord;
  accessContext: AccessContext;
  currentUser: User | null;
  externalFormRef?: { current: HTMLFormElement | null };
  onDirtyChange?: (isDirty: boolean) => void;
  onDone: () => void;
}) {
  const [locations, setLocations] = useState<string[]>([]);
  const [brands, setBrands] = useState<string[]>([]);
  const [departments, setDepartments] = useState<string[]>([]);
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserProfile[]>([]);
  const [patrimonyNumber, setPatrimonyNumber] = useState(asset.patrimonyNumber);
  const [assetTypeId, setAssetTypeId] = useState(asset.assetTypeId);
  const [description, setDescription] = useState(asset.description);
  const [physicalLocation, setPhysicalLocation] = useState(asset.physicalLocation);
  const [responsible, setResponsible] = useState(asset.responsible ?? accessContext.selectedCompany?.tradeName ?? "");
  const [department, setDepartment] = useState(asset.department ?? "");
  const [brand, setBrand] = useState(asset.brand ?? "");
  const [model, setModel] = useState(asset.model ?? "");
  const [serialNumber, setSerialNumber] = useState(asset.serialNumber ?? "");
  const [vehiclePlate, setVehiclePlate] = useState(asset.vehiclePlate ?? "");
  const [chassis, setChassis] = useState(asset.chassis ?? "");
  const [renavam, setRenavam] = useState(asset.renavam ?? "");
  const [manufactureYear, setManufactureYear] = useState(asset.manufactureYear ? String(asset.manufactureYear) : "");
  const [modelYear, setModelYear] = useState(asset.modelYear ? String(asset.modelYear) : "");
  const [mileageKm, setMileageKm] = useState(asset.mileageKm ? String(asset.mileageKm) : "");
  const [builtAreaM2, setBuiltAreaM2] = useState(asset.builtAreaM2 ? String(asset.builtAreaM2) : "");
  const [dimensions, setDimensions] = useState(asset.dimensions ?? "");
  const [acquisitionValue, setAcquisitionValue] = useState(asset.acquisitionValue ? formatCurrency(asset.acquisitionValue) : "");
  const [purchaseDate, setPurchaseDate] = useState(asset.purchaseDate ?? "");
  const [status, setStatus] = useState<Asset["status"]>(asset.status);
  const [inactiveReason, setInactiveReason] = useState(asset.inactiveReason ?? "");
  const [availableForExternalService, setAvailableForExternalService] = useState(asset.availableForExternalService === true);
  const [assetGroups, setAssetGroups] = useState<AssetGroup[]>([]);
  const [assetGroup, setAssetGroup] = useState(asset.assetGroup ?? "");
  const [customFields, setCustomFields] = useState<Record<string, string | number | boolean | null>>(asset.customFields ?? {});
  const [conservationState, setConservationState] = useState<Asset["conservationState"]>(asset.conservationState);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const internalFormRef = useRef<HTMLFormElement | null>(null);
  const formRef = externalFormRef ?? internalFormRef;
  const canEditPatrimony = accessContext.globalRole === "MASTER";
  const company = accessContext.scopedCompanies.find((item) => item.id === asset.companyId) ?? accessContext.selectedCompany;
  const externalServicesEnabled = company?.settings?.externalServicesEnabled === true;
  const canManageAssetGroups = accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN" || accessContext.companyRole === "GERENTE";
  const selectedAssetType = assetTypes.find((item) => item.id === assetTypeId);
  const assetTypeKind = getAssetTypeKind(selectedAssetType?.name ?? "");
  const selectedDynamicFields = selectedAssetType?.dynamicFields ?? [];

  useEffect(() => subscribeAssetTypes(setAssetTypes, (message) => console.warn("Não foi possível carregar tipos de imobilizado.", message)), []);
  useEffect(() => subscribeGlobalReferences("departments", (items) => setDepartments(items.map((item) => item.name)), (message) => console.warn("Não foi possível carregar departamentos.", message)), []);

  useEffect(() => {
    const unsubscribeLocations = subscribeCompanyLocations(asset.companyId, (items) => setLocations(items.map((item) => item.name)), (message) => console.warn("Não foi possível carregar locais.", message));
    const unsubscribeBrands = subscribeCompanyBrands(asset.companyId, (items) => setBrands(items.map((item) => item.name)), (message) => console.warn("Não foi possível carregar marcas.", message));
    const unsubscribeUsers = subscribeCompanyUsers(asset.companyId, setCompanyUsers, (message) => console.warn("Não foi possível carregar responsaveis.", message));
    const unsubscribeAssetGroups = subscribeAssetGroups(asset.companyId, setAssetGroups, (message) => console.warn("Não foi possível carregar grupos de bens.", message));
    return () => {
      unsubscribeLocations();
      unsubscribeBrands();
      unsubscribeUsers();
      unsubscribeAssetGroups();
    };
  }, [asset.companyId]);

  const responsibleOptions = useMemo(() => {
    const users = companyUsers.map((profile) => profile.fullName ?? profile.displayName ?? profile.email).filter(Boolean);
    return [company?.tradeName ?? "", ...users].filter(Boolean);
  }, [company, companyUsers]);
  const selectedResponsibleUser = useMemo(
    () => companyUsers.find((profile) => responsibleMatches(responsible, userResponsibleLabels(profile))),
    [companyUsers, responsible]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    if (externalServicesEnabled && availableForExternalService && !assetGroup) {
      setMessage("Selecione um grupo de bens para liberar este bem em serviços externos.");
      return;
    }

    setIsSaving(true);
    try {
      await updateAsset(asset.companyId, asset.id, {
        patrimonyNumber,
        description,
        shortDescription: description,
        assetTypeId,
        physicalLocation,
        department,
        responsible: responsible || company?.tradeName || "",
        conservationState,
        serialNumber,
        brand,
        model,
        vehiclePlate,
        chassis,
        renavam,
        manufactureYear: manufactureYear ? Number(manufactureYear) : undefined,
        modelYear: modelYear ? Number(modelYear) : undefined,
        mileageKm: parseCurrencyNumber(mileageKm),
        builtAreaM2: parseCurrencyNumber(builtAreaM2),
        dimensions,
        acquisitionValue: parseCurrencyNumber(acquisitionValue),
        purchaseDate,
        status,
        inactiveReason,
        availableForExternalService: externalServicesEnabled ? availableForExternalService : false,
        assetGroup: externalServicesEnabled && availableForExternalService ? assetGroup : undefined,
        customFields,
        responsibleUserId: selectedResponsibleUser?.id,
        canEditPatrimony
      }, currentUser);
      setIsDirty(false);
      onDirtyChange?.(false);
      onDone();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o bem.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <form ref={formRef} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" onSubmit={handleSubmit} onChange={() => { setIsDirty(true); onDirtyChange?.(true); }}>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700 md:col-span-2">Tipo de imobilizado<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={assetTypeId} onChange={(event) => setAssetTypeId(event.target.value)}>{assetTypes.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</select></label>
        <label className="text-sm font-medium text-slate-700">Patrimônio<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={patrimonyNumber} disabled={!canEditPatrimony} onChange={(event) => setPatrimonyNumber(event.target.value)} /></label>
        <label className="text-sm font-medium text-slate-700">Valor de aquisição<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={acquisitionValue} onChange={(event) => setAcquisitionValue(formatCurrencyMask(event.target.value))} placeholder="R$ 0,00" /></label>
        <label className="text-sm font-medium text-slate-700">Data de aquisição<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="date" value={purchaseDate} onChange={(event) => setPurchaseDate(event.target.value)} /></label>
        <label className="text-sm font-medium text-slate-700 md:col-span-2">Descricao<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={description} onChange={(event) => setDescription(event.target.value.toLocaleUpperCase("pt-BR"))} /></label>
        <ReferenceSelectWithCreate label="Local fisico" value={physicalLocation} onChange={setPhysicalLocation} options={locations} onCreate={(value) => saveCompanyLocation(asset.companyId, value)} />
        <ReferenceSelectWithCreate label="Responsável" value={responsible} onChange={setResponsible} options={responsibleOptions} allowCreate={false} />
        <ReferenceSelectWithCreate label="Departamento/setor" value={department} onChange={setDepartment} options={departments} onCreate={(value) => saveGlobalReference("departments", value)} />
        {(assetTypeKind === "generic" || assetTypeKind === "vehicle") ? <ReferenceSelectWithCreate label="Marca" value={brand} onChange={setBrand} options={brands} onCreate={(value) => saveCompanyBrand(asset.companyId, value)} /> : null}
        {assetTypeKind === "generic" ? <label className="text-sm font-medium text-slate-700">Modelo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={model} onChange={(event) => setModel(event.target.value.toLocaleUpperCase("pt-BR"))} /></label> : null}
        {assetTypeKind === "generic" ? <label className="text-sm font-medium text-slate-700">Número de série<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={serialNumber} onChange={(event) => setSerialNumber(event.target.value.toLocaleUpperCase("pt-BR"))} /></label> : null}
        {assetTypeKind === "vehicle" ? <label className="text-sm font-medium text-slate-700">Placa<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={vehiclePlate} onChange={(event) => setVehiclePlate(event.target.value.toLocaleUpperCase("pt-BR"))} /></label> : null}
        {assetTypeKind === "vehicle" ? <label className="text-sm font-medium text-slate-700">Chassi<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={chassis} onChange={(event) => setChassis(event.target.value.toLocaleUpperCase("pt-BR"))} /></label> : null}
        {assetTypeKind === "vehicle" ? <label className="text-sm font-medium text-slate-700">Renavam<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={renavam} onChange={(event) => setRenavam(event.target.value)} /></label> : null}
        {assetTypeKind === "vehicle" ? <label className="text-sm font-medium text-slate-700">Ano fabricação<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" maxLength={4} value={manufactureYear} onChange={(event) => setManufactureYear(event.target.value.replace(/\D/g, "").slice(0, 4))} /></label> : null}
        {assetTypeKind === "vehicle" ? <label className="text-sm font-medium text-slate-700">Ano modelo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" maxLength={4} value={modelYear} onChange={(event) => setModelYear(event.target.value.replace(/\D/g, "").slice(0, 4))} /></label> : null}
        {assetTypeKind === "vehicle" ? <label className="text-sm font-medium text-slate-700">Quilometragem<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={mileageKm} onChange={(event) => setMileageKm(event.target.value)} /></label> : null}
        {assetTypeKind === "building" ? <label className="text-sm font-medium text-slate-700">Area construida m2<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="decimal" value={builtAreaM2} onChange={(event) => setBuiltAreaM2(event.target.value)} /></label> : null}
        {assetTypeKind === "building" ? <label className="text-sm font-medium text-slate-700">Endereco / dimensoes<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={dimensions} onChange={(event) => setDimensions(event.target.value.toLocaleUpperCase("pt-BR"))} /></label> : null}
        <label className="text-sm font-medium text-slate-700">Estado de conservação<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={conservationState} onChange={(event) => setConservationState(event.target.value as Asset["conservationState"])}><option value="novo">novo</option><option value="otimo">otimo</option><option value="bom">bom</option><option value="regular">regular</option><option value="ruim">ruim</option><option value="sucata">sucata</option></select></label>
        <label className="text-sm font-medium text-slate-700">Status<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value as Asset["status"])}><option value="ativo">Ativo</option><option value="inativo">Inativo</option><option value="baixado">Baixado</option><option value="vendido">Vendido</option><option value="perdido">Perdido</option><option value="em_manutencao">Em manutenção</option><option value="em_auditoria">Em auditoria</option></select></label>
        {status !== "ativo" ? <label className="text-sm font-medium text-slate-700 md:col-span-2">Motivo / observação da inativação<textarea className="focus-ring mt-1 min-h-20 w-full rounded-md border border-slate-300 px-3 py-2" value={inactiveReason} onChange={(event) => setInactiveReason(event.target.value)} placeholder="Venda, manutenção, defeito, perda, baixa patrimonial..." /></label> : null}
        {selectedDynamicFields.length ? <DynamicFieldsInput fields={selectedDynamicFields} values={customFields} onChange={setCustomFields} /> : null}
        {externalServicesEnabled ? (
          <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700 md:col-span-2">
            <input type="checkbox" checked={availableForExternalService} onChange={(event) => setAvailableForExternalService(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" />
            <span>
              <span className="block font-semibold text-ink">Disponível para serviços externos</span>
              <span className="text-slate-500">Permite seleção do bem em saídas temporárias e conferência de retorno.</span>
            </span>
          </label>
        ) : null}
        {externalServicesEnabled && availableForExternalService ? (
          <div className="md:col-span-2">
            <ReferenceSelectWithCreate
              label="Grupo de bens"
              value={assetGroup}
              onChange={setAssetGroup}
              options={assetGroups.filter((group) => group.active).map((group) => group.name)}
              onCreate={canManageAssetGroups ? (name) => { if (currentUser) return createAssetGroup(asset.companyId, name, currentUser); } : undefined}
              allowCreate={canManageAssetGroups}
            />
            <p className="mt-1 text-xs text-slate-500">Define quais usuários poderão solicitar este bem em serviços externos, conforme o acesso configurado ao grupo.</p>
          </div>
        ) : null}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <PrimaryButton type="submit" disabled={isSaving}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar alterações"}</PrimaryButton>
        <SecondaryButton onClick={() => isDirty ? setShowUnsavedPrompt(true) : onDone()}>Cancelar</SecondaryButton>
      </div>
      {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
      {isSaving ? <ProcessingOverlay title="Atualizando bem" description="Salvando as alterações no cadastro do imobilizado." /> : null}
      {showUnsavedPrompt ? (
        <UnsavedChangesPrompt
          isSaving={isSaving}
          onCancel={() => setShowUnsavedPrompt(false)}
          onDiscard={() => {
            setShowUnsavedPrompt(false);
            setIsDirty(false);
            onDirtyChange?.(false);
            onDone();
          }}
          onSave={() => {
            setShowUnsavedPrompt(false);
            formRef.current?.requestSubmit();
          }}
        />
      ) : null}
    </form>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <div><dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt><dd className="mt-1 font-medium text-ink">{value}</dd></div>;
}

function DepreciationChart({
  data,
  elapsedMonths,
  currentBookValue
}: {
  data: Array<{ label: string; month: number; bookValue: number; accumulatedDepreciation: number }>;
  elapsedMonths: number;
  currentBookValue: number;
}) {
  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 className="font-semibold text-ink">Vida útil e desvalorização</h3>
          <p className="text-sm text-slate-500">Projeção baseada na tabela de depreciação vinculada ao tipo do bem.</p>
        </div>
        <p className="text-sm font-semibold text-brand-700">Valor atual: {formatCurrency(currentBookValue)}</p>
      </div>
      <div className="mt-4 h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ left: 8, right: 8, top: 12, bottom: 8 }}>
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(value) => formatCurrency(Number(value)).replace(/\u00a0/g, " ")} width={92} />
            <Tooltip
              formatter={(value, name) => [formatCurrency(Number(value)), name === "bookValue" ? "Valor contábil" : "Depreciação acumulada"]}
              labelFormatter={(label) => `Idade: ${label}`}
            />
            <Line type="monotone" dataKey="bookValue" name="Valor contábil" stroke="#0f766e" strokeWidth={3} dot={{ r: 3 }} />
            <Line type="monotone" dataKey="accumulatedDepreciation" name="Depreciação acumulada" stroke="#f59e0b" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {elapsedMonths ? <p className="mt-2 text-xs text-slate-500">Idade considerada no cálculo: {Math.floor(elapsedMonths / 12)} ano(s) e {elapsedMonths % 12} mês(es).</p> : null}
    </section>
  );
}

function PhotosPage({
  asset,
  currentUser,
  imageCompression,
  onBack
}: {
  asset: AssetRecord;
  currentUser: User | null;
  imageCompression: SystemSettings["imageCompression"];
  onBack: () => void;
}) {
  const [photoUrls, setPhotoUrls] = useState<string[]>(asset.photoUrls.slice(0, 4));
  const [galleryIndex, setGalleryIndex] = useState<number | null>(null);
  const [zoom, setZoom] = useState(1);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setPhotoUrls(asset.photoUrls.slice(0, 4));
  }, [asset.photoUrls]);

  async function handleAddPhotos(files: FileList | null) {
    const selectedFiles = Array.from(files ?? []);
    if (cameraInputRef.current) cameraInputRef.current.value = "";
    if (galleryInputRef.current) galleryInputRef.current.value = "";

    if (!selectedFiles.length) return;
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }
    if (photoUrls.length >= 4) {
      setMessage("Limite de 4 fotos atingido.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      const remainingSlots = 4 - photoUrls.length;
      const compressedFiles = await compressImageFiles(selectedFiles.slice(0, remainingSlots), imageCompression);
      await addAssetPhotos(asset.companyId, asset.id, photoUrls, compressedFiles, currentUser);
      setMessage("Fotos adicionadas com sucesso.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível adicionar as fotos.");
    } finally {
      setIsSaving(false);
    }
  }

  async function persistPhotos(nextPhotoUrls: string[]) {
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    const previousPhotoUrls = photoUrls;
    setPhotoUrls(nextPhotoUrls);
    setIsSaving(true);
    try {
      await updateAssetPhotos(asset.companyId, asset.id, nextPhotoUrls, currentUser);
      setMessage("Fotos atualizadas.");
    } catch (error) {
      setPhotoUrls(previousPhotoUrls);
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar as fotos.");
    } finally {
      setIsSaving(false);
    }
  }

  function movePhoto(index: number, direction: -1 | 1) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= photoUrls.length) return;
    const nextPhotoUrls = [...photoUrls];
    [nextPhotoUrls[index], nextPhotoUrls[targetIndex]] = [nextPhotoUrls[targetIndex], nextPhotoUrls[index]];
    void persistPhotos(nextPhotoUrls);
  }

  function removePhoto(index: number) {
    void persistPhotos(photoUrls.filter((_, itemIndex) => itemIndex !== index));
  }

  const activePhoto = galleryIndex === null ? null : photoUrls[galleryIndex];

  return (
    <>
      <PageHeader
        title="Fotos do bem"
        subtitle={`${asset.patrimonyNumber} - ${asset.description}`}
        action={
          <div className="flex flex-wrap gap-2">
            <SecondaryButton onClick={onBack}><RotateCcw size={16} /> Voltar</SecondaryButton>
            <SecondaryButton onClick={() => cameraInputRef.current?.click()} disabled={photoUrls.length >= 4 || isSaving}><Camera size={16} /> Camera</SecondaryButton>
            <PrimaryButton onClick={() => galleryInputRef.current?.click()} disabled={photoUrls.length >= 4 || isSaving}><Upload size={16} /> Galeria</PrimaryButton>
          </div>
        }
      />
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(event) => void handleAddPhotos(event.target.files)} />
      <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(event) => void handleAddPhotos(event.target.files)} />

      <div className="p-4 lg:p-6">
        <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-600 shadow-sm">
          {photoUrls.length}/4 fotos cadastradas. A primeira foto da lista fica como foto principal.
          {message ? <p className="mt-2 font-medium text-brand-700">{message}</p> : null}
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {photoUrls.map((photoUrl, index) => (
            <div key={photoUrl} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <button type="button" onClick={() => { setGalleryIndex(index); setZoom(1); }} className="group relative block w-full">
                <img src={photoUrl} alt={`Foto ${index + 1} do bem`} className="h-56 w-full object-cover" />
                <span className="absolute inset-0 flex items-center justify-center bg-slate-950/0 text-white opacity-0 transition group-hover:bg-slate-950/35 group-hover:opacity-100"><ZoomIn size={26} /></span>
              </button>
              <div className="flex items-center justify-between gap-2 p-3">
                <span className="text-sm font-semibold text-ink">{index === 0 ? "Principal" : `Foto ${index + 1}`}</span>
                <div className="flex gap-1">
                  <button type="button" className="focus-ring rounded-md border border-slate-200 p-2 text-slate-600 disabled:opacity-40" disabled={index === 0 || isSaving} onClick={() => movePhoto(index, -1)} aria-label="Subir foto"><ChevronLeft size={16} /></button>
                  <button type="button" className="focus-ring rounded-md border border-slate-200 p-2 text-slate-600 disabled:opacity-40" disabled={index === photoUrls.length - 1 || isSaving} onClick={() => movePhoto(index, 1)} aria-label="Descer foto"><ChevronRight size={16} /></button>
                  <button type="button" className="focus-ring rounded-md border border-red-200 p-2 text-red-600 disabled:opacity-40" disabled={isSaving} onClick={() => removePhoto(index)} aria-label="Excluir foto"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - photoUrls.length) }).map((_, index) => (
            <button key={`empty-${index}`} type="button" onClick={() => galleryInputRef.current?.click()} className="flex h-56 flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-white text-sm font-medium text-slate-500">
              <Plus size={20} />
              Adicionar foto
            </button>
          ))}
        </div>
      </div>

      {activePhoto ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 p-4">
          <button type="button" className="absolute right-4 top-4 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white" onClick={() => setGalleryIndex(null)}>Fechar</button>
          <button type="button" className="absolute left-4 rounded-full bg-white/10 p-3 text-white disabled:opacity-30" disabled={galleryIndex === 0} onClick={() => { setGalleryIndex((current) => current === null ? current : Math.max(0, current - 1)); setZoom(1); }}><ChevronLeft size={24} /></button>
          <img src={activePhoto} alt="Foto ampliada do bem" className="max-h-[82vh] max-w-[86vw] object-contain transition-transform" style={{ transform: `scale(${zoom})` }} />
          <button type="button" className="absolute right-4 rounded-full bg-white/10 p-3 text-white disabled:opacity-30" disabled={galleryIndex === photoUrls.length - 1} onClick={() => { setGalleryIndex((current) => current === null ? current : Math.min(photoUrls.length - 1, current + 1)); setZoom(1); }}><ChevronRight size={24} /></button>
          <div className="absolute bottom-5 flex items-center gap-2 rounded-full bg-white/10 p-2 text-white">
            <button type="button" className="rounded-full px-3 py-1 text-sm font-semibold" onClick={() => setZoom((current) => Math.max(1, Number((current - 0.25).toFixed(2))))}>-</button>
            <span className="min-w-16 text-center text-sm">{Math.round(zoom * 100)}%</span>
            <button type="button" className="rounded-full px-3 py-1 text-sm font-semibold" onClick={() => setZoom((current) => Math.min(3, Number((current + 0.25).toFixed(2))))}>+</button>
          </div>
        </div>
      ) : null}
      {isSaving ? <ProcessingOverlay title="Atualizando fotos" description="Enviando, ordenando ou removendo imagens do bem." /> : null}
    </>
  );
}
function CompaniesPage({
  accessContext,
  onSelectCompany,
  onSaveCompany
}: {
  accessContext: AccessContext;
  onSelectCompany: (companyId: string | null) => void;
  onSaveCompany: (payload: CompanyFormPayload) => Promise<void>;
}) {
  const [statusFilter, setStatusFilter] = useState<Company["status"] | "todas">("todas");
  const [search, setSearch] = useState("");
  const [editingCompanyId, setEditingCompanyId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [logoPreviewUrl, setLogoPreviewUrl] = useState("");
  const [logoObjectUrl, setLogoObjectUrl] = useState("");
  const [logoFit, setLogoFit] = useState<"contain" | "cover">("contain");
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const canManageCompanies = accessContext.globalRole === "MASTER";
  const canViewAudit = accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN";
  const editingCompany = accessContext.accessibleCompanies.find((company) => company.id === editingCompanyId) ?? null;
  const filteredCompanies = accessContext.accessibleCompanies.filter((company) => {
    const matchesSearch = matchesCompanySearch(company, search);
    const matchesStatus = statusFilter === "todas" ? true : company.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    setLogoPreviewUrl(editingCompany?.logoUrl ?? "");
    setLogoFit("contain");
    setIsDirty(false);
  }, [editingCompany]);

  useEffect(() => {
    return () => {
      if (logoObjectUrl) {
        URL.revokeObjectURL(logoObjectUrl);
      }
    };
  }, [logoObjectUrl]);

  function handleLogoPreviewChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.currentTarget.files?.[0];

    if (logoObjectUrl) {
      URL.revokeObjectURL(logoObjectUrl);
      setLogoObjectUrl("");
    }

    if (!file) {
      setLogoPreviewUrl(editingCompany?.logoUrl ?? "");
      return;
    }

    const nextObjectUrl = URL.createObjectURL(file);
    setLogoObjectUrl(nextObjectUrl);
    setLogoPreviewUrl(nextObjectUrl);
  }

  async function handleCompanySubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormMessage("");
    const formData = new FormData(event.currentTarget);
    const cnpj = String(formData.get("cnpj") ?? "");
    const cnpjRaw = normalizeCnpj(cnpj);

    if (!isValidCnpj(cnpj)) {
      setFormMessage("CNPJ invalido. Confira os digitos e tente novamente.");
      return;
    }

    const duplicatedCnpj = accessContext.accessibleCompanies.some((company) => {
      const companyCnpjRaw = company.cnpjRaw || normalizeCnpj(company.cnpj);
      return company.id !== editingCompany?.id && companyCnpjRaw === cnpjRaw;
    }) || await companyCnpjExists(cnpjRaw, editingCompany?.id);

    if (duplicatedCnpj) {
      setFormMessage("Ja existe uma empresa cadastrada com este CNPJ.");
      return;
    }

    const logoFile = formData.get("logo");

    setIsSaving(true);
    try {
      await onSaveCompany({
      id: editingCompany?.id,
      cnpj,
      cnpjRaw,
      legalName: String(formData.get("legalName") ?? ""),
      tradeName: String(formData.get("tradeName") ?? ""),
      city: String(formData.get("city") ?? ""),
      state: String(formData.get("state") ?? ""),
      zipCode: String(formData.get("zipCode") ?? ""),
      phone: String(formData.get("phone") ?? ""),
      address: String(formData.get("address") ?? ""),
      responsibleName: String(formData.get("responsibleName") ?? ""),
      responsibleContact: String(formData.get("responsibleContact") ?? ""),
      status: String(formData.get("status") ?? "ativa") as Company["status"],
      isAuditorCompany: formData.get("isAuditorCompany") === "on",
      logoFile: logoFile instanceof File && logoFile.size > 0 ? logoFile : null
      });

      setIsFormOpen(false);
      setEditingCompanyId(null);
      setIsDirty(false);
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "Não foi possível salvar a empresa.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title={isFormOpen ? editingCompany ? "Editar empresa" : "Cadastrar empresa" : "Empresas"}
        subtitle={isFormOpen ? "Atualize os dados cadastrais da empresa." : "Busque por nome, CNPJ formatado ou CNPJ sem pontuação."}
        action={
          isFormOpen ? (
            <SecondaryButton onClick={() => isDirty ? setShowUnsavedPrompt(true) : setIsFormOpen(false)}><RotateCcw size={16} /> Voltar para lista</SecondaryButton>
          ) : (
            <PrimaryButton onClick={() => {
              setEditingCompanyId(null);
              setIsFormOpen(true);
            }}><Plus size={16} /> Cadastrar empresa</PrimaryButton>
          )
        }
      />
      <div className="p-4 lg:p-6">
        {isFormOpen ? (
          <section className="mx-auto max-w-4xl rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          {canManageCompanies ? (
            <p className="mt-1 text-sm text-slate-500">Edição, inativação de empresas e inativação de usuários disponíveis para MASTER.</p>
          ) : null}
          <form ref={formRef} key={editingCompany?.id ?? "new-company"} className="mt-4 grid gap-4 md:grid-cols-2" onSubmit={handleCompanySubmit} onChange={() => setIsDirty(true)}>
            <label className="text-sm font-medium text-slate-700">
              CNPJ
              <input
                name="cnpj"
                className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                defaultValue={editingCompany?.cnpj}
                inputMode="text"
                maxLength={18}
                required
                onChange={(event) => {
                  event.currentTarget.value = formatCnpjInput(event.currentTarget.value);
                }}
                placeholder="00.AAA.000/0000-00"
              />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Razão Social
              <input name="legalName" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingCompany?.legalName} onChange={forceUppercaseInput} required />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Nome Fantasia
              <input name="tradeName" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingCompany?.tradeName} onChange={forceUppercaseInput} required />
            </label>
            <div className="grid grid-cols-[1fr_96px] gap-3">
              <label className="text-sm font-medium text-slate-700">
                Cidade
                <input name="city" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingCompany?.city} onChange={forceUppercaseInput} required />
              </label>
              <label className="text-sm font-medium text-slate-700">
                UF
                <select name="state" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingCompany?.state ?? ""} required>
                  <option value="" disabled>Selecione</option>
                  {brazilStates.map((state) => <option key={state} value={state}>{state}</option>)}
                </select>
              </label>
            </div>
            <label className="text-sm font-medium text-slate-700">
              Logo da empresa
              <div className="mt-1 flex items-center gap-2 rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-3">
                <Upload size={16} className="text-slate-500" />
                <input name="logo" type="file" accept="image/*" className="min-w-0 flex-1 text-sm text-slate-600" onChange={handleLogoPreviewChange} />
              </div>
              {logoPreviewUrl ? (
                <div className="mt-3 grid gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 sm:grid-cols-[112px_1fr] sm:items-center">
                  <div className="flex h-28 w-28 items-center justify-center rounded-md bg-white ring-1 ring-slate-200">
                    <img src={logoPreviewUrl} alt="Preview da logo" className="h-full w-full rounded-md" style={{ objectFit: logoFit }} />
                  </div>
                  <label className="text-sm font-medium text-slate-700">
                    Enquadramento
                    <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={logoFit} onChange={(event) => setLogoFit(event.target.value as "contain" | "cover")}>
                      <option value="contain">Conter logo inteira</option>
                      <option value="cover">Preencher quadro</option>
                    </select>
                  </label>
                </div>
              ) : null}
            </label>
            <label className="text-sm font-medium text-slate-700">
              Telefone
              <input
                className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                name="phone"
                defaultValue={editingCompany?.phone}
                inputMode="numeric"
                maxLength={15}
                onChange={(event) => {
                  event.currentTarget.value = formatPhoneInput(event.currentTarget.value);
                }}
                placeholder="(00) 00000-0000"
              />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Endereço
              <input name="address" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingCompany?.address} onChange={forceUppercaseInput} required />
            </label>
            <label className="text-sm font-medium text-slate-700">
              CEP
              <input
                name="zipCode"
                className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                defaultValue={editingCompany?.zipCode}
                inputMode="numeric"
                maxLength={10}
                onChange={(event) => {
                  event.currentTarget.value = formatZipCodeInput(event.currentTarget.value);
                }}
                placeholder="00.000-000"
              />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Nome do responsável
              <input name="responsibleName" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" placeholder="Responsável principal" />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Contato do responsável
              <input name="responsibleContact" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" placeholder="E-mail ou telefone" />
            </label>
            <label className="text-sm font-medium text-slate-700">
              Status
              <select name="status" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingCompany?.status ?? "ativa"} disabled={!canManageCompanies}>
                <option value="ativa">Ativa</option>
                <option value="inativa">Inativa</option>
                <option value="bloqueada">Bloqueada</option>
              </select>
            </label>
            <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm font-medium text-slate-700">
              <input name="isAuditorCompany" type="checkbox" defaultChecked={editingCompany?.isAuditorCompany === true} disabled={!canManageCompanies} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" />
              <span>
                Empresa auditora
                <span className="mt-1 block text-xs font-normal text-slate-500">Habilita esta empresa para receber liberacoes de auditoria e vincular usuários com perfil AUDITOR.</span>
              </span>
            </label>
            <div className="flex flex-wrap gap-2 md:col-span-2">
              <PrimaryButton type="submit" disabled={isSaving}><Save size={16} /> {isSaving ? "Salvando..." : editingCompany ? "Salvar alterações" : "Salvar empresa"}</PrimaryButton>
              {editingCompany ? <SecondaryButton onClick={() => onSelectCompany(editingCompany.id)}><Check size={16} /> Usar no dashboard</SecondaryButton> : null}
              {editingCompany && canManageCompanies ? <SecondaryButton><CircleSlash size={16} /> Inativar empresa</SecondaryButton> : null}
            </div>
            {formMessage ? <p className="text-sm font-medium text-rose-600 md:col-span-2">{formMessage}</p> : null}
            {editingCompany && canViewAudit ? (
              <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2 md:grid-cols-2">
                <Info label="Criado por" value={editingCompany.createdByName ?? editingCompany.createdBy} />
                <Info label="Criado em" value={formatAuditDate(editingCompany.createdAt)} />
                <Info label="Última alteração por" value={editingCompany.updatedByName ?? editingCompany.updatedBy ?? "-"} />
                <Info label="Última alteração em" value={formatAuditDate(editingCompany.updatedAt)} />
              </div>
            ) : null}
          </form>
          </section>
        ) : (
          <section className="space-y-4">
          <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_180px]">
            <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2">
              <Search size={16} className="text-slate-400" />
              <input
                className="min-w-0 flex-1 outline-none"
                placeholder="Buscar por nome, CNPJ ou cidade"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as Company["status"] | "todas")}>
              <option value="todas">Status: todos</option>
              <option value="ativa">Ativas</option>
              <option value="inativa">Inativas</option>
              <option value="bloqueada">Bloqueadas</option>
            </select>
          </div>

          {filteredCompanies.length ? (
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              {filteredCompanies.map((company) => (
                <button
                  key={company.id}
                  type="button"
                  onClick={() => {
                    setEditingCompanyId(company.id);
                    setIsFormOpen(true);
                  }}
                  className="grid w-full gap-3 border-b border-slate-100 p-4 text-left last:border-b-0 hover:bg-slate-50 lg:grid-cols-[1fr_160px_130px_120px] lg:items-center"
                >
                  <div>
                    <p className="font-semibold text-ink">{company.tradeName}</p>
                    <p className="mt-1 text-sm text-slate-500">{company.legalName}</p>
                    <p className="mt-1 text-xs text-slate-500">{company.cnpj}</p>
                  </div>
                  <span className="text-sm text-slate-600">{company.city}/{company.state}</span>
                  <StatusPill tone={company.status === "ativa" ? "green" : company.status === "bloqueada" ? "red" : "neutral"}>{company.status}</StatusPill>
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    {company.isAuditorCompany ? <StatusPill tone="blue">Auditora</StatusPill> : null}
                    <span className="text-sm font-medium text-brand-700">Alterar</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <EmptyState title="Nenhuma empresa encontrada" description="Ajuste os filtros ou cadastre uma nova empresa." />
          )}
        </section>
        )}
      </div>
      {showUnsavedPrompt ? (
        <UnsavedChangesPrompt
          isSaving={isSaving}
          onCancel={() => setShowUnsavedPrompt(false)}
          onDiscard={() => {
            setShowUnsavedPrompt(false);
            setIsDirty(false);
            setIsFormOpen(false);
            setEditingCompanyId(null);
          }}
          onSave={() => {
            setShowUnsavedPrompt(false);
            formRef.current?.requestSubmit();
          }}
        />
      ) : null}
    </>
  );
}

function ResponsiblesPageV2({
  accessContext,
  accessProfile,
  currentUser,
  currentUserId,
  currentUserName,
  currentUserEmail,
  initialAssetId,
  onInitialAssetConsumed
}: {
  accessContext: AccessContext;
  accessProfile: UserAccessProfile;
  currentUser: User | null;
  currentUserId: string;
  currentUserName: string;
  currentUserEmail: string;
  initialAssetId?: string | null;
  onInitialAssetConsumed?: () => void;
}) {
  const [companyUsers, setCompanyUsers] = useState<UserProfile[]>([]);
  const [requests, setRequests] = useState<ResponsibilityTransferRequest[]>([]);
  const [search, setSearch] = useState("");
  const [selectedResponsibleId, setSelectedResponsibleId] = useState<string | null>(null);
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [targetUserId, setTargetUserId] = useState("");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [inspectionContext, setInspectionContext] = useState<{ request: ResponsibilityTransferRequest; asset: AssetRecord; templates: ChecklistTemplate[] } | null>(null);
  const isPreselectingAssetRef = useRef(false);
  const isMaster = accessContext.globalRole === "MASTER";
  const isAdmin = accessContext.globalRole === "ADMIN";
  const isManager = accessProfile.manageableCompanyIds.length > 0;
  const canListManagedUsers = isMaster || isAdmin || isManager;
  const canApproveTransfers = isMaster || isAdmin || isManager;
  const viewerLabels = useMemo(() => [currentUserName, currentUserEmail, currentUserId].filter(Boolean), [currentUserEmail, currentUserId, currentUserName]);
  const scopedCompanyIds = useMemo(() => accessContext.scopedCompanies.map((company) => company.id), [accessContext.scopedCompanies]);

  useEffect(() => {
    if (!scopedCompanyIds.length) {
      setCompanyUsers([]);
      return () => undefined;
    }

    const usersById = new Map<string, UserProfile>();
    const unsubscribes = scopedCompanyIds.map((companyId) => subscribeCompanyUsers(
      companyId,
      (items) => {
        items.forEach((item) => usersById.set(item.id, item));
        setCompanyUsers(Array.from(usersById.values()).sort((left, right) => userDisplayName(left).localeCompare(userDisplayName(right))));
      },
      (errorMessage) => console.warn("Não foi possível carregar responsaveis.", errorMessage)
    ));

    return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
  }, [scopedCompanyIds]);

  useEffect(() => subscribeResponsibilityTransfers(
    scopedCompanyIds,
    setRequests,
    (errorMessage) => console.warn("Não foi possível carregar transferencias.", errorMessage)
  ), [scopedCompanyIds]);

  const responsibleUsers = useMemo<UserProfile[]>(() => {
    if (canListManagedUsers) {
      return companyUsers;
    }

    return [{
      id: currentUserId,
      fullName: currentUserName,
      displayName: currentUserName,
      email: currentUserEmail,
      globalRole: "USER",
      status: "ativo",
      companyIds: scopedCompanyIds,
      createdAt: new Date().toISOString(),
      createdBy: currentUserId
    }];
  }, [canListManagedUsers, companyUsers, currentUserEmail, currentUserId, currentUserName, scopedCompanyIds]);

  const visibleAssets = useMemo(() => {
    if (canListManagedUsers) {
      return accessContext.scopedAssets;
    }

    return accessContext.scopedAssets.filter((asset) => asset.responsibleUserId === currentUserId || responsibleMatches(asset.responsible, viewerLabels));
  }, [accessContext.scopedAssets, canListManagedUsers, currentUserId, viewerLabels]);

  const rows = useMemo(() => {
    const normalizedSearch = normalizeSearch(search);

    return responsibleUsers
      .map((profile) => {
        const labels = userResponsibleLabels(profile);
        const assignedAssets = visibleAssets.filter((asset) => asset.responsibleUserId === profile.id || responsibleMatches(asset.responsible, labels));
        return { profile, labels, assignedAssets };
      })
      .filter((row) => {
        const searchable = normalizeSearch([...row.labels, ...row.assignedAssets.map((asset) => asset.patrimonyNumber)].join(" "));
        return !normalizedSearch || searchable.includes(normalizedSearch);
      });
  }, [responsibleUsers, search, visibleAssets]);

  const selectedRow = rows.find((row) => row.profile.id === selectedResponsibleId) ?? rows[0] ?? null;
  const transferAssets = selectedRow?.assignedAssets ?? [];
  const selectedAssets = transferAssets.filter((asset) => selectedAssetIds.includes(asset.id));
  const selectedCompanyIds = Array.from(new Set(selectedAssets.map((asset) => asset.companyId)));
  const transferCompanyId = selectedCompanyIds.length === 1 ? selectedCompanyIds[0] : "";
  const transferCompany = accessContext.scopedCompanies.find((company) => company.id === transferCompanyId) ?? null;
  const allowSelfTransfer = transferCompany?.settings?.allowResponsibleSelfTransfer ?? false;
  const targetUsers = transferCompanyId
    ? companyUsers.filter((profile) => profile.companyIds?.includes(transferCompanyId) && profile.id !== selectedRow?.profile.id)
    : [];
  const targetUser = targetUsers.find((profile) => profile.id === targetUserId) ?? null;
  const visibleRequests = requests.filter((request) => canApproveTransfers || request.requestedBy === currentUserId || request.toUserId === currentUserId);
  const pendingForMe = requests.filter((request) => request.toUserId === currentUserId && request.status === "aguardando_confirmacao");
  const canSubmitTransfer = Boolean(currentUser && selectedRow && targetUser && selectedAssets.length && transferCompanyId && selectedCompanyIds.length === 1);

  useEffect(() => {
    if (!initialAssetId || !rows.length) {
      return;
    }

    const rowWithAsset = rows.find((row) => row.assignedAssets.some((asset) => asset.id === initialAssetId));
    const asset = rowWithAsset?.assignedAssets.find((item) => item.id === initialAssetId);
    if (!rowWithAsset || !asset) {
      return;
    }

    isPreselectingAssetRef.current = true;
    setSelectedResponsibleId(rowWithAsset.profile.id);
    setSelectedAssetIds([asset.id]);
    onInitialAssetConsumed?.();
  }, [initialAssetId, onInitialAssetConsumed, rows]);

  useEffect(() => {
    if (isPreselectingAssetRef.current) {
      isPreselectingAssetRef.current = false;
      setTargetUserId("");
      return;
    }

    setSelectedAssetIds([]);
    setTargetUserId("");
  }, [selectedRow?.profile.id]);

  function toggleAsset(assetId: string) {
    setSelectedAssetIds((current) => current.includes(assetId) ? current.filter((item) => item !== assetId) : [...current, assetId]);
  }

  function toggleAllAssets() {
    setSelectedAssetIds((current) => current.length === transferAssets.length ? [] : transferAssets.map((asset) => asset.id));
  }

  async function handleSubmitTransfer() {
    if (!currentUser || !selectedRow || !targetUser || !transferCompanyId) {
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      if (isMaster) {
        await directTransferResponsibility(transferCompanyId, selectedAssets, {
          userId: targetUser.id,
          userName: userDisplayName(targetUser),
          userEmail: targetUser.email
        }, currentUser);
        setMessage("Responsabilidade transferida diretamente pelo MASTER.");
      } else {
        await createResponsibilityTransferRequest({
          companyId: transferCompanyId,
          assets: selectedAssets,
          fromUserId: selectedRow.profile.id,
          fromUserName: userDisplayName(selectedRow.profile),
          toUserId: targetUser.id,
          toUserName: userDisplayName(targetUser),
          toUserEmail: targetUser.email,
          requiresManagerApproval: !allowSelfTransfer && !canApproveTransfers
        }, currentUser);
        setMessage(!allowSelfTransfer && !canApproveTransfers ? "Solicitacao criada e aguardando aprovacao." : "Solicitacao enviada para aceite do novo responsavel.");
      }
      setSelectedAssetIds([]);
      setTargetUserId("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível solicitar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleApproveRequest(request: ResponsibilityTransferRequest) {
    if (!currentUser) {
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      await approveResponsibilityTransfer(request.companyId, request.id, currentUser);
      setMessage("Transferencia aprovada. Aguardando aceite do novo responsavel.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível aprovar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRejectRequest(request: ResponsibilityTransferRequest) {
    if (!currentUser) {
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      await rejectResponsibilityTransfer(request.companyId, request.id, currentUser);
      setMessage("Transferencia recusada.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível recusar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleConfirmRequest(request: ResponsibilityTransferRequest, condition?: { inspectionId: string; conditionAssessed: Asset["conservationState"] }) {
    if (!currentUser) {
      return;
    }

    if (!condition && request.assetIds.length === 1) {
      const singleAsset = accessContext.scopedAssets.find((asset) => asset.id === request.assetIds[0]);
      if (singleAsset) {
        try {
          const allTemplates = await fetchChecklistTemplatesOnce(request.companyId);
          const templates = resolveChecklistTemplatesForAsset(allTemplates, singleAsset, checklistContextForInspection("transferencia"));
          if (templates.length) {
            setInspectionContext({ request, asset: singleAsset, templates });
            return;
          }
        } catch {
          // segue sem checklist se a consulta falhar
        }
      }
    }

    setIsSaving(true);
    setMessage("");
    try {
      await acceptResponsibilityTransfer(request, currentUser, condition ? { conditionAtTransfer: condition.conditionAssessed, inspectionId: condition.inspectionId } : undefined);
      setMessage("Transferencia confirmada e histórico registrado nos bens.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível confirmar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Responsáveis"
        subtitle={canListManagedUsers ? "Consulte responsabilidades no seu escopo e transfira bens entre usuários da mesma empresa." : "Consulte os bens sob sua responsabilidade e solicite transferências quando permitido."}
      />
      <div className="grid gap-4 p-4 lg:grid-cols-[360px_1fr] lg:p-6">
        <section className="space-y-3">
          <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2">
            <Search size={16} className="text-slate-400" />
            <input className="min-w-0 flex-1 outline-none" placeholder="Buscar responsável, email ou patrimônio" value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {rows.map((row) => (
              <button
                key={row.profile.id}
                type="button"
                onClick={() => setSelectedResponsibleId(row.profile.id)}
                className={`grid w-full gap-2 border-b border-slate-100 p-4 text-left last:border-b-0 hover:bg-slate-50 ${selectedRow?.profile.id === row.profile.id ? "bg-brand-50" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-ink">{userDisplayName(row.profile)}</p>
                    <p className="mt-1 text-sm text-slate-500">{row.profile.email}</p>
                  </div>
                  <StatusPill tone={row.assignedAssets.length ? "blue" : "neutral"}>{row.assignedAssets.length}</StatusPill>
                </div>
              </button>
            ))}
            {!rows.length ? <div className="p-4"><EmptyState title="Nenhum responsável encontrado" description="Ajuste a busca ou confira as empresas liberadas." /></div> : null}
          </div>
        </section>

        <section className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-base font-semibold text-ink">{selectedRow ? userDisplayName(selectedRow.profile) : "Responsável"}</h2>
                <p className="mt-1 text-sm text-slate-500">{transferAssets.length} item(ns) atribuído(s)</p>
                {transferCompany ? <p className="mt-1 text-xs text-slate-500">Empresa da transferência: {transferCompany.tradeName}</p> : null}
              </div>
              <div className="grid gap-2 md:grid-cols-[260px_auto]">
                <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={targetUserId} onChange={(event) => setTargetUserId(event.target.value)} disabled={!transferCompanyId}>
                  <option value="">Novo responsável</option>
                  {targetUsers.map((profile) => <option key={profile.id} value={profile.id}>{userDisplayName(profile)} - {profile.email}</option>)}
                </select>
                <PrimaryButton onClick={() => void handleSubmitTransfer()} disabled={!canSubmitTransfer || isSaving}><MoveRight size={16} /> {isMaster ? "Transferir agora" : "Solicitar"}</PrimaryButton>
              </div>
            </div>
            {selectedCompanyIds.length > 1 ? <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Selecione bens de apenas uma empresa por transferência.</p> : null}
            {transferCompany ? (
              <p className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                {allowSelfTransfer ? "Esta empresa permite transferência entre usuários sem aprovação de gerente." : "Esta empresa exige aprovação de GERENTE, ADMIN ou MASTER para transferências solicitadas por usuário comum."}
              </p>
            ) : null}
            {message ? <p className="mt-3 rounded-md border border-brand-200 bg-brand-50 p-3 text-sm text-brand-800">{message}</p> : null}
            {transferAssets.length ? <div className="mt-4"><SecondaryButton onClick={toggleAllAssets}>{selectedAssetIds.length === transferAssets.length ? "Limpar seleção" : "Selecionar todos"}</SecondaryButton></div> : null}
            <div className="mt-4 overflow-hidden rounded-md border border-slate-200">
              {transferAssets.map((asset) => (
                <label key={asset.id} className="grid cursor-pointer gap-2 border-b border-slate-100 p-3 last:border-b-0 hover:bg-slate-50 md:grid-cols-[32px_1fr_150px_120px] md:items-center">
                  <input type="checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700 md:mt-0" checked={selectedAssetIds.includes(asset.id)} onChange={() => toggleAsset(asset.id)} />
                  <div>
                    <p className="font-medium text-ink">{asset.patrimonyNumber} - {asset.description}</p>
                    <p className="mt-1 text-sm text-slate-500">{asset.physicalLocation} · {accessContext.scopedCompanies.find((company) => company.id === asset.companyId)?.tradeName ?? asset.companyId}</p>
                  </div>
                  <span className="text-sm text-slate-600">{asset.status}</span>
                  <AuditStatus status={asset.auditStatus} />
                </label>
              ))}
              {!transferAssets.length ? <div className="p-4"><EmptyState title="Sem itens atribuídos" description="Nenhum bem do escopo atual está vinculado a este responsável." /></div> : null}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="font-semibold text-ink">Solicitações de transferência</h2>
            <div className="mt-3 grid gap-3">
              {visibleRequests.map((request) => (
                <div key={request.id} className="grid gap-3 rounded-md border border-slate-200 p-3 md:grid-cols-[1fr_auto] md:items-center">
                  <div>
                    <p className="font-medium text-ink">{request.fromUserName} para {request.toUserName}</p>
                    <p className="mt-1 text-sm text-slate-500">{request.assetIds.length} item(ns) - {transferRequestStatusLabel(request.status)}</p>
                    <p className="mt-1 text-xs text-slate-500">Patrimônios: {request.assetPatrimonyNumbers.join(", ")}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {request.status === "aguardando_aprovacao" && canApproveTransfers ? <SecondaryButton onClick={() => void handleApproveRequest(request)}><Check size={16} /> Aprovar</SecondaryButton> : null}
                    {request.status === "aguardando_aprovacao" && canApproveTransfers ? <SecondaryButton onClick={() => void handleRejectRequest(request)}><CircleSlash size={16} /> Recusar</SecondaryButton> : null}
                    {request.status === "aguardando_confirmacao" && (request.toUserId === currentUserId || isMaster) ? <SecondaryButton onClick={() => void handleConfirmRequest(request)}><CheckCircle2 size={16} /> Aceitar transferência</SecondaryButton> : null}
                    {request.status === "concluida" ? <StatusPill tone="green">Concluída</StatusPill> : null}
                    {request.status === "recusada" ? <StatusPill tone="red">Recusada</StatusPill> : null}
                  </div>
                </div>
              ))}
              {!visibleRequests.length ? <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500">Nenhuma solicitação aberta no seu escopo.</p> : null}
            </div>
            {pendingForMe.length ? <p className="mt-3 text-sm font-medium text-brand-700">Você tem {pendingForMe.length} transferência(s) aguardando aceite.</p> : null}
          </div>
        </section>
      </div>
      {inspectionContext ? (
        <AssetInspectionModal
          companyId={inspectionContext.request.companyId}
          asset={inspectionContext.asset}
          context="transferencia"
          templates={inspectionContext.templates}
          currentUser={currentUser}
          onClose={() => setInspectionContext(null)}
          onCompleted={(result) => {
            const request = inspectionContext.request;
            setInspectionContext(null);
            void handleConfirmRequest(request, result);
          }}
        />
      ) : null}
    </>
  );
}


function normalizeSearch(value: string) {
  return value.trim().toLocaleUpperCase("pt-BR");
}

function responsibleMatches(responsible: string | undefined, labels: string[]) {
  const normalizedResponsible = normalizeSearch(responsible ?? "");
  return labels.some((label) => normalizeSearch(label) === normalizedResponsible);
}

function userDisplayName(profile: Pick<UserProfile, "displayName" | "fullName" | "email">) {
  return profile.fullName ?? profile.displayName ?? profile.email;
}

function userResponsibleLabels(profile: Pick<UserProfile, "id" | "displayName" | "fullName" | "email">) {
  return [profile.fullName, profile.displayName, profile.email, profile.id].filter(Boolean) as string[];
}

function normalizeDynamicFieldId(value: string) {
  return value
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 48);
}

function formatDynamicFieldValue(value: string | number | boolean | null | undefined, unit?: string) {
  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return `${String(value)}${unit ? ` ${unit}` : ""}`;
}

function transferRequestStatusLabel(status: ResponsibilityTransferRequest["status"]) {
  const labels = {
    aguardando_aprovacao: "Aguardando aprovação",
    aguardando_confirmacao: "Aguardando aceite do destino",
    concluida: "Concluída",
    recusada: "Recusada"
  };

  return labels[status];
}

function companyTransferStatusLabel(status: CompanyTransferRequest["status"]) {
  const labels = {
    aguardando_master: "Aguardando MASTER",
    aprovada: "Aprovada",
    recusada: "Recusada"
  };

  return labels[status];
}

function companyTransferTypeLabel(type: CompanyTransferRequest["transferType"]) {
  const labels = {
    com_nota: "Com nota fiscal",
    sem_nota: "Sem nota fiscal",
    comodato: "Comodato",
    remanejamento_operacional: "Remanejamento operacional",
    correcao_cadastral: "Correcao cadastral"
  };

  return labels[type];
}

function CompanyTransfersPage({
  accessContext,
  currentUser,
  transfers,
  canOpen
}: {
  accessContext: AccessContext;
  currentUser: User | null;
  transfers: CompanyTransferRequest[];
  canOpen: boolean;
}) {
  const isMaster = accessContext.globalRole === "MASTER";
  const originCompanies = accessContext.scopedCompanies.filter((company) => company.status !== "inativa");
  const [originCompanyId, setOriginCompanyId] = useState(originCompanies[0]?.id ?? "");
  const [destinationCompanyId, setDestinationCompanyId] = useState("");
  const [destinationCnpj, setDestinationCnpj] = useState("");
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [transferType, setTransferType] = useState<CompanyTransferRequest["transferType"]>("sem_nota");
  const [statusFilter, setStatusFilter] = useState<CompanyTransferRequest["status"] | "todos">("todos");
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const originCompany = originCompanies.find((company) => company.id === originCompanyId) ?? originCompanies[0];
  const destinationCompany = accessContext.accessibleCompanies.find((company) => company.id === destinationCompanyId)
    ?? accessContext.accessibleCompanies.find((company) => (company.cnpjRaw || normalizeCnpj(company.cnpj)) === normalizeCnpj(destinationCnpj));
  const destinationOptions = accessContext.accessibleCompanies.filter((company) => company.id !== originCompany?.id && company.status !== "inativa");
  const originAssets = accessContext.scopedAssets.filter((asset) => asset.companyId === originCompany?.id && asset.status !== "baixado");
  const selectedAssets = originAssets.filter((asset) => selectedAssetIds.includes(asset.id));
  const visibleTransfers = transfers.filter((transfer) => statusFilter === "todos" || transfer.status === statusFilter);

  useEffect(() => {
    if (!originCompanyId && originCompanies[0]) {
      setOriginCompanyId(originCompanies[0].id);
    }
  }, [originCompanies, originCompanyId]);

  useEffect(() => {
    setSelectedAssetIds((current) => current.filter((assetId) => originAssets.some((asset) => asset.id === assetId)));
  }, [originAssets]);

  function toggleAsset(assetId: string) {
    setSelectedAssetIds((current) => current.includes(assetId)
      ? current.filter((item) => item !== assetId)
      : [...current, assetId]
    );
  }

  async function handleCreateTransfer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    if (!currentUser || !originCompany) {
      setMessage("Usuário ou empresa de origem nao encontrado.");
      return;
    }
    if (!destinationCompany) {
      setMessage("Informe o CNPJ de uma empresa cadastrada e acessivel ao seu usuário.");
      return;
    }
    if (!selectedAssets.length) {
      setMessage("Selecione ao menos um bem para transferir.");
      return;
    }

    setIsSaving(true);
    try {
      await createCompanyTransferRequest({
        originCompanyId: originCompany.id,
        originCompanyName: originCompany.tradeName,
        destinationCompanyId: destinationCompany.id,
        destinationCompanyName: destinationCompany.tradeName,
        destinationCnpj: destinationCompany.cnpj,
        assets: selectedAssets,
        reason,
        transferType
      }, currentUser);
      setSelectedAssetIds([]);
      setDestinationCompanyId("");
      setDestinationCnpj("");
      setReason("");
      setMessage("Pedido de transferencia criado e aguardando liberação do MASTER.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível criar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleApproveTransfer(request: CompanyTransferRequest) {
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      await approveCompanyTransfer(request, currentUser);
      setMessage("Transferencia aprovada e bens movidos para a empresa destino.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível aprovar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleRejectTransfer(request: CompanyTransferRequest) {
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      await rejectCompanyTransfer(request.id, currentUser);
      setMessage("Transferencia recusada.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível recusar a transferencia.");
    } finally {
      setIsSaving(false);
    }
  }

  if (!canOpen) {
    return (
      <>
        <PageHeader title="Transferências" subtitle="Transferências entre empresas com aprovação do MASTER." />
        <div className="p-4 lg:p-6">
          <EmptyState title="Acesso restrito" description="Somente GERENTE, ADMIN e MASTER acessam transferencias entre empresas." />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Transferências" subtitle="Crie pedidos entre empresas por CNPJ e acompanhe a liberação pelo MASTER." />
      <div className="grid gap-4 p-4 lg:grid-cols-[1fr_1fr] lg:p-6">
        <form className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" onSubmit={handleCreateTransfer}>
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Novo pedido</h2>
              <p className="mt-1 text-sm text-slate-500">Selecione bens da origem e informe o CNPJ da empresa destino.</p>
            </div>
            <StatusPill tone="yellow">Aprova MASTER</StatusPill>
          </div>
          <div className="mt-4 grid gap-3">
            <label className="text-sm font-medium text-slate-700">Empresa origem<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={originCompany?.id ?? ""} onChange={(event) => { setOriginCompanyId(event.target.value); setDestinationCompanyId(""); setDestinationCnpj(""); }} required><option value="">Selecionar</option>{originCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}</select></label>
            <label className="text-sm font-medium text-slate-700">Destino no meu acesso<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={destinationCompanyId} onChange={(event) => { const company = accessContext.accessibleCompanies.find((item) => item.id === event.target.value); setDestinationCompanyId(event.target.value); setDestinationCnpj(company?.cnpj ?? ""); }}><option value="">Selecionar empresa ou informar CNPJ abaixo</option>{destinationOptions.map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}</select></label>
            <label className="text-sm font-medium text-slate-700">CNPJ destino<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="text" value={destinationCnpj} onChange={(event) => { setDestinationCompanyId(""); setDestinationCnpj(formatCnpjInput(event.target.value)); }} placeholder="00.AAA.000/0000-00" /></label>
            {destinationCnpj ? (
              <div className={`rounded-md border p-3 text-sm ${destinationCompany ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>
                {destinationCompany ? `Destino encontrado: ${destinationCompany.tradeName}` : "Destino ainda nao encontrado no escopo carregado para este usuário."}
              </div>
            ) : null}
            <label className="text-sm font-medium text-slate-700">Tipo de transferencia<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={transferType} onChange={(event) => setTransferType(event.target.value as CompanyTransferRequest["transferType"])}>{(["sem_nota", "com_nota", "comodato", "remanejamento_operacional", "correcao_cadastral"] as CompanyTransferRequest["transferType"][]).map((type) => <option key={type} value={type}>{companyTransferTypeLabel(type)}</option>)}</select></label>
            <label className="text-sm font-medium text-slate-700">Justificativa<textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Motivo, documento de apoio ou observacao da transferencia." /></label>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-ink">Bens da origem</p>
              <span className="text-xs font-medium text-slate-500">{selectedAssetIds.length} selecionado(s)</span>
            </div>
            <div className="mt-2 max-h-80 overflow-auto rounded-md border border-slate-200">
              {originAssets.map((asset) => (
                <label key={asset.id} className="grid cursor-pointer gap-3 border-b border-slate-100 p-3 last:border-b-0 hover:bg-slate-50 md:grid-cols-[auto_96px_1fr] md:items-center">
                  <input type="checkbox" className="h-4 w-4 rounded border-slate-300" checked={selectedAssetIds.includes(asset.id)} onChange={() => toggleAsset(asset.id)} />
                  <AssetPhotoPager asset={asset} />
                  <span className="min-w-0">
                    <span className="block truncate font-semibold text-ink">{asset.patrimonyNumber} - {asset.description}</span>
                    <span className="mt-1 block truncate text-sm text-slate-500">{asset.physicalLocation || "Sem local"} - {asset.responsible || originCompany?.tradeName}</span>
                  </span>
                </label>
              ))}
              {!originAssets.length ? <div className="p-3"><EmptyState title="Sem bens na origem" description="A empresa selecionada nao possui bens ativos no escopo atual." /></div> : null}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <PrimaryButton type="submit" disabled={isSaving || !selectedAssets.length}><MoveRight size={16} /> {isSaving ? "Salvando..." : "Criar pedido"}</PrimaryButton>
            {selectedAssetIds.length ? <SecondaryButton type="button" onClick={() => setSelectedAssetIds([])}>Limpar selecao</SecondaryButton> : null}
          </div>
          {message ? <p className="mt-3 rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
        </form>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Histórico</h2>
              <p className="mt-1 text-sm text-slate-500">Origem e destino visualizam o registro no proprio menu.</p>
            </div>
            <select className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as CompanyTransferRequest["status"] | "todos")}>
              <option value="todos">Todos</option>
              <option value="aguardando_master">Pendentes</option>
              <option value="aprovada">Aprovadas</option>
              <option value="recusada">Recusadas</option>
            </select>
          </div>
          <div className="mt-4 grid gap-3">
            {visibleTransfers.map((request) => (
              <div key={request.id} className="rounded-md border border-slate-200 p-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-ink">{request.originCompanyName} <MoveRight size={14} className="inline" /> {request.destinationCompanyName}</p>
                    <p className="mt-1 text-sm text-slate-500">{request.assetIds.length} bem(ns) - {companyTransferTypeLabel(request.transferType)}</p>
                    <p className="mt-1 text-xs text-slate-500">Patrimônios: {request.assetPatrimonyNumbers.join(", ")}</p>
                  </div>
                  <StatusPill tone={request.status === "aprovada" ? "green" : request.status === "recusada" ? "red" : "yellow"}>{companyTransferStatusLabel(request.status)}</StatusPill>
                </div>
                {request.reason ? <p className="mt-3 rounded-md bg-slate-50 p-2 text-sm text-slate-600">{request.reason}</p> : null}
                <p className="mt-2 text-xs text-slate-500">Solicitado por {request.requestedByName} {request.createdAt ? `em ${formatAuditDate(request.createdAt)}` : ""}</p>
                {request.approvedByName ? <p className="mt-1 text-xs text-emerald-700">Liberado por {request.approvedByName} {request.approvedAt ? `em ${formatAuditDate(request.approvedAt)}` : ""}</p> : null}
                {isMaster && request.status === "aguardando_master" ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <SecondaryButton onClick={() => void handleApproveTransfer(request)} disabled={isSaving}><CheckCircle2 size={16} /> Aprovar</SecondaryButton>
                    <SecondaryButton onClick={() => void handleRejectTransfer(request)} disabled={isSaving}><CircleSlash size={16} /> Recusar</SecondaryButton>
                  </div>
                ) : null}
              </div>
            ))}
            {!visibleTransfers.length ? <EmptyState title="Sem transferencias" description="Ainda nao existem pedidos neste escopo ou filtro." /> : null}
          </div>
        </section>
      </div>
    </>
  );
}

type ExternalServicesTab = "services" | "calendar" | "availability" | "mine" | "out" | "usage" | "groups" | "checklists" | "rules";
type ExternalServiceFilter = "ativos" | "proximos" | "todos";

function ExternalServicesPage({
  accessContext,
  accessProfile,
  currentUser,
  currentUserName,
  companyUsers,
  services,
  initialServiceId,
  onInitialServiceConsumed,
  onOpenAsset
}: {
  accessContext: AccessContext;
  accessProfile: UserAccessProfile;
  currentUser: User | null;
  currentUserName: string;
  companyUsers: ManagedUser[];
  services: ExternalService[];
  initialServiceId?: string | null;
  onInitialServiceConsumed?: () => void;
  onOpenAsset: (assetId: string) => void;
}) {
  const selectedCompany = accessContext.selectedCompany;
  const selectedPermissions = selectedCompany ? accessProfile.companyPermissions[selectedCompany.id] : undefined;
  const canCreate = canAccess(selectedPermissions, "canCreateExternalServices", accessContext.globalRole);
  const canEdit = canAccess(selectedPermissions, "canEditExternalServices", accessContext.globalRole);
  const canClose = canAccess(selectedPermissions, "canCloseExternalServices", accessContext.globalRole);
  const canAssign = canAccess(selectedPermissions, "canAssignAssetsToExternalService", accessContext.globalRole);
  const canConfirm = canAccess(selectedPermissions, "canConfirmExternalServiceAssets", accessContext.globalRole);
  const canApprove = canAccess(selectedPermissions, "canApproveExternalServiceAssets", accessContext.globalRole);
  const canReturn = canAccess(selectedPermissions, "canReturnExternalServiceAssets", accessContext.globalRole);
  const canReport = canAccess(selectedPermissions, "canGenerateExternalServiceReports", accessContext.globalRole);
  const canManageAssetGroups = canAccess(selectedPermissions, "canManageAssetGroups", accessContext.globalRole);
  const canManageChecklistTemplates = canAccess(selectedPermissions, "canManageChecklistTemplates", accessContext.globalRole);
  const canPerformInspections = canAccess(selectedPermissions, "canPerformInspections", accessContext.globalRole);
  const canManageCompanySettings = canAccess(selectedPermissions, "canManageCompanySettings", accessContext.globalRole);
  const canManageServiceFlow = canEdit || canClose;
  const rules = useMemo(() => resolveExternalServiceRules(selectedCompany?.settings?.externalServiceRules), [selectedCompany?.settings?.externalServiceRules]);
  const [assetGroups, setAssetGroups] = useState<AssetGroup[]>([]);
  const [checklistTemplates, setChecklistTemplates] = useState<ChecklistTemplate[]>([]);
  const [reservations, setReservations] = useState<AssetReservation[]>([]);
  const [reservationsError, setReservationsError] = useState("");
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [participants, setParticipants] = useState<ExternalServiceParticipant[]>([]);
  const [items, setItems] = useState<ExternalServiceItem[]>([]);
  const [events, setEvents] = useState<ExternalServiceEvent[]>([]);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingService, setEditingService] = useState<ExternalService | null>(null);
  const [isAssetPickerOpen, setIsAssetPickerOpen] = useState(false);
  const [assetPickerInitialQuery, setAssetPickerInitialQuery] = useState("");
  const [activeExternalTab, setActiveExternalTab] = useState<ExternalServicesTab>("services");
  const [serviceFilter, setServiceFilter] = useState<ExternalServiceFilter>("ativos");
  const [serviceSearch, setServiceSearch] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState<"confirmMine" | "approveReturns" | null>(null);
  const [reassignTarget, setReassignTarget] = useState<ExternalServiceItem | null>(null);
  const [actionsTarget, setActionsTarget] = useState<ExternalServiceItem | null>(null);
  const [statusModalAsset, setStatusModalAsset] = useState<AssetRecord | null>(null);
  const [statusModalPreset, setStatusModalPreset] = useState<{ status: Asset["status"]; reason?: string; externalServiceId?: string; externalServiceItemId?: string; externalServiceTitle?: string } | null>(null);
  const [divergenceTarget, setDivergenceTarget] = useState<ExternalServiceItem | null>(null);
  const [lostConfirmTarget, setLostConfirmTarget] = useState<ExternalServiceItem | null>(null);
  const [metricModal, setMetricModal] = useState<"active" | "confirmation" | "release" | "return" | "approval" | "divergent" | null>(null);
  const [noteTarget, setNoteTarget] = useState<ExternalServiceItem | null>(null);
  const [pdfParticipantIds, setPdfParticipantIds] = useState<string[] | null>(null);
  const [itemSearch, setItemSearch] = useState("");
  const [itemParticipantFilter, setItemParticipantFilter] = useState("todos");
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [message, setMessage] = useState("");
  const [scanTarget, setScanTarget] = useState<"add" | "return" | null>(null);
  const [inspectionRequest, setInspectionRequest] = useState<{ item: ExternalServiceItem; asset: AssetRecord; context: InspectionContext; templates: ChecklistTemplate[]; proceed: (inspectionId?: string) => void } | null>(null);
  const [isConferenceOpen, setIsConferenceOpen] = useState(false);
  const [pickupPopResults, setPickupPopResults] = useState<Record<string, string>>({});
  const [archiveTarget, setArchiveTarget] = useState<ExternalService | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ExternalService | null>(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const todayKey = dateKey(new Date());
  const isNoiseService = (service: ExternalService) => Boolean(service.deletedAt) || service.status === "cancelado" || (service.status === "concluido" && (service.divergentAssetsCount ?? 0) === 0);
  const normalizedServiceSearch = serviceSearch.trim().toLowerCase();
  const visibleServices = services
    .filter((service) => {
      if (serviceFilter === "ativos") return !isNoiseService(service);
      if (serviceFilter === "proximos") return !service.deletedAt && ["rascunho", "aberto", "aguardando_confirmacao"].includes(service.status) && service.startDate.slice(0, 10) >= todayKey;
      return true;
    })
    .filter((service) => !normalizedServiceSearch || [service.title, service.serviceCode ?? "", service.clientName ?? "", service.location ?? ""].join(" ").toLowerCase().includes(normalizedServiceSearch))
    .sort((left, right) => serviceFilter === "todos" ? right.startDate.localeCompare(left.startDate) : left.startDate.localeCompare(right.startDate));
  const selectedService = services.find((service) => service.id === selectedServiceId) ?? visibleServices[0] ?? services[0] ?? null;
  const companyServiceUsers = useMemo(() => selectedCompany
    ? companyUsers.filter((profile) => profile.companyAccess.some((access) => access.companyId === selectedCompany.id && access.status === "ativo"))
    : [], [companyUsers, selectedCompany]);
  const currentUserId = currentUser?.uid ?? "";
  const myGroupNames = useMemo(
    () => new Set(assetGroups.filter((group) => group.memberUserIds.includes(currentUserId)).map((group) => group.name)),
    [assetGroups, currentUserId]
  );
  const companyExternalAssets = useMemo(() => accessContext.scopedAssets.filter((asset) => asset.companyId === selectedCompany?.id && asset.availableForExternalService === true), [accessContext.scopedAssets, selectedCompany?.id]);
  const assetsById = useMemo(() => new Map(accessContext.scopedAssets.map((asset) => [asset.id, asset])), [accessContext.scopedAssets]);
  // Bens que podem entrar no servico: a disponibilidade no periodo e checada pelas reservas
  // (um bem que esta fora hoje pode ser reservado para um servico futuro).
  const pickerAssets = useMemo(() => companyExternalAssets.filter((asset) => (
    asset.status === "ativo"
    && asset.externalServiceStatus !== "extraviado"
    && !items.some((item) => item.assetId === asset.id)
    && (canManageAssetGroups || canAssign || (asset.assetGroup ? myGroupNames.has(asset.assetGroup) : false))
  )), [canAssign, canManageAssetGroups, companyExternalAssets, items, myGroupNames]);
  const summary = {
    pendingConfirmation: items.filter((item) => item.status === "pendente_confirmacao").length,
    pendingRelease: items.filter((item) => item.status === "confirmado_com_responsavel").length,
    pendingReturn: items.filter((item) => item.status === "retorno_pendente").length,
    pendingReturnApproval: items.filter((item) => item.status === "retorno_confirmado").length,
    divergent: items.filter((item) => ["devolvido_com_divergencia", "nao_devolvido", "extraviado"].includes(item.status)).length,
    overdue: items.filter((item) => isItemReturnOverdue(item)).length
  };
  const globalSummary = {
    active: services.filter((service) => !service.deletedAt && ["aberto", "aguardando_confirmacao", "em_andamento", "em_retorno"].includes(service.status)).length,
    out: companyExternalAssets.filter((asset) => asset.externalServiceStatus === "em_servico" || asset.externalServiceStatus === "em_retorno").length,
    reserved: companyExternalAssets.filter((asset) => asset.externalServiceStatus === "reservado").length,
    overdue: companyExternalAssets.filter((asset) => isAssetReturnOverdue(asset)).length,
    divergent: companyExternalAssets.filter((asset) => asset.externalServiceStatus === "divergente" || asset.externalServiceStatus === "extraviado").length
  };
  const nonTerminalItemStatuses: ExternalServiceItemStatus[] = ["pendente_confirmacao", "confirmado_com_responsavel", "em_uso", "retorno_pendente", "retorno_confirmado"];
  const pendingItemsCount = items.filter((item) => nonTerminalItemStatuses.includes(item.status)).length;
  const canStartService = selectedService ? ["aberto", "aguardando_confirmacao"].includes(selectedService.status) : false;
  const canStartReturn = selectedService?.status === "em_andamento";
  const canConcludeService = selectedService?.status === "em_retorno" && pendingItemsCount === 0;
  const myItems = items.filter((item) => item.assignedToUserId === currentUserId);
  const myPendingItems = myItems.filter((item) => item.status === "pendente_confirmacao");
  const pendingPickupItems = items.filter((item) => item.status === "pendente_confirmacao" || item.status === "confirmado_com_responsavel");
  const pendingUserRequests = items.filter((item) => item.status === "confirmado_com_responsavel");
  const pendingReturnApprovals = items.filter((item) => item.status === "retorno_confirmado");
  const confirmationItems = items.filter((item) => item.status === "pendente_confirmacao");
  const returnPendingItems = items.filter((item) => item.status === "retorno_pendente");
  const divergentItems = items.filter((item) => ["devolvido_com_divergencia", "nao_devolvido", "extraviado"].includes(item.status));
  const activeServices = services.filter((service) => !service.deletedAt && ["aberto", "aguardando_confirmacao", "em_andamento", "em_retorno"].includes(service.status));
  const isSelectedServiceLocked = Boolean(selectedService?.deletedAt) || selectedService?.status === "cancelado";

  function templatesFor(item: ExternalServiceItem, context: InspectionContext) {
    const asset = assetsById.get(item.assetId);
    return asset ? resolveChecklistTemplatesForAsset(checklistTemplates, asset, checklistContextForInspection(context)) : [];
  }

  // POP aplicavel: precisa estar ligado nas regras do gerente, existir checklist para o bem
  // e o usuario poder registrar inspecoes (exigido pelas regras do Firestore).
  function isPopEnabledFor(context: InspectionContext) {
    if (context === "retirada") return rules.pickupPopEnabled;
    if (context === "devolucao") return rules.returnPopEnabled;
    return true;
  }

  function pickupPopRequired(item: ExternalServiceItem) {
    return rules.pickupPopEnabled && canPerformInspections && !item.pickupInspectionId && templatesFor(item, "retirada").length > 0;
  }

  function returnPopPending(item: ExternalServiceItem) {
    return rules.returnPopEnabled && !item.returnInspectionId && templatesFor(item, "devolucao").length > 0;
  }

  // Retornos "limpos" para aprovar em lote: sem observacao de saida e sem POP pendente
  const cleanPendingReturnApprovals = pendingReturnApprovals.filter((item) => !item.exitNotes && !returnPopPending(item));

  function getItemActionState(item: ExternalServiceItem) {
    if (isSelectedServiceLocked) {
      return { canConfirm: false, canApprove: false, canMarkReturned: false, canApproveReturn: false, canReassign: false, canSelfCheck: false, canResolveDivergence: false };
    }

    return {
      canConfirm: canConfirm && item.assignedToUserId === currentUserId && item.status === "pendente_confirmacao",
      canApprove: canApprove && (item.status === "confirmado_com_responsavel" || item.status === "pendente_confirmacao"),
      canMarkReturned: canReturn && item.assignedToUserId === currentUserId && ["em_uso", "confirmado_com_responsavel", "retorno_pendente"].includes(item.status),
      canApproveReturn: canApprove && item.status === "retorno_confirmado",
      canReassign: (canAssign || (selectedService?.allowPeerItemTransfer === true && item.assignedToUserId === currentUserId))
        && !["devolvido", "devolvido_com_divergencia", "nao_devolvido", "extraviado"].includes(item.status),
      canSelfCheck: canPerformInspections && item.assignedToUserId === currentUserId && item.status === "em_uso" && templatesFor(item, "auditoria").length > 0,
      canResolveDivergence: canApprove && ["devolvido_com_divergencia", "nao_devolvido", "extraviado"].includes(item.status)
    };
  }

  const normalizedItemSearch = itemSearch.trim().toLowerCase();
  const filteredServiceItems = items.filter((item) => {
    const matchesParticipant = itemParticipantFilter === "todos" || item.assignedToUserId === itemParticipantFilter;
    const searchable = [item.patrimonyNumber, item.internalCode ?? "", item.serialNumber ?? "", item.description, item.assignedToUserName ?? ""].join(" ").toLowerCase();
    return matchesParticipant && (!normalizedItemSearch || searchable.includes(normalizedItemSearch));
  });
  const itemGroups = [
    ...participants.map((participant) => ({ key: participant.userId, name: participant.userName, items: filteredServiceItems.filter((item) => item.assignedToUserId === participant.userId) })),
    { key: "sem-responsavel", name: "Sem responsável", items: filteredServiceItems.filter((item) => !item.assignedToUserId || !participants.some((participant) => participant.userId === item.assignedToUserId)) }
  ].filter((group) => group.items.length);
  const externalAssetsOut = companyExternalAssets.filter((asset) => (
    asset.externalServiceStatus
    && asset.externalServiceStatus !== "disponivel"
    && (asset.currentExternalServiceId || asset.externalServiceStatus === "em_servico" || asset.externalServiceStatus === "em_retorno")
  ));

  useEffect(() => {
    if (!selectedServiceId && visibleServices.length) {
      setSelectedServiceId(visibleServices[0].id);
    }
  }, [selectedServiceId, visibleServices]);

  useEffect(() => {
    if (!initialServiceId || !services.some((service) => service.id === initialServiceId)) {
      return;
    }

    openService(initialServiceId);
    onInitialServiceConsumed?.();
    // openService so troca estado local; nao precisa entrar nas dependencias
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialServiceId, onInitialServiceConsumed, services]);

  useEffect(() => {
    if (!selectedCompany) {
      setAssetGroups([]);
      return () => undefined;
    }

    return subscribeAssetGroups(selectedCompany.id, setAssetGroups, (errorMessage) => console.warn("Não foi possível carregar grupos de bens.", errorMessage));
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      setChecklistTemplates([]);
      return () => undefined;
    }

    return subscribeChecklistTemplates(selectedCompany.id, setChecklistTemplates, (errorMessage) => console.warn("Não foi possível carregar checklists.", errorMessage));
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany) {
      setReservations([]);
      return () => undefined;
    }

    setReservationsError("");
    return subscribeCompanyReservations(selectedCompany.id, setReservations, (errorMessage) => {
      console.warn("Não foi possível carregar as reservas dos equipamentos.", errorMessage);
      setReservationsError("Não foi possível carregar as reservas dos equipamentos. Se acabou de atualizar o sistema, o índice do banco pode ainda estar sendo criado; tente de novo em alguns minutos.");
    });
  }, [selectedCompany]);

  useEffect(() => {
    if (!selectedCompany || !selectedService) {
      setParticipants([]);
      setItems([]);
      setEvents([]);
      return () => undefined;
    }

    const unsubscribeParticipants = subscribeExternalServiceParticipants(selectedCompany.id, selectedService.id, setParticipants, (errorMessage) => console.warn("Não foi possível carregar participantes.", errorMessage));
    const unsubscribeItems = subscribeExternalServiceItems(selectedCompany.id, selectedService.id, setItems, (errorMessage) => console.warn("Não foi possível carregar bens do serviço externo.", errorMessage));
    const unsubscribeEvents = subscribeExternalServiceEvents(selectedCompany.id, selectedService.id, setEvents, (errorMessage) => console.warn("Não foi possível carregar histórico do serviço externo.", errorMessage));
    return () => {
      unsubscribeParticipants();
      unsubscribeItems();
      unsubscribeEvents();
    };
  }, [selectedCompany, selectedService]);

  function openService(serviceId: string) {
    setSelectedServiceId(serviceId);
    setActiveExternalTab("services");
    setIsMobileDetailOpen(true);
    setItemSearch("");
    setItemParticipantFilter("todos");
    setPickupPopResults({});
    setIsHistoryExpanded(false);
    const service = services.find((candidate) => candidate.id === serviceId);
    if (service && !visibleServices.some((candidate) => candidate.id === serviceId)) {
      setServiceFilter("todos");
    }
  }

  async function handleAddParticipant(userId: string) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    const profile = companyServiceUsers.find((item) => item.id === userId);
    if (!profile) {
      return;
    }

    try {
      await addExternalServiceParticipant(selectedCompany.id, selectedService.id, {
        userId: profile.id,
        userName: userDisplayName(profile),
        userEmail: profile.email
      }, participants.length, currentUser);
      setMessage("Participante adicionado.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível adicionar o participante.");
    }
  }

  async function handleAddAssets(assets: AssetRecord[], participantId: string, mode: ExternalServiceItem["assignmentMode"], notesByAssetId: Record<string, string>) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    const participant = participants.find((item) => item.userId === participantId);
    if (!participant) {
      setMessage("Selecione um participante para receber o bem.");
      return;
    }

    const failures: string[] = [];
    let added = 0;
    for (const asset of assets) {
      try {
        await addExternalServiceItem(selectedCompany.id, selectedService, asset, participant, mode, items.length + added, currentUser, notesByAssetId[asset.id] ?? "");
        added += 1;
      } catch (error) {
        failures.push(error instanceof Error ? error.message : `Não foi possível vincular ${asset.patrimonyNumber}.`);
      }
    }

    setMessage(failures.length
      ? `${added} bem(ns) reservado(s). ${failures.join(" ")}`
      : mode === "usuario_solicitou" ? `${added} bem(ns) reservado(s) para você. A saída depende da conferência.` : `${added} bem(ns) reservado(s) para ${participant.userName}.`);
    if (!failures.length) {
      setIsAssetPickerOpen(false);
      setAssetPickerInitialQuery("");
    }
  }

  async function handleReassignItem(item: ExternalServiceItem, participantId: string, notes: string) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    const participant = participants.find((entry) => entry.userId === participantId);
    if (!participant) {
      setMessage("Selecione um participante para reatribuir o bem.");
      return;
    }

    try {
      await reassignExternalServiceItem(selectedCompany.id, selectedService.id, item, participant, currentUser, notes);
      setReassignTarget(null);
      setMessage("Bem reatribuído.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível reatribuir o bem.");
    }
  }

  async function markReturned(item: ExternalServiceItem, returnInspectionId?: string) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    try {
      await markExternalServiceItemReturned(selectedCompany.id, selectedService.id, item, currentUser, { returnInspectionId });
      setMessage("Devolução registrada. Aguardando conferência do retorno.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível registrar a devolução.");
    }
  }

  function handleMarkReturned(item: ExternalServiceItem) {
    // POP de retorno atribuido ao responsavel: ele preenche ao devolver
    if (rules.returnPopEnabled && rules.returnPopPerformer === "responsavel") {
      maybeStartInspection(item, "devolucao", (inspectionId) => void markReturned(item, inspectionId));
      return;
    }

    void markReturned(item);
  }

  async function handleItemStatus(item: ExternalServiceItem, status: ExternalServiceItemStatus, notes = "", onSuccess?: () => void, inspectionId?: string) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    try {
      await updateExternalServiceItemStatus(selectedCompany.id, selectedService.id, item, status, currentUser, notes, { inspectionId });
      // Sem conferencia obrigatoria, a confirmacao do responsavel ja registra a saida
      if (status === "confirmado_com_responsavel" && !rules.requirePickupConference) {
        await releaseExternalServiceItem(selectedCompany.id, selectedService.id, { ...item, status: "confirmado_com_responsavel" }, currentUser, "Saída sem conferência do gerente (regra da empresa).");
        await markExternalServiceStartedIfPending(selectedCompany.id, selectedService, currentUser).catch(() => undefined);
      }
      setMessage(status === "devolvido_com_divergencia"
        ? "Devolução registrada com divergência. Se houver avaria, acesse a ficha do bem para encaminhar para manutenção."
        : status === "confirmado_com_responsavel"
          ? rules.requirePickupConference ? "Item confirmado. Aguardando a conferência de saída." : "Item confirmado e liberado."
          : status === "devolvido"
            ? "Retorno aprovado. O bem está disponível."
            : "Item atualizado.");
      onSuccess?.();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o item.");
    }
  }

  async function handlePickupRelease(entries: PickupConferenceEntry[]) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    for (const entry of entries) {
      await releaseExternalServiceItem(selectedCompany.id, selectedService.id, entry.item, currentUser, "", {
        exitNotes: entry.exitNotes,
        pickupInspectionId: pickupPopResults[entry.item.id]
      });
    }
    await markExternalServiceStartedIfPending(selectedCompany.id, selectedService, currentUser).catch(() => undefined);
    setIsConferenceOpen(false);
    setMessage(`${entries.length} bem(ns) conferido(s) e liberado(s) para saída.`);
  }

  async function handleResolveDivergence(item: ExternalServiceItem, resolution: ExternalServiceItemStatus, notes: string, reactivateAsset: boolean) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    try {
      await resolveExternalServiceItemDivergence(selectedCompany.id, selectedService.id, item, resolution, currentUser, notes);
      if (reactivateAsset) {
        const asset = assetsById.get(item.assetId);
        if (asset) {
          await updateAssetStatus(selectedCompany.id, asset, {
            status: "ativo",
            reason: `Bem recolhido/reclassificado a partir do item ${item.patrimonyNumber} no serviço "${selectedService.title}" (${formatExternalServiceItemStatus(item.status)} -> ${formatExternalServiceItemStatus(resolution)}).`,
            eventDate: new Date().toISOString().slice(0, 10),
            resolutionNotes: notes || undefined,
            externalServiceId: selectedService.id,
            externalServiceItemId: item.id,
            externalServiceTitle: selectedService.title
          }, currentUser);
        }
      }
      setMessage("Situação do bem atualizada.");
      setDivergenceTarget(null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível resolver a divergência.");
    }
  }

  async function handleArchiveService(service: ExternalService) {
    if (!selectedCompany || !currentUser) {
      return;
    }

    try {
      await archiveExternalService(selectedCompany.id, service, currentUser);
      setMessage("Serviço arquivado.");
      setArchiveTarget(null);
      if (selectedServiceId === service.id) {
        setSelectedServiceId("");
        setIsMobileDetailOpen(false);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível arquivar o serviço.");
    }
  }

  async function handleRestoreService(service: ExternalService) {
    if (!selectedCompany || !currentUser) {
      return;
    }

    try {
      await restoreExternalService(selectedCompany.id, service.id, currentUser);
      setMessage("Serviço restaurado.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível restaurar o serviço.");
    }
  }

  async function handleCancelService(service: ExternalService) {
    if (!selectedCompany || !currentUser) {
      return;
    }

    try {
      await updateExternalServiceStatus(selectedCompany.id, service.id, "cancelado", currentUser, items);
      setCancelTarget(null);
      setMessage("Serviço cancelado. As reservas dos bens foram liberadas.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível cancelar o serviço.");
    }
  }

  function maybeStartInspection(item: ExternalServiceItem, context: InspectionContext, proceed: (inspectionId?: string) => void) {
    const asset = assetsById.get(item.assetId);
    const templates = asset && isPopEnabledFor(context) && canPerformInspections ? templatesFor(item, context) : [];
    if (!asset || !templates.length) {
      proceed();
      return;
    }

    setInspectionRequest({ item, asset, context, templates, proceed });
  }

  function approveReturn(item: ExternalServiceItem, status: "devolvido" | "devolvido_com_divergencia") {
    // O conferente so executa o POP se o responsavel nao preencheu ao devolver
    if (item.returnInspectionId) {
      void handleItemStatus(item, status);
      return;
    }

    maybeStartInspection(item, "devolucao", (inspectionId) => void handleItemStatus(item, status, "", undefined, inspectionId));
  }

  async function handleBulkAction() {
    if (!bulkAction || !selectedCompany || !selectedService || !currentUser) {
      return;
    }

    const targetItems = bulkAction === "confirmMine" ? myPendingItems : cleanPendingReturnApprovals;
    try {
      for (const item of targetItems) {
        if (bulkAction === "confirmMine") {
          await updateExternalServiceItemStatus(selectedCompany.id, selectedService.id, item, "confirmado_com_responsavel", currentUser, "Confirmação em lote após conferência manual.");
        } else {
          await updateExternalServiceItemStatus(selectedCompany.id, selectedService.id, item, "devolvido", currentUser, "Retorno aprovado em lote após conferência manual.");
        }
      }
      setBulkAction(null);
      setMessage(`${targetItems.length} item(ns) atualizado(s).`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível confirmar os itens em lote.");
    }
  }

  async function handleSaveItemNotes(item: ExternalServiceItem, notes: string) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    try {
      await updateExternalServiceItemNotes(selectedCompany.id, selectedService.id, item, { exitNotes: notes }, currentUser);
      setNoteTarget(null);
      setMessage("Observação salva.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar a observação.");
    }
  }

  async function handleServiceStatus(status: ExternalService["status"]) {
    if (!selectedCompany || !selectedService || !currentUser) {
      return;
    }

    if (status === "em_andamento" && !canStartService) {
      return;
    }

    if (status === "concluido" && !canConcludeService) {
      setMessage(`Ainda há ${pendingItemsCount} bem(ns) não devolvido(s). Confira o retorno antes de concluir.`);
      return;
    }

    try {
      await updateExternalServiceStatus(selectedCompany.id, selectedService.id, status, currentUser, items);
      setMessage(status === "concluido" ? "Serviço concluído." : "Serviço atualizado.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o serviço.");
    }
  }

  async function handleStartReturn() {
    if (!selectedCompany || !selectedService || !currentUser || !canStartReturn) {
      return;
    }

    try {
      await startExternalServiceReturn(selectedCompany.id, selectedService, items, participants, currentUser);
      setMessage("Retorno iniciado. Os participantes com bens foram avisados.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível iniciar o retorno.");
    }
  }

  async function handleUpdateServiceDetails(service: ExternalService, input: ExternalServiceFormInput) {
    if (!selectedCompany || !currentUser) {
      return;
    }

    await updateExternalServiceDetails(selectedCompany.id, service, {
      companyId: selectedCompany.id,
      participantUserIds: [],
      ...input
    }, currentUser, service.id === selectedService?.id ? items : []);
    setEditingService(null);
    setMessage("Serviço atualizado.");
  }

  async function handleExportExternalServicePdf(participantIds: string[], includeSignatures: boolean) {
    if (!selectedCompany || !selectedService) {
      return;
    }

    setIsPdfGenerating(true);
    try {
      await exportExternalServicePdf({
        company: selectedCompany,
        service: selectedService,
        participants,
        items,
        generatedBy: currentUserName,
        imobiLogoUrl: logoImobiFull,
        version: SERVICE_WORKER_VERSION,
        participantIds,
        includeSignatures,
        currentUser
      });
      setPdfParticipantIds(null);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível gerar o PDF.");
    } finally {
      setIsPdfGenerating(false);
    }
  }

  async function handleSaveRules(nextRules: ExternalServiceRules) {
    if (!selectedCompany || !currentUser) {
      throw new Error("Selecione uma empresa.");
    }

    await saveExternalServiceRules(selectedCompany.id, nextRules, currentUser);
  }

  function findServiceFromScan(value: string) {
    const rawValue = value.trim();
    const uriMatch = rawValue.match(/imobi:\/\/external-service\/([^/]+)\/([^/]+)\/?([^/]*)?/i);
    if (uriMatch) {
      return services.find((service) => service.companyId === uriMatch[1] && service.id === uriMatch[2]) ?? null;
    }

    const normalized = rawValue.toLowerCase();
    return services.find((service) => (
      service.id.toLowerCase() === normalized
      || service.serviceCode?.toLowerCase() === normalized
      || rawValue.includes(service.id)
      || (service.serviceCode ? normalized.includes(service.serviceCode.toLowerCase()) : false)
    )) ?? null;
  }

  function findCompanyAssetFromScan(value: string) {
    const normalized = value.trim().toLowerCase();
    return accessContext.scopedAssets.find((asset) => (
      asset.companyId === selectedCompany?.id
      && (
        asset.patrimonyNumber.toLowerCase() === normalized
        || asset.internalCode.toLowerCase() === normalized
        || asset.serialNumber?.toLowerCase() === normalized
        || value.includes(asset.id)
      )
    )) ?? null;
  }

  function handleScan(value: string) {
    const normalized = value.trim().toLowerCase();
    const scannedService = findServiceFromScan(value);
    const asset = findCompanyAssetFromScan(value);
    const returnItem = items.find((item) => (
      item.patrimonyNumber.toLowerCase() === normalized
      || item.internalCode?.toLowerCase() === normalized
      || item.serialNumber?.toLowerCase() === normalized
      || value.includes(item.assetId)
    ));
    const canUseAssetScan = canAssign || selectedService?.allowSelfAssignAssets === true;

    if (scannedService) {
      openService(scannedService.id);
      setMessage(`Serviço aberto pelo QR: ${scannedService.serviceCode ?? scannedService.title}.`);
    } else if (scanTarget === "add" && asset) {
      const isAvailable = pickerAssets.some((item) => item.id === asset.id);
      if (!canUseAssetScan) {
        setMessage("Seu usuário não tem permissão para reservar bens neste serviço.");
      } else if (!isAvailable) {
        setMessage(`Bem localizado: ${asset.patrimonyNumber} - ${asset.description}. Este bem não está disponível para serviço externo.`);
      } else {
        setAssetPickerInitialQuery(asset.patrimonyNumber);
        setIsAssetPickerOpen(true);
        setMessage(`Bem localizado para inclusão: ${asset.patrimonyNumber}.`);
      }
    } else if (scanTarget === "add") {
      setMessage("Código lido, mas nenhum serviço ou bem desta empresa foi encontrado.");
    } else if (scanTarget === "return" && returnItem) {
      if (canReturn && returnItem.assignedToUserId === currentUserId && ["em_uso", "confirmado_com_responsavel", "retorno_pendente"].includes(returnItem.status)) {
        handleMarkReturned(returnItem);
      } else if (canApprove && returnItem.status === "retorno_confirmado") {
        approveReturn(returnItem, "devolvido");
      } else {
        setMessage(`Bem ${returnItem.patrimonyNumber} não está pendente de devolução ou aprovação pelo seu usuário.`);
      }
    } else if (scanTarget === "return" && asset) {
      setMessage(`Bem localizado: ${asset.patrimonyNumber} - ${asset.description}. Ele não está vinculado ao serviço selecionado.`);
    } else {
      setMessage("Código lido, mas nenhum bem elegível foi encontrado neste serviço.");
    }

    setScanTarget(null);
  }

  if (!selectedCompany) {
    return <AccessDeniedPage title="Serviços externos" description="Selecione uma empresa para controlar saídas temporárias de bens." />;
  }

  const tabs: Array<{ id: ExternalServicesTab; label: string; icon: ReactNode; badge?: number }> = [
    { id: "services", label: "Serviços", icon: <LayoutList size={16} />, badge: globalSummary.active },
    { id: "calendar", label: "Calendário", icon: <CalendarDays size={16} /> },
    { id: "availability", label: "Disponibilidade", icon: <CalendarRange size={16} /> },
    { id: "mine", label: "Comigo", icon: <UserRound size={16} />, badge: reservations.filter((reservation) => reservation.responsibleUserId === currentUserId && (reservation.status === "reservado" || reservation.status === "em_uso")).length },
    { id: "out", label: "Bens fora", icon: <PackageOpen size={16} />, badge: externalAssetsOut.length },
    { id: "usage", label: "Uso", icon: <Gauge size={16} /> },
    ...(canManageAssetGroups ? [{ id: "groups" as const, label: "Grupos", icon: <Boxes size={16} /> }] : []),
    ...(canManageChecklistTemplates ? [{ id: "checklists" as const, label: "POPs", icon: <ClipboardList size={16} />, badge: checklistTemplates.length }] : []),
    ...(canManageCompanySettings || canEdit ? [{ id: "rules" as const, label: "Regras", icon: <Settings2 size={16} /> }] : [])
  ];
  const kpis: Array<{ label: string; value: number; tone?: "amber" | "rose"; onClick: () => void }> = [
    { label: "Serviços ativos", value: globalSummary.active, onClick: () => setMetricModal("active") },
    { label: "Bens fora", value: globalSummary.out, onClick: () => setActiveExternalTab("out") },
    { label: "Reservados", value: globalSummary.reserved, onClick: () => setActiveExternalTab("availability") },
    { label: "Atrasados", value: globalSummary.overdue, tone: globalSummary.overdue ? "rose" : undefined, onClick: () => setActiveExternalTab("out") },
    { label: "Divergências", value: globalSummary.divergent, tone: globalSummary.divergent ? "amber" : undefined, onClick: () => setActiveExternalTab("out") }
  ];
  const pendingChips: Array<{ key: "confirmation" | "release" | "return" | "approval" | "divergent"; label: string; value: number; tone: "yellow" | "red" | "blue" }> = [
    { key: "confirmation", label: "Aguardando retirada", value: summary.pendingConfirmation, tone: "yellow" },
    { key: "release", label: "Aguardando liberação", value: summary.pendingRelease, tone: "blue" },
    { key: "return", label: "Retorno pendente", value: summary.pendingReturn, tone: "yellow" },
    { key: "approval", label: "Conferir retorno", value: summary.pendingReturnApproval, tone: "blue" },
    { key: "divergent", label: "Divergências", value: summary.divergent, tone: "red" }
  ];
  const visibleEvents = isHistoryExpanded ? events : events.slice(0, 6);

  return (
    <>
      <PageHeader
        title="Serviços externos"
        subtitle="Reserva, saída, responsáveis, prazos e retorno de equipamentos."
        action={(
          <>
            <SecondaryButton onClick={() => setScanTarget("add")}><QrCode size={16} /> Ler QR</SecondaryButton>
            {canCreate ? <PrimaryButton onClick={() => setIsCreateOpen(true)}><Plus size={16} /> Novo serviço</PrimaryButton> : null}
          </>
        )}
      />
      <div className="space-y-4 p-3 sm:p-4 lg:p-6">
        <div className="-mx-3 flex snap-x gap-2 overflow-x-auto px-3 pb-1 sm:mx-0 sm:grid sm:grid-cols-5 sm:overflow-visible sm:px-0 sm:pb-0">
          {kpis.map((kpi) => (
            <button
              key={kpi.label}
              type="button"
              onClick={kpi.onClick}
              className={`focus-ring min-w-[8.5rem] shrink-0 snap-start rounded-xl border p-3 text-left shadow-sm transition hover:border-brand-300 sm:min-w-0 ${kpi.tone === "rose" ? "border-rose-200 bg-rose-50" : kpi.tone === "amber" ? "border-amber-200 bg-amber-50" : "border-slate-200 bg-white"}`}
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-slate-500">{kpi.label}</p>
              <p className={`mt-1 text-2xl font-semibold ${kpi.tone === "rose" ? "text-rose-700" : kpi.tone === "amber" ? "text-amber-800" : "text-ink"}`}>{kpi.value}</p>
            </button>
          ))}
        </div>

        <nav className="-mx-3 overflow-x-auto px-3 sm:mx-0 sm:px-0" aria-label="Seções de serviços externos">
          <div className="inline-flex min-w-full gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveExternalTab(tab.id)}
                aria-current={activeExternalTab === tab.id ? "page" : undefined}
                className={`focus-ring inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition ${activeExternalTab === tab.id ? "bg-brand-700 text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}
              >
                {tab.icon}
                {tab.label}
                {tab.badge ? <span className={`rounded-full px-1.5 text-[11px] ${activeExternalTab === tab.id ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"}`}>{tab.badge}</span> : null}
              </button>
            ))}
          </div>
        </nav>

        {message ? (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">
            <p>{message}</p>
            <button type="button" onClick={() => setMessage("")} aria-label="Fechar aviso" className="focus-ring shrink-0 rounded p-0.5 hover:bg-brand-100"><CloseIcon size={16} /></button>
          </div>
        ) : null}
        {reservationsError && ["calendar", "availability", "usage", "mine"].includes(activeExternalTab) ? <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{reservationsError}</p> : null}

        {activeExternalTab === "calendar" ? <ExternalServicesCalendar services={services} reservations={reservations} onSelectService={openService} /> : null}

        {activeExternalTab === "availability" ? (
          <EquipmentAvailabilityBoard
            assets={companyExternalAssets}
            reservations={reservations}
            services={services}
            assetGroups={assetGroups}
            onSelectService={openService}
            onOpenAsset={onOpenAsset}
          />
        ) : null}

        {activeExternalTab === "usage" ? <EquipmentUsagePanel assets={companyExternalAssets} reservations={reservations} onOpenAsset={onOpenAsset} /> : null}

        {activeExternalTab === "rules" ? <ExternalServiceRulesPanel rules={rules} canEdit={canManageCompanySettings} onSave={handleSaveRules} /> : null}

        {activeExternalTab === "mine" ? (
          <MyEquipmentPanel reservations={reservations} currentUserId={currentUserId} assets={accessContext.scopedAssets} services={services} onSelectService={openService} />
        ) : null}

        {activeExternalTab === "groups" && canManageAssetGroups ? (
          <ExternalServiceAssetGroupsPanel
            companyId={selectedCompany.id}
            groups={assetGroups}
            companyServiceUsers={companyServiceUsers}
            unassignedAssetsCount={companyExternalAssets.filter((asset) => !asset.assetGroup).length}
            currentUser={currentUser}
          />
        ) : null}

        {activeExternalTab === "checklists" && canManageChecklistTemplates ? (
          <>
            <p className="rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-600">
              POPs (procedimentos operacionais padrão) são as listas de tarefas de teste e registro de funcionamento. Pelas regras atuais, o POP de retirada está <strong>{rules.pickupPopEnabled ? "ligado" : "desligado"}</strong> e o de retorno está <strong>{rules.returnPopEnabled ? `ligado (${rules.returnPopPerformer === "responsavel" ? "preenchido pelo responsável" : "preenchido pelo conferente"})` : "desligado"}</strong>.
            </p>
            <ExternalServiceChecklistsPanel
              companyId={selectedCompany.id}
              templates={checklistTemplates}
              assets={companyExternalAssets}
              assetGroups={assetGroups}
              currentUser={currentUser}
            />
          </>
        ) : null}

        {activeExternalTab === "out" ? (
          <section className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
            <h2 className="font-semibold text-ink">Bens fora ou reservados</h2>
            <p className="mt-1 text-sm text-slate-500">Equipamentos que estão em algum serviço agora ou já têm reserva.</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {externalAssetsOut.map((asset) => {
                const service = services.find((item) => item.id === asset.currentExternalServiceId);
                const overdue = isAssetReturnOverdue(asset);
                return (
                  <div key={asset.id} className={`flex min-w-0 overflow-hidden rounded-lg border ${overdue ? "border-rose-200" : "border-slate-200"}`}>
                    <span className="w-1.5 shrink-0" style={{ backgroundColor: asset.currentExternalServiceId ? serviceColor(asset.currentExternalServiceId) : "#94a3b8" }} />
                    <button type="button" onClick={() => (asset.currentExternalServiceId ? openService(asset.currentExternalServiceId) : onOpenAsset(asset.id))} className="focus-ring flex min-w-0 flex-1 items-center gap-3 p-2.5 text-left hover:bg-slate-50">
                      {asset.mainPhotoUrl ? <img src={asset.mainPhotoUrl} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" /> : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400"><PackageCheck size={18} /></span>}
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1">
                          <span className="truncate text-sm font-semibold text-ink">{asset.patrimonyNumber}</span>
                          <StatusPill tone={asset.externalServiceStatus === "em_servico" ? "blue" : asset.externalServiceStatus === "em_retorno" ? "yellow" : asset.externalServiceStatus === "divergente" || asset.externalServiceStatus === "extraviado" ? "red" : "neutral"}>{formatAssetExternalServiceStatus(asset.externalServiceStatus ?? "reservado")}</StatusPill>
                          {overdue ? <StatusPill tone="red">Atrasado</StatusPill> : null}
                        </span>
                        <span className="block truncate text-xs text-slate-600">{asset.description}</span>
                        <span className="block truncate text-[11px] text-slate-500">{service?.title ?? "-"} · {asset.currentExternalServiceParticipantName ?? "sem responsável"}{asset.currentExternalServiceExpectedReturnAt ? ` · volta ${formatDateOnly(asset.currentExternalServiceExpectedReturnAt)}` : ""}</span>
                      </span>
                    </button>
                    <button type="button" onClick={() => onOpenAsset(asset.id)} aria-label={`Ver bem ${asset.patrimonyNumber}`} className="focus-ring flex shrink-0 items-center border-l border-slate-100 px-2.5 text-brand-700 hover:bg-brand-50">
                      <PackageSearch size={16} />
                    </button>
                  </div>
                );
              })}
            </div>
            {!externalAssetsOut.length ? <EmptyState title="Nenhum bem fora" description="Os bens alocados em serviços externos aparecem aqui." /> : null}
          </section>
        ) : null}

        {activeExternalTab === "services" ? (
          <div className="grid gap-4 xl:grid-cols-[340px_minmax(0,1fr)]">
            <section className={`min-w-0 rounded-xl border border-slate-200 bg-white shadow-sm ${isMobileDetailOpen ? "hidden xl:block" : ""}`}>
              <div className="space-y-2 border-b border-slate-200 p-3">
                <label className="relative block">
                  <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <input className="focus-ring w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" placeholder="Buscar serviço, cliente ou local" value={serviceSearch} onChange={(event) => setServiceSearch(event.target.value)} />
                </label>
                <div className="grid grid-cols-3 gap-1 rounded-lg bg-slate-100 p-1 text-xs font-semibold">
                  {([["ativos", "Ativos"], ["proximos", "Próximos"], ["todos", "Todos"]] as Array<[ExternalServiceFilter, string]>).map(([value, label]) => (
                    <button key={value} type="button" onClick={() => setServiceFilter(value)} className={`focus-ring rounded-md py-1.5 ${serviceFilter === value ? "bg-white text-ink shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{label}</button>
                  ))}
                </div>
              </div>
              <ul className="max-h-none divide-y divide-slate-100 xl:max-h-[calc(100vh-22rem)] xl:overflow-y-auto">
                {visibleServices.map((service) => {
                  const total = service.assetsCount ?? 0;
                  const returned = service.returnedAssetsCount ?? 0;
                  const span = serviceSpan(service);
                  const isLate = Boolean(span && ["em_andamento", "em_retorno"].includes(service.status) && service.expectedEndDate && service.expectedEndDate.slice(0, 10) < todayKey);
                  const startsToday = service.startDate.slice(0, 10) === todayKey;
                  return (
                    <li key={service.id}>
                      <button
                        type="button"
                        onClick={() => openService(service.id)}
                        className={`focus-ring flex w-full gap-3 p-3 text-left transition ${selectedService?.id === service.id ? "bg-brand-50" : "hover:bg-slate-50"} ${service.deletedAt ? "opacity-60" : ""}`}
                      >
                        <span className="w-1 shrink-0 self-stretch rounded-full" style={{ backgroundColor: serviceColor(service.id) }} />
                        <span className="min-w-0 flex-1">
                          <span className="flex items-start justify-between gap-2">
                            <span className="min-w-0 truncate font-semibold text-ink">{service.title}</span>
                            <span className="shrink-0 whitespace-nowrap"><StatusPill tone={externalServiceStatusTone(service.status)}>{formatExternalServiceStatus(service.status)}</StatusPill></span>
                          </span>
                          <span className="mt-0.5 block text-xs text-slate-500">{formatServicePeriod(service.startDate, service.expectedEndDate)}</span>
                          {service.clientName || service.location ? <span className="block truncate text-xs text-slate-500">{[service.clientName, service.location].filter(Boolean).join(" · ")}</span> : null}
                          <span className="mt-1.5 flex flex-wrap items-center gap-1">
                            <span className="text-[11px] font-semibold text-brand-700">{service.serviceCode ?? service.id.slice(0, 8).toUpperCase()}</span>
                            <span className="text-[11px] text-slate-400">·</span>
                            <span className="text-[11px] text-slate-500">{total} bem(ns) · {service.participantsCount ?? 0} pessoa(s)</span>
                            {startsToday ? <StatusPill tone="blue">Hoje</StatusPill> : null}
                            {isLate ? <StatusPill tone="red">Atrasado</StatusPill> : null}
                            {service.deletedAt ? <StatusPill>Arquivado</StatusPill> : null}
                          </span>
                          {total > 0 && ["em_retorno", "concluido"].includes(service.status) ? (
                            <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-slate-100">
                              <span className="block h-full rounded-full bg-emerald-700" style={{ width: `${Math.min(100, Math.round((returned / total) * 100))}%` }} />
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
              {!visibleServices.length ? (
                <div className="p-3">
                  <EmptyState
                    title={services.length ? "Nada neste filtro" : "Nenhum serviço externo"}
                    description={services.length ? "Troque o filtro ou a busca para ver outros serviços." : "Crie o primeiro serviço quando houver uma saída temporária de equipamentos."}
                  />
                </div>
              ) : null}
            </section>

            <section className={`min-w-0 ${isMobileDetailOpen ? "" : "hidden xl:block"}`}>
              {selectedService ? (
                <div className="space-y-4">
                  <button type="button" onClick={() => setIsMobileDetailOpen(false)} className="focus-ring inline-flex items-center gap-1 rounded-md px-1 py-1 text-sm font-semibold text-brand-700 xl:hidden">
                    <ArrowLeft size={16} /> Serviços
                  </button>

                  <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                    <div className="h-1.5" style={{ backgroundColor: serviceColor(selectedService.id) }} />
                    <div className="space-y-4 p-3 sm:p-4">
                      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <h2 className="text-lg font-semibold text-ink">{selectedService.title}</h2>
                            <StatusPill tone={externalServiceStatusTone(selectedService.status)}>{formatExternalServiceStatus(selectedService.status)}</StatusPill>
                            {selectedService.deletedAt ? <StatusPill>Arquivado</StatusPill> : null}
                          </div>
                          <p className="mt-0.5 text-xs font-semibold text-brand-700">{selectedService.serviceCode ?? selectedService.id.slice(0, 8).toUpperCase()}</p>
                          {selectedService.description ? <p className="mt-1 text-sm text-slate-600">{selectedService.description}</p> : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {canReport ? <SecondaryButton onClick={() => setPdfParticipantIds([])} disabled={isPdfGenerating}><FileDown size={16} /> Extrato PDF</SecondaryButton> : null}
                          {canEdit && !isSelectedServiceLocked ? <SecondaryButton onClick={() => setEditingService(selectedService)}><Edit3 size={16} /> Editar</SecondaryButton> : null}
                        </div>
                      </div>

                      <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                        <div className="rounded-lg bg-slate-50 p-2.5"><dt className="text-[11px] uppercase tracking-wide text-slate-500">Período</dt><dd className="mt-0.5 font-medium text-ink">{formatDateOnly(selectedService.startDate)} → {formatDateOnly(selectedService.expectedEndDate)}</dd></div>
                        <div className="rounded-lg bg-slate-50 p-2.5"><dt className="text-[11px] uppercase tracking-wide text-slate-500">Cliente</dt><dd className="mt-0.5 truncate font-medium text-ink">{selectedService.clientName || "-"}</dd></div>
                        <div className="rounded-lg bg-slate-50 p-2.5"><dt className="text-[11px] uppercase tracking-wide text-slate-500">Local</dt><dd className="mt-0.5 truncate font-medium text-ink">{selectedService.location || "-"}</dd></div>
                        <div className="rounded-lg bg-slate-50 p-2.5"><dt className="text-[11px] uppercase tracking-wide text-slate-500">Bens</dt><dd className="mt-0.5 font-medium text-ink">{items.length} · {participants.length} pessoa(s)</dd></div>
                      </dl>

                      <ServiceStatusStepper status={selectedService.status} />

                      {!isSelectedServiceLocked ? (
                        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                          {canApprove && pendingPickupItems.length ? <PrimaryButton onClick={() => setIsConferenceOpen(true)}><PackageCheck size={16} /> Conferir retirada ({pendingPickupItems.length})</PrimaryButton> : null}
                          {myPendingItems.length && canConfirm ? <SecondaryButton onClick={() => setBulkAction("confirmMine")}><Check size={16} /> Confirmar meus itens ({myPendingItems.length})</SecondaryButton> : null}
                          {(canAssign || (selectedService.allowSelfAssignAssets === true && canConfirm && participants.some((participant) => participant.userId === currentUserId))) ? (
                            <SecondaryButton onClick={() => { setAssetPickerInitialQuery(""); setIsAssetPickerOpen(true); }}><PackageSearch size={16} /> {canAssign ? "Reservar bens" : "Reservar para mim"}</SecondaryButton>
                          ) : null}
                          {canEdit && canStartService && !pendingPickupItems.length && items.length ? <SecondaryButton onClick={() => void handleServiceStatus("em_andamento")}><CheckCircle2 size={16} /> Iniciar</SecondaryButton> : null}
                          {canManageServiceFlow && canStartReturn ? <PrimaryButton onClick={() => void handleStartReturn()}><RotateCcw size={16} /> Iniciar retorno</PrimaryButton> : null}
                          {canReturn && ["em_andamento", "em_retorno"].includes(selectedService.status) ? <SecondaryButton onClick={() => setScanTarget("return")}><QrCode size={16} /> Ler retorno</SecondaryButton> : null}
                          {canApprove && cleanPendingReturnApprovals.length ? <SecondaryButton onClick={() => setBulkAction("approveReturns")}><ShieldCheck size={16} /> Aprovar retornos ({cleanPendingReturnApprovals.length})</SecondaryButton> : null}
                          {canClose && selectedService.status === "em_retorno" ? <PrimaryButton onClick={() => void handleServiceStatus("concluido")} disabled={!canConcludeService}><Check size={16} /> Concluir</PrimaryButton> : null}
                          {canClose && !["concluido", "cancelado"].includes(selectedService.status) ? <SecondaryButton onClick={() => setCancelTarget(selectedService)}><Ban size={16} /> Cancelar</SecondaryButton> : null}
                        </div>
                      ) : null}
                      {canClose && selectedService.status === "em_retorno" && pendingItemsCount > 0 ? <p className="text-xs text-amber-700">Ainda há {pendingItemsCount} bem(ns) sem retorno conferido.</p> : null}
                      {canClose && (selectedService.status === "concluido" || selectedService.status === "cancelado") ? (
                        <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-3">
                          {!selectedService.deletedAt ? <SecondaryButton onClick={() => setArchiveTarget(selectedService)}><Trash2 size={16} /> Arquivar</SecondaryButton> : <SecondaryButton onClick={() => void handleRestoreService(selectedService)}><RotateCcw size={16} /> Restaurar</SecondaryButton>}
                        </div>
                      ) : null}
                      {selectedService.deletedAt ? <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Serviço arquivado. Retirada, devolução e edição ficam bloqueadas até restaurar.</p> : null}
                    </div>
                  </div>

                  {pendingChips.some((chip) => chip.value) || summary.overdue ? (
                    <div className="flex flex-wrap gap-2">
                      {pendingChips.filter((chip) => chip.value).map((chip) => (
                        <button key={chip.key} type="button" onClick={() => setMetricModal(chip.key)} className="focus-ring inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:border-brand-300">
                          <StatusPill tone={chip.tone}>{chip.value}</StatusPill> {chip.label}
                        </button>
                      ))}
                      {summary.overdue ? <span className="inline-flex items-center gap-2 rounded-full border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm font-medium text-rose-700"><AlertTriangle size={14} /> {summary.overdue} atrasado(s)</span> : null}
                    </div>
                  ) : null}

                  <div className="grid gap-4 2xl:grid-cols-[minmax(0,1fr)_300px]">
                    <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="font-semibold text-ink">Equipamentos</h3>
                        <span className="text-xs text-slate-500">Toque em um item para ver as ações</span>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-[1fr_220px]">
                        <label className="relative block">
                          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input className="focus-ring w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" placeholder="Patrimônio, bem, série ou responsável" value={itemSearch} onChange={(event) => setItemSearch(event.target.value)} />
                        </label>
                        <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={itemParticipantFilter} onChange={(event) => setItemParticipantFilter(event.target.value)} aria-label="Responsável">
                          <option value="todos">Todos os responsáveis</option>
                          {participants.map((participant) => <option key={participant.userId} value={participant.userId}>{participant.userName}</option>)}
                        </select>
                      </div>
                      <div className="mt-3 space-y-4">
                        {itemGroups.map((group) => (
                          <div key={group.key}>
                            <div className="mb-2 flex items-center gap-2">
                              <Avatar name={group.name} size="sm" />
                              <p className="min-w-0 truncate text-sm font-semibold text-ink">{group.name}</p>
                              <span className="text-xs text-slate-500">{group.items.length} bem(ns)</span>
                              {canReport && group.key !== "sem-responsavel" ? (
                                <button type="button" onClick={() => setPdfParticipantIds([group.key])} className="focus-ring ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold text-brand-700 hover:bg-brand-50"><FileDown size={14} /> Termo</button>
                              ) : null}
                            </div>
                            <div className="grid gap-2 md:grid-cols-2">
                              {group.items.map((item) => (
                                <ExternalServiceItemCard
                                  key={item.id}
                                  item={item}
                                  pickupPopPending={item.status === "pendente_confirmacao" || item.status === "confirmado_com_responsavel" ? pickupPopRequired(item) : false}
                                  returnPopPending={item.status === "retorno_confirmado" && returnPopPending(item)}
                                  onOpenActions={() => setActionsTarget(item)}
                                />
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      {!filteredServiceItems.length ? <EmptyState title={items.length ? "Nenhum bem encontrado" : "Nenhum bem reservado"} description={items.length ? "Ajuste a busca ou o filtro de responsável." : "Use \"Reservar bens\" para vincular equipamentos disponíveis no período."} /> : null}
                    </div>

                    <aside className="space-y-4">
                      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
                        <h3 className="font-semibold text-ink">Participantes</h3>
                        <ul className="mt-2 space-y-1.5">
                          {participants.map((participant) => {
                            const count = items.filter((item) => item.assignedToUserId === participant.userId).length;
                            return (
                              <li key={participant.id} className="flex items-center gap-2 rounded-lg px-1 py-1">
                                <Avatar name={participant.userName} size="sm" />
                                <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{participant.userName}{participant.userId === currentUserId ? " (você)" : ""}</span>
                                <span className="text-xs text-slate-500">{count} bem(ns)</span>
                              </li>
                            );
                          })}
                        </ul>
                        {!participants.length ? <p className="mt-2 text-sm text-slate-500">Ninguém neste serviço ainda.</p> : null}
                        {canEdit && !isSelectedServiceLocked ? (
                          <select className="focus-ring mt-3 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value="" onChange={(event) => void handleAddParticipant(event.target.value)} aria-label="Adicionar participante">
                            <option value="">+ Adicionar participante</option>
                            {companyServiceUsers.filter((profile) => !participants.some((participant) => participant.userId === profile.id)).map((profile) => <option key={profile.id} value={profile.id}>{userDisplayName(profile)}</option>)}
                          </select>
                        ) : null}
                        {!canEdit && !isSelectedServiceLocked && selectedService.allowSelfEnrollment && !participants.some((participant) => participant.userId === currentUserId) ? (
                          <div className="mt-3"><SecondaryButton onClick={() => void handleAddParticipant(currentUserId)}><UserPlus size={16} /> Entrar no serviço</SecondaryButton></div>
                        ) : null}
                        <div className="mt-3 flex flex-wrap gap-1 text-[11px]">
                          {selectedService.allowSelfAssignAssets ? <StatusPill tone="blue">Participantes reservam</StatusPill> : <StatusPill>Só o gerente reserva</StatusPill>}
                          {selectedService.allowSelfEnrollment ? <StatusPill tone="blue">Entrada livre</StatusPill> : null}
                          {selectedService.allowPeerItemTransfer ? <StatusPill tone="blue">Transferência em campo</StatusPill> : null}
                        </div>
                      </div>

                      {events.length ? (
                        <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:p-4">
                          <h3 className="flex items-center gap-2 font-semibold text-ink"><History size={16} className="text-slate-500" /> Histórico</h3>
                          <ol className="mt-3 space-y-3 border-l border-slate-200 pl-4">
                            {visibleEvents.map((event) => (
                              <li key={event.id} className="relative">
                                <span className="absolute -left-[1.3rem] top-1.5 h-2 w-2 rounded-full bg-brand-600" />
                                <p className="text-sm text-slate-700">{event.description}</p>
                                <p className="text-[11px] text-slate-500">{formatAuditDate(event.createdAt)} · {event.userName}</p>
                              </li>
                            ))}
                          </ol>
                          {events.length > 6 ? (
                            <button type="button" onClick={() => setIsHistoryExpanded((current) => !current)} className="focus-ring mt-3 text-xs font-semibold text-brand-700 hover:underline">
                              {isHistoryExpanded ? "Mostrar menos" : `Ver tudo (${events.length})`}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </aside>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <EmptyState title="Selecione um serviço" description="Os detalhes aparecem aqui após escolher um serviço na lista." />
                </div>
              )}
            </section>
          </div>
        ) : null}
      </div>

      {isCreateOpen || editingService ? (
        <ExternalServiceCreateModal
          companyId={selectedCompany.id}
          users={companyServiceUsers}
          currentUser={currentUser}
          rules={rules}
          services={services}
          service={editingService ?? undefined}
          onClose={() => {
            setIsCreateOpen(false);
            setEditingService(null);
          }}
          onCreated={(serviceId) => {
            setIsCreateOpen(false);
            setEditingService(null);
            openService(serviceId);
            setMessage("Serviço criado. Agora reserve os equipamentos.");
          }}
          onUpdated={handleUpdateServiceDetails}
        />
      ) : null}
      {isAssetPickerOpen && selectedService ? (
        <ExternalServiceAssetPickerModal
          service={selectedService}
          assets={pickerAssets}
          reservations={reservations}
          participants={participants}
          currentUserId={currentUserId}
          canAssign={canAssign}
          initialQuery={assetPickerInitialQuery}
          onClose={() => {
            setIsAssetPickerOpen(false);
            setAssetPickerInitialQuery("");
          }}
          onAdd={(assets, participantId, mode, notes) => handleAddAssets(assets, participantId, mode, notes)}
        />
      ) : null}
      {isConferenceOpen && selectedService ? (
        <PickupConferenceModal
          service={selectedService}
          items={pendingPickupItems}
          participants={participants}
          popRequiredFor={pickupPopRequired}
          popDone={pickupPopResults}
          onRunPop={(item) => {
            const asset = assetsById.get(item.assetId);
            if (!asset) {
              return;
            }
            setInspectionRequest({
              item,
              asset,
              context: "retirada",
              templates: templatesFor(item, "retirada"),
              proceed: (inspectionId) => {
                if (inspectionId) {
                  setPickupPopResults((current) => ({ ...current, [item.id]: inspectionId }));
                }
              }
            });
          }}
          onRelease={handlePickupRelease}
          onClose={() => setIsConferenceOpen(false)}
        />
      ) : null}
      {pdfParticipantIds && selectedService ? (
        <ExternalServicePdfModal
          participants={participants}
          items={items}
          initialParticipantIds={pdfParticipantIds}
          isGenerating={isPdfGenerating}
          onGenerate={(participantIds, includeSignatures) => void handleExportExternalServicePdf(participantIds, includeSignatures)}
          onClose={() => setPdfParticipantIds(null)}
        />
      ) : null}
      {bulkAction ? (
        <ExternalServiceBulkConfirmModal
          count={bulkAction === "confirmMine" ? myPendingItems.length : cleanPendingReturnApprovals.length}
          actionLabel={bulkAction === "confirmMine" ? "Confirmar meus itens" : "Aprovar retornos sem pendência"}
          onCancel={() => setBulkAction(null)}
          onConfirm={() => void handleBulkAction()}
        />
      ) : null}
      {reassignTarget ? (
        <ExternalServiceReassignModal
          item={reassignTarget}
          participants={participants}
          onClose={() => setReassignTarget(null)}
          onReassign={(participantId, notes) => void handleReassignItem(reassignTarget, participantId, notes)}
        />
      ) : null}
      {noteTarget ? (
        <ExternalServiceNoteModal
          item={noteTarget}
          onClose={() => setNoteTarget(null)}
          onSave={(notes) => void handleSaveItemNotes(noteTarget, notes)}
        />
      ) : null}
      {actionsTarget ? (
        <ExternalServiceItemActionsModal
          item={actionsTarget}
          {...getItemActionState(actionsTarget)}
          returnPopPending={returnPopPending(actionsTarget)}
          onConfirm={() => maybeStartInspection(actionsTarget, "retirada", (inspectionId) => void handleItemStatus(actionsTarget, "confirmado_com_responsavel", actionsTarget.exitNotes ?? "", undefined, inspectionId))}
          onApprove={() => setIsConferenceOpen(true)}
          onMarkReturned={() => handleMarkReturned(actionsTarget)}
          onApproveReturn={() => approveReturn(actionsTarget, "devolvido")}
          onDivergence={() => approveReturn(actionsTarget, "devolvido_com_divergencia")}
          onNotReturned={() => void handleItemStatus(actionsTarget, "nao_devolvido")}
          onLost={() => void handleItemStatus(actionsTarget, "extraviado", "", () => setLostConfirmTarget(actionsTarget))}
          onReassign={() => setReassignTarget(actionsTarget)}
          onSelfCheck={() => maybeStartInspection(actionsTarget, "auditoria", () => setMessage("Conferência registrada."))}
          onEditNotes={() => setNoteTarget(actionsTarget)}
          onResolveDivergence={() => setDivergenceTarget(actionsTarget)}
          onOpenAsset={() => onOpenAsset(actionsTarget.assetId)}
          onClose={() => setActionsTarget(null)}
        />
      ) : null}
      {inspectionRequest && selectedCompany ? (
        <AssetInspectionModal
          companyId={selectedCompany.id}
          externalServiceId={selectedService?.id}
          externalServiceItemId={inspectionRequest.item.id}
          pickupNotes={inspectionRequest.item.exitNotes}
          asset={inspectionRequest.asset}
          context={inspectionRequest.context}
          templates={inspectionRequest.templates}
          currentUser={currentUser}
          onClose={() => setInspectionRequest(null)}
          onCompleted={(result) => {
            const proceed = inspectionRequest.proceed;
            setInspectionRequest(null);
            proceed(result.inspectionId);
          }}
        />
      ) : null}
      {archiveTarget ? (
        <ConfirmModal
          title="Arquivar serviço externo"
          message={`"${archiveTarget.title}" será arquivado e deixará de aparecer na lista por padrão. Os dados continuam salvos e o serviço pode ser restaurado a qualquer momento.`}
          confirmLabel="Arquivar"
          onConfirm={() => void handleArchiveService(archiveTarget)}
          onClose={() => setArchiveTarget(null)}
        />
      ) : null}
      {cancelTarget ? (
        <ConfirmModal
          title="Cancelar serviço externo"
          message={`"${cancelTarget.title}" será cancelado e as reservas dos equipamentos serão liberadas. Bens que já saíram continuam marcados até a devolução. Esta ação fica registrada no histórico.`}
          confirmLabel="Cancelar serviço"
          onConfirm={() => void handleCancelService(cancelTarget)}
          onClose={() => setCancelTarget(null)}
        />
      ) : null}
      {statusModalAsset ? (
        <AssetStatusModal
          asset={statusModalAsset}
          currentUser={currentUser}
          preset={statusModalPreset ?? undefined}
          onClose={() => {
            setStatusModalAsset(null);
            setStatusModalPreset(null);
          }}
        />
      ) : null}
      {divergenceTarget && selectedService ? (
        <ExternalServiceDivergenceModal
          item={divergenceTarget}
          asset={assetsById.get(divergenceTarget.assetId) ?? null}
          onResolve={(resolution, notes, reactivateAsset) => void handleResolveDivergence(divergenceTarget, resolution, notes, reactivateAsset)}
          onClose={() => setDivergenceTarget(null)}
        />
      ) : null}
      {lostConfirmTarget && selectedService ? (
        <ConfirmModal
          title="Inativar o bem agora?"
          message={`O bem ${lostConfirmTarget.patrimonyNumber} foi marcado como extraviado no serviço "${selectedService.title}". Você pode inativá-lo agora (o histórico registrará o serviço e a data em que foi extraviado) ou fazer isso depois pela lista de divergências.`}
          confirmLabel="Inativar agora"
          onConfirm={() => {
            const asset = assetsById.get(lostConfirmTarget.assetId);
            if (asset) {
              setStatusModalAsset(asset);
              setStatusModalPreset({
                status: "perdido",
                reason: `Extraviado no serviço "${selectedService.title}" (item ${lostConfirmTarget.patrimonyNumber}) em ${formatDateOnly(new Date().toISOString())}.`,
                externalServiceId: selectedService.id,
                externalServiceItemId: lostConfirmTarget.id,
                externalServiceTitle: selectedService.title
              });
            }
            setLostConfirmTarget(null);
          }}
          onClose={() => setLostConfirmTarget(null)}
        />
      ) : null}
      {metricModal === "active" ? (
        <ExternalServiceActiveListModal
          services={activeServices}
          onSelect={(service) => {
            openService(service.id);
            setMetricModal(null);
          }}
          onClose={() => setMetricModal(null)}
        />
      ) : null}
      {metricModal && metricModal !== "active" ? (
        <ExternalServiceMetricSummaryModal
          title={
            metricModal === "confirmation" ? "Aguardando retirada"
              : metricModal === "release" ? "Aguardando liberação"
              : metricModal === "return" ? "Retorno pendente"
              : metricModal === "approval" ? "Aguardando conferência do retorno"
              : "Divergências, não devolvidos e extraviados"
          }
          items={
            metricModal === "confirmation" ? confirmationItems
              : metricModal === "release" ? pendingUserRequests
              : metricModal === "return" ? returnPendingItems
              : metricModal === "approval" ? pendingReturnApprovals
              : divergentItems
          }
          showInactivateAction={metricModal === "divergent"}
          onInactivate={(item) => {
            const asset = assetsById.get(item.assetId);
            if (asset && selectedService) {
              setStatusModalAsset(asset);
              setStatusModalPreset(item.status === "extraviado" ? {
                status: "perdido",
                reason: `Extraviado no serviço "${selectedService.title}" (item ${item.patrimonyNumber}).`,
                externalServiceId: selectedService.id,
                externalServiceItemId: item.id,
                externalServiceTitle: selectedService.title
              } : {
                status: asset.status,
                externalServiceId: selectedService.id,
                externalServiceItemId: item.id,
                externalServiceTitle: selectedService.title
              });
              setMetricModal(null);
            }
          }}
          onResolve={(item) => {
            setDivergenceTarget(item);
            setMetricModal(null);
          }}
          onClose={() => setMetricModal(null)}
        />
      ) : null}
      {isPdfGenerating ? <ExternalServicePdfLoadingOverlay /> : null}
      {scanTarget ? <CodeScannerModal title={scanTarget === "return" ? "Ler bem devolvido ou serviço" : "Ler serviço ou bem"} onClose={() => setScanTarget(null)} onScan={handleScan} /> : null}
    </>
  );
}

function ExternalServiceItemCard({
  item,
  pickupPopPending = false,
  returnPopPending = false,
  onOpenActions
}: {
  item: ExternalServiceItem;
  pickupPopPending?: boolean;
  returnPopPending?: boolean;
  onOpenActions: () => void;
}) {
  const overdue = isItemReturnOverdue(item);
  return (
    <button
      type="button"
      onClick={onOpenActions}
      className={`focus-ring flex min-w-0 items-center gap-3 rounded-lg border bg-white p-2.5 text-left shadow-sm transition hover:border-brand-300 active:bg-slate-50 ${overdue ? "border-rose-200" : "border-slate-200"}`}
    >
      {item.mainPhotoUrl ? <img src={item.mainPhotoUrl} alt="" className="h-12 w-12 shrink-0 rounded-md object-cover" /> : <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-slate-100 text-slate-400"><PackageCheck size={18} /></span>}
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <span className="truncate text-sm font-semibold text-ink">{item.patrimonyNumber}</span>
          {item.exitNotes ? <StickyNote size={13} className="shrink-0 text-amber-700" aria-label="Tem observação de saída" /> : null}
        </span>
        <span className="block truncate text-xs text-slate-600">{item.description}</span>
        <span className="mt-1 flex flex-wrap gap-1">
          <StatusPill tone={externalServiceItemTone(item.status)}>{formatExternalServiceItemStatus(item.status)}</StatusPill>
          {overdue ? <StatusPill tone="red">Atrasado</StatusPill> : null}
          {pickupPopPending ? <StatusPill tone="yellow">POP de retirada</StatusPill> : null}
          {returnPopPending ? <StatusPill tone="yellow">POP de retorno</StatusPill> : null}
          {item.returnInspectionId && item.status === "retorno_confirmado" ? <StatusPill tone="green">POP preenchido</StatusPill> : null}
        </span>
      </span>
      <MoreHorizontal size={16} className="shrink-0 text-slate-400" />
    </button>
  );
}

function ExternalServiceItemActionsModal({
  item,
  canConfirm,
  canApprove,
  canMarkReturned,
  canApproveReturn,
  canReassign,
  canSelfCheck,
  canResolveDivergence,
  returnPopPending,
  onConfirm,
  onApprove,
  onMarkReturned,
  onApproveReturn,
  onDivergence,
  onNotReturned,
  onLost,
  onReassign,
  onSelfCheck,
  onEditNotes,
  onResolveDivergence,
  onOpenAsset,
  onClose
}: {
  item: ExternalServiceItem;
  canConfirm: boolean;
  canApprove: boolean;
  canMarkReturned: boolean;
  canApproveReturn: boolean;
  canReassign: boolean;
  canSelfCheck: boolean;
  canResolveDivergence: boolean;
  returnPopPending: boolean;
  onConfirm: () => void;
  onApprove: () => void;
  onMarkReturned: () => void;
  onApproveReturn: () => void;
  onDivergence: () => void;
  onNotReturned: () => void;
  onLost: () => void;
  onReassign: () => void;
  onSelfCheck: () => void;
  onEditNotes: () => void;
  onResolveDivergence: () => void;
  onOpenAsset: () => void;
  onClose: () => void;
}) {
  const auditLines = [
    item.confirmedByUserName && item.confirmedAt ? `Confirmado por ${item.confirmedByUserName} em ${formatAuditDate(item.confirmedAt)}` : "",
    item.exitCheckedByUserName && item.exitCheckedAt ? `Saída conferida por ${item.exitCheckedByUserName} em ${formatAuditDate(item.exitCheckedAt)}` : item.approvedByUserName && item.approvedAt ? `Liberado por ${item.approvedByUserName} em ${formatAuditDate(item.approvedAt)}` : "",
    item.returnedByUserName && item.returnedAt ? `Devolvido por ${item.returnedByUserName} em ${formatAuditDate(item.returnedAt)}${item.returnInspectionId ? " (com POP de retorno)" : ""}` : "",
    item.returnCheckedByUserName && item.returnCheckedAt ? `Retorno conferido por ${item.returnCheckedByUserName} em ${formatAuditDate(item.returnCheckedAt)}` : ""
  ].filter(Boolean);
  const hasActions = canConfirm || canApprove || canMarkReturned || canApproveReturn || canReassign || canSelfCheck || canResolveDivergence;

  function runAndClose(action: () => void) {
    action();
    onClose();
  }

  return (
    <SheetModal
      title={item.patrimonyNumber}
      subtitle={item.description}
      onClose={onClose}
      size="md"
    >
      <div className="flex items-start gap-3">
        {item.mainPhotoUrl ? <img src={item.mainPhotoUrl} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" /> : null}
        <div className="min-w-0 space-y-1 text-sm">
          <div className="flex flex-wrap gap-1">
            <StatusPill tone={externalServiceItemTone(item.status)}>{formatExternalServiceItemStatus(item.status)}</StatusPill>
            {isItemReturnOverdue(item) ? <StatusPill tone="red">Atrasado</StatusPill> : null}
          </div>
          <p className="text-slate-600">Responsável: <span className="font-medium text-ink">{item.assignedToUserName ?? "-"}</span></p>
          {item.serialNumber ? <p className="text-slate-500">Série: {item.serialNumber}</p> : null}
          <button type="button" onClick={() => runAndClose(onOpenAsset)} className="focus-ring inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:underline"><PackageSearch size={13} /> Ver ficha e histórico do bem</button>
        </div>
      </div>
      {auditLines.length ? <div className="mt-3 space-y-1 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">{auditLines.map((line) => <p key={line}>{line}</p>)}</div> : null}
      <div className="mt-3 rounded-lg border border-slate-200 p-3 text-sm text-slate-600">
        <p className="font-semibold text-ink">Observação de saída</p>
        <p className="mt-1">{item.exitNotes || "Nenhuma observação registrada."}</p>
        {item.returnNotes ? <p className="mt-2"><span className="font-semibold text-ink">Retorno:</span> {item.returnNotes}</p> : null}
        <button type="button" onClick={() => runAndClose(onEditNotes)} className="focus-ring mt-2 text-xs font-semibold text-brand-700 hover:underline">Editar observação</button>
      </div>
      {canApproveReturn && returnPopPending ? <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800">O POP de retorno ainda não foi preenchido. Ele será aberto ao aprovar o retorno.</p> : null}
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {canConfirm ? <PrimaryButton onClick={() => runAndClose(onConfirm)}><Check size={16} /> Confirmar recebimento</PrimaryButton> : null}
        {canApprove ? <PrimaryButton onClick={() => runAndClose(onApprove)}><PackageCheck size={16} /> Conferir e liberar saída</PrimaryButton> : null}
        {canMarkReturned ? <PrimaryButton onClick={() => runAndClose(onMarkReturned)}><RotateCcw size={16} /> Devolver</PrimaryButton> : null}
        {canApproveReturn ? <PrimaryButton onClick={() => runAndClose(onApproveReturn)}><Check size={16} /> Aprovar retorno</PrimaryButton> : null}
        {canApproveReturn ? <SecondaryButton onClick={() => runAndClose(onDivergence)}><AlertTriangle size={16} /> Voltou com divergência</SecondaryButton> : null}
        {canApproveReturn ? <SecondaryButton onClick={() => runAndClose(onNotReturned)}><CircleSlash size={16} /> Não devolvido</SecondaryButton> : null}
        {canApproveReturn ? <SecondaryButton onClick={() => runAndClose(onLost)}><AlertTriangle size={16} /> Extraviado</SecondaryButton> : null}
        {canReassign ? <SecondaryButton onClick={() => runAndClose(onReassign)}><UserPlus size={16} /> Passar para outro participante</SecondaryButton> : null}
        {canSelfCheck ? <SecondaryButton onClick={() => runAndClose(onSelfCheck)}><ClipboardCheck size={16} /> Conferir agora</SecondaryButton> : null}
        {canResolveDivergence ? <SecondaryButton onClick={() => runAndClose(onResolveDivergence)}><RotateCcw size={16} /> Resolver situação</SecondaryButton> : null}
      </div>
      {!hasActions ? <p className="mt-3 text-sm text-slate-500">Nenhuma ação disponível para este item no momento.</p> : null}
    </SheetModal>
  );
}

function ExternalServiceReassignModal({
  item,
  participants,
  onClose,
  onReassign
}: {
  item: ExternalServiceItem;
  participants: ExternalServiceParticipant[];
  onClose: () => void;
  onReassign: (participantId: string, notes: string) => void;
}) {
  const candidateParticipants = participants.filter((participant) => participant.userId !== item.assignedToUserId);
  const [participantId, setParticipantId] = useState(candidateParticipants[0]?.userId ?? "");
  const [notes, setNotes] = useState("");

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Reatribuir bem</h2>
            <p className="mt-1 text-sm text-slate-500">{item.patrimonyNumber} | {item.description} | Atual: {item.assignedToUserName ?? "-"}</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <label className="mt-4 block text-sm font-medium text-slate-700">
          Novo responsável
          <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={participantId} onChange={(event) => setParticipantId(event.target.value)}>
            <option value="">Selecionar participante</option>
            {candidateParticipants.map((participant) => <option key={participant.userId} value={participant.userId}>{participant.userName}</option>)}
          </select>
        </label>
        <label className="mt-3 block text-sm font-medium text-slate-700">
          Observação (opcional)
          <textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>
        <p className="mt-2 text-xs text-slate-500">O novo responsável precisará confirmar o recebimento do bem.</p>
        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => onReassign(participantId, notes)} disabled={!participantId}><UserPlus size={16} /> Reatribuir</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ExternalServiceActiveListModal({
  services,
  onSelect,
  onClose
}: {
  services: ExternalService[];
  onSelect: (service: ExternalService) => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <h2 className="font-semibold text-ink">Serviços em andamento</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 max-h-[60vh] space-y-2 overflow-y-auto">
          {services.map((service) => (
            <button
              key={service.id}
              type="button"
              onClick={() => onSelect(service)}
              className="w-full rounded-md border border-slate-200 p-3 text-left hover:bg-slate-50"
            >
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold text-ink">{service.title}</p>
                <StatusPill tone={service.status === "em_retorno" ? "yellow" : "blue"}>{formatExternalServiceStatus(service.status)}</StatusPill>
              </div>
              <p className="mt-1 text-xs font-semibold text-brand-700">Código: {service.serviceCode ?? service.id.slice(0, 8).toUpperCase()}</p>
              <p className="mt-1 text-sm text-slate-500">{formatDateOnly(service.startDate)} | {service.assetsCount ?? 0} bem(ns)</p>
            </button>
          ))}
          {!services.length ? <EmptyState title="Nenhum serviço em andamento" description="Serviços ativos aparecem aqui." /> : null}
        </div>
      </div>
    </div>
  );
}

function ExternalServiceMetricSummaryModal({
  title,
  items,
  showInactivateAction,
  onInactivate,
  onResolve,
  onClose
}: {
  title: string;
  items: ExternalServiceItem[];
  showInactivateAction: boolean;
  onInactivate: (item: ExternalServiceItem) => void;
  onResolve?: (item: ExternalServiceItem) => void;
  onClose: () => void;
}) {
  const divergentStatuses: ExternalServiceItemStatus[] = ["devolvido_com_divergencia", "nao_devolvido", "extraviado"];
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">Referente ao serviço selecionado.</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 max-h-[60vh] space-y-2 overflow-y-auto">
          {items.map((item) => (
            <div key={item.id} className="rounded-md border border-slate-200 p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-ink">{item.patrimonyNumber}</p>
                  <p className="text-sm text-slate-600">{item.description}</p>
                  <p className="mt-1 text-xs text-slate-500">Responsável: {item.assignedToUserName ?? "-"}</p>
                </div>
                <StatusPill tone={externalServiceItemTone(item.status)}>{formatExternalServiceItemStatus(item.status)}</StatusPill>
              </div>
              {item.exitNotes ? <p className="mt-2 rounded-md bg-slate-50 p-2 text-xs text-slate-600">Observação: {item.exitNotes}</p> : null}
              {item.resolutionNotes ? <p className="mt-2 rounded-md bg-emerald-50 p-2 text-xs text-emerald-800">Resolução: {item.resolutionNotes}</p> : null}
              {showInactivateAction && divergentStatuses.includes(item.status) ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {onResolve ? <SecondaryButton onClick={() => onResolve(item)}><RotateCcw size={16} /> Resolver situação</SecondaryButton> : null}
                  <SecondaryButton onClick={() => onInactivate(item)}><CircleSlash size={16} /> Ajustar status do bem</SecondaryButton>
                </div>
              ) : null}
            </div>
          ))}
          {!items.length ? <EmptyState title="Nada por aqui" description="Nenhum item se enquadra neste resumo no momento." /> : null}
        </div>
      </div>
    </div>
  );
}

function ExternalServiceDivergenceModal({
  item,
  asset,
  onResolve,
  onClose
}: {
  item: ExternalServiceItem;
  asset: AssetRecord | null;
  onResolve: (resolution: ExternalServiceItemStatus, notes: string, reactivateAsset: boolean) => void;
  onClose: () => void;
}) {
  const [resolution, setResolution] = useState<ExternalServiceItemStatus>(item.status);
  const [notes, setNotes] = useState("");
  const [reactivateAsset, setReactivateAsset] = useState(resolution === "devolvido");
  const canReactivate = Boolean(asset && asset.status !== "ativo");

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Resolver situação do bem</h2>
            <p className="mt-1 text-sm text-slate-500">{item.patrimonyNumber} - {item.description}</p>
            <div className="mt-2"><StatusPill tone={externalServiceItemTone(item.status)}>Atual: {formatExternalServiceItemStatus(item.status)}</StatusPill></div>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 grid gap-3">
          <label className="text-sm font-medium text-slate-700">Nova situação
            <select
              className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
              value={resolution}
              onChange={(event) => {
                const next = event.target.value as ExternalServiceItemStatus;
                setResolution(next);
                setReactivateAsset(next === "devolvido");
              }}
            >
              <option value="devolvido">Devolvido (bem encontrado/recolhido normalmente)</option>
              <option value="devolvido_com_divergencia">Devolvido com divergência</option>
              <option value="nao_devolvido">Não devolvido</option>
              <option value="extraviado">Extraviado</option>
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700">Observação da resolução
            <textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Ex.: bem encontrado no depósito, devolvido pelo colaborador após contato, etc." />
          </label>
          {canReactivate ? (
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              <input type="checkbox" className="mt-0.5" checked={reactivateAsset} onChange={(event) => setReactivateAsset(event.target.checked)} />
              Reativar o bem para operação (volta o status do bem para "Ativo")
            </label>
          ) : null}
        </div>
        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => onResolve(resolution, notes, canReactivate && reactivateAsset)}><Save size={16} /> Salvar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ExternalServiceBulkConfirmModal({
  count,
  actionLabel,
  onCancel,
  onConfirm
}: {
  count: number;
  actionLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[100] flex min-h-screen items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-lg border border-amber-200 bg-white p-6 shadow-panel">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-amber-100 text-amber-700">
            <AlertTriangle size={26} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-ink">{actionLabel}?</h2>
            <p className="mt-3 text-sm leading-6 text-slate-700">
              Os itens devem ser conferidos um a um. Esta ação existe apenas para facilitar o registro quando todos os bens já foram conferidos fisicamente antes de lançar no sistema.
            </p>
            <p className="mt-3 rounded-md bg-amber-50 p-3 text-sm font-semibold text-amber-800">{count} item(ns) serão atualizados com o seu usuário e data atual.</p>
          </div>
        </div>
        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <SecondaryButton onClick={onCancel}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={onConfirm} disabled={!count}><Check size={16} /> {actionLabel}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ExternalServiceNoteModal({
  item,
  onClose,
  onSave
}: {
  item: ExternalServiceItem;
  onClose: () => void;
  onSave: (notes: string) => void;
}) {
  const [notes, setNotes] = useState(item.exitNotes ?? "");

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Observação do bem</h2>
            <p className="mt-1 text-sm text-slate-500">{item.patrimonyNumber} | {item.description}</p>
            <p className="mt-1 text-xs text-slate-500">Registrada na retirada, disponível para conferência na devolução.</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <textarea className="focus-ring mt-4 min-h-36 w-full rounded-md border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} />
        <div className="mt-4 flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-4">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => onSave(notes)}><Save size={16} /> Salvar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ExternalServicePdfLoadingOverlay() {
  return (
    <div className="fixed inset-0 z-[120] flex min-h-screen items-center justify-center bg-slate-950/75 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-5 text-center shadow-panel">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-md bg-brand-50 text-brand-700">
          <Loader2 className="animate-spin" size={26} />
        </div>
        <h2 className="mt-4 text-base font-semibold text-ink">Gerando PDF</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">
          Carregando logos e miniaturas dos bens. Isso pode levar alguns segundos.
        </p>
      </div>
    </div>
  );
}

function ConfirmModal({
  title,
  message,
  confirmLabel = "Confirmar",
  tone = "danger",
  onConfirm,
  onClose
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  tone?: "danger" | "default";
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-[130] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <h2 className="font-semibold text-ink">{title}</h2>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        <div className="mt-4 flex justify-end gap-2">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <button
            type="button"
            onClick={onConfirm}
            className={`focus-ring inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60 ${tone === "danger" ? "bg-rose-600 hover:bg-rose-500" : "bg-brand-700 hover:bg-brand-600"}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

type ExternalServiceFormInput = {
  title: string;
  description: string;
  startDate: string;
  expectedEndDate?: string;
  clientName?: string;
  location?: string;
  notes?: string;
  allowSelfEnrollment?: boolean;
  allowSelfAssignAssets?: boolean;
  allowPeerItemTransfer?: boolean;
};

function ExternalServiceCreateModal({
  companyId,
  users,
  currentUser,
  rules,
  services,
  service,
  onClose,
  onCreated,
  onUpdated
}: {
  companyId: string;
  users: ManagedUser[];
  currentUser: User | null;
  rules: ExternalServiceRules;
  services: ExternalService[];
  service?: ExternalService;
  onClose: () => void;
  onCreated: (serviceId: string) => void;
  onUpdated: (service: ExternalService, input: ExternalServiceFormInput) => Promise<void>;
}) {
  const [title, setTitle] = useState(service?.title ?? "");
  const [description, setDescription] = useState(service?.description ?? "");
  const [startDate, setStartDate] = useState(service?.startDate ? service.startDate.slice(0, 10) : dateKey(new Date()));
  const [expectedEndDate, setExpectedEndDate] = useState(service?.expectedEndDate ? service.expectedEndDate.slice(0, 10) : "");
  const [clientName, setClientName] = useState(service?.clientName ?? "");
  const [location, setLocation] = useState(service?.location ?? "");
  const [notes, setNotes] = useState(service?.notes ?? "");
  const [participantUserIds, setParticipantUserIds] = useState<string[]>([]);
  const [participantSearch, setParticipantSearch] = useState("");
  const [allowSelfEnrollment, setAllowSelfEnrollment] = useState(service ? service.allowSelfEnrollment === true : rules.selfEnrollmentByDefault);
  const [allowSelfAssignAssets, setAllowSelfAssignAssets] = useState(service ? service.allowSelfAssignAssets === true : rules.participantsCanReserveByDefault);
  const [allowPeerItemTransfer, setAllowPeerItemTransfer] = useState(service ? service.allowPeerItemTransfer === true : rules.peerTransferByDefault);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const overlapping = services.filter((candidate) => (
    candidate.id !== service?.id
    && !candidate.deletedAt
    && !["concluido", "cancelado"].includes(candidate.status)
    && periodsOverlap(startDate, expectedEndDate || startDate, candidate.startDate, candidate.expectedEndDate)
  ));
  const filteredUsers = users.filter((profile) => !participantSearch.trim() || userDisplayName(profile).toLowerCase().includes(participantSearch.trim().toLowerCase()));

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser) {
      setMessage("Usuário autenticado não encontrado.");
      return;
    }

    if (expectedEndDate && expectedEndDate < startDate) {
      setMessage("A previsão de fim não pode ser anterior ao início.");
      return;
    }

    setIsSaving(true);
    try {
      const input: ExternalServiceFormInput = { title, description, startDate, expectedEndDate, clientName, location, notes, allowSelfEnrollment, allowSelfAssignAssets, allowPeerItemTransfer };
      if (service) {
        await onUpdated(service, input);
        return;
      }

      const participants: ExternalServiceParticipantInput[] = users.map((profile) => ({
        userId: profile.id,
        userName: userDisplayName(profile),
        userEmail: profile.email
      }));
      const serviceId = await createExternalService({ companyId, participantUserIds, ...input }, participants, currentUser);
      onCreated(serviceId);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o serviço externo.");
    } finally {
      setIsSaving(false);
    }
  }

  const inputClass = "focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2";

  return (
    <div className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-4">
      <form className="flex max-h-[94vh] w-full flex-col rounded-t-2xl border border-slate-200 bg-white shadow-panel sm:max-h-[90vh] sm:max-w-2xl sm:rounded-xl" onSubmit={handleSubmit}>
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3">
          <div>
            <h2 className="font-semibold text-ink">{service ? "Editar serviço externo" : "Novo serviço externo"}</h2>
            <p className="mt-0.5 text-sm text-slate-500">{service ? "Mudar o período atualiza as reservas dos equipamentos." : "Defina o período, a equipe e quem pode reservar equipamentos."}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar" className="focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CloseIcon size={18} /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-4">
          <fieldset className="grid gap-3 sm:grid-cols-2">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Serviço</legend>
            <label className="text-sm font-medium text-slate-700 sm:col-span-2">Nome do serviço / evento<input className={inputClass} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ex.: Casamento Ana e Pedro" required /></label>
            <label className="text-sm font-medium text-slate-700">Início<input className={inputClass} type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} required /></label>
            <label className="text-sm font-medium text-slate-700">Previsão de fim<input className={inputClass} type="date" value={expectedEndDate} min={startDate} onChange={(event) => setExpectedEndDate(event.target.value)} /></label>
            {overlapping.length ? (
              <p className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 p-2.5 text-xs text-sky-700 sm:col-span-2">
                <CalendarDays size={14} className="mt-0.5 shrink-0" />
                <span>{overlapping.length} serviço(s) no mesmo período: {overlapping.slice(0, 3).map((candidate) => candidate.title).join(", ")}{overlapping.length > 3 ? "…" : ""}. A disponibilidade de cada equipamento aparece ao reservar.</span>
              </p>
            ) : null}
            <label className="text-sm font-medium text-slate-700">Cliente<input className={inputClass} value={clientName} onChange={(event) => setClientName(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700">Local<input className={inputClass} value={location} onChange={(event) => setLocation(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700 sm:col-span-2">Descrição<textarea className={`${inputClass} min-h-20`} value={description} onChange={(event) => setDescription(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700 sm:col-span-2">Observações internas<textarea className={`${inputClass} min-h-16`} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
          </fieldset>

          {!service ? (
            <fieldset>
              <legend className="mb-2 flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-500">
                <span>Equipe ({participantUserIds.length})</span>
              </legend>
              <input className="focus-ring mb-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Buscar pessoa" value={participantSearch} onChange={(event) => setParticipantSearch(event.target.value)} />
              <div className="grid max-h-48 gap-1.5 overflow-y-auto rounded-lg border border-slate-200 p-2 sm:grid-cols-2">
                {filteredUsers.map((profile) => {
                  const checked = participantUserIds.includes(profile.id);
                  return (
                    <label key={profile.id} className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm ${checked ? "bg-brand-50 text-ink" : "text-slate-700 hover:bg-slate-50"}`}>
                      <input type="checkbox" checked={checked} onChange={(event) => setParticipantUserIds((current) => event.target.checked ? [...current, profile.id] : current.filter((id) => id !== profile.id))} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
                      <Avatar name={userDisplayName(profile)} size="sm" />
                      <span className="min-w-0 truncate">{userDisplayName(profile)}</span>
                    </label>
                  );
                })}
                {!filteredUsers.length ? <p className="p-2 text-sm text-slate-500">Nenhum usuário encontrado.</p> : null}
              </div>
            </fieldset>
          ) : null}

          <fieldset className="grid gap-2">
            <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Permissões neste serviço</legend>
            <ToggleRow title="Participantes podem reservar" description="Quem está na equipe reserva equipamentos para si (dos grupos que tem acesso). A saída depende da conferência." checked={allowSelfAssignAssets} onChange={setAllowSelfAssignAssets} />
            <ToggleRow title="Entrada livre na equipe" description="Qualquer usuário da empresa pode entrar neste serviço sozinho." checked={allowSelfEnrollment} onChange={setAllowSelfEnrollment} />
            <ToggleRow title="Transferência em campo" description="Quem está com um bem pode passá-lo direto para outro participante." checked={allowPeerItemTransfer} onChange={setAllowPeerItemTransfer} />
          </fieldset>
          {message ? <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">{message}</p> : null}
        </div>
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 px-4 py-3">
          <SecondaryButton onClick={onClose} disabled={isSaving}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={isSaving}>{isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {service ? "Salvar alterações" : "Criar serviço"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}

function ExternalServiceAssetGroupsPanel({
  companyId,
  groups,
  companyServiceUsers,
  unassignedAssetsCount,
  currentUser
}: {
  companyId: string;
  groups: AssetGroup[];
  companyServiceUsers: ManagedUser[];
  unassignedAssetsCount: number;
  currentUser: User | null;
}) {
  const [newGroupName, setNewGroupName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [expandedGroupId, setExpandedGroupId] = useState("");
  const [message, setMessage] = useState("");

  async function handleCreateGroup() {
    if (!newGroupName.trim() || !currentUser) {
      return;
    }

    setIsSaving(true);
    try {
      await createAssetGroup(companyId, newGroupName, currentUser);
      setNewGroupName("");
      setMessage("Grupo de bens criado.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível criar o grupo.");
    } finally {
      setIsSaving(false);
    }
  }

  async function toggleMember(group: AssetGroup, userId: string) {
    if (!currentUser) {
      return;
    }

    const nextMemberUserIds = group.memberUserIds.includes(userId)
      ? group.memberUserIds.filter((id) => id !== userId)
      : [...group.memberUserIds, userId];

    try {
      await updateAssetGroupMembers(companyId, group.id, nextMemberUserIds, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar os membros do grupo.");
    }
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div>
        <h2 className="font-semibold text-ink">Grupos de bens</h2>
        <p className="text-sm text-slate-500">Defina quais usuários podem visualizar e solicitar cada grupo de bens em serviços externos.</p>
      </div>
      {unassignedAssetsCount > 0 ? (
        <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          {unassignedAssetsCount} bem(ns) liberado(s) para serviços externos ainda sem grupo definido. Eles ficam visíveis apenas para gerentes até serem migrados.
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        <input
          className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2"
          placeholder="Nome do novo grupo"
          value={newGroupName}
          onChange={(event) => setNewGroupName(event.target.value)}
        />
        <PrimaryButton onClick={() => void handleCreateGroup()} disabled={isSaving || !newGroupName.trim()}>
          <Plus size={16} /> Novo grupo
        </PrimaryButton>
      </div>
      {message ? <p className="mt-2 text-sm text-slate-600">{message}</p> : null}
      <div className="mt-4 space-y-2">
        {groups.map((group) => (
          <div key={group.id} className="rounded-md border border-slate-200">
            <button
              type="button"
              onClick={() => setExpandedGroupId((current) => current === group.id ? "" : group.id)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
            >
              <span className="font-semibold text-ink">{group.name}</span>
              <span className="text-xs text-slate-500">{group.memberUserIds.length} usuário(s)</span>
            </button>
            {expandedGroupId === group.id ? (
              <div className="border-t border-slate-200 p-3">
                {companyServiceUsers.length ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {companyServiceUsers.map((profile) => (
                      <label key={profile.id} className="flex items-center gap-2 text-sm text-slate-700">
                        <input
                          type="checkbox"
                          checked={group.memberUserIds.includes(profile.id)}
                          onChange={() => void toggleMember(group, profile.id)}
                          className="h-4 w-4 rounded border-slate-300 text-brand-700"
                        />
                        {userDisplayName(profile)}
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Nenhum usuário com acesso a serviços externos nesta empresa.</p>
                )}
              </div>
            ) : null}
          </div>
        ))}
      </div>
      {!groups.length ? <EmptyState title="Nenhum grupo de bens" description="Crie um grupo para começar a organizar o acesso aos bens de serviços externos." /> : null}
    </section>
  );
}

function checklistAnswerTypeLabel(type: ChecklistAnswerType) {
  const labels: Record<ChecklistAnswerType, string> = {
    sim_nao: "Sim/Não",
    conforme_nao_conforme: "Conforme/Não conforme",
    texto: "Texto",
    numero: "Número",
    foto: "Foto",
    selecao: "Seleção",
    observacao: "Observação"
  };
  return labels[type] ?? type;
}

function emptyChecklistQuestion(): ChecklistQuestion {
  return {
    id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label: "",
    type: "sim_nao",
    required: true,
    requireDetailOnNegative: false
  };
}

function checklistContextLabel(context: ChecklistContext) {
  return context === "retirada" ? "Retirada" : "Devolução";
}

function ExternalServiceChecklistsPanel({
  companyId,
  templates,
  assets,
  assetGroups,
  currentUser
}: {
  companyId: string;
  templates: ChecklistTemplate[];
  assets: AssetRecord[];
  assetGroups: AssetGroup[];
  currentUser: User | null;
}) {
  const [activeContext, setActiveContext] = useState<ChecklistContext>("devolucao");
  const [assetTypes, setAssetTypes] = useState<AssetTypeOption[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ChecklistTemplate | null>(null);
  const [cloneQuestions, setCloneQuestions] = useState<ChecklistQuestion[] | null>(null);
  const [isBulkOpen, setIsBulkOpen] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => (
    subscribeAssetTypes(setAssetTypes, () => undefined)
  ), []);

  const contextTemplates = templates.filter((template) => template.context === activeContext);
  const generalTemplates = contextTemplates.filter((template) => template.scope === "geral");
  const typeTemplates = contextTemplates.filter((template) => template.scope === "tipo");
  const itemTemplates = contextTemplates.filter((template) => template.scope === "bem");
  const legacyTemplates = contextTemplates.filter((template) => template.scope === "grupo");

  function assetLabelFor(template: ChecklistTemplate) {
    const asset = assets.find((item) => item.id === template.assetId);
    return asset ? `${asset.patrimonyNumber} - ${asset.description}` : "Bem removido ou fora do escopo atual";
  }

  function assetTypeLabelFor(template: ChecklistTemplate) {
    return assetTypes.find((item) => item.id === template.assetTypeId)?.name ?? template.assetTypeId ?? "-";
  }

  async function handleToggleActive(template: ChecklistTemplate) {
    if (!currentUser) {
      return;
    }

    try {
      await setChecklistTemplateActive(companyId, template.id, !template.active, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o checklist.");
    }
  }

  function openNew() {
    setEditingTemplate(null);
    setCloneQuestions(null);
    setIsFormOpen(true);
  }

  function openDuplicate(template: ChecklistTemplate) {
    setEditingTemplate(null);
    setCloneQuestions(template.questions.map((question) => ({ ...question, id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` })));
    setIsFormOpen(true);
  }

  function openEdit(template: ChecklistTemplate) {
    setEditingTemplate(template);
    setCloneQuestions(null);
    setIsFormOpen(true);
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-ink">Checklists de inspeção</h2>
          <p className="text-sm text-slate-500">Um bem pode ter checklist padrão (todos os bens), por tipo de bem e específico dele ao mesmo tempo — todos aparecem juntos na hora de conferir.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {itemTemplates.length > 1 ? <SecondaryButton onClick={() => setIsBulkOpen(true)}><ListChecks size={16} /> Editar em lote</SecondaryButton> : null}
          <PrimaryButton onClick={openNew}><Plus size={16} /> Novo checklist</PrimaryButton>
        </div>
      </div>

      <div className="mt-3 flex gap-2 rounded-md border border-slate-200 bg-slate-50 p-1">
        {(["devolucao", "retirada"] as ChecklistContext[]).map((context) => (
          <button
            key={context}
            type="button"
            onClick={() => setActiveContext(context)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${activeContext === context ? "bg-white text-ink shadow-sm" : "text-slate-500 hover:text-ink"}`}
          >
            {checklistContextLabel(context)} ({templates.filter((template) => template.context === context).length})
          </button>
        ))}
      </div>

      {message ? <p className="mt-2 text-sm text-slate-600">{message}</p> : null}

      <div className="mt-4 space-y-2">
        {generalTemplates.map((template) => (
          <div key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-brand-200 bg-brand-50/40 p-3">
            <div>
              <p className="font-semibold text-ink">
                {template.name}
                <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-[11px] font-medium text-brand-700">Padrão (todos os bens)</span>
              </p>
              <p className="mt-1 text-xs text-slate-500">{template.questions.length} pergunta(s)</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill tone={template.active ? "green" : "neutral"}>{template.active ? "Ativo" : "Inativo"}</StatusPill>
              <SecondaryButton onClick={() => openEdit(template)}><Edit3 size={16} /> Editar</SecondaryButton>
              <SecondaryButton onClick={() => void handleToggleActive(template)}>{template.active ? "Desativar" : "Ativar"}</SecondaryButton>
            </div>
          </div>
        ))}

        {typeTemplates.map((template) => (
          <div key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-indigo-200 bg-indigo-50/40 p-3">
            <div>
              <p className="font-semibold text-ink">
                {template.name}
                <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-medium text-indigo-700">Tipo: {assetTypeLabelFor(template)}</span>
              </p>
              <p className="mt-1 text-xs text-slate-500">{template.questions.length} pergunta(s)</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill tone={template.active ? "green" : "neutral"}>{template.active ? "Ativo" : "Inativo"}</StatusPill>
              <SecondaryButton onClick={() => openEdit(template)}><Edit3 size={16} /> Editar</SecondaryButton>
              <SecondaryButton onClick={() => void handleToggleActive(template)}>{template.active ? "Desativar" : "Ativar"}</SecondaryButton>
            </div>
          </div>
        ))}

        {itemTemplates.map((template) => (
          <div key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 p-3">
            <div>
              <p className="font-semibold text-ink">{template.name}</p>
              <p className="mt-1 text-xs text-slate-500">Bem: {assetLabelFor(template)} · {template.questions.length} pergunta(s)</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusPill tone={template.active ? "green" : "neutral"}>{template.active ? "Ativo" : "Inativo"}</StatusPill>
              <SecondaryButton onClick={() => openDuplicate(template)}><Copy size={16} /> Duplicar</SecondaryButton>
              <SecondaryButton onClick={() => openEdit(template)}><Edit3 size={16} /> Editar</SecondaryButton>
              <SecondaryButton onClick={() => void handleToggleActive(template)}>{template.active ? "Desativar" : "Ativar"}</SecondaryButton>
            </div>
          </div>
        ))}
      </div>

      {!contextTemplates.length ? <EmptyState title="Nenhum checklist cadastrado" description={`Crie um checklist de ${checklistContextLabel(activeContext).toLowerCase()} padrão, por tipo de bem ou específico de um bem.`} /> : null}

      {legacyTemplates.length ? (
        <details className="mt-4 rounded-md border border-slate-200 p-3 text-sm">
          <summary className="cursor-pointer font-medium text-slate-600">Checklists antigos por grupo ({legacyTemplates.length})</summary>
          <div className="mt-2 space-y-2">
            {legacyTemplates.map((template) => (
              <div key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 p-3">
                <div>
                  <p className="font-semibold text-ink">{template.name}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Grupo: {assetGroups.find((group) => group.name === template.assetGroup)?.name ?? template.assetGroup ?? "-"}
                    {" · "}{template.questions.length} pergunta(s)
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill tone={template.active ? "green" : "neutral"}>{template.active ? "Ativo" : "Inativo"}</StatusPill>
                  <SecondaryButton onClick={() => openEdit(template)}><Edit3 size={16} /> Editar</SecondaryButton>
                  <SecondaryButton onClick={() => void handleToggleActive(template)}>{template.active ? "Desativar" : "Ativar"}</SecondaryButton>
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {isFormOpen ? (
        <ChecklistTemplateFormModal
          companyId={companyId}
          template={editingTemplate}
          defaultContext={activeContext}
          assets={assets}
          assetGroups={assetGroups}
          assetTypes={assetTypes}
          initialQuestions={cloneQuestions ?? undefined}
          otherItemTemplates={itemTemplates}
          currentUser={currentUser}
          onClose={() => setIsFormOpen(false)}
        />
      ) : null}

      {isBulkOpen ? (
        <ChecklistBulkAddStepModal
          companyId={companyId}
          templates={itemTemplates}
          assets={assets}
          currentUser={currentUser}
          onClose={() => setIsBulkOpen(false)}
        />
      ) : null}
    </section>
  );
}

function ChecklistTemplateFormModal({
  companyId,
  template,
  defaultContext,
  assets,
  assetGroups,
  assetTypes,
  initialQuestions,
  otherItemTemplates,
  currentUser,
  onClose
}: {
  companyId: string;
  template: ChecklistTemplate | null;
  defaultContext: ChecklistContext;
  assets: AssetRecord[];
  assetGroups: AssetGroup[];
  assetTypes: AssetTypeOption[];
  initialQuestions?: ChecklistQuestion[];
  otherItemTemplates: ChecklistTemplate[];
  currentUser: User | null;
  onClose: () => void;
}) {
  const isLegacyScope = Boolean(template && template.scope === "grupo");
  const [name, setName] = useState(template?.name ?? "");
  const [context, setContext] = useState<ChecklistContext>(template?.context ?? defaultContext);
  const [scope, setScope] = useState<ChecklistTemplateScope>(template?.scope ?? "bem");
  const [assetId, setAssetId] = useState(template?.assetId ?? "");
  const [assetQuery, setAssetQuery] = useState("");
  const [assetTypeId, setAssetTypeId] = useState(template?.assetTypeId ?? "");
  const [duplicateFromId, setDuplicateFromId] = useState("");
  const [returnStorageLocation, setReturnStorageLocation] = useState(template?.returnStorageLocation ?? "");
  const [questions, setQuestions] = useState<ChecklistQuestion[]>(
    initialQuestions?.length ? initialQuestions : template?.questions.length ? template.questions : [emptyChecklistQuestion()]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  const selectedAsset = assets.find((asset) => asset.id === assetId);
  const filteredAssets = assetQuery.trim()
    ? assets.filter((asset) => [asset.patrimonyNumber, asset.internalCode, asset.description, asset.serialNumber ?? ""].join(" ").toLowerCase().includes(assetQuery.trim().toLowerCase()))
    : assets;
  const duplicateOptions = otherItemTemplates.filter((item) => item.id !== template?.id);

  function updateQuestion(id: string, patch: Partial<ChecklistQuestion>) {
    setQuestions((current) => current.map((question) => question.id === id ? { ...question, ...patch } : question));
  }

  function removeQuestion(id: string) {
    setQuestions((current) => current.filter((question) => question.id !== id));
  }

  function handleDuplicateFrom(sourceId: string) {
    setDuplicateFromId(sourceId);
    const source = duplicateOptions.find((item) => item.id === sourceId);
    if (!source) {
      return;
    }
    setQuestions(source.questions.map((question) => ({ ...question, id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}` })));
  }

  async function handleSave() {
    if (!currentUser) {
      return;
    }

    if (!name.trim()) {
      setMessage("Informe o nome do checklist.");
      return;
    }

    if (!isLegacyScope && scope === "bem" && !assetId) {
      setMessage("Selecione o bem.");
      return;
    }

    if (!isLegacyScope && scope === "tipo" && !assetTypeId) {
      setMessage("Selecione o tipo de bem.");
      return;
    }

    const cleanedQuestions = questions.filter((question) => question.label.trim());
    if (!cleanedQuestions.length) {
      setMessage("Adicione ao menos uma pergunta com título.");
      return;
    }

    setIsSaving(true);
    try {
      const input = {
        companyId,
        name,
        scope: isLegacyScope ? template!.scope : scope,
        context: isLegacyScope ? template!.context : context,
        assetGroup: isLegacyScope ? template?.assetGroup : undefined,
        assetTypeId: !isLegacyScope && scope === "tipo" ? assetTypeId : undefined,
        assetId: !isLegacyScope && scope === "bem" ? assetId : undefined,
        questions: cleanedQuestions,
        returnStorageLocation
      };
      if (template) {
        await updateChecklistTemplate(companyId, template.id, input, currentUser);
      } else {
        await createChecklistTemplate(input, currentUser);
      }
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o checklist.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <h2 className="font-semibold text-ink">{template ? "Editar checklist" : "Novo checklist"}</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700">
            Nome do checklist
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex.: Câmera fotográfica" />
          </label>
          <label className="text-sm text-slate-700">
            Usado na
            <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={context} onChange={(event) => setContext(event.target.value as ChecklistContext)} disabled={isLegacyScope}>
              <option value="devolucao">Devolução</option>
              <option value="retirada">Retirada</option>
            </select>
          </label>
          {!isLegacyScope ? (
            <label className="text-sm text-slate-700">
              Aplicar a
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={scope} onChange={(event) => { setScope(event.target.value as ChecklistTemplateScope); setAssetId(""); setAssetTypeId(""); }}>
                <option value="bem">Este bem</option>
                <option value="tipo">Bens de um tipo</option>
                <option value="geral">Todos os bens (padrão)</option>
              </select>
            </label>
          ) : (
            <label className="text-sm text-slate-700">
              Aplicar a
              <input
                className="focus-ring mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-500"
                disabled
                value={`Grupo: ${assetGroups.find((group) => group.name === template?.assetGroup)?.name ?? template?.assetGroup ?? "-"}`}
              />
              <span className="mt-1 block text-xs text-slate-500">Escopo antigo, mantido só para compatibilidade. Para mudar, duplique este checklist para um bem específico.</span>
            </label>
          )}
          {!isLegacyScope && scope === "tipo" ? (
            <label className="text-sm text-slate-700 md:col-span-2">
              Tipo de bem
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={assetTypeId} onChange={(event) => setAssetTypeId(event.target.value)}>
                <option value="">Selecione um tipo</option>
                {assetTypes.map((assetType) => <option key={assetType.id} value={assetType.id}>{assetType.name}</option>)}
              </select>
              {!assetTypes.length ? <span className="mt-1 block text-xs text-amber-600">Nenhum tipo cadastrado. Configure a tabela de imobilizados.</span> : null}
            </label>
          ) : null}
          {!isLegacyScope && scope === "bem" ? (
            <div className="text-sm text-slate-700 md:col-span-2">
              <span>Bem</span>
              {selectedAsset ? (
                <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-slate-300 px-3 py-2">
                  <span>{selectedAsset.patrimonyNumber} - {selectedAsset.description}</span>
                  <button type="button" onClick={() => setAssetId("")} className="text-xs font-medium text-brand-700 hover:underline">Trocar</button>
                </div>
              ) : (
                <>
                  <input
                    className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                    placeholder="Buscar por patrimônio, código, descrição ou série"
                    value={assetQuery}
                    onChange={(event) => setAssetQuery(event.target.value)}
                  />
                  <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200">
                    {filteredAssets.slice(0, 30).map((asset) => (
                      <button
                        key={asset.id}
                        type="button"
                        onClick={() => { setAssetId(asset.id); setAssetQuery(""); }}
                        className="flex w-full items-center justify-between gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-slate-50"
                      >
                        <span>{asset.patrimonyNumber} - {asset.description}</span>
                      </button>
                    ))}
                    {!filteredAssets.length ? <p className="px-3 py-2 text-xs text-slate-500">Nenhum bem encontrado.</p> : null}
                  </div>
                </>
              )}
            </div>
          ) : null}
          {!isLegacyScope && scope === "bem" && duplicateOptions.length ? (
            <label className="text-sm text-slate-700 md:col-span-2">
              Duplicar perguntas de outro bem (opcional)
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={duplicateFromId} onChange={(event) => handleDuplicateFrom(event.target.value)}>
                <option value="">Começar em branco</option>
                {duplicateOptions.map((item) => {
                  const asset = assets.find((candidate) => candidate.id === item.assetId);
                  const optionLabel = asset ? `${asset.patrimonyNumber} - ${asset.description}` : item.name;
                  return <option key={item.id} value={item.id}>{optionLabel} ({item.questions.length} pergunta(s))</option>;
                })}
              </select>
            </label>
          ) : null}
          <label className="text-sm text-slate-700 md:col-span-2">
            Local de guarda na devolução (opcional)
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={returnStorageLocation} onChange={(event) => setReturnStorageLocation(event.target.value)} placeholder="Ex.: Armário 3, prateleira B" />
          </label>
        </div>

        <div className="mt-4 space-y-3">
          <h3 className="text-sm font-semibold text-ink">Perguntas</h3>
          {questions.map((question, index) => (
            <div key={question.id} className="rounded-md border border-slate-200 p-3">
              <div className="grid gap-2 md:grid-cols-[1fr_180px_auto]">
                <input
                  className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm"
                  placeholder={`Pergunta ${index + 1}`}
                  value={question.label}
                  onChange={(event) => updateQuestion(question.id, { label: event.target.value })}
                />
                <select
                  className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm"
                  value={question.type}
                  onChange={(event) => updateQuestion(question.id, { type: event.target.value as ChecklistAnswerType })}
                >
                  {(["sim_nao", "conforme_nao_conforme", "texto", "numero", "foto", "selecao", "observacao"] as ChecklistAnswerType[]).map((type) => (
                    <option key={type} value={type}>{checklistAnswerTypeLabel(type)}</option>
                  ))}
                </select>
                <button type="button" onClick={() => removeQuestion(question.id)} className="focus-ring inline-flex h-9 w-9 items-center justify-center self-start rounded-md border border-slate-200 text-slate-500 hover:text-rose-600"><Trash2 size={16} /></button>
              </div>
              {question.type === "selecao" ? (
                <input
                  className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                  placeholder="Opções separadas por vírgula"
                  value={(question.options ?? []).join(", ")}
                  onChange={(event) => updateQuestion(question.id, { options: event.target.value.split(",").map((option) => option.trim()).filter(Boolean) })}
                />
              ) : null}
              <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={question.required} onChange={(event) => updateQuestion(question.id, { required: event.target.checked })} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-700" />
                  Obrigatório
                </label>
                {question.type === "sim_nao" || question.type === "conforme_nao_conforme" ? (
                  <label className="flex items-center gap-1">
                    <input type="checkbox" checked={question.requireDetailOnNegative === true} onChange={(event) => updateQuestion(question.id, { requireDetailOnNegative: event.target.checked })} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-700" />
                    Exigir descrição e foto quando a resposta indicar problema
                  </label>
                ) : null}
              </div>
            </div>
          ))}
          <SecondaryButton onClick={() => setQuestions((current) => [...current, emptyChecklistQuestion()])}><Plus size={16} /> Adicionar pergunta</SecondaryButton>
        </div>

        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={isSaving}>Salvar checklist</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ChecklistBulkAddStepModal({
  companyId,
  templates,
  assets,
  currentUser,
  onClose
}: {
  companyId: string;
  templates: ChecklistTemplate[];
  assets: AssetRecord[];
  currentUser: User | null;
  onClose: () => void;
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set(templates.map((template) => template.id)));
  const [label, setLabel] = useState("");
  const [type, setType] = useState<ChecklistAnswerType>("sim_nao");
  const [options, setOptions] = useState("");
  const [required, setRequired] = useState(true);
  const [requireDetailOnNegative, setRequireDetailOnNegative] = useState(false);
  const [insertMode, setInsertMode] = useState<"start" | "end" | "position" | "afterLabel">("end");
  const [position, setPosition] = useState(1);
  const [afterLabel, setAfterLabel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [result, setResult] = useState<{ updated: number; skipped: string[] } | null>(null);

  function assetLabelFor(template: ChecklistTemplate) {
    const asset = assets.find((item) => item.id === template.assetId);
    return asset ? `${asset.patrimonyNumber} - ${asset.description}` : template.name;
  }

  const existingLabels = Array.from(new Set(
    templates
      .filter((template) => selectedIds.has(template.id))
      .flatMap((template) => template.questions.map((question) => question.label.trim()))
      .filter(Boolean)
  )).sort((left, right) => left.localeCompare(right, "pt-BR"));

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function toggleAll() {
    setSelectedIds((current) => current.size === templates.length ? new Set<string>() : new Set(templates.map((template) => template.id)));
  }

  async function handleApply() {
    if (!currentUser) {
      return;
    }

    if (!label.trim()) {
      setMessage("Informe o texto do novo passo.");
      return;
    }

    const targets = templates.filter((template) => selectedIds.has(template.id));
    if (!targets.length) {
      setMessage("Selecione ao menos um checklist.");
      return;
    }

    if (insertMode === "afterLabel" && !afterLabel.trim()) {
      setMessage("Selecione a pergunta de referência.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    const skipped: string[] = [];
    let updated = 0;

    try {
      for (const template of targets) {
        const newQuestion: ChecklistQuestion = {
          id: `q-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          label: label.trim(),
          type,
          required,
          requireDetailOnNegative: type === "sim_nao" || type === "conforme_nao_conforme" ? requireDetailOnNegative : undefined,
          options: type === "selecao" ? options.split(",").map((option) => option.trim()).filter(Boolean) : undefined
        };

        let insertIndex = template.questions.length;
        if (insertMode === "start") {
          insertIndex = 0;
        } else if (insertMode === "position") {
          insertIndex = Math.max(0, Math.min(template.questions.length, position - 1));
        } else if (insertMode === "afterLabel") {
          const refIndex = template.questions.findIndex((question) => question.label.trim().toLowerCase() === afterLabel.trim().toLowerCase());
          if (refIndex === -1) {
            skipped.push(assetLabelFor(template));
            continue;
          }
          insertIndex = refIndex + 1;
        }

        const nextQuestions = [...template.questions];
        nextQuestions.splice(insertIndex, 0, newQuestion);

        await updateChecklistTemplate(companyId, template.id, {
          companyId,
          name: template.name,
          scope: template.scope,
          context: template.context,
          assetGroup: template.assetGroup,
          assetTypeId: template.assetTypeId,
          assetId: template.assetId,
          questions: nextQuestions,
          returnStorageLocation: template.returnStorageLocation
        }, currentUser);
        updated += 1;
      }

      setResult({ updated, skipped });
      if (!skipped.length) {
        onClose();
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível aplicar a alteração em lote.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Adicionar passo em lote</h2>
            <p className="mt-1 text-sm text-slate-500">Adicione uma nova pergunta a vários checklists de bens de uma só vez.</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>

        <div className="mt-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink">Checklists selecionados ({selectedIds.size}/{templates.length})</h3>
            <button type="button" onClick={toggleAll} className="text-xs font-medium text-brand-700 hover:underline">
              {selectedIds.size === templates.length ? "Desmarcar todos" : "Selecionar todos"}
            </button>
          </div>
          <div className="mt-2 grid max-h-40 gap-1 overflow-y-auto rounded-md border border-slate-200 p-2 sm:grid-cols-2">
            {templates.map((template) => (
              <label key={template.id} className="flex items-center gap-2 rounded px-2 py-1 text-sm text-slate-700 hover:bg-slate-50">
                <input type="checkbox" checked={selectedIds.has(template.id)} onChange={() => toggleSelected(template.id)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
                {assetLabelFor(template)} <span className="text-xs text-slate-400">({template.questions.length})</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-[1fr_180px]">
          <input className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Texto do novo passo" value={label} onChange={(event) => setLabel(event.target.value)} />
          <select className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm" value={type} onChange={(event) => setType(event.target.value as ChecklistAnswerType)}>
            {(["sim_nao", "conforme_nao_conforme", "texto", "numero", "foto", "selecao", "observacao"] as ChecklistAnswerType[]).map((option) => (
              <option key={option} value={option}>{checklistAnswerTypeLabel(option)}</option>
            ))}
          </select>
        </div>
        {type === "selecao" ? (
          <input className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Opções separadas por vírgula" value={options} onChange={(event) => setOptions(event.target.value)} />
        ) : null}
        <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-600">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={required} onChange={(event) => setRequired(event.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-700" />
            Obrigatório
          </label>
          {type === "sim_nao" || type === "conforme_nao_conforme" ? (
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={requireDetailOnNegative} onChange={(event) => setRequireDetailOnNegative(event.target.checked)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-700" />
              Exigir descrição e foto quando a resposta indicar problema
            </label>
          ) : null}
        </div>

        <div className="mt-4 rounded-md border border-slate-200 p-3">
          <p className="text-sm font-semibold text-ink">Onde inserir</p>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-700">
            <label className="flex items-center gap-1"><input type="radio" name="insertMode" checked={insertMode === "start"} onChange={() => setInsertMode("start")} /> No início</label>
            <label className="flex items-center gap-1"><input type="radio" name="insertMode" checked={insertMode === "end"} onChange={() => setInsertMode("end")} /> No final</label>
            <label className="flex items-center gap-1"><input type="radio" name="insertMode" checked={insertMode === "position"} onChange={() => setInsertMode("position")} /> Em uma posição específica</label>
            <label className="flex items-center gap-1"><input type="radio" name="insertMode" checked={insertMode === "afterLabel"} onChange={() => setInsertMode("afterLabel")} /> Logo após uma pergunta</label>
          </div>
          {insertMode === "position" ? (
            <div className="mt-2">
              <input type="number" min={1} className="focus-ring w-32 rounded-md border border-slate-300 px-3 py-2 text-sm" value={position} onChange={(event) => setPosition(Math.max(1, Number(event.target.value) || 1))} />
              <p className="mt-1 text-xs text-slate-500">Posição contada a partir de 1. Checklists mais curtos recebem o passo no final.</p>
            </div>
          ) : null}
          {insertMode === "afterLabel" ? (
            <div className="mt-2">
              <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={afterLabel} onChange={(event) => setAfterLabel(event.target.value)}>
                <option value="">Selecione a pergunta de referência</option>
                {existingLabels.map((existingLabel) => <option key={existingLabel} value={existingLabel}>{existingLabel}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-500">Checklists selecionados que não tiverem essa pergunta serão ignorados nesta aplicação.</p>
            </div>
          ) : null}
        </div>

        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        {result ? (
          <p className="mt-3 text-sm text-slate-600">
            {result.updated} checklist(s) atualizado(s).
            {result.skipped.length ? ` Ignorados por não terem a pergunta de referência: ${result.skipped.join(", ")}.` : ""}
          </p>
        ) : null}

        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>{result ? "Fechar" : "Cancelar"}</SecondaryButton>
          <PrimaryButton onClick={() => void handleApply()} disabled={isSaving || !selectedIds.size}>Aplicar a {selectedIds.size} checklist(s)</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

type ChecklistAnswerDraft = {
  value: ChecklistAnswer["value"];
  detailText: string;
  files: File[];
};

function emptyAnswerDraft(): ChecklistAnswerDraft {
  return { value: null, detailText: "", files: [] };
}

function AssetInspectionModal({
  companyId,
  externalServiceId,
  externalServiceItemId,
  pickupNotes,
  asset,
  context,
  templates,
  currentUser,
  onClose,
  onCompleted
}: {
  companyId: string;
  externalServiceId?: string;
  externalServiceItemId?: string;
  pickupNotes?: string;
  asset: AssetRecord;
  context: InspectionContext;
  templates: ChecklistTemplate[];
  currentUser: User | null;
  onClose: () => void;
  onCompleted: (result: { inspectionId: string; conditionAssessed: Asset["conservationState"] }) => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, ChecklistAnswerDraft>>({});
  const [conditionAssessed, setConditionAssessed] = useState<Asset["conservationState"]>(asset.conservationState);
  const [hasNewDamage, setHasNewDamage] = useState(false);
  const [damageDescription, setDamageDescription] = useState("");
  const [damageCategory, setDamageCategory] = useState<DamageCategory>("avaria");
  const [damageFiles, setDamageFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");
  const [previousInspections, setPreviousInspections] = useState<AssetInspection[]>([]);
  const [existingDamages, setExistingDamages] = useState<AssetDamage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [issues, setIssues] = useState<string[]>([]);

  useEffect(() => (
    subscribeAssetInspections(companyId, asset.id, setPreviousInspections, () => undefined)
  ), [companyId, asset.id]);

  useEffect(() => (
    subscribeAssetDamages(companyId, asset.id, setExistingDamages, () => undefined)
  ), [companyId, asset.id]);

  const openDamages = existingDamages.filter((damage) => damage.status === "aberta");
  const allQuestions = templates.flatMap((checklistTemplate) => checklistTemplate.questions);
  const returnStorageLocations = Array.from(new Set(templates.map((checklistTemplate) => checklistTemplate.returnStorageLocation).filter((location): location is string => Boolean(location))));

  function draftFor(questionId: string): ChecklistAnswerDraft {
    return drafts[questionId] ?? emptyAnswerDraft();
  }

  function updateDraft(questionId: string, patch: Partial<ChecklistAnswerDraft>) {
    setDrafts((current) => ({ ...current, [questionId]: { ...draftFor(questionId), ...patch } }));
  }

  const lastInspection = previousInspections[0];
  const title = context === "retirada" ? "Inspeção de retirada" : context === "devolucao" ? "Inspeção de devolução" : context === "transferencia" ? "Inspeção de transferência" : "Inspeção de auditoria";

  async function handleSave() {
    if (!currentUser) {
      return;
    }

    const answersForValidation: ChecklistAnswer[] = allQuestions.map((question) => {
      const draft = draftFor(question.id);
      const value = question.type === "foto" ? (draft.files.length ? "anexado" : null) : draft.value;
      return {
        questionId: question.id,
        label: question.label,
        type: question.type,
        value,
        detailText: draft.detailText,
        photoUrls: draft.files.length ? ["pendente"] : []
      };
    });

    const validationIssues = validateChecklistAnswers(allQuestions, answersForValidation);
    if (hasNewDamage && !damageDescription.trim()) {
      validationIssues.push({ questionId: "damage", message: "Descreva a avaria identificada." });
    }
    if (validationIssues.length) {
      setIssues(validationIssues.map((issue) => issue.message));
      return;
    }

    setIssues([]);
    setIsSaving(true);
    try {
      const inspectionId = generateInspectionId(companyId, asset.id);
      let pendingPhotosCount = 0;

      const finalAnswers: ChecklistAnswer[] = await Promise.all(allQuestions.map(async (question) => {
        const draft = draftFor(question.id);
        let photoUrls: string[] = [];
        if (draft.files.length) {
          if (isOnline()) {
            try {
              photoUrls = await uploadInspectionPhotos(companyId, asset.id, draft.files);
            } catch {
              for (const file of draft.files) {
                await queueOfflineInspectionPhoto({ companyId, assetId: asset.id, inspectionId, questionId: question.id, file });
              }
              pendingPhotosCount += draft.files.length;
            }
          } else {
            for (const file of draft.files) {
              await queueOfflineInspectionPhoto({ companyId, assetId: asset.id, inspectionId, questionId: question.id, file });
            }
            pendingPhotosCount += draft.files.length;
          }
        }
        const value = question.type === "foto" ? (photoUrls.length || pendingPhotosCount ? "anexado" : null) : draft.value;
        return {
          questionId: question.id,
          label: question.label,
          type: question.type,
          value,
          detailText: draft.detailText.trim() || undefined,
          photoUrls: photoUrls.length ? photoUrls : undefined
        };
      }));

      await createAssetInspection({
        id: inspectionId,
        companyId,
        assetId: asset.id,
        context,
        externalServiceId,
        externalServiceItemId,
        checklistTemplateIds: templates.map((checklistTemplate) => checklistTemplate.id),
        checklistTemplateNames: templates.map((checklistTemplate) => checklistTemplate.name),
        answers: finalAnswers,
        conditionAssessed,
        hasNewDamage,
        notes
      }, currentUser);

      if (hasNewDamage) {
        const damageId = generateDamageId(companyId, asset.id);
        let damagePhotoUrls: string[] = [];
        if (damageFiles.length) {
          if (isOnline()) {
            try {
              damagePhotoUrls = await uploadInspectionPhotos(companyId, asset.id, damageFiles);
            } catch {
              for (const file of damageFiles) {
                await queueOfflineDamagePhoto({ companyId, assetId: asset.id, damageId, file });
              }
            }
          } else {
            for (const file of damageFiles) {
              await queueOfflineDamagePhoto({ companyId, assetId: asset.id, damageId, file });
            }
          }
        }
        await createAssetDamage({
          id: damageId,
          companyId,
          assetId: asset.id,
          externalServiceId,
          externalServiceItemId,
          inspectionId,
          description: damageDescription,
          category: damageCategory,
          photoUrls: damagePhotoUrls,
          preexisting: false
        }, currentUser);
      }

      onCompleted({ inspectionId, conditionAssessed });
    } catch (error) {
      setIssues([error instanceof Error ? error.message : "Não foi possível salvar a inspeção."]);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">{title}</h2>
            <p className="mt-1 text-sm text-slate-600">{asset.patrimonyNumber} - {asset.description}</p>
            <p className="mt-1 text-xs text-slate-500">Checklist: {templates.map((checklistTemplate) => checklistTemplate.name).join(", ")}</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>

        {lastInspection ? (
          <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
            <p className="font-semibold text-ink">Última inspeção registrada</p>
            <p className="mt-1">{formatAuditDate(lastInspection.createdAt)} - {lastInspection.performedByUserName}</p>
            {lastInspection.conditionAssessed ? <p className="mt-1">Condição avaliada: {lastInspection.conditionAssessed}</p> : null}
            {lastInspection.hasNewDamage ? <p className="mt-1 font-semibold text-rose-600">Avaria identificada nessa inspeção.</p> : null}
            {lastInspection.notes ? <p className="mt-1">Observação: {lastInspection.notes}</p> : null}
          </div>
        ) : null}

        {context === "devolucao" && pickupNotes ? (
          <div className="mt-3 rounded-md border border-brand-200 bg-brand-50 p-3 text-xs text-brand-900">
            <p className="font-semibold">Observação registrada na retirada</p>
            <p className="mt-1">{pickupNotes}</p>
          </div>
        ) : null}

        {openDamages.length ? (
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            <p className="font-semibold">Avarias já registradas neste bem</p>
            {openDamages.map((damage) => (
              <div key={damage.id} className="mt-2 flex gap-2">
                {damage.photoUrls[0] ? <img src={damage.photoUrls[0]} alt={damage.description} className="h-12 w-12 rounded object-cover" /> : null}
                <div>
                  <p>{damage.description}</p>
                  <p className="text-amber-700">Registrada em {formatAuditDate(damage.createdAt)} - {damageCategoryLabel(damage.category)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : null}

        {context === "devolucao" && returnStorageLocations.length ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900">
            <MapPin size={16} className="mt-0.5 shrink-0" />
            <p><span className="font-semibold">Guardar em:</span> {returnStorageLocations.join(" · ")}</p>
          </div>
        ) : null}

        <div className="mt-4 space-y-4">
          {templates.map((checklistTemplate) => (
            <div key={checklistTemplate.id} className="space-y-3">
              {templates.length > 1 ? <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{checklistTemplate.name}</p> : null}
              {checklistTemplate.questions.map((question) => {
                const draft = draftFor(question.id);
                const isNegative = (question.type === "sim_nao" && draft.value === "nao") || (question.type === "conforme_nao_conforme" && draft.value === "nao_conforme");
                return (
                  <div key={question.id} className="rounded-md border border-slate-200 p-3">
                    <p className="text-sm font-medium text-ink">{question.label}{question.required ? " *" : ""}</p>
                    {question.type === "sim_nao" ? (
                      <div className="mt-2 flex gap-2">
                        <SecondaryButton onClick={() => updateDraft(question.id, { value: "sim" })}>{draft.value === "sim" ? "✓ " : ""}Sim</SecondaryButton>
                        <SecondaryButton onClick={() => updateDraft(question.id, { value: "nao" })}>{draft.value === "nao" ? "✓ " : ""}Não</SecondaryButton>
                      </div>
                    ) : null}
                    {question.type === "conforme_nao_conforme" ? (
                      <div className="mt-2 flex gap-2">
                        <SecondaryButton onClick={() => updateDraft(question.id, { value: "conforme" })}>{draft.value === "conforme" ? "✓ " : ""}Conforme</SecondaryButton>
                        <SecondaryButton onClick={() => updateDraft(question.id, { value: "nao_conforme" })}>{draft.value === "nao_conforme" ? "✓ " : ""}Não conforme</SecondaryButton>
                      </div>
                    ) : null}
                    {question.type === "texto" || question.type === "observacao" ? (
                      <textarea className="focus-ring mt-2 min-h-16 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={String(draft.value ?? "")} onChange={(event) => updateDraft(question.id, { value: event.target.value })} />
                    ) : null}
                    {question.type === "numero" ? (
                      <input type="number" className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={draft.value === null || draft.value === undefined ? "" : String(draft.value)} onChange={(event) => updateDraft(question.id, { value: event.target.value === "" ? null : Number(event.target.value) })} />
                    ) : null}
                    {question.type === "selecao" ? (
                      <select className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={String(draft.value ?? "")} onChange={(event) => updateDraft(question.id, { value: event.target.value })}>
                        <option value="">Selecione</option>
                        {(question.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    ) : null}
                    {question.type === "foto" ? (
                      <input type="file" accept="image/*" capture="environment" multiple className="mt-2 w-full text-sm" onChange={(event) => updateDraft(question.id, { files: Array.from(event.target.files ?? []) })} />
                    ) : null}
                    {question.requireDetailOnNegative && isNegative ? (
                      <div className="mt-2 space-y-2 rounded-md bg-amber-50 p-2">
                        <textarea className="focus-ring w-full rounded-md border border-amber-300 px-3 py-2 text-sm" placeholder="Descreva o problema encontrado" value={draft.detailText} onChange={(event) => updateDraft(question.id, { detailText: event.target.value })} />
                        <input type="file" accept="image/*" capture="environment" multiple className="w-full text-sm" onChange={(event) => updateDraft(question.id, { files: Array.from(event.target.files ?? []) })} />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700">
            Condição avaliada
            <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={conditionAssessed} onChange={(event) => setConditionAssessed(event.target.value as Asset["conservationState"])}>
              <option value="novo">novo</option>
              <option value="otimo">ótimo</option>
              <option value="bom">bom</option>
              <option value="regular">regular</option>
              <option value="ruim">ruim</option>
              <option value="sucata">sucata</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={hasNewDamage} onChange={(event) => setHasNewDamage(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
            Avaria nova identificada nesta inspeção
          </label>
        </div>
        {hasNewDamage ? (
          <div className="mt-3 space-y-2 rounded-md border border-rose-200 bg-rose-50 p-3">
            <p className="text-sm font-semibold text-rose-900">Detalhes da avaria nova</p>
            <textarea className="focus-ring w-full rounded-md border border-rose-300 px-3 py-2 text-sm" placeholder="Descreva a avaria (ex.: risco na lateral, tela trincada)" value={damageDescription} onChange={(event) => setDamageDescription(event.target.value)} />
            <select className="focus-ring w-full rounded-md border border-rose-300 px-3 py-2 text-sm" value={damageCategory} onChange={(event) => setDamageCategory(event.target.value as DamageCategory)}>
              <option value="avaria">Avaria</option>
              <option value="desgaste_natural">Desgaste natural</option>
              <option value="mau_uso">Mau uso</option>
              <option value="manutencao_necessaria">Manutenção necessária</option>
              <option value="outro">Outro</option>
            </select>
            <input type="file" accept="image/*" capture="environment" multiple className="w-full text-sm" onChange={(event) => setDamageFiles(Array.from(event.target.files ?? []))} />
          </div>
        ) : null}
        <label className="mt-3 block text-sm text-slate-700">
          Observações gerais
          <textarea className="focus-ring mt-1 min-h-16 w-full rounded-md border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} />
        </label>

        {issues.length ? (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
            {issues.map((issue) => <p key={issue}>{issue}</p>)}
          </div>
        ) : null}

        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={isSaving}>Concluir inspeção</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ExternalServiceAssetPickerModal({
  service,
  assets,
  reservations,
  participants,
  currentUserId,
  canAssign,
  initialQuery = "",
  onClose,
  onAdd
}: {
  service: ExternalService;
  assets: AssetRecord[];
  reservations: AssetReservation[];
  participants: ExternalServiceParticipant[];
  currentUserId: string;
  canAssign: boolean;
  initialQuery?: string;
  onClose: () => void;
  onAdd: (assets: AssetRecord[], participantId: string, mode: ExternalServiceItem["assignmentMode"], notesByAssetId: Record<string, string>) => Promise<void>;
}) {
  const [query, setQuery] = useState(initialQuery);
  const [groupFilter, setGroupFilter] = useState("todos");
  const [onlyFree, setOnlyFree] = useState(true);
  const selectableParticipants = canAssign ? participants : participants.filter((participant) => participant.userId === currentUserId);
  const [participantId, setParticipantId] = useState(selectableParticipants.length === 1 ? selectableParticipants[0].userId : "");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [notesByAssetId, setNotesByAssetId] = useState<Record<string, string>>({});
  const [noteOpenFor, setNoteOpenFor] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const mode: ExternalServiceItem["assignmentMode"] = canAssign ? "gerente_atribuiu" : "usuario_solicitou";
  const groups = Array.from(new Set(assets.map((asset) => asset.assetGroup).filter((group): group is string => Boolean(group)))).sort();
  const rows = assets
    .map((asset) => ({ asset, conflict: findAssetConflict(reservations, asset.id, service) }))
    .filter(({ asset }) => {
      const normalized = query.trim().toLowerCase();
      return !normalized || [asset.patrimonyNumber, asset.internalCode, asset.description, asset.serialNumber ?? "", asset.brand ?? "", asset.model ?? ""].join(" ").toLowerCase().includes(normalized);
    })
    .filter(({ asset }) => groupFilter === "todos" || asset.assetGroup === groupFilter)
    .filter(({ conflict }) => !onlyFree || !conflict)
    .sort((left, right) => Number(Boolean(left.conflict)) - Number(Boolean(right.conflict)) || left.asset.patrimonyNumber.localeCompare(right.asset.patrimonyNumber));
  const selectedAssets = assets.filter((asset) => selectedIds.includes(asset.id));
  const freeCount = assets.filter((asset) => !findAssetConflict(reservations, asset.id, service)).length;

  function toggle(assetId: string) {
    setSelectedIds((current) => current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId]);
  }

  async function handleAdd() {
    setIsSaving(true);
    try {
      await onAdd(selectedAssets, participantId, mode, notesByAssetId);
      setSelectedIds([]);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <SheetModal
      title={canAssign ? "Reservar equipamentos" : "Reservar para mim"}
      subtitle={<>{service.title} · {formatServicePeriod(service.startDate, service.expectedEndDate)}</>}
      onClose={onClose}
      size="xl"
      zIndex="z-[90]"
      footer={(
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center">
          <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm sm:w-64" value={participantId} onChange={(event) => setParticipantId(event.target.value)} aria-label="Responsável">
            <option value="">Responsável pelos bens…</option>
            {selectableParticipants.map((participant) => <option key={participant.userId} value={participant.userId}>{participant.userName}</option>)}
          </select>
          {!selectableParticipants.length ? <span className="text-xs text-amber-700">{canAssign ? "Adicione participantes ao serviço primeiro." : "Você precisa estar na equipe do serviço."}</span> : null}
          <div className="flex gap-2 sm:ml-auto">
            <SecondaryButton onClick={onClose} disabled={isSaving}>Fechar</SecondaryButton>
            <PrimaryButton onClick={() => void handleAdd()} disabled={!participantId || !selectedAssets.length || isSaving}>{isSaving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Reservar {selectedAssets.length || ""}</PrimaryButton>
          </div>
        </div>
      )}
    >
      <div className="grid gap-2 sm:grid-cols-[1fr_200px_auto] sm:items-center">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input className="focus-ring w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm" placeholder="Patrimônio, código, descrição, marca ou série" value={query} onChange={(event) => setQuery(event.target.value)} autoFocus />
        </label>
        <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={groupFilter} onChange={(event) => setGroupFilter(event.target.value)} aria-label="Grupo">
          <option value="todos">Todos os grupos</option>
          {groups.map((group) => <option key={group} value={group}>{group}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={onlyFree} onChange={(event) => setOnlyFree(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
          Só livres ({freeCount})
        </label>
      </div>
      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {rows.map(({ asset, conflict }) => {
          const isSelected = selectedIds.includes(asset.id);
          return (
            <div key={asset.id} className={`rounded-lg border p-2.5 ${conflict ? "border-slate-200 bg-slate-50 opacity-75" : isSelected ? "border-brand-300 bg-brand-50" : "border-slate-200 bg-white"}`}>
              <label className={`flex items-start gap-3 ${conflict ? "cursor-not-allowed" : "cursor-pointer"}`}>
                <input type="checkbox" className="mt-1 h-5 w-5 shrink-0 rounded border-slate-300 text-brand-700" checked={isSelected} disabled={Boolean(conflict)} onChange={() => toggle(asset.id)} aria-label={`Selecionar ${asset.patrimonyNumber}`} />
                {asset.mainPhotoUrl ? <img src={asset.mainPhotoUrl} alt="" className="h-14 w-14 shrink-0 rounded-md object-cover" /> : <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md bg-slate-100 text-[10px] text-slate-500">Sem foto</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-ink">{asset.patrimonyNumber}</span>
                  <span className="block truncate text-xs text-slate-600">{asset.description}</span>
                  <span className="block truncate text-[11px] text-slate-500">{[asset.assetGroup, asset.serialNumber ? `Série ${asset.serialNumber}` : "", asset.physicalLocation].filter(Boolean).join(" · ")}</span>
                  {conflict ? (
                    <span className="mt-1 block text-[11px] font-semibold text-rose-700">Ocupado: {conflict.serviceTitle} ({formatServicePeriod(conflict.plannedStartAt, conflict.expectedReturnAt)})</span>
                  ) : (
                    <span className="mt-1 block text-[11px] font-semibold text-emerald-700">Livre no período{asset.externalServiceStatus === "em_servico" ? " · fora agora, volta antes" : ""}</span>
                  )}
                </span>
              </label>
              {isSelected ? (
                noteOpenFor === asset.id || notesByAssetId[asset.id] ? (
                  <input
                    className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                    placeholder="Observação da reserva (opcional)"
                    value={notesByAssetId[asset.id] ?? ""}
                    onChange={(event) => setNotesByAssetId((current) => ({ ...current, [asset.id]: event.target.value }))}
                    autoFocus
                  />
                ) : (
                  <button type="button" onClick={() => setNoteOpenFor(asset.id)} className="focus-ring mt-1.5 text-xs font-semibold text-brand-700 hover:underline">+ Observação</button>
                )
              ) : null}
            </div>
          );
        })}
      </div>
      {!rows.length ? <EmptyState title="Nenhum equipamento" description={onlyFree ? "Nenhum equipamento livre neste período com esses filtros. Desmarque \"Só livres\" para ver os ocupados." : "Confira se o bem está liberado para serviços externos, ativo e no seu grupo de acesso."} /> : null}
    </SheetModal>
  );
}

function UsersPage({
  accessContext,
  accessProfile,
  onSaveUser
}: {
  accessContext: AccessContext;
  accessProfile: UserAccessProfile;
  onSaveUser: (payload: SaveUserInput, previousAssignments: CompanyAccess[]) => Promise<void>;
}) {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [search, setSearch] = useState("");
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanyToAssign, setSelectedCompanyToAssign] = useState("");
  const [assignedCompanies, setAssignedCompanies] = useState<Array<{ companyId: string; companyRole: CompanyRole }>>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPrompt, setShowUnsavedPrompt] = useState(false);
  const formRef = useRef<HTMLFormElement | null>(null);
  const isMaster = accessContext.globalRole === "MASTER";
  const isAdmin = accessContext.globalRole === "ADMIN";
  const isManager = accessProfile.manageableCompanyIds.length > 0;
  const canOpenUsers = isMaster || isAdmin || isManager;
  const canViewAudit = isMaster || isAdmin;
  const manageableCompanyIds = useMemo(
    () => isMaster || isAdmin ? accessContext.accessibleCompanies.map((company) => company.id) : accessProfile.manageableCompanyIds,
    [accessContext.accessibleCompanies, accessProfile.manageableCompanyIds, isAdmin, isMaster]
  );
  const manageableCompanyIdsKey = manageableCompanyIds.join("|");
  const editableCompanies = accessContext.accessibleCompanies.filter((company) => manageableCompanyIds.includes(company.id));
  const editingUser = users.find((profile) => profile.id === editingUserId) ?? null;
  const editingUserCompanyAccess = useMemo(() => editingUser?.companyAccess ?? [], [editingUser?.companyAccess]);
  const assignedCompanyIds = assignedCompanies.map((assignment) => assignment.companyId);
  const companySearchResults = editableCompanies
    .filter((company) => !assignedCompanyIds.includes(company.id) && matchesCompanySearch(company, companySearch))
    .slice(0, 8);
  const filteredUsers = users.filter((profile) => {
    const normalizedSearch = search.trim().toLocaleUpperCase("pt-BR");
    const searchable = [profile.fullName, profile.displayName, profile.email, profile.cpf].join(" ").toLocaleUpperCase("pt-BR");
    return !normalizedSearch || searchable.includes(normalizedSearch);
  });

  useEffect(() => {
    if (!canOpenUsers) {
      setUsers([]);
      return () => undefined;
    }

    return subscribeManagedUsers(accessProfile, setUsers, (message) => {
      console.warn("Não foi possivel carregar usuários.", message);
    });
  }, [accessProfile, canOpenUsers]);

  useEffect(() => {
    if (!isFormOpen) {
      setCompanySearch("");
      setSelectedCompanyToAssign("");
      setAssignedCompanies([]);
      return;
    }

    setCompanySearch("");
    setSelectedCompanyToAssign("");
    setIsDirty(false);
    setAssignedCompanies(
      editingUserCompanyAccess
        .filter((access) => manageableCompanyIds.includes(access.companyId))
        .map((access) => ({
          companyId: access.companyId,
          companyRole: access.companyRole === "AUDITOR" ? "CONSULTA" : access.companyRole
        }))
    );
  }, [editingUser?.id, editingUserCompanyAccess, isFormOpen, manageableCompanyIds, manageableCompanyIdsKey]);

  function canEditUser(profile: ManagedUser | null) {
    if (!profile || isMaster) {
      return true;
    }

    if (profile.globalRole === "MASTER") {
      return false;
    }

    return isAdmin || profile.companyAccess.some((access) => manageableCompanyIds.includes(access.companyId));
  }

  async function handleFindUnassignedUsers() {
    setFormMessage("");
    try {
      setUsers((currentUsers) => {
        const byId = new Map(currentUsers.map((profile) => [profile.id, profile]));
        return currentUsers.map((profile) => byId.get(profile.id) ?? profile);
      });
      const foundUsers = await findUnassignedUsers(search);
      setUsers((currentUsers) => {
        const byId = new Map(currentUsers.map((profile) => [profile.id, profile]));
        foundUsers.forEach((profile) => byId.set(profile.id, profile));
        return Array.from(byId.values());
      });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "Não foi possivel buscar usuários sem empresa.");
    }
  }

  function handleAssignCompany(companyId = selectedCompanyToAssign) {
    if (!companyId || assignedCompanyIds.includes(companyId)) {
      return;
    }

    setAssignedCompanies((currentAssignments) => [
      ...currentAssignments,
      { companyId, companyRole: "CONSULTA" }
    ]);
    setIsDirty(true);
    setCompanySearch("");
    setSelectedCompanyToAssign("");
  }

  function handleChangeAssignedCompanyRole(companyId: string, companyRole: CompanyRole) {
    setAssignedCompanies((currentAssignments) =>
      currentAssignments.map((assignment) =>
        assignment.companyId === companyId ? { ...assignment, companyRole } : assignment
      )
    );
    setIsDirty(true);
  }

  function handleRemoveAssignedCompany(companyId: string) {
    setAssignedCompanies((currentAssignments) => currentAssignments.filter((assignment) => assignment.companyId !== companyId));
    setIsDirty(true);
  }

  async function handleUserSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setFormMessage("");

    if (!canEditUser(editingUser)) {
      setFormMessage("Voce não tem permissão para alterar este usuário.");
      return;
    }

    const formData = new FormData(event.currentTarget);
    const cpf = String(formData.get("cpf") ?? "");
    const birthDate = String(formData.get("birthDate") ?? "");
    const assignments = assignedCompanies.map<UserCompanyAssignmentInput>((assignment) => ({
      companyId: assignment.companyId,
      companyRole: assignment.companyRole,
      permissions: permissionsForRole(assignment.companyRole),
      status: "ativo"
    }));

    if (onlyDigits(cpf).length !== 11) {
      setFormMessage("CPF incompleto. Use o formato 000.000.000-00.");
      return;
    }

    if (onlyDigits(birthDate).length !== 8) {
      setFormMessage("Data de nascimento incompleta. Use dd/mm/aaaa.");
      return;
    }

    setIsSaving(true);
    try {
      await onSaveUser({
        id: editingUser?.id,
        cpf,
        cpfRaw: onlyDigits(cpf),
        fullName: String(formData.get("fullName") ?? "").toLocaleUpperCase("pt-BR"),
        birthDate,
        email: String(formData.get("email") ?? ""),
        phone: String(formData.get("phone") ?? ""),
        address: String(formData.get("address") ?? "").toLocaleUpperCase("pt-BR"),
        city: String(formData.get("city") ?? "").toLocaleUpperCase("pt-BR"),
        state: String(formData.get("state") ?? ""),
        zipCode: String(formData.get("zipCode") ?? ""),
        globalRole: String(formData.get("globalRole") ?? "USER") as GlobalRole,
        status: String(formData.get("status") ?? "ativo") as UserProfile["status"],
        assignments
      }, editingUser?.companyAccess ?? []);

      setIsFormOpen(false);
      setEditingUserId(null);
      setIsDirty(false);
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "Não foi possivel salvar o usuário.");
    } finally {
      setIsSaving(false);
    }
  }

  if (!canOpenUsers) {
    return (
      <>
        <PageHeader title="Usuários e permissoes" subtitle="Seu perfil não possui acesso a esta tela." />
        <div className="p-4 lg:p-6">
          <EmptyState title="Acesso indisponível" description="Usuários padrão e empresas auditoras não acessam o cadastro de usuários." />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={isFormOpen ? editingUser ? "Editar usuário" : "Cadastrar usuário" : "Usuários e permissoes"}
        subtitle={isMaster ? "MASTER altera niveis e empresas de qualquer usuário." : isAdmin ? "ADMIN lista e cadastra usuários, sem alterar MASTER." : "GERENTE gerencia usuários das empresas liberadas."}
        action={isFormOpen ? (
          <SecondaryButton onClick={() => {
            if (isDirty) {
              setShowUnsavedPrompt(true);
              return;
            }
            setIsFormOpen(false);
            setEditingUserId(null);
          }}><RotateCcw size={16} /> Voltar para lista</SecondaryButton>
        ) : (
          <PrimaryButton onClick={() => {
            setEditingUserId(null);
            setIsFormOpen(true);
          }}><UserPlus size={16} /> Novo usuário</PrimaryButton>
        )}
      />
      <div className="p-4 lg:p-6">
        {isFormOpen ? (
          <section className="mx-auto max-w-5xl rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <form ref={formRef} key={editingUser?.id ?? "new-user"} className="grid gap-4 md:grid-cols-2" onSubmit={handleUserSubmit} onChange={() => setIsDirty(true)}>
              <label className="text-sm font-medium text-slate-700">CPF
                <input name="cpf" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.cpf} inputMode="numeric" maxLength={14} placeholder="000.000.000-00" required onChange={(event) => {
                  event.currentTarget.value = formatCpfInput(event.currentTarget.value);
                }} />
              </label>
              <label className="text-sm font-medium text-slate-700">Nome completo
                <input name="fullName" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingUser?.fullName ?? editingUser?.displayName} required onChange={forceUppercaseInput} />
              </label>
              <label className="text-sm font-medium text-slate-700">Data de nascimento
                <input name="birthDate" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.birthDate} inputMode="numeric" maxLength={10} placeholder="dd/mm/aaaa" required onChange={(event) => {
                  event.currentTarget.value = formatDateInput(event.currentTarget.value);
                }} />
              </label>
              <label className="text-sm font-medium text-slate-700">Email
                <input name="email" type="email" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.email} required />
              </label>
              <label className="text-sm font-medium text-slate-700">Telefone de contato
                <input name="phone" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.phone} inputMode="numeric" maxLength={15} placeholder="(00) 00000-0000" onChange={(event) => {
                  event.currentTarget.value = formatPhoneInput(event.currentTarget.value);
                }} />
              </label>
              <label className="text-sm font-medium text-slate-700">Endereco
                <input name="address" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingUser?.address} onChange={forceUppercaseInput} />
              </label>
              <div className="grid grid-cols-[1fr_96px] gap-3">
                <label className="text-sm font-medium text-slate-700">Cidade
                  <input name="city" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" defaultValue={editingUser?.city} onChange={forceUppercaseInput} />
                </label>
                <label className="text-sm font-medium text-slate-700">UF
                  <select name="state" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.state ?? ""}>
                    <option value="">UF</option>
                    {brazilStates.map((state) => <option key={state} value={state}>{state}</option>)}
                  </select>
                </label>
              </div>
              <label className="text-sm font-medium text-slate-700">CEP
                <input name="zipCode" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.zipCode} inputMode="numeric" maxLength={10} placeholder="00.000-000" onChange={(event) => {
                  event.currentTarget.value = formatZipCodeInput(event.currentTarget.value);
                }} />
              </label>
              <label className="text-sm font-medium text-slate-700">Nivel global
                <select name="globalRole" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.globalRole ?? "USER"} disabled={!isMaster}>
                  <option value="USER">USER</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="MASTER">MASTER</option>
                </select>
              </label>
              <label className="text-sm font-medium text-slate-700">Status
                <select name="status" className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" defaultValue={editingUser?.status ?? "ativo"}>
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                  <option value="bloqueado">Bloqueado</option>
                </select>
              </label>
              <div className="md:col-span-2">
                <h2 className="text-sm font-semibold text-ink">Empresas atribuidas</h2>
                <div className="mt-2 grid gap-3">
                  <div className="grid gap-2 rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2">
                      <Search size={16} className="text-slate-400" />
                      <input
                        className="min-w-0 flex-1 outline-none"
                        placeholder="Buscar empresa por nome ou CNPJ"
                        value={companySearch}
                        onChange={(event) => {
                          setCompanySearch(event.target.value);
                          setSelectedCompanyToAssign("");
                        }}
                      />
                    </div>
                    {companySearch.trim() ? (
                      <div className="grid gap-2">
                        {companySearchResults.map((company) => (
                          <button
                            key={company.id}
                            type="button"
                            onClick={() => handleAssignCompany(company.id)}
                            className="grid gap-1 rounded-md border border-slate-200 bg-white p-3 text-left hover:border-brand-300 hover:bg-brand-50"
                          >
                            <span className="font-medium text-ink">{company.tradeName}</span>
                            <span className="text-xs text-slate-500">{company.legalName} - {company.cnpj}</span>
                          </button>
                        ))}
                        {!companySearchResults.length ? <p className="rounded-md border border-dashed border-slate-300 bg-white p-3 text-sm text-slate-500">Nenhuma empresa disponivel encontrada.</p> : null}
                      </div>
                    ) : null}
                    <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                      <select
                        className="focus-ring rounded-md border border-slate-300 bg-white px-3 py-2"
                        value={selectedCompanyToAssign}
                        onChange={(event) => setSelectedCompanyToAssign(event.target.value)}
                      >
                        <option value="">Selecionar empresa disponivel</option>
                        {editableCompanies
                          .filter((company) => !assignedCompanyIds.includes(company.id))
                          .map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}
                      </select>
                      <SecondaryButton onClick={() => handleAssignCompany()}><Plus size={16} /> Atribuir</SecondaryButton>
                    </div>
                  </div>

                  {assignedCompanies.map((assignment) => {
                    const company = editableCompanies.find((item) => item.id === assignment.companyId);

                    if (!company) {
                      return null;
                    }

                    return (
                      <div key={assignment.companyId} className="grid gap-3 rounded-md border border-slate-200 p-3 md:grid-cols-[1fr_180px_auto] md:items-center">
                        <div>
                          <p className="font-medium text-ink">{company.tradeName}</p>
                          <p className="mt-1 text-xs text-slate-500">{company.legalName} - {company.cnpj}</p>
                        </div>
                        <select
                          className="focus-ring rounded-md border border-slate-300 px-3 py-2"
                          value={assignment.companyRole}
                          onChange={(event) => handleChangeAssignedCompanyRole(assignment.companyId, event.target.value as CompanyRole)}
                        >
                          <option value="CONSULTA">Consulta</option>
                          <option value="LANCADOR">Modificar</option>
                          <option value="GERENTE">Gerente</option>
                          <option value="AUDITOR">Auditor</option>
                        </select>
                        <SecondaryButton onClick={() => handleRemoveAssignedCompany(assignment.companyId)}><CircleSlash size={16} /> Remover</SecondaryButton>
                      </div>
                    );
                  })}
                  {!assignedCompanies.length ? <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500">Nenhuma empresa atribuida a este usuário.</p> : null}
                  {!editableCompanies.length ? <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500">Nenhuma empresa disponivel para atribuicao.</p> : null}
                </div>
              </div>
              {canViewAudit && editingUser ? (
                <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm md:col-span-2 md:grid-cols-2">
                  <Info label="Criado por" value={editingUser.createdByName ?? editingUser.createdBy} />
                  <Info label="Criado em" value={formatAuditDate(editingUser.createdAt)} />
                  <Info label="Última alteração por" value={editingUser.updatedByName ?? editingUser.updatedBy ?? "-"} />
                  <Info label="Última alteração em" value={formatAuditDate(editingUser.updatedAt)} />
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2 md:col-span-2">
                <PrimaryButton type="submit" disabled={isSaving || !canEditUser(editingUser)}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar usuário"}</PrimaryButton>
              </div>
              {formMessage ? <p className="text-sm font-medium text-rose-600 md:col-span-2">{formMessage}</p> : null}
            </form>
          </section>
        ) : (
          <section className="space-y-4">
            <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_auto]">
              <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2">
                <Search size={16} className="text-slate-400" />
                <input className="min-w-0 flex-1 outline-none" placeholder="Buscar por nome, CPF ou email" value={search} onChange={(event) => setSearch(event.target.value)} />
              </div>
              {isManager && !isAdmin && !isMaster ? <SecondaryButton onClick={() => void handleFindUnassignedUsers()}><Search size={16} /> Buscar sem empresa</SecondaryButton> : null}
            </div>
            {formMessage ? <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm font-medium text-rose-700">{formMessage}</p> : null}
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              {filteredUsers.map((profile) => {
                const companyNames = profile.companyAccess.map((access) => accessContext.accessibleCompanies.find((company) => company.id === access.companyId)?.tradeName ?? access.companyId).join(", ");
                return (
                  <button key={profile.id} type="button" onClick={() => {
                    setEditingUserId(profile.id);
                    setIsFormOpen(true);
                  }} className="grid w-full gap-3 border-b border-slate-100 p-4 text-left last:border-b-0 hover:bg-slate-50 md:grid-cols-[1fr_120px_120px_120px] md:items-center">
                    <div>
                      <p className="font-semibold text-ink">{profile.fullName ?? profile.displayName}</p>
                      <p className="mt-1 text-sm text-slate-500">{profile.email}</p>
                      <p className="mt-1 text-xs text-slate-500">{companyNames || "Sem empresa liberada"}</p>
                    </div>
                    <StatusPill tone={profile.globalRole === "MASTER" ? "blue" : profile.globalRole === "ADMIN" ? "yellow" : "neutral"}>{profile.globalRole}</StatusPill>
                    <StatusPill tone={profile.status === "ativo" ? "green" : profile.status === "bloqueado" ? "red" : "neutral"}>{profile.status}</StatusPill>
                    <span className="text-sm font-medium text-brand-700 md:text-right">{canEditUser(profile) ? "Alterar" : "Somente leitura"}</span>
                  </button>
                );
              })}
              {!filteredUsers.length ? <div className="p-4"><EmptyState title="Nenhum usuário encontrado" description="Ajuste os filtros ou cadastre um novo usuário." /></div> : null}
            </div>
          </section>
        )}
      </div>
      {showUnsavedPrompt ? (
        <UnsavedChangesPrompt
          isSaving={isSaving}
          onCancel={() => setShowUnsavedPrompt(false)}
          onDiscard={() => {
            setShowUnsavedPrompt(false);
            setIsDirty(false);
            setIsFormOpen(false);
            setEditingUserId(null);
          }}
          onSave={() => {
            setShowUnsavedPrompt(false);
            formRef.current?.requestSubmit();
          }}
        />
      ) : null}
    </>
  );
}

type DepreciationPanelMode = "list" | "form" | "assets" | "migrate";

function DepreciationPage({ currentUser, accessContext, rules }: { currentUser: User | null; accessContext: AccessContext; rules: DepreciationRule[] }) {
  const canEdit = accessContext.globalRole === "MASTER";
  const [mode, setMode] = useState<DepreciationPanelMode>("list");
  const [selectedRuleId, setSelectedRuleId] = useState("");
  const [ruleSearch, setRuleSearch] = useState("");
  const [ruleStatusFilter, setRuleStatusFilter] = useState<"todos" | "ativas" | "inativas">("todos");
  const [ruleMethodFilter, setRuleMethodFilter] = useState<DepreciationMethod | "todos">("todos");
  const selectedRule = rules.find((rule) => rule.id === selectedRuleId);
  const [form, setForm] = useState({
    assetTypeName: "",
    usefulLifeYears: "",
    annualRate: "",
    residualValuePercent: "0",
    method: "linear_mensal" as DepreciationMethod,
    legalReference: "",
    notes: "",
    active: true,
    dynamicFields: [] as AssetDynamicField[]
  });
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [assetSearch, setAssetSearch] = useState("");
  const [assetSourceRuleFilter, setAssetSourceRuleFilter] = useState("todos");
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([]);
  const [dynamicFieldOptionsDraft, setDynamicFieldOptionsDraft] = useState<Record<number, string>>({});

  useEffect(() => {
    if (mode !== "form" || !selectedRule) {
      setForm({ assetTypeName: "", usefulLifeYears: "", annualRate: "", residualValuePercent: "0", method: "linear_mensal", legalReference: "", notes: "", active: true, dynamicFields: [] });
      return;
    }

    const nextDynamicFields = selectedRule.dynamicFields ?? [];
    setForm({
      assetTypeName: selectedRule.assetTypeName,
      usefulLifeYears: selectedRule.usefulLifeYears === null ? "" : String(selectedRule.usefulLifeYears),
      annualRate: String(selectedRule.annualRate),
      residualValuePercent: String(selectedRule.residualValuePercent),
      method: selectedRule.method,
      legalReference: selectedRule.legalReference ?? "",
      notes: selectedRule.notes ?? "",
      active: selectedRule.active,
      dynamicFields: nextDynamicFields
    });
    setDynamicFieldOptionsDraft(Object.fromEntries(nextDynamicFields.map((field, index) => [index, (field.options ?? []).join(", ")])));
  }, [mode, selectedRule]);

  const filteredRules = useMemo(() => rules.filter((rule) => {
    const searchableText = [rule.assetTypeName, rule.legalReference ?? "", rule.notes ?? "", rule.method].join(" ").toLowerCase();
    const matchesSearch = !ruleSearch.trim() || searchableText.includes(ruleSearch.trim().toLowerCase());
    const matchesStatus = ruleStatusFilter === "todos" || (ruleStatusFilter === "ativas" ? rule.active : !rule.active);
    const matchesMethod = ruleMethodFilter === "todos" || rule.method === ruleMethodFilter;
    return matchesSearch && matchesStatus && matchesMethod;
  }), [rules, ruleMethodFilter, ruleSearch, ruleStatusFilter]);

  const assetsByRule = useMemo(() => {
    const map = new Map<string, AssetRecord[]>();
    accessContext.scopedAssets.forEach((asset) => {
      const current = map.get(asset.assetTypeId) ?? [];
      current.push(asset);
      map.set(asset.assetTypeId, current);
    });
    return map;
  }, [accessContext.scopedAssets]);

  const selectedRuleAssets = selectedRule ? assetsByRule.get(selectedRule.id) ?? [] : [];
  const migrationCandidates = useMemo(() => accessContext.scopedAssets.filter((asset) => {
    if (selectedRule && asset.assetTypeId === selectedRule.id) {
      return false;
    }

    const searchableText = [asset.patrimonyNumber, asset.description, asset.brand ?? "", asset.model ?? "", asset.physicalLocation, asset.responsible ?? ""].join(" ").toLowerCase();
    const matchesSearch = !assetSearch.trim() || searchableText.includes(assetSearch.trim().toLowerCase());
    const matchesSource = assetSourceRuleFilter === "todos" || asset.assetTypeId === assetSourceRuleFilter;
    return matchesSearch && matchesSource;
  }), [accessContext.scopedAssets, assetSearch, assetSourceRuleFilter, selectedRule]);

  function openCreateForm() {
    setSelectedRuleId("");
    setMode("form");
    setMessage("");
  }

  function openEditForm(ruleId: string) {
    setSelectedRuleId(ruleId);
    setMode("form");
    setMessage("");
  }

  function openRuleAssets(ruleId: string) {
    setSelectedRuleId(ruleId);
    setMode("assets");
    setMessage("");
  }

  function openMigration(ruleId: string) {
    setSelectedRuleId(ruleId);
    setSelectedAssetIds([]);
    setAssetSearch("");
    setAssetSourceRuleFilter("todos");
    setMode("migrate");
    setMessage("");
  }

  function goBackToList() {
    setMode("list");
    setSelectedRuleId("");
    setSelectedAssetIds([]);
    setMessage("");
  }

  async function handleSaveRule(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser || !canEdit) {
      setMessage("Somente MASTER pode alterar tabelas de depreciação.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      const normalizedDynamicFields = form.dynamicFields.map((field, index) => ({
        ...field,
        options: (dynamicFieldOptionsDraft[index] ?? (field.options ?? []).join(", "))
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      }));
      await saveDepreciationRule({
        id: selectedRule?.id,
        assetTypeName: form.assetTypeName,
        usefulLifeYears: form.usefulLifeYears ? Number(form.usefulLifeYears) : null,
        annualRate: Number(form.annualRate || 0),
        residualValuePercent: Number(form.residualValuePercent || 0),
        method: form.method,
        legalReference: form.legalReference,
        notes: form.notes,
        active: form.active,
        dynamicFields: normalizedDynamicFields
      }, currentUser);
      goBackToList();
      setMessage("Tabela de depreciação salva.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar a tabela.");
    } finally {
      setIsSaving(false);
    }
  }

  function addDynamicField(template?: Partial<AssetDynamicField>) {
    const label = template?.label ?? "Novo campo";
    const id = normalizeDynamicFieldId(template?.id ?? label);
    setForm((current) => ({
      ...current,
      dynamicFields: [
        ...current.dynamicFields,
        {
          id: current.dynamicFields.some((field) => field.id === id) ? `${id}_${current.dynamicFields.length + 1}` : id,
          label,
          type: template?.type ?? "text",
          required: template?.required ?? false,
          unit: template?.unit ?? "",
          placeholder: template?.placeholder ?? "",
          options: template?.options ?? []
        }
      ]
    }));
    setDynamicFieldOptionsDraft((current) => ({ ...current, [form.dynamicFields.length]: (template?.options ?? []).join(", ") }));
  }

  function updateDynamicField(index: number, patch: Partial<AssetDynamicField>) {
    setForm((current) => ({
      ...current,
      dynamicFields: current.dynamicFields.map((field, fieldIndex) => {
        if (fieldIndex !== index) {
          return field;
        }

        const next = { ...field, ...patch };
        if (patch.label && (!patch.id || patch.id === field.id)) {
          next.id = normalizeDynamicFieldId(patch.label);
        }
        return next;
      })
    }));
  }

  function removeDynamicField(index: number) {
    setForm((current) => ({
      ...current,
      dynamicFields: current.dynamicFields.filter((_, fieldIndex) => fieldIndex !== index)
    }));
  }

  function toggleMigrationAsset(assetId: string) {
    setSelectedAssetIds((current) => current.includes(assetId) ? current.filter((item) => item !== assetId) : [...current, assetId]);
  }

  function toggleAllMigrationAssets() {
    const visibleIds = migrationCandidates.map((asset) => asset.id);
    setSelectedAssetIds((current) => current.length === visibleIds.length ? [] : visibleIds);
  }

  async function handleMigrateAssets() {
    if (!currentUser || !canEdit || !selectedRule) {
      setMessage("Somente MASTER pode migrar bens entre tabelas.");
      return;
    }

    const selectedAssets = accessContext.scopedAssets.filter((asset) => selectedAssetIds.includes(asset.id));
    if (!selectedAssets.length) {
      setMessage("Selecione pelo menos um bem para migrar.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      await updateAssetsDepreciationRule(selectedAssets, selectedRule.id, currentUser);
      setSelectedAssetIds([]);
      setMode("assets");
      setMessage(`${selectedAssets.length} bem(ns) migrado(s) para ${selectedRule.assetTypeName}.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível migrar os bens.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Tipos e depreciação"
        subtitle="Tabelas globais usadas no calculo automatico de vida util e desvalorizacao dos bens."
        action={
          <div className="flex flex-wrap gap-2">
            {mode !== "list" ? <SecondaryButton onClick={goBackToList}><RotateCcw size={16} /> Voltar para lista</SecondaryButton> : null}
            {mode === "list" && canEdit ? <PrimaryButton onClick={openCreateForm}><Plus size={16} /> Nova tabela</PrimaryButton> : null}
          </div>
        }
      />
      <div className="p-4 lg:p-6">
        {mode === "list" ? (
          <section>
            <div className="mb-4 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:grid-cols-[1fr_180px_220px_auto]">
              <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2">
                <Search size={16} className="text-slate-400" />
                <input className="min-w-0 flex-1 outline-none" placeholder="Buscar tabela, referencia ou observacao" value={ruleSearch} onChange={(event) => setRuleSearch(event.target.value)} />
              </div>
              <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={ruleStatusFilter} onChange={(event) => setRuleStatusFilter(event.target.value as typeof ruleStatusFilter)}>
                <option value="todos">Status: todos</option>
                <option value="ativas">Ativas</option>
                <option value="inativas">Inativas</option>
              </select>
              <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={ruleMethodFilter} onChange={(event) => setRuleMethodFilter(event.target.value as DepreciationMethod | "todos")}>
                <option value="todos">Metodo: todos</option>
                <option value="linear_mensal">Linear mensal</option>
                <option value="linear_anual">Linear anual</option>
                <option value="valor_manual_auditado">Valor manual auditado</option>
                <option value="valor_mercado">Valor estimado de mercado</option>
              </select>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">{filteredRules.length} tabela(s)</div>
            </div>

            {filteredRules.length ? (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                {filteredRules.map((rule) => {
                  const linkedAssets = assetsByRule.get(rule.id)?.length ?? 0;
                  return (
                    <div key={rule.id} className="grid gap-3 border-b border-slate-100 p-4 last:border-b-0 lg:grid-cols-[1fr_130px_120px_120px_250px] lg:items-center">
                      <div className="min-w-0">
                        <p className="font-semibold text-ink">{rule.assetTypeName}</p>
                        <p className="mt-1 truncate text-sm text-slate-500">{rule.notes || rule.legalReference || "Sem observacoes"}</p>
                      </div>
                      <span className="text-sm text-slate-700">{rule.usefulLifeYears ? `${rule.usefulLifeYears} anos` : "Não depreciável"}</span>
                      <span className="text-sm text-slate-700">{rule.annualRate}% a.a.</span>
                      <StatusPill tone={rule.active ? "green" : "neutral"}>{rule.active ? "Ativa" : "Inativa"}</StatusPill>
                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        <SecondaryButton onClick={() => openRuleAssets(rule.id)}><Search size={16} /> {linkedAssets} bens</SecondaryButton>
                        {canEdit ? <SecondaryButton onClick={() => openMigration(rule.id)}><MoveRight size={16} /> Migrar</SecondaryButton> : null}
                        {canEdit ? <PrimaryButton onClick={() => openEditForm(rule.id)}><Edit3 size={16} /> Editar</PrimaryButton> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="Nenhuma tabela encontrada" description="Ajuste a busca/filtros ou cadastre uma nova tabela de depreciação." />
            )}
          </section>
        ) : null}

        {mode === "form" ? (
          <form className="mx-auto max-w-3xl rounded-lg border border-slate-200 bg-white p-4 shadow-sm" onSubmit={handleSaveRule}>
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">{selectedRule ? "Editar tabela" : "Nova tabela"}</h2>
              <p className="mt-1 text-sm text-slate-500">Somente MASTER pode salvar alterações.</p>
            </div>
          </div>
          <div className="grid gap-3">
            <label className="text-sm font-medium text-slate-700">Tipo de bem<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.assetTypeName} onChange={(event) => setForm((current) => ({ ...current, assetTypeName: event.target.value }))} disabled={!canEdit} required /></label>
            <label className="text-sm font-medium text-slate-700">Vida util em anos<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} step={0.5} placeholder="Vazio para nao depreciavel" value={form.usefulLifeYears} onChange={(event) => setForm((current) => ({ ...current, usefulLifeYears: event.target.value }))} disabled={!canEdit} /></label>
            <label className="text-sm font-medium text-slate-700">Taxa anual %<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} step={0.01} value={form.annualRate} onChange={(event) => setForm((current) => ({ ...current, annualRate: event.target.value }))} disabled={!canEdit} /></label>
            <label className="text-sm font-medium text-slate-700">Valor residual %<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={0} max={100} step={0.01} value={form.residualValuePercent} onChange={(event) => setForm((current) => ({ ...current, residualValuePercent: event.target.value }))} disabled={!canEdit} /></label>
            <label className="text-sm font-medium text-slate-700">Metodo<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.method} onChange={(event) => setForm((current) => ({ ...current, method: event.target.value as DepreciationMethod }))} disabled={!canEdit}><option value="linear_mensal">Linear mensal</option><option value="linear_anual">Linear anual</option><option value="valor_manual_auditado">Valor manual auditado</option><option value="valor_mercado">Valor estimado de mercado</option></select></label>
            <label className="text-sm font-medium text-slate-700">Referencia<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.legalReference} onChange={(event) => setForm((current) => ({ ...current, legalReference: event.target.value }))} disabled={!canEdit} /></label>
            <label className="text-sm font-medium text-slate-700">Observacoes<textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} disabled={!canEdit} /></label>
            <section className="rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-ink">Campos dinamicos do cadastro</h3>
                  <p className="mt-1 text-xs text-slate-500">Defina os campos que aparecem quando este tipo e selecionado no cadastro do bem.</p>
                </div>
                {canEdit ? (
                  <div className="flex flex-wrap gap-2">
                    <SecondaryButton type="button" onClick={() => addDynamicField({ label: "Voltagem", type: "text", unit: "V" })}>Voltagem</SecondaryButton>
                    <SecondaryButton type="button" onClick={() => addDynamicField({ label: "Potencia", type: "number", unit: "W" })}>Potencia</SecondaryButton>
                    <SecondaryButton type="button" onClick={() => addDynamicField({ label: "Placa", type: "text" })}>Placa</SecondaryButton>
                    <SecondaryButton type="button" onClick={() => addDynamicField()}><Plus size={16} /> Campo</SecondaryButton>
                  </div>
                ) : null}
              </div>
              <div className="mt-3 grid gap-3">
                {form.dynamicFields.map((field, index) => (
                  <div key={`dynamic-field-${index}`} className="grid gap-2 rounded-md border border-slate-200 bg-white p-3 md:grid-cols-[1fr_120px_100px_1fr_80px_auto] md:items-end">
                    <label className="text-xs font-medium text-slate-600">Rótulo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-2 py-2 text-sm" value={field.label} onChange={(event) => updateDynamicField(index, { label: event.target.value })} disabled={!canEdit} /></label>
                    <label className="text-xs font-medium text-slate-600">Tipo<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-2 py-2 text-sm" value={field.type} onChange={(event) => updateDynamicField(index, { type: event.target.value as AssetDynamicFieldType })} disabled={!canEdit}><option value="text">Texto</option><option value="number">Numero</option><option value="date">Data</option><option value="textarea">Texto longo</option><option value="select">Lista</option></select></label>
                    <label className="text-xs font-medium text-slate-600">Unidade<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-2 py-2 text-sm" value={field.unit ?? ""} onChange={(event) => updateDynamicField(index, { unit: event.target.value })} disabled={!canEdit} placeholder="W, V, m2" /></label>
                    <label className="text-xs font-medium text-slate-600">Opções de lista<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-2 py-2 text-sm" value={dynamicFieldOptionsDraft[index] ?? (field.options ?? []).join(", ")} onChange={(event) => setDynamicFieldOptionsDraft((current) => ({ ...current, [index]: event.target.value }))} onBlur={(event) => updateDynamicField(index, { options: event.target.value.split(",").map((item) => item.trim()).filter(Boolean) })} disabled={!canEdit || field.type !== "select"} placeholder="Opção A, Opção B" /></label>
                    <label className="flex items-center gap-2 pb-2 text-xs font-medium text-slate-600"><input type="checkbox" checked={Boolean(field.required)} onChange={(event) => updateDynamicField(index, { required: event.target.checked })} disabled={!canEdit} />Obrig.</label>
                    {canEdit ? <SecondaryButton type="button" onClick={() => removeDynamicField(index)}><Trash2 size={16} /></SecondaryButton> : null}
                  </div>
                ))}
                {!form.dynamicFields.length ? <p className="rounded-md border border-dashed border-slate-300 p-3 text-sm text-slate-500">Nenhum campo dinamico configurado para este tipo.</p> : null}
              </div>
            </section>
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={form.active} onChange={(event) => setForm((current) => ({ ...current, active: event.target.checked }))} disabled={!canEdit} className="h-4 w-4 rounded border-slate-300 text-brand-700" />Tabela ativa</label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <PrimaryButton type="submit" disabled={!canEdit || isSaving}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar tabela"}</PrimaryButton>
          </div>
          {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
          {!canEdit ? <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Este painel e somente leitura para o seu perfil.</p> : null}
        </form>
        ) : null}

        {mode === "assets" && selectedRule ? (
          <section className="grid gap-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <h2 className="font-semibold text-ink">Bens vinculados a {selectedRule.assetTypeName}</h2>
                  <p className="mt-1 text-sm text-slate-500">{selectedRuleAssets.length} bem(ns) encontrados no escopo atual.</p>
                </div>
                {canEdit ? <PrimaryButton onClick={() => openMigration(selectedRule.id)}><MoveRight size={16} /> Migrar bens para esta tabela</PrimaryButton> : null}
              </div>
            </div>
            {message ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
            {selectedRuleAssets.length ? (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                {selectedRuleAssets.map((asset) => (
                  <div key={asset.id} className="grid gap-2 border-b border-slate-100 p-4 last:border-b-0 md:grid-cols-[140px_1fr_180px_180px] md:items-center">
                    <strong className="text-ink">{asset.patrimonyNumber}</strong>
                    <span className="text-sm text-slate-700">{asset.description}</span>
                    <span className="text-sm text-slate-500">{asset.physicalLocation || "Sem local"}</span>
                    <span className="text-sm text-slate-500">{asset.responsible || "Sem responsavel"}</span>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState title="Nenhum bem nesta tabela" description="Use a migracao em lote para vincular bens existentes a esta tabela de depreciação." />
            )}
          </section>
        ) : null}

        {mode === "migrate" && selectedRule ? (
          <section className="grid gap-4">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h2 className="font-semibold text-ink">Migrar bens para {selectedRule.assetTypeName}</h2>
                  <p className="mt-1 text-sm text-slate-500">Selecione bens do escopo atual e aplique esta tabela de depreciação em lote.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <SecondaryButton onClick={toggleAllMigrationAssets} disabled={!migrationCandidates.length}>{selectedAssetIds.length === migrationCandidates.length && migrationCandidates.length ? "Limpar selecao" : "Selecionar visiveis"}</SecondaryButton>
                  <PrimaryButton onClick={() => void handleMigrateAssets()} disabled={!canEdit || isSaving || !selectedAssetIds.length}><Save size={16} /> {isSaving ? "Migrando..." : `Migrar ${selectedAssetIds.length}`}</PrimaryButton>
                </div>
              </div>
            </div>
            <div className="grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:grid-cols-[1fr_240px_150px]">
              <div className="flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2">
                <Search size={16} className="text-slate-400" />
                <input className="min-w-0 flex-1 outline-none" placeholder="Buscar patrimonio, descricao, local ou responsavel" value={assetSearch} onChange={(event) => setAssetSearch(event.target.value)} />
              </div>
              <select className="focus-ring rounded-md border border-slate-300 px-3 py-2" value={assetSourceRuleFilter} onChange={(event) => setAssetSourceRuleFilter(event.target.value)}>
                <option value="todos">Tabela atual: todas</option>
                {rules.map((rule) => <option key={rule.id} value={rule.id}>{rule.assetTypeName}</option>)}
              </select>
              <div className="rounded-md bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700">{migrationCandidates.length} bem(ns)</div>
            </div>
            {message ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
            {migrationCandidates.length ? (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                {migrationCandidates.map((asset) => {
                  const currentRule = rules.find((rule) => rule.id === asset.assetTypeId);
                  return (
                    <label key={asset.id} className="grid cursor-pointer gap-3 border-b border-slate-100 p-4 last:border-b-0 hover:bg-slate-50 md:grid-cols-[32px_120px_1fr_180px_180px] md:items-center">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 text-brand-700" checked={selectedAssetIds.includes(asset.id)} onChange={() => toggleMigrationAsset(asset.id)} />
                      <strong className="text-ink">{asset.patrimonyNumber}</strong>
                      <span className="text-sm text-slate-700">{asset.description}</span>
                      <span className="text-sm text-slate-500">{currentRule?.assetTypeName ?? "Sem tabela"}</span>
                      <span className="text-sm text-slate-500">{asset.physicalLocation || "Sem local"}</span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="Nenhum bem disponivel" description="Todos os bens visiveis ja estao nesta tabela ou os filtros nao retornaram resultados." />
            )}
            {!canEdit ? <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Este painel e somente leitura para o seu perfil.</p> : null}
          </section>
        ) : null}
      </div>
    </>
  );
}

function ReportsPage({ accessContext, accessProfile, depreciationRules, currentUser, currentUserName }: { accessContext: AccessContext; accessProfile: UserAccessProfile; depreciationRules: DepreciationRule[]; currentUser: User | null; currentUserName: string }) {
  const hasMultiCompanyManagerAccess = Object.values(accessProfile.companyRoles).includes("GERENTE") && accessContext.accessibleCompanies.length > 1;
  const canSelectMultipleCompanies = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || hasMultiCompanyManagerAccess;
  const defaultCompanies = useMemo(() => accessContext.selectedCompany
    ? [accessContext.selectedCompany]
    : accessContext.scopedCompanies.length
      ? accessContext.scopedCompanies
      : accessContext.accessibleCompanies.length
        ? accessContext.accessibleCompanies
        : [seedCompany], [accessContext.accessibleCompanies, accessContext.scopedCompanies, accessContext.selectedCompany]);
  const defaultCompanyLabel = accessContext.selectedCompany ? accessContext.selectedCompany.tradeName : "Todas as empresas";
  const [isMultiCompanyOpen, setIsMultiCompanyOpen] = useState(false);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const [isCsvModalOpen, setIsCsvModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [companySearch, setCompanySearch] = useState("");
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>(defaultCompanies.map((company) => company.id));
  const [statusFilter, setStatusFilter] = useState<Asset["status"] | "todos">("todos");
  const [includeInactiveAssets, setIncludeInactiveAssets] = useState(false);
  const [groupBy, setGroupBy] = useState<AssetReportGroupBy>("none");
  const [includeTotals, setIncludeTotals] = useState(true);
  const [auditFilter, setAuditFilter] = useState<"todos" | "auditado" | "nao_auditado" | "pendente_revisao" | "divergente">("todos");
  const [selectedFields, setSelectedFields] = useState<AssetReportField[]>([
    "company",
    "patrimonyNumber",
    "description",
    "physicalLocation",
    "responsible",
    "status",
    "purchaseDate",
    "assetAge",
    "acquisitionValue",
    "bookValue",
    "auditStatus"
  ]);

  useEffect(() => {
    setSelectedCompanyIds(defaultCompanies.map((company) => company.id));
  }, [defaultCompanies]);

  const reportCompanies = accessContext.accessibleCompanies.filter((company) => selectedCompanyIds.includes(company.id));
  const fallbackCompanies = reportCompanies.length ? reportCompanies : defaultCompanies;
  const filteredReportCompanies = accessContext.accessibleCompanies.filter((company) => matchesCompanySearch(company, companySearch));
  const reportCompanyIds = new Set(fallbackCompanies.map((company) => company.id));
  const reportAssets = accessContext.scopedAssets.filter((asset) =>
    reportCompanyIds.has(asset.companyId)
    && (includeInactiveAssets || statusFilter !== "todos" || asset.status === "ativo")
    && (statusFilter === "todos" || asset.status === statusFilter)
    && (auditFilter === "todos" || (asset.auditStatus ?? "nao_auditado") === auditFilter)
  );
  const reportTotals = useMemo(() => reportAssets.reduce((totals, asset) => {
    const depreciation = calculateLinearMonthlyDepreciation(asset, findDepreciationRuleForAsset(asset, depreciationRules));
    return {
      acquisition: totals.acquisition + (asset.acquisitionValue ?? 0),
      book: totals.book + depreciation.currentBookValue,
      accumulated: totals.accumulated + depreciation.accumulatedDepreciation
    };
  }, { acquisition: 0, book: 0, accumulated: 0 }), [depreciationRules, reportAssets]);
  function toggleReportCompany(companyId: string) {
    if (!canSelectMultipleCompanies) {
      setSelectedCompanyIds([companyId]);
      return;
    }

    setSelectedCompanyIds((current) => {
      const next = current.includes(companyId) ? current.filter((id) => id !== companyId) : [...current, companyId];
      return next.length ? next : [companyId];
    });
  }

  function toggleReportField(field: AssetReportField) {
    setSelectedFields((current) => {
      const next = current.includes(field) ? current.filter((item) => item !== field) : [...current, field];
      return next.length ? next : current;
    });
  }

  function handleOpenMultiCompany() {
    if (!canSelectMultipleCompanies) {
      return;
    }

    setIsMultiCompanyOpen(true);
  }

  function handleExportCsv() {
    exportAssetsCsv(fallbackCompanies, reportAssets, selectedFields, depreciationRules);
    setIsCsvModalOpen(false);
  }

  async function handleExportPdf(includePhotos: boolean) {
    setIsPdfModalOpen(false);
    setIsExportingPdf(true);
    try {
      await exportAssetsPdf({ companies: fallbackCompanies, assets: reportAssets, includePhotos, imobiLogoUrl: logoImobiFull, generatedBy: currentUserName, depreciationRules, groupBy, includeTotals });
    } finally {
      setIsExportingPdf(false);
    }
  }

  const activeFilterCount = [
    statusFilter !== "todos",
    auditFilter !== "todos",
    groupBy !== "none",
    includeInactiveAssets,
    !includeTotals
  ].filter(Boolean).length;

  return (
    <>
      <PageHeader
        title="Relatórios"
        subtitle="Por padrão, o relatorio abre com a empresa selecionada. Multiempresa e CSV avancado ficam sob demanda."
        action={
          <div className="flex max-w-5xl flex-wrap items-center justify-end gap-2">
            <div className="hidden rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-left text-xs text-slate-600 xl:block">
              <span className="font-semibold text-ink">{fallbackCompanies.length}</span> empresa(s) - <span className="font-semibold text-ink">{reportAssets.length}</span> bem(ns)
              <span className="ml-2 text-slate-400">Aquisição {formatCurrency(reportTotals.acquisition)}</span>
            </div>
            <SecondaryButton onClick={() => setIsFiltersModalOpen(true)}><SlidersHorizontal size={16} /> Filtros {activeFilterCount ? `(${activeFilterCount})` : ""}</SecondaryButton>
            {canSelectMultipleCompanies ? <SecondaryButton onClick={handleOpenMultiCompany}>Relatório Multiempresa</SecondaryButton> : null}
            <PrimaryButton onClick={() => setIsPdfModalOpen(true)}><FileDown size={16} /> Exportar PDF</PrimaryButton>
            <SecondaryButton onClick={() => setIsCsvModalOpen(true)}><Download size={16} /> Exportar CSV</SecondaryButton>
          </div>
        }
      />
      <div className="p-4 lg:p-6">
        <div className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm xl:grid-cols-[1fr_280px]">
          <div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-ink">Empresa base do relatorio</p>
                <p className="mt-1 text-sm text-slate-500">{defaultCompanyLabel}</p>
              </div>
            </div>

            {false ? (
              <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 p-3">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm font-semibold text-brand-900">Selecionar empresas</p>
                    <p className="text-xs text-brand-800">{selectedCompanyIds.length} empresa(s) marcada(s)</p>
                  </div>
                  <div className="relative sm:w-80">
                    <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-700" />
                    <input className="focus-ring w-full rounded-md border border-brand-200 bg-white py-2 pl-9 pr-3 text-sm" placeholder="Buscar por nome ou CNPJ" value={companySearch} onChange={(event) => setCompanySearch(event.target.value)} />
                  </div>
                </div>
                <div className="mt-3 grid max-h-64 gap-2 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
                  {filteredReportCompanies.map((company) => (
                    <button key={company.id} type="button" onClick={() => toggleReportCompany(company.id)} className={`rounded-md border px-3 py-2 text-sm font-medium ${selectedCompanyIds.includes(company.id) ? "border-brand-500 bg-white text-brand-800" : "border-brand-100 bg-brand-50 text-brand-700"}`}>
                      <span className="block truncate text-left">{company.tradeName}</span>
                      <span className="block truncate text-left text-xs font-normal opacity-80">{company.cnpj}</span>
                    </button>
                  ))}
                  {!filteredReportCompanies.length ? <p className="rounded-md bg-white px-3 py-2 text-sm text-brand-800">Nenhuma empresa encontrada.</p> : null}
                </div>
              </div>
            ) : null}

            <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
                <span className="font-semibold text-ink">{fallbackCompanies.length}</span>
                <span>empresa(s) selecionada(s)</span>
                <span className="text-slate-300">|</span>
                <span className="font-semibold text-ink">{reportAssets.length}</span>
                <span>bem(ns) filtrado(s)</span>
              </div>
              <div className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
                <span className="rounded-md bg-white px-3 py-2 text-slate-700">Aquisição: <strong>{formatCurrency(reportTotals.acquisition)}</strong></span>
                <span className="rounded-md bg-white px-3 py-2 text-slate-700">Valor desvalorizado: <strong>{formatCurrency(reportTotals.book)}</strong></span>
                <span className="rounded-md bg-white px-3 py-2 text-slate-700">Desvalorização acumulada: <strong>{formatCurrency(reportTotals.accumulated)}</strong></span>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {fallbackCompanies.map((company) => <StatusPill key={company.id} tone="blue">{company.tradeName}</StatusPill>)}
              </div>
            </div>
          </div>

          <div className="hidden">
            <label className="text-sm font-medium text-slate-700">Status<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as Asset["status"] | "todos")}><option value="todos">Todos ativos</option><option value="ativo">Ativo</option><option value="inativo">Inativo</option><option value="baixado">Baixado</option><option value="vendido">Vendido</option><option value="perdido">Perdido</option><option value="em_manutencao">Em manutenção</option><option value="em_auditoria">Em auditoria</option></select></label>
            <label className="text-sm font-medium text-slate-700">Auditoria<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={auditFilter} onChange={(event) => setAuditFilter(event.target.value as typeof auditFilter)}><option value="todos">Todos</option><option value="auditado">Auditado</option><option value="nao_auditado">Não auditado</option><option value="pendente_revisao">Pendente revisão</option><option value="divergente">Divergente</option></select></label>
            <label className="text-sm font-medium text-slate-700">Agrupar por<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={groupBy} onChange={(event) => setGroupBy(event.target.value as AssetReportGroupBy)}><option value="none">Sem agrupamento</option><option value="assetTypeId">Tipo</option><option value="responsible">Responsável</option><option value="physicalLocation">Local</option></select></label>
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"><input type="checkbox" checked={includeInactiveAssets} onChange={(event) => setIncludeInactiveAssets(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" /><span>Incluir bens inativados<span className="block text-xs text-slate-500">Por padrão o relatório considera somente bens ativos.</span></span></label>
            <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"><input type="checkbox" checked={includeTotals} onChange={(event) => setIncludeTotals(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" /><span>Somar final do relatório<span className="block text-xs text-slate-500">Inclui aquisição, valor desvalorizado e depreciação acumulada.</span></span></label>
          </div>
        </div>

        <div className="mt-4">
          <AssetsPage
            accessContext={{ ...accessContext, scopedAssets: reportAssets, scopedCompanies: fallbackCompanies }}
            accessProfile={defaultAccessProfile}
            currentUser={currentUser}
            compact
            hideHeader
            onOpen={() => undefined}
            onTransferResponsibles={() => undefined}
            onCreate={() => undefined}
          />
        </div>
      </div>

      {isMultiCompanyOpen ? (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h2 className="font-semibold text-ink">Relatório Multiempresa</h2>
                <p className="mt-1 text-sm text-slate-500">Busque e selecione as empresas que devem entrar no relatorio.</p>
              </div>
              <button type="button" onClick={() => setIsMultiCompanyOpen(false)} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center">
              <div className="relative">
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-brand-700" />
                <input className="focus-ring w-full rounded-md border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm" placeholder="Buscar por nome ou CNPJ" value={companySearch} onChange={(event) => setCompanySearch(event.target.value)} />
              </div>
              <StatusPill tone="blue">{selectedCompanyIds.length} empresa(s)</StatusPill>
            </div>
            <div className="mt-4 grid max-h-[56vh] gap-2 overflow-y-auto sm:grid-cols-2 xl:grid-cols-3">
              {filteredReportCompanies.map((company) => (
                <button key={company.id} type="button" onClick={() => toggleReportCompany(company.id)} className={`rounded-md border px-3 py-2 text-sm font-medium ${selectedCompanyIds.includes(company.id) ? "border-brand-500 bg-brand-50 text-brand-800" : "border-slate-200 bg-white text-slate-700 hover:border-brand-200"}`}>
                  <span className="block truncate text-left">{company.tradeName}</span>
                  <span className="block truncate text-left text-xs font-normal opacity-80">{company.cnpj}</span>
                </button>
              ))}
              {!filteredReportCompanies.length ? <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">Nenhuma empresa encontrada.</p> : null}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500">{fallbackCompanies.length} empresa(s) e {reportAssets.length} bem(ns) no relatorio.</p>
              <PrimaryButton onClick={() => setIsMultiCompanyOpen(false)}><Check size={16} /> Aplicar empresas</PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {isFiltersModalOpen ? (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h2 className="font-semibold text-ink">Filtros do relatorio</h2>
                <p className="mt-1 text-sm text-slate-500">Ajuste o escopo dos bens antes de exportar ou conferir a lista.</p>
              </div>
              <button type="button" onClick={() => setIsFiltersModalOpen(false)} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="text-sm font-medium text-slate-700">Status
                <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as Asset["status"] | "todos")}><option value="todos">Todos ativos</option><option value="ativo">Ativo</option><option value="inativo">Inativo</option><option value="baixado">Baixado</option><option value="vendido">Vendido</option><option value="perdido">Perdido</option><option value="em_manutencao">Em manutenção</option><option value="em_auditoria">Em auditoria</option></select>
              </label>
              <label className="text-sm font-medium text-slate-700">Auditoria
                <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={auditFilter} onChange={(event) => setAuditFilter(event.target.value as typeof auditFilter)}><option value="todos">Todos</option><option value="auditado">Auditado</option><option value="nao_auditado">Não auditado</option><option value="pendente_revisao">Pendente revisão</option><option value="divergente">Divergente</option></select>
              </label>
              <label className="text-sm font-medium text-slate-700 md:col-span-2">Agrupar por
                <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={groupBy} onChange={(event) => setGroupBy(event.target.value as AssetReportGroupBy)}><option value="none">Sem agrupamento</option><option value="assetTypeId">Tipo</option><option value="responsible">Responsável</option><option value="physicalLocation">Local</option></select>
              </label>
              <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"><input type="checkbox" checked={includeInactiveAssets} onChange={(event) => setIncludeInactiveAssets(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" /><span>Incluir bens inativados<span className="block text-xs text-slate-500">Por padrão o relatorio considera somente bens ativos.</span></span></label>
              <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700"><input type="checkbox" checked={includeTotals} onChange={(event) => setIncludeTotals(event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" /><span>Somar final do relatorio<span className="block text-xs text-slate-500">Inclui aquisicao, valor desvalorizado e depreciação acumulada.</span></span></label>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500">{activeFilterCount} filtro(s) ativo(s), {reportAssets.length} bem(ns) encontrados.</p>
              <PrimaryButton onClick={() => setIsFiltersModalOpen(false)}><Check size={16} /> Aplicar filtros</PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {isPdfModalOpen ? (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
            <div className="border-b border-slate-200 pb-3">
              <h2 className="font-semibold text-ink">Exportar PDF</h2>
              <p className="mt-1 text-sm text-slate-500">Deseja incluir as fotos dos bens no relatorio?</p>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <button type="button" onClick={() => void handleExportPdf(false)} className="rounded-lg border border-slate-200 bg-white p-4 text-left hover:border-brand-300 hover:bg-brand-50">
                <p className="font-semibold text-ink">Sem fotos</p>
                <p className="mt-1 text-sm text-slate-500">Mais espaco para descricoes e dados em tabela.</p>
              </button>
              <button type="button" onClick={() => void handleExportPdf(true)} className="rounded-lg border border-brand-200 bg-brand-50 p-4 text-left hover:border-brand-500">
                <p className="font-semibold text-brand-900">Com fotos</p>
                <p className="mt-1 text-sm text-brand-900">Layout por bem com ate 4 fotos e dados laterais.</p>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <SecondaryButton onClick={() => setIsPdfModalOpen(false)}>Cancelar</SecondaryButton>
            </div>
          </div>
        </div>
      ) : null}

      {isExportingPdf ? <ProcessingOverlay title="Gerando relatorio em PDF" description="Carregando dados, preparando imagens e montando as paginas. Esse processo pode demorar um pouco." /> : null}

      {isCsvModalOpen ? (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="w-full max-w-3xl rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h2 className="font-semibold text-ink">Exportar CSV</h2>
                <p className="mt-1 text-sm text-slate-500">Selecione os campos que devem sair no arquivo. Os filtros da tela serao respeitados.</p>
              </div>
              <button type="button" onClick={() => setIsCsvModalOpen(false)} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {assetReportFields.map((field) => (
                <button key={field.id} type="button" onClick={() => toggleReportField(field.id)} className={`rounded-md border px-3 py-2 text-sm font-medium ${selectedFields.includes(field.id) ? "border-brand-300 bg-brand-50 text-brand-800" : "border-slate-200 bg-white text-slate-600"}`}>
                  {field.label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
              <p className="text-sm text-slate-500">{selectedFields.length} campo(s), {reportAssets.length} bem(ns), {fallbackCompanies.length} empresa(s).</p>
              <div className="flex flex-wrap gap-2">
                <SecondaryButton onClick={() => setIsCsvModalOpen(false)}>Cancelar</SecondaryButton>
                <PrimaryButton onClick={handleExportCsv}><Download size={16} /> Baixar CSV</PrimaryButton>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
function AuditPage({
  accessContext,
  currentUser,
  releases,
  onSaveRelease
}: {
  accessContext: AccessContext;
  currentUser: User | null;
  releases: AuditorAccessRelease[];
  onSaveRelease: (input: SaveAuditorReleaseInput) => Promise<void>;
}) {
  const canManageReleases = accessContext.globalRole === "MASTER" || accessContext.globalRole === "ADMIN";
  const canAudit = canManageReleases || accessContext.companyRole === "AUDITOR" || accessContext.auditorReleasedCompanyIds.length > 0;
  const auditorCompanies = accessContext.accessibleCompanies.filter((company) => company.isAuditorCompany);
  const clientCompanies = accessContext.accessibleCompanies.filter((company) => !company.isAuditorCompany);
  const [selectedAssetId, setSelectedAssetId] = useState("");
  const [selectedAuditCompanyId, setSelectedAuditCompanyId] = useState("");
  const [selectedAuditorCompanyId, setSelectedAuditorCompanyId] = useState(auditorCompanies[0]?.id ?? "");
  const [releaseAuditorCompanyId, setReleaseAuditorCompanyId] = useState(auditorCompanies[0]?.id ?? "");
  const [releaseClientCompanyId, setReleaseClientCompanyId] = useState(clientCompanies[0]?.id ?? "");
  const [status, setStatus] = useState<AssetAuditRecord["status"]>("auditado");
  const [valuationMethod, setValuationMethod] = useState<AuditValuationMethod>("manual_auditor");
  const [auditedValue, setAuditedValue] = useState("");
  const [referenceTitle, setReferenceTitle] = useState("");
  const [referenceUrl, setReferenceUrl] = useState("");
  const [physicalCondition, setPhysicalCondition] = useState("");
  const [divergenceNotes, setDivergenceNotes] = useState("");
  const [notes, setNotes] = useState("");
  const [history, setHistory] = useState<AssetAuditRecord[]>([]);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const activeAuditCompanyIds = new Set([
    ...accessContext.auditorReleasedCompanyIds,
    ...releases.filter((release) => release.status === "ativo").map((release) => release.clientCompanyId)
  ]);
  const auditCompanies = accessContext.scopedCompanies.filter((company) => activeAuditCompanyIds.has(company.id));
  const auditableAssets = accessContext.scopedAssets.filter((asset) => activeAuditCompanyIds.has(asset.companyId));
  const companyAuditableAssets = auditableAssets.filter((asset) => !selectedAuditCompanyId || asset.companyId === selectedAuditCompanyId);
  const selectedAsset = companyAuditableAssets.find((asset) => asset.id === selectedAssetId) ?? companyAuditableAssets[0];
  const selectedAuditorCompany = auditorCompanies.find((company) => company.id === selectedAuditorCompanyId) ?? auditorCompanies[0];
  const releaseAuditorCompany = auditorCompanies.find((company) => company.id === releaseAuditorCompanyId);
  const releaseClientCompany = clientCompanies.find((company) => company.id === releaseClientCompanyId);

  useEffect(() => {
    if (!selectedAuditorCompanyId && auditorCompanies[0]) {
      setSelectedAuditorCompanyId(auditorCompanies[0].id);
    }
    if (!releaseAuditorCompanyId && auditorCompanies[0]) {
      setReleaseAuditorCompanyId(auditorCompanies[0].id);
    }
    if (!releaseClientCompanyId && clientCompanies[0]) {
      setReleaseClientCompanyId(clientCompanies[0].id);
    }
  }, [auditorCompanies, clientCompanies, releaseAuditorCompanyId, releaseClientCompanyId, selectedAuditorCompanyId]);

  useEffect(() => {
    if (!selectedAuditCompanyId && auditCompanies[0]) {
      setSelectedAuditCompanyId(auditCompanies[0].id);
    }
    if (selectedAuditCompanyId && !auditCompanies.some((company) => company.id === selectedAuditCompanyId)) {
      setSelectedAuditCompanyId(auditCompanies[0]?.id ?? "");
    }
  }, [auditCompanies, selectedAuditCompanyId]);

  useEffect(() => {
    if (!selectedAssetId && companyAuditableAssets[0]) {
      setSelectedAssetId(companyAuditableAssets[0].id);
    }
    if (selectedAssetId && !companyAuditableAssets.some((asset) => asset.id === selectedAssetId)) {
      setSelectedAssetId(companyAuditableAssets[0]?.id ?? "");
    }
  }, [companyAuditableAssets, selectedAssetId]);

  useEffect(() => {
    if (!selectedAsset) {
      setHistory([]);
      return () => undefined;
    }

    return subscribeAssetAuditHistory(
      selectedAsset.companyId,
      selectedAsset.id,
      setHistory,
      (errorMessage) => console.warn("Não foi possível carregar histórico de auditoria.", errorMessage)
    );
  }, [selectedAsset]);

  async function handleReleaseSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    if (!releaseAuditorCompany || !releaseClientCompany) {
      setMessage("Selecione empresa auditora e empresa cliente.");
      return;
    }

    setIsSaving(true);
    try {
      await onSaveRelease({
        auditorCompanyId: releaseAuditorCompany.id,
        auditorCompanyName: releaseAuditorCompany.tradeName,
        clientCompanyId: releaseClientCompany.id,
        clientCompanyName: releaseClientCompany.tradeName,
        status: "ativo",
        permissions: { canAuditAssets: true, canViewValues: true, canEditAssetData: false }
      });
      setMessage("Liberacao de auditoria salva.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar a liberação.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleAuditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setMessage("");
    if (!currentUser || !selectedAsset || !selectedAuditorCompany) {
      setMessage("Selecione bem e empresa auditora para auditar.");
      return;
    }

    setIsSaving(true);
    try {
      await saveAssetAudit({
        companyId: selectedAsset.companyId,
        assetId: selectedAsset.id,
        auditorCompanyId: selectedAuditorCompany.id,
        auditorCompanyName: selectedAuditorCompany.tradeName,
        auditorUserName: currentUser.displayName ?? currentUser.email ?? currentUser.uid,
        status,
        valuationMethod,
        auditedValue: parseCurrencyNumber(auditedValue),
        conservationState: selectedAsset.conservationState,
        physicalCondition,
        divergenceNotes,
        referenceTitle,
        referenceUrl,
        notes
      }, currentUser);
      setMessage("Auditoria registrada no bem.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível registrar auditoria.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <PageHeader title="Auditoria" subtitle="Liberacao de empresas auditoras, revisão de bens e registro de selo de auditoria." />
      <div className="grid gap-4 p-4 lg:grid-cols-[1fr_1fr] lg:p-6">
        {canManageReleases ? (
          <form className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm" onSubmit={handleReleaseSubmit}>
            <h2 className="font-semibold text-ink">Liberar empresa para auditoria</h2>
            <p className="mt-1 text-sm text-slate-500">A empresa precisa estar marcada como auditora no cadastro de empresas.</p>
            <div className="mt-4 grid gap-3">
              <label className="text-sm font-medium text-slate-700">Empresa auditora<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={releaseAuditorCompanyId} onChange={(event) => setReleaseAuditorCompanyId(event.target.value)} required><option value="">Selecionar</option>{auditorCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName}</option>)}</select></label>
              <label className="text-sm font-medium text-slate-700">Empresa cliente<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={releaseClientCompanyId} onChange={(event) => setReleaseClientCompanyId(event.target.value)} required><option value="">Selecionar</option>{clientCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName}</option>)}</select></label>
              <PrimaryButton type="submit" disabled={isSaving || !auditorCompanies.length || !clientCompanies.length}><ShieldCheck size={16} /> Liberar auditoria</PrimaryButton>
            </div>
          </form>
        ) : null}

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <h2 className="font-semibold text-ink">Empresas liberadas</h2>
          <div className="mt-4 grid gap-2">
            {releases.map((release) => (
              <div key={release.id} className="rounded-md border border-slate-200 p-3">
                <p className="font-medium text-ink">{release.clientCompanyName}</p>
                <p className="mt-1 text-sm text-slate-500">Auditada por {release.auditorCompanyName}</p>
                <StatusPill tone={release.status === "ativo" ? "green" : "neutral"}>{release.status}</StatusPill>
              </div>
            ))}
            {!releases.length ? <EmptyState title="Nenhuma liberação" description="Libere uma empresa cliente para uma empresa auditora antes de iniciar auditorias." /> : null}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Bens liberados para auditoria</h2>
              <p className="mt-1 text-sm text-slate-500">A auditoria so aparece quando existe uma liberação ativa para a empresa.</p>
            </div>
            <select className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm" value={selectedAuditCompanyId} onChange={(event) => setSelectedAuditCompanyId(event.target.value)}>
              <option value="">Selecionar empresa</option>
              {auditCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName}</option>)}
            </select>
          </div>
          <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
            {companyAuditableAssets.map((asset) => (
              <button key={asset.id} type="button" onClick={() => setSelectedAssetId(asset.id)} className={`grid w-full gap-3 border-b border-slate-100 p-3 text-left last:border-b-0 hover:bg-slate-50 md:grid-cols-[132px_1fr_150px] md:items-center ${selectedAssetId === asset.id ? "bg-brand-50" : "bg-white"}`}>
                <AssetPhotoPager asset={asset} />
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-ink">{asset.patrimonyNumber} - {asset.description}</span>
                  <span className="mt-1 block truncate text-sm text-slate-500">{asset.physicalLocation || "Sem local"} - {asset.responsible || "Sem responsavel"}</span>
                  <span className="mt-1 block truncate text-xs text-slate-500">{[asset.brand, asset.model, asset.serialNumber ? `Serie ${asset.serialNumber}` : ""].filter(Boolean).join(" - ") || "Sem detalhes tecnicos"}</span>
                </span>
                <span className="flex flex-wrap gap-2 md:justify-end">
                  <AuditStatus status={asset.auditStatus} />
                  {asset.auditorCompanyName ? <StatusPill tone="green">Auditada por {asset.auditorCompanyName}</StatusPill> : null}
                </span>
              </button>
            ))}
            {!companyAuditableAssets.length ? (
              <div className="p-4">
                <EmptyState title="Sem bens liberados" description="Selecione uma empresa com liberação ativa ou solicite/libere o servico de auditoria." />
              </div>
            ) : null}
          </div>
        </section>

        <form className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2" onSubmit={handleAuditSubmit}>
          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <h2 className="font-semibold text-ink">Registrar auditoria de bem</h2>
              <p className="mt-1 text-sm text-slate-500">A empresa cliente vera apenas "Auditada por Nome da Empresa"; auditores veem metodo, data, hora e usuário.</p>
            </div>
            <div className="flex flex-wrap gap-2 lg:justify-end">
              {selectedAsset?.auditorCompanyName ? <StatusPill tone="green">Auditada por {selectedAsset.auditorCompanyName}</StatusPill> : null}
              <PrimaryButton type="submit" disabled={!canAudit || isSaving || !selectedAsset || !selectedAuditorCompany}><Save size={16} /> {isSaving ? "Salvando..." : "Registrar auditoria"}</PrimaryButton>
            </div>
            <label className="text-sm font-medium text-slate-700">Empresa auditora usada<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedAuditorCompanyId} onChange={(event) => setSelectedAuditorCompanyId(event.target.value)} required><option value="">Selecionar</option>{auditorCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName}</option>)}</select></label>
            <label className="text-sm font-medium text-slate-700">Bem para auditar<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedAsset?.id ?? ""} onChange={(event) => setSelectedAssetId(event.target.value)} required><option value="">Selecionar</option>{companyAuditableAssets.map((asset) => <option key={asset.id} value={asset.id}>{asset.patrimonyNumber} - {asset.description}</option>)}</select></label>
            <label className="text-sm font-medium text-slate-700">Status<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value as AssetAuditRecord["status"])}><option value="auditado">Auditado</option><option value="divergente">Divergente</option><option value="pendente_revisao">Pendente de revisão</option></select></label>
            <label className="text-sm font-medium text-slate-700">Metodo de valor<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={valuationMethod} onChange={(event) => setValuationMethod(event.target.value as AuditValuationMethod)}><option value="manual_auditor">Manual pelo auditor</option><option value="desvalorizacao_estado_atual">Desvalorizacao pelo estado atual</option><option value="base_dados_referencia">Base de dados de referencia</option><option value="comparacao_ecommerce">Comparacao com e-commerce</option></select></label>
            <label className="text-sm font-medium text-slate-700">Valor auditado<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="decimal" value={auditedValue} onChange={(event) => setAuditedValue(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700">Titulo/base da referencia<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={referenceTitle} onChange={(event) => setReferenceTitle(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700 lg:col-span-2">Link da referencia<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={referenceUrl} onChange={(event) => setReferenceUrl(event.target.value)} placeholder="URL de e-commerce, tabela, print ou base usada" /></label>
            <label className="text-sm font-medium text-slate-700">Situacao fisica<textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={physicalCondition} onChange={(event) => setPhysicalCondition(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700">Divergencias<textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={divergenceNotes} onChange={(event) => setDivergenceNotes(event.target.value)} /></label>
            <label className="text-sm font-medium text-slate-700 lg:col-span-2">Observacoes do auditor<textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
          </div>
          {message ? <p className="mt-3 rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
          {!canAudit ? <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Seu usuário nao possui empresa liberada para auditoria.</p> : null}
        </form>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:col-span-2">
          <h2 className="font-semibold text-ink">Histórico detalhado para auditores</h2>
          <div className="mt-4 grid gap-3">
            {history.map((record) => (
              <div key={record.id} className="rounded-md border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium text-ink">{record.auditorCompanyName}</p>
                  <StatusPill tone={record.status === "auditado" ? "green" : record.status === "divergente" ? "red" : "yellow"}>{record.status}</StatusPill>
                </div>
                <p className="mt-1 text-sm text-slate-500">Usuário: {record.auditorUserName} - {formatAuditDate(record.createdAt)}</p>
                <p className="mt-1 text-sm text-slate-500">Metodo: {record.valuationMethod} {record.auditedValue ? `- ${formatCurrency(record.auditedValue)}` : ""}</p>
                {record.referenceUrl ? <p className="mt-1 text-sm text-brand-700">{record.referenceUrl}</p> : null}
                {record.notes ? <p className="mt-2 text-sm text-slate-600">{record.notes}</p> : null}
              </div>
            ))}
            {!history.length ? <EmptyState title="Sem histórico" description="Selecione um bem auditado para consultar os registros detalhados." /> : null}
          </div>
        </section>
      </div>
    </>
  );
}

function LogsPage() {
  return <SimpleList title="Logs e trilha de auditoria" items={["login", "cadastro_bem", "upload_foto", "geracao_relatorio", "conferencia_balanco", "impressao_etiqueta"]} action="Filtrar logs" />;
}

type SettingsPanel = "home" | "profile" | "photo" | "password" | "company" | "labels" | "compression" | "security" | "identity";

function SettingsPage({
  currentUser,
  currentProfile,
  settings,
  accessContext,
  accessProfile,
  onOpenLabelTemplates,
  onOpenContractTemplates,
  onSignOut
}: {
  currentUser: User | null;
  currentProfile: UserProfile | null;
  settings: SystemSettings;
  accessContext: AccessContext;
  accessProfile: UserAccessProfile;
  onOpenLabelTemplates: () => void;
  onOpenContractTemplates: () => void;
  onSignOut: () => void;
}) {
  const [activePanel, setActivePanel] = useState<SettingsPanel>("home");
  const [compression, setCompression] = useState(settings.imageCompression);
  const [accountDraft, setAccountDraft] = useState({
    fullName: currentProfile?.fullName ?? currentProfile?.displayName ?? currentUser?.displayName ?? "",
    email: currentProfile?.email ?? currentUser?.email ?? "",
    phone: currentProfile?.phone ?? "",
    address: currentProfile?.address ?? "",
    city: currentProfile?.city ?? "",
    state: currentProfile?.state ?? "",
    zipCode: currentProfile?.zipCode ?? ""
  });
  const [companySettings, setCompanySettings] = useState<CompanyOperationalSettings>(accessContext.selectedCompany?.settings ?? defaultCompanyOperationalSettings);
  const [labelTemplates, setLabelTemplates] = useState<LabelTemplate[]>(seedLabelTemplates.map(normalizeLabelTemplate));
  const [accountMessage, setAccountMessage] = useState("");
  const [companyMessage, setCompanyMessage] = useState("");
  const [message, setMessage] = useState("");
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const selectedCompany = accessContext.selectedCompany;
  const selectedCompanyPermissions = selectedCompany ? accessProfile.companyPermissions[selectedCompany.id] : undefined;
  const canManageCompanySettings = Boolean(selectedCompany) && (
    accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || accessProfile.companyRoles[selectedCompany!.id] === "GERENTE"
    || canAccess(selectedCompanyPermissions, "canManageCompanySettings", accessContext.globalRole)
  );
  const canSeeCompanySettings = accessContext.globalRole === "MASTER"
    || accessContext.globalRole === "ADMIN"
    || Object.values(accessProfile.companyRoles).includes("GERENTE");
  const canManageAdvancedSettings = accessContext.globalRole === "MASTER";

  useEffect(() => {
    setCompression(settings.imageCompression);
  }, [settings.imageCompression]);

  useEffect(() => {
    setAccountDraft({
      fullName: currentProfile?.fullName ?? currentProfile?.displayName ?? currentUser?.displayName ?? "",
      email: currentProfile?.email ?? currentUser?.email ?? "",
      phone: currentProfile?.phone ?? "",
      address: currentProfile?.address ?? "",
      city: currentProfile?.city ?? "",
      state: currentProfile?.state ?? "",
      zipCode: currentProfile?.zipCode ?? ""
    });
  }, [currentProfile, currentUser]);

  useEffect(() => {
    setCompanySettings(selectedCompany?.settings ?? defaultCompanyOperationalSettings);
  }, [selectedCompany]);

  useEffect(() => subscribeGlobalLabelTemplates(
    (items) => setLabelTemplates(items.length ? items.map(normalizeLabelTemplate).filter((item) => item.active) : seedLabelTemplates.map(normalizeLabelTemplate)),
    (message) => console.warn("Não foi possível carregar modelos para configuracoes.", message)
  ), []);

  async function handleSaveAccount() {
    if (!currentUser) {
      setAccountMessage("Usuário autenticado nao encontrado.");
      return;
    }

    try {
      await updateSelfUserAccount(currentUser, accountDraft);
      setAccountMessage("Dados da conta salvos.");
    } catch (error) {
      setAccountMessage(error instanceof Error ? error.message : "Não foi possível salvar sua conta.");
    }
  }

  async function handlePasswordReset() {
    const email = accountDraft.email || currentUser?.email || "";
    if (!email) {
      setAccountMessage("E-mail da conta nao encontrado.");
      return;
    }

    try {
      await sendPasswordReset(email);
      setAccountMessage("Enviamos o link para troca de senha no seu e-mail.");
    } catch (error) {
      setAccountMessage(error instanceof Error ? error.message : "Não foi possível enviar o link de senha.");
    }
  }

  async function handleAccountPhoto(file: File | null) {
    if (!currentUser) {
      setAccountMessage("Usuário autenticado nao encontrado.");
      return;
    }

    setAccountMessage(file ? "Enviando foto..." : "Removendo foto...");
    try {
      const files = file ? await compressImageFiles([file], settings.imageCompression) : [];
      await updateSelfUserPhoto(currentUser, files[0] ?? null);
      setAccountMessage(file ? "Foto da conta atualizada." : "Foto da conta removida.");
    } catch (error) {
      setAccountMessage(error instanceof Error ? error.message : "Não foi possível atualizar a foto.");
    }
  }

  async function handleSaveCompanySettings() {
    if (!currentUser || !selectedCompany) {
      setCompanyMessage("Selecione uma empresa para editar as configuracoes.");
      return;
    }

    if (!canManageCompanySettings) {
      setCompanyMessage("Seu perfil nao permite alterar configuracoes da empresa.");
      return;
    }

    try {
      await saveCompanyOperationalSettings(selectedCompany.id, companySettings, currentUser);
      setCompanyMessage("Configurações da empresa salvas.");
    } catch (error) {
      setCompanyMessage(error instanceof Error ? error.message : "Não foi possível salvar a empresa.");
    }
  }

  async function handleSaveCompression() {
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    try {
      await saveImageCompressionSettings(compression, currentUser);
      setMessage("Padrao de compactacao salvo.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar a compactacao.");
    }
  }

  function renderPanel() {
    if (activePanel === "profile") {
      return (
        <SettingsSection title="Editar perfil" subtitle="Dados principais da sua conta no IMOBI." onBack={() => setActivePanel("home")}>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-sm font-medium text-slate-700 md:col-span-2">Nome completo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={accountDraft.fullName} onChange={(event) => setAccountDraft((current) => ({ ...current, fullName: event.target.value.toLocaleUpperCase("pt-BR") }))} /></label>
            <label className="text-sm font-medium text-slate-700 md:col-span-2">E-mail<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-slate-600" value={accountDraft.email} readOnly /></label>
            <label className="text-sm font-medium text-slate-700">Telefone<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={accountDraft.phone} onChange={(event) => setAccountDraft((current) => ({ ...current, phone: formatPhoneInput(event.target.value) }))} /></label>
            <label className="text-sm font-medium text-slate-700">CEP<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" maxLength={10} placeholder="00.000-000" value={accountDraft.zipCode} onChange={(event) => setAccountDraft((current) => ({ ...current, zipCode: formatZipCodeInput(event.target.value) }))} /></label>
            <label className="text-sm font-medium text-slate-700 md:col-span-2">Endereco<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={accountDraft.address} onChange={(event) => setAccountDraft((current) => ({ ...current, address: event.target.value }))} /></label>
            <label className="text-sm font-medium text-slate-700">Cidade<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" value={accountDraft.city} onChange={(event) => setAccountDraft((current) => ({ ...current, city: event.target.value.toLocaleUpperCase("pt-BR") }))} /></label>
            <label className="text-sm font-medium text-slate-700">Estado<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={accountDraft.state} onChange={(event) => setAccountDraft((current) => ({ ...current, state: event.target.value }))}><option value="">UF</option>{brazilStates.map((state) => <option key={state} value={state}>{state}</option>)}</select></label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2"><PrimaryButton onClick={() => void handleSaveAccount()}><Save size={16} /> Salvar perfil</PrimaryButton></div>
          {accountMessage ? <p className="mt-3 text-sm text-slate-600">{accountMessage}</p> : null}
        </SettingsSection>
      );
    }

    if (activePanel === "photo") {
      return (
        <SettingsSection title="Foto da conta" subtitle="Troque, adicione ou remova a foto do seu perfil." onBack={() => setActivePanel("home")}>
          <div className="flex items-center gap-4 rounded-md border border-dashed border-slate-300 bg-slate-50 p-4">
            <img src={currentProfile?.photoUrl ?? currentUser?.photoURL ?? logoImobiFull} alt="Foto do usuário" className="h-20 w-20 rounded-full object-cover ring-1 ring-slate-200" />
            <div>
              <p className="font-semibold text-ink">Upload de foto</p>
              <p className="mt-1 text-sm text-slate-500">A imagem sera compactada usando o padrão global antes do envio.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <PrimaryButton onClick={() => photoInputRef.current?.click()}><Upload size={16} /> Enviar foto</PrimaryButton>
                <SecondaryButton onClick={() => void handleAccountPhoto(null)}><Trash2 size={16} /> Remover foto</SecondaryButton>
              </div>
              <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={(event) => void handleAccountPhoto(event.target.files?.[0] ?? null)} />
            </div>
          </div>
          {accountMessage ? <p className="mt-3 text-sm text-slate-600">{accountMessage}</p> : null}
        </SettingsSection>
      );
    }

    if (activePanel === "password") {
      return (
        <SettingsSection title="Alterar senha" subtitle="Envie o link seguro de redefinicao para o e-mail da conta." onBack={() => setActivePanel("home")}>
          <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">E-mail: <span className="font-semibold text-ink">{accountDraft.email || currentUser?.email || "Não informado"}</span></p>
          <div className="mt-4 flex flex-wrap gap-2"><PrimaryButton onClick={() => void handlePasswordReset()}><KeyRound size={16} /> Enviar link de senha</PrimaryButton><SecondaryButton onClick={onSignOut}><LogOut size={16} /> Sair</SecondaryButton></div>
          {accountMessage ? <p className="mt-3 text-sm text-slate-600">{accountMessage}</p> : null}
        </SettingsSection>
      );
    }

    if (activePanel === "company") {
      return (
        <SettingsSection title="Configurações da empresa" subtitle="Parâmetros da empresa selecionada no cabeçalho." onBack={() => setActivePanel("home")}>
          {!canManageCompanySettings ? <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Disponivel somente para GERENTE, ADMIN e MASTER com uma empresa selecionada.</p> : null}
          <div className="grid gap-3">
            <p className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">Empresa ativa: <span className="font-semibold text-ink">{selectedCompany?.tradeName ?? "Selecione uma empresa no cabeçalho"}</span></p>
            <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700"><input type="checkbox" checked={companySettings.allowResponsibleSelfTransfer} onChange={(event) => setCompanySettings((current) => ({ ...current, allowResponsibleSelfTransfer: event.target.checked }))} disabled={!canManageCompanySettings} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" /><span>Permitir transferencia de responsabilidade sem supervisao<span className="mt-1 block text-xs font-normal text-slate-500">Quando desativado, transferencias passam por perfil autorizado.</span></span></label>
            <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700"><input type="checkbox" checked={companySettings.externalServicesEnabled} onChange={(event) => setCompanySettings((current) => ({ ...current, externalServicesEnabled: event.target.checked, features: { ...(current.features ?? {}), externalServices: event.target.checked } }))} disabled={!canManageCompanySettings} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" /><span>Ativar controle de serviços externos<span className="mt-1 block text-xs font-normal text-slate-500">Permite criar serviços externos, vincular participantes e controlar saída temporária de bens com conferência de retorno.</span></span></label>
            <label className="text-sm font-medium text-slate-700">Modelo padrão de etiqueta<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={companySettings.defaultLabelTemplateId} onChange={(event) => setCompanySettings((current) => ({ ...current, defaultLabelTemplateId: event.target.value }))} disabled={!canManageCompanySettings}><option value="">Escolher na emissao</option>{labelTemplates.map((template) => <option key={template.id} value={template.id}>{template.name} - {template.widthMm}x{template.heightMm}mm</option>)}</select></label>
            <label className="text-sm font-medium text-slate-700">Responsável padrão em novo bem<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={companySettings.defaultResponsibleMode} onChange={(event) => setCompanySettings((current) => ({ ...current, defaultResponsibleMode: event.target.value as CompanyOperationalSettings["defaultResponsibleMode"] }))} disabled={!canManageCompanySettings}><option value="company">Usar a própria empresa quando vazio</option><option value="required">Exigir responsavel selecionado</option></select></label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2"><PrimaryButton onClick={() => void handleSaveCompanySettings()} disabled={!canManageCompanySettings}><Save size={16} /> Salvar empresa</PrimaryButton></div>
          {companyMessage ? <p className="mt-3 text-sm text-slate-600">{companyMessage}</p> : null}
        </SettingsSection>
      );
    }

    if (activePanel === "compression") {
      return (
        <SettingsSection title="Compactacao de fotos" subtitle="Padrao global usado antes de enviar fotos para o Storage." onBack={() => setActivePanel("home")}>
          {!canManageAdvancedSettings ? <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Disponivel somente para MASTER.</p> : null}
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="flex items-center gap-2 text-sm font-medium text-slate-700"><input type="checkbox" checked={compression.enabled} onChange={(event) => setCompression((current) => ({ ...current, enabled: event.target.checked }))} disabled={!canManageAdvancedSettings} className="h-4 w-4 rounded border-slate-300 text-brand-700" />Ativar</label>
            <label className="text-sm font-medium text-slate-700">Largura maxima<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={640} max={4096} value={compression.maxWidth} onChange={(event) => setCompression((current) => ({ ...current, maxWidth: Number(event.target.value) }))} disabled={!canManageAdvancedSettings} /></label>
            <label className="text-sm font-medium text-slate-700">Altura maxima<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={640} max={4096} value={compression.maxHeight} onChange={(event) => setCompression((current) => ({ ...current, maxHeight: Number(event.target.value) }))} disabled={!canManageAdvancedSettings} /></label>
            <label className="text-sm font-medium text-slate-700">Qualidade<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" min={35} max={95} value={Math.round(compression.quality * 100)} onChange={(event) => setCompression((current) => ({ ...current, quality: Number(event.target.value) / 100 }))} disabled={!canManageAdvancedSettings} /></label>
          </div>
          <div className="mt-4"><PrimaryButton onClick={handleSaveCompression} disabled={!canManageAdvancedSettings}><Save size={16} /> Salvar compactacao</PrimaryButton></div>
          {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
        </SettingsSection>
      );
    }

    if (activePanel === "security" || activePanel === "identity") {
      return (
        <SettingsSection title={activePanel === "security" ? "Seguranca global" : "Identidade e relatorios"} subtitle="Area global reservada ao MASTER." onBack={() => setActivePanel("home")}>
          <p className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">{canManageAdvancedSettings ? "Base preparada para evoluir estes parametros globais." : "Disponivel somente para MASTER."}</p>
        </SettingsSection>
      );
    }

    return (
      <div className="space-y-5 p-4 lg:p-6">
        <SettingsGroup title="Configuracao de usuário" subtitle="Dados da sua conta, foto, senha e saida.">
          <SettingsMenuCard icon={<UserCircle size={20} />} title="Editar perfil" description="Nome, telefone, endereco e dados de contato." onClick={() => setActivePanel("profile")} />
          <SettingsMenuCard icon={<Image size={20} />} title="Foto da conta" description="Trocar ou adicionar uma foto ao perfil." onClick={() => setActivePanel("photo")} />
          <SettingsMenuCard icon={<KeyRound size={20} />} title="Alterar senha" description="Enviar link seguro de redefinicao por e-mail." onClick={() => setActivePanel("password")} />
        </SettingsGroup>

        {canSeeCompanySettings ? (
          <SettingsGroup title="Configuracao de empresa" subtitle="Parâmetros da empresa selecionada no cabeçalho.">
            <SettingsMenuCard icon={<Building2 size={20} />} title="Empresa selecionada" description={selectedCompany ? `${selectedCompany.tradeName} - transferencia, etiquetas e padroes.` : "Selecione uma empresa para alterar parametros."} onClick={() => setActivePanel("company")} disabled={!canManageCompanySettings} />
          </SettingsGroup>
        ) : null}

        {canManageAdvancedSettings ? (
          <SettingsGroup title="Configuracao avancada" subtitle="Menus globais reservados ao MASTER do IMOBI.">
            <SettingsMenuCard icon={<LayoutTemplate size={20} />} title="Modelos globais de etiquetas" description="Layouts, campos, QR Code, código de barras e preview." onClick={onOpenLabelTemplates} badge="MASTER" />
            <SettingsMenuCard icon={<FileText size={20} />} title="Modelos de contrato" description="Editor de modelo padrão para gerar contratos em PDF por empresa." onClick={onOpenContractTemplates} badge="MASTER" />
            <SettingsMenuCard icon={<SlidersHorizontal size={20} />} title="Compactacao de fotos" description="Tamanho e qualidade padrão das imagens do sistema." onClick={() => setActivePanel("compression")} badge="MASTER" />
            <SettingsMenuCard icon={<Lock size={20} />} title="Seguranca global" description="2FA futuro, sessoes, dominios e politicas globais." onClick={() => setActivePanel("security")} badge="MASTER" />
            <SettingsMenuCard icon={<Settings size={20} />} title="Identidade e relatorios" description="Logo, rodape de PDF e padroes visuais globais." onClick={() => setActivePanel("identity")} badge="MASTER" />
          </SettingsGroup>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <PageHeader title="Configurações" subtitle="Escolha um conjunto para abrir apenas o que precisa alterar." />
      {renderPanel()}
    </>
  );
}

function SettingsGroup({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3">
        <h2 className="font-semibold text-ink">{title}</h2>
        <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
      </div>
      <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">{children}</div>
    </section>
  );
}

function SettingsMenuCard({ icon, title, description, onClick, disabled = false, badge }: { icon: ReactNode; title: string; description: string; onClick: () => void; disabled?: boolean; badge?: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} className={`flex min-h-24 items-center gap-3 rounded-md border p-3 text-left transition ${disabled ? "cursor-not-allowed border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50"}`}>
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-slate-100 text-brand-700">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className="font-semibold text-ink">{title}</span>
          {badge ? <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">{badge}</span> : null}
        </span>
        <span className="mt-1 block text-sm text-slate-500">{description}</span>
      </span>
      <ChevronRight size={18} className="shrink-0 text-slate-400" />
    </button>
  );
}

function SettingsSection({ title, subtitle, onBack, children }: { title: string; subtitle: string; onBack: () => void; children: ReactNode }) {
  return (
    <div className="p-4 lg:p-6">
      <section className="mx-auto max-w-4xl rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="font-semibold text-ink">{title}</h2>
            <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
          </div>
          <SecondaryButton onClick={onBack}><ChevronLeft size={16} /> Voltar</SecondaryButton>
        </div>
        {children}
      </section>
    </div>
  );
}
function ServiceRequestPage() {
  return <SimpleList title="Solicitação de serviços" items={seedServiceCatalog.map((item) => item.name)} action="Solicitar serviço" />;
}

function ServiceRequestPageV2({
  accessContext,
  currentUser,
  serviceCatalog,
  serviceRequests,
  planRequiredMessage = ""
}: {
  accessContext: AccessContext;
  currentUser: User | null;
  serviceCatalog: ServiceCatalogItem[];
  serviceRequests: ServiceRequestRecord[];
  planRequiredMessage?: string;
}) {
  const activeServices = serviceCatalog.filter((service) => service.status === "ativo");
  const defaultCompanyId = accessContext.selectedCompany?.id ?? accessContext.scopedCompanies[0]?.id ?? "";
  const [selectedCompanyId, setSelectedCompanyId] = useState(defaultCompanyId);
  const [showUpgradePlans, setShowUpgradePlans] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [serviceLocation, setServiceLocation] = useState("");
  const [serviceScope, setServiceScope] = useState("");
  const [estimatedAssets, setEstimatedAssets] = useState("");
  const [comboServices, setComboServices] = useState({ levantamento: false, etiquetas: false, balanco: false });
  const [urgency, setUrgency] = useState<"baixa" | "normal" | "alta">("normal");
  const [message, setMessage] = useState("");
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const selectedCompany = accessContext.scopedCompanies.find((company) => company.id === selectedCompanyId) ?? accessContext.selectedCompany ?? accessContext.scopedCompanies[0] ?? null;
  const selectedService = activeServices.find((service) => service.id === selectedServiceId);
  const planServices = activeServices
    .filter((service) => service.serviceCategory === "licenca_mensal" || service.billingType === "mensal")
    .sort((left, right) => (left.baseValue || left.minimumValue || 0) - (right.baseValue || right.minimumValue || 0));
  const oneOffServices = activeServices.filter((service) => !planServices.some((plan) => plan.id === service.id));
  const companyPlanRequests = selectedCompany
    ? serviceRequests.filter((request) => request.companyId === selectedCompany.id && request.serviceCategory === "licenca_mensal" && !["cancelado", "solicitado", "em_analise", "orcamento_enviado"].includes(request.status))
    : [];
  const settingsPlanId = selectedCompany?.settings?.activePlanId;
  const activePlanRequest = companyPlanRequests[0];
  const activePlan = settingsPlanId
    ? planServices.find((service) => service.id === settingsPlanId)
    : activePlanRequest ? planServices.find((service) => service.id === activePlanRequest.serviceId) : undefined;
  const activePlanPrice = activePlan ? activePlan.baseValue || activePlan.minimumValue || 0 : 0;
  const visiblePlans = activePlan && !showUpgradePlans
    ? []
    : planServices.filter((plan) => !activePlan || (plan.baseValue || plan.minimumValue || 0) > activePlanPrice);
  const quantity = Math.max(1, Number(estimatedAssets.replace(/\D/g, "")) || 1);
  const visibleRequests = serviceRequests
    .filter((request) => !selectedCompany || request.companyId === selectedCompany.id || request.requesterUserId === currentUser?.uid)
    .slice(0, 6);

  useEffect(() => {
    if (!selectedCompanyId && defaultCompanyId) {
      setSelectedCompanyId(defaultCompanyId);
    }
  }, [defaultCompanyId, selectedCompanyId]);

  useEffect(() => {
    if (!selectedServiceId && oneOffServices[0]?.id) {
      setSelectedServiceId(oneOffServices[0].id);
    }
  }, [oneOffServices, selectedServiceId]);

  async function handlePlanCheckout(service: ServiceCatalogItem) {
    if (!selectedCompany || !currentUser) {
      setMessage("Selecione uma empresa e confirme a sessao autenticada.");
      return;
    }

    try {
      setIsCreatingCheckout(true);
      setMessage("");
      const requestId = await createServiceRequest({
        companyId: selectedCompany.id,
        companyName: selectedCompany.tradeName,
        service,
        description: `Contratacao rapida do plano ${service.name}.`,
        urgency: "normal",
        quantity: 1
      }, currentUser);
      if (service.allowDirectCheckout === false || service.id.includes("corporate")) {
        setMessage("Solicitacao do plano Corporate enviada para cotação do MASTER.");
        return;
      }
      const result = await createMercadoPagoPreference({
        companyId: selectedCompany.id,
        serviceId: service.id,
        description: `Plano ${service.name}`,
        urgency: "normal",
        quantity: 1,
        serviceRequestId: requestId
      });
      if (result.initPoint || result.sandboxInitPoint) {
        window.open(result.initPoint || result.sandboxInitPoint, "_blank", "noopener,noreferrer");
      }
      setMessage("Checkout aberto em ambiente seguro.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível iniciar o checkout.");
    } finally {
      setIsCreatingCheckout(false);
    }
  }

  async function handlePayQuote(request: ServiceRequestRecord) {
    if (!currentUser) {
      setMessage("Sessao nao encontrada.");
      return;
    }

    try {
      setIsCreatingCheckout(true);
      setMessage("");
      const result = await createMercadoPagoPreference({
        companyId: request.companyId,
        serviceId: request.serviceId,
        description: request.description,
        urgency: request.urgency,
        quantity: request.estimatedAssetsCount ?? 1,
        serviceRequestId: request.id
      });
      if (result.initPoint || result.sandboxInitPoint) {
        window.open(result.initPoint || result.sandboxInitPoint, "_blank", "noopener,noreferrer");
      }
      setMessage("Pagamento aberto em ambiente seguro.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível abrir o pagamento.");
    } finally {
      setIsCreatingCheckout(false);
    }
  }

  function buildServiceDescription(service: ServiceCatalogItem) {
    const selectedExtras = Object.entries(comboServices)
      .filter(([, enabled]) => enabled)
      .map(([key]) => key)
      .join(", ");
    return [
      `Serviço solicitado: ${service.name}.`,
      serviceLocation ? `Local informado: ${serviceLocation}.` : "",
      serviceScope ? `Escopo: ${serviceScope}.` : "",
      selectedExtras ? `Combo solicitado: ${selectedExtras}.` : "",
      `Quantidade aproximada: ${quantity}.`
    ].filter(Boolean).join("\n");
  }

  async function handleRequestQuote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCompany || !currentUser || !selectedService) {
      setMessage("Selecione empresa e servico para solicitar cotação.");
      return;
    }

    try {
      setIsCreatingCheckout(true);
      setMessage("");
      await createServiceRequest({
        companyId: selectedCompany.id,
        companyName: selectedCompany.tradeName,
        service: selectedService,
        description: buildServiceDescription(selectedService),
        urgency,
        quantity
      }, currentUser);
      setServiceLocation("");
      setServiceScope("");
      setEstimatedAssets("");
      setComboServices({ levantamento: false, etiquetas: false, balanco: false });
      setIsServiceModalOpen(false);
      setMessage("Cotação solicitada. O MASTER recebera no Faturamento para montar os valores.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível solicitar cotação.");
    } finally {
      setIsCreatingCheckout(false);
    }
  }

  return (
    <>
      <PageHeader title="Serviços" subtitle="Planos de acesso e cotacoes avulsas para operação patrimonial." />
      {isCreatingCheckout ? <ProcessingOverlay title="Processando solicitacao" description="Registrando a operação comercial e preparando o proximo passo." /> : null}
      <div className="space-y-4 p-4 lg:p-6">
        {planRequiredMessage ? (
          <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="font-semibold text-amber-950">Plano necessario para liberar o uso</p>
                <p className="mt-1 text-sm text-amber-800">{planRequiredMessage}</p>
              </div>
              <StatusPill tone="yellow">Aguardando plano</StatusPill>
            </div>
          </section>
        ) : null}
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-sm font-semibold text-ink">Empresa selecionada</p>
              <p className="mt-1 text-sm text-slate-500">Escolha a empresa que vai contratar plano ou solicitar servicos.</p>
            </div>
            <select className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 lg:w-96" value={selectedCompanyId} onChange={(event) => setSelectedCompanyId(event.target.value)}>
              {accessContext.scopedCompanies.map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}
            </select>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
            <div>
              <h2 className="text-xl font-semibold text-ink">Planos da plataforma</h2>
              <p className="mt-1 text-sm text-slate-500">
                {activePlan ? `${selectedCompany?.tradeName ?? "A empresa"} possui plano ativo. Precisa ampliar limites?` : "Nenhum plano ativo detectado para esta empresa. Escolha um plano para liberar a plataforma."}
              </p>
            </div>
            {activePlan ? <SecondaryButton onClick={() => setShowUpgradePlans((current) => !current)}><MoveRight size={16} /> {showUpgradePlans ? "Ocultar upgrades" : "Ver upgrades"}</SecondaryButton> : null}
          </div>

          {activePlan && !showUpgradePlans ? (
            <button type="button" onClick={() => setShowUpgradePlans(true)} className="mt-4 w-full rounded-lg border border-brand-200 bg-brand-50 p-4 text-left transition hover:border-brand-400">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-brand-950">Plano atual: {activePlan.name}</p>
                  <p className="mt-1 text-sm text-brand-800">Clique aqui para comparar opcoes superiores e fazer upgrade quando precisar.</p>
                </div>
                <StatusPill tone="green">Ativo</StatusPill>
              </div>
            </button>
          ) : (
            <div className="mt-4 grid gap-4 lg:grid-cols-4 md:grid-cols-2">
              {visiblePlans.map((plan, index) => (
                <article key={plan.id} className={`flex min-h-64 flex-col rounded-lg border p-4 ${index === 1 ? "border-brand-300 bg-brand-50 shadow-sm" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h3 className="font-semibold text-ink">{plan.name}</h3>
                      <p className="mt-2 line-clamp-3 text-sm text-slate-500">{plan.description}</p>
                    </div>
                    {index === 1 ? <StatusPill tone="blue">Popular</StatusPill> : null}
                  </div>
                  <div className="mt-5">
                    <p className="text-xs font-semibold uppercase text-slate-500">Mensalidade</p>
                    <p className="mt-1 text-3xl font-semibold text-ink">{plan.hidePrice || plan.id.includes("corporate") ? "Sob proposta" : formatCurrency(plan.baseValue || plan.minimumValue || 0)}</p>
                    {plan.suggestedRange && !plan.hidePrice && !plan.id.includes("corporate") ? <p className="mt-1 text-xs text-slate-500">{plan.suggestedRange}</p> : null}
                  </div>
                  <div className="mt-4 grid gap-2 text-sm text-slate-600">
                    <span className="rounded-md bg-slate-50 px-3 py-2">Bens: {plan.maxAssets ? `ate ${plan.maxAssets}` : "sob proposta"}</span>
                    <span className="rounded-md bg-slate-50 px-3 py-2">Usuários: {plan.maxUsers ? `ate ${plan.maxUsers}` : "sob proposta"}</span>
                  </div>
                  <div className="mt-auto pt-4">
                    <PrimaryButton onClick={() => void handlePlanCheckout(plan)} disabled={isCreatingCheckout}>
                      {plan.allowDirectCheckout === false || plan.id.includes("corporate") ? <Bell size={16} /> : <CreditCard size={16} />}
                      {plan.allowDirectCheckout === false || plan.id.includes("corporate") ? "Solicitar orçamento" : "Assinar agora"}
                    </PrimaryButton>
                  </div>
                </article>
              ))}
              {!visiblePlans.length ? <EmptyState title="Sem planos superiores" description="A empresa ja esta no maior plano disponivel no catalogo atual." /> : null}
            </div>
          )}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <h2 className="text-xl font-semibold text-ink">Serviços avulsos</h2>
              <p className="mt-1 text-sm text-slate-500">Levantamento inicial, etiquetas, auditoria, balanco, saneamento de planilha e treinamento entram como cotação para o MASTER.</p>
            </div>
            <PrimaryButton onClick={() => setIsServiceModalOpen(true)}><LayoutTemplate size={16} /> Solicitar cotação</PrimaryButton>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {oneOffServices.slice(0, 3).map((service) => (
              <div key={service.id} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="font-semibold text-ink">{service.name}</p>
                <p className="mt-1 line-clamp-2 text-sm text-slate-500">{service.description}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3">
              <h2 className="font-semibold text-ink">Solicitacoes recentes</h2>
              <StatusPill tone="blue">{visibleRequests.length}</StatusPill>
            </div>
            <div className="mt-4 grid gap-3">
              {visibleRequests.map((request) => (
                <div key={request.id} className="rounded-md border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-ink">{request.serviceName || request.serviceId}</p>
                      <p className="mt-1 text-xs text-slate-500">{request.companyName || request.companyId} - {formatDateOnly(request.createdAt)}</p>
                    </div>
                    <StatusPill tone={request.status === "concluido" ? "green" : request.status === "cancelado" ? "red" : "yellow"}>{formatServiceStatus(request.status)}</StatusPill>
                  </div>
                  <p className="mt-2 text-sm font-semibold text-slate-700">{formatCurrency(request.estimatedAmount ?? 0)}</p>
                  {request.status === "orcamento_enviado" ? (
                    <div className="mt-3 grid gap-2">
                      {request.quoteNotes ? <p className="rounded-md bg-slate-50 p-2 text-xs text-slate-600">{request.quoteNotes}</p> : null}
                      <div className="flex flex-wrap gap-2">
                        <PrimaryButton onClick={() => void handlePayQuote(request)}><CreditCard size={16} /> Aceitar e pagar</PrimaryButton>
                        <SecondaryButton onClick={() => currentUser ? void updateServiceRequestStatus(request.id, "cancelado", currentUser) : undefined}>Recusar</SecondaryButton>
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
              {!visibleRequests.length ? <EmptyState title="Sem solicitacoes" description="As solicitacoes feitas aparecem aqui para acompanhamento rapido." /> : null}
            </div>
          </section>

        {message ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
      </div>

      {isServiceModalOpen ? (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-5xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
            <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h2 className="font-semibold text-ink">Cotação de servico avulso</h2>
                <p className="mt-1 text-sm text-slate-500">Escolha o servico, informe escopo e envie para o MASTER montar o orçamento.</p>
              </div>
              <button type="button" onClick={() => setIsServiceModalOpen(false)} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
            </div>

            <form className="mt-4 grid gap-4" onSubmit={handleRequestQuote}>
              <div className="grid gap-3 lg:grid-cols-3 md:grid-cols-2">
                {oneOffServices.map((service) => (
                  <button key={service.id} type="button" onClick={() => setSelectedServiceId(service.id)} className={`rounded-lg border p-4 text-left transition ${selectedService?.id === service.id ? "border-brand-400 bg-brand-50 ring-2 ring-brand-100" : "border-slate-200 bg-white hover:border-brand-200"}`}>
                    <span className="block font-semibold text-ink">{service.name}</span>
                    <span className="mt-2 line-clamp-3 block text-sm text-slate-500">{service.description}</span>
                    <span className="mt-4 flex flex-wrap gap-2">
                      <StatusPill tone="blue">{service.serviceCategory ?? "servico"}</StatusPill>
                      {service.minimumQuantity ? <StatusPill tone="neutral">min. {service.minimumQuantity}</StatusPill> : null}
                    </span>
                  </button>
                ))}
              </div>

              <div className="grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 lg:grid-cols-3">
                <label className="text-sm font-medium text-slate-700">Local / area
                  <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={serviceLocation} onChange={(event) => setServiceLocation(event.target.value)} placeholder="Ex.: loja centro, estoque, unidade 02" />
                </label>
                <label className="text-sm font-medium text-slate-700">Quantidade aproximada
                  <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={estimatedAssets} onChange={(event) => setEstimatedAssets(event.target.value.replace(/\D/g, ""))} placeholder="Ex.: 1200" />
                </label>
                <label className="text-sm font-medium text-slate-700">Urgencia
                  <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={urgency} onChange={(event) => setUrgency(event.target.value as typeof urgency)}>
                    <option value="baixa">Baixa</option>
                    <option value="normal">Normal</option>
                    <option value="alta">Alta</option>
                  </select>
                </label>
                <label className="text-sm font-medium text-slate-700 lg:col-span-3">Itens, grupos ou observacoes
                  <textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={serviceScope} onChange={(event) => setServiceScope(event.target.value)} placeholder="Informe se sera levantamento do zero, quais bens entram no balanco, quais precisam de etiqueta, prazos e detalhes importantes." />
                </label>
              </div>

              <div className="rounded-lg border border-brand-100 bg-brand-50 p-4">
                <p className="text-sm font-semibold text-brand-950">Combo operacional</p>
                <p className="mt-1 text-sm text-brand-800">Marque uma ou mais etapas quando quiser levantamento, etiquetagem e conferencia no mesmo pedido.</p>
                <div className="mt-3 grid gap-2 md:grid-cols-3">
                  <label className="flex gap-2 rounded-md bg-white p-3 text-sm text-slate-700"><input type="checkbox" checked={comboServices.levantamento} onChange={(event) => setComboServices((current) => ({ ...current, levantamento: event.target.checked }))} /> Levantamento do zero</label>
                  <label className="flex gap-2 rounded-md bg-white p-3 text-sm text-slate-700"><input type="checkbox" checked={comboServices.etiquetas} onChange={(event) => setComboServices((current) => ({ ...current, etiquetas: event.target.checked }))} /> Etiquetar bens</label>
                  <label className="flex gap-2 rounded-md bg-white p-3 text-sm text-slate-700"><input type="checkbox" checked={comboServices.balanco} onChange={(event) => setComboServices((current) => ({ ...current, balanco: event.target.checked }))} /> Conferir balanco</label>
                </div>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4">
                <p className="text-sm text-slate-500">A cotação aparece para o MASTER no Faturamento. Quando respondida, a empresa recebe o valor para pagar pelo gateway.</p>
                <div className="flex flex-wrap gap-2">
                  <SecondaryButton type="button" onClick={() => setIsServiceModalOpen(false)}>Cancelar</SecondaryButton>
                  <PrimaryButton type="submit" disabled={!selectedService || isCreatingCheckout}><Bell size={16} /> Solicitar cotação</PrimaryButton>
                </div>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </>
  );
}

function formatServiceStatus(status: string) {
  const labels: Record<string, string> = {
    solicitado: "Solicitado",
    em_analise: "Em analise",
    orcamento_enviado: "Orçamento enviado",
    aguardando_aprovacao: "Aguardando aprovacao",
    aprovado: "Aprovado",
    liberado: "Liberado",
    em_execucao: "Em execucao",
    concluido: "Concluido",
    cancelado: "Cancelado"
  };
  return labels[status] ?? status.replace(/_/g, " ");
}

function CommercialPanelPage({
  currentUser,
  companies,
  assets,
  users,
  serviceCatalog,
  serviceRequests,
  paymentSettings
}: {
  currentUser: User | null;
  companies: CompanyRecord[];
  assets: AssetRecord[];
  users: ManagedUser[];
  serviceCatalog: ServiceCatalogItem[];
  serviceRequests: ServiceRequestRecord[];
  paymentSettings: PaymentSettings;
}) {
  const [editingServiceId, setEditingServiceId] = useState(serviceCatalog[0]?.id ?? "");
  const editingService = serviceCatalog.find((service) => service.id === editingServiceId) ?? serviceCatalog[0];
  const planServices = serviceCatalog
    .filter((service) => service.status === "ativo" && (service.serviceCategory === "licenca_mensal" || service.billingType === "mensal"))
    .sort((left, right) => (left.baseValue || left.minimumValue || 0) - (right.baseValue || right.minimumValue || 0));
  const [activeModal, setActiveModal] = useState<"requests" | "quote" | "companies" | "plans" | "services" | "payments" | null>(null);
  const [periodStart, setPeriodStart] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`;
  });
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const [companyPlanFilter, setCompanyPlanFilter] = useState("all");
  const [form, setForm] = useState({
    name: editingService?.name ?? "",
    description: editingService?.description ?? "",
    billingType: editingService?.billingType ?? "por_bem",
    baseValue: formatCurrency(editingService?.baseValue ?? 0),
    minimumValue: formatCurrency(editingService?.minimumValue ?? 0),
    minimumQuantity: String(editingService?.minimumQuantity ?? 1),
    valuePerAsset: formatCurrency(editingService?.valuePerAsset ?? 0),
    maxDiscountPercent: String(editingService?.maxDiscountPercent ?? 0),
    maxAssets: String(editingService?.maxAssets ?? 0),
    maxUsers: String(editingService?.maxUsers ?? 0),
    allowDirectCheckout: editingService?.allowDirectCheckout ?? true,
    hidePrice: editingService?.hidePrice ?? editingService?.id === "acesso-corporate",
    serviceCategory: editingService?.serviceCategory ?? "servico_avulso",
    suggestedRange: editingService?.suggestedRange ?? "",
    quoteValidityDays: String(editingService?.quoteValidityDays ?? 7),
    adjustmentType: editingService?.adjustmentType ?? "none",
    adjustmentValue: formatCurrency(editingService?.adjustmentValue ?? 0),
    requiresMasterApproval: editingService?.requiresMasterApproval ?? true,
    releasesAutomatically: editingService?.releasesAutomatically ?? false,
    status: editingService?.status ?? "ativo",
    internalNotes: editingService?.internalNotes ?? ""
  });
  const [message, setMessage] = useState("");
  const [quoteMessage, setQuoteMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isQuoteSaving, setIsQuoteSaving] = useState(false);
  const [isCompanyCommercialSaving, setIsCompanyCommercialSaving] = useState(false);
  const [companyCommercialMessage, setCompanyCommercialMessage] = useState("");
  const [isPaymentSaving, setIsPaymentSaving] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [selectedRequestId, setSelectedRequestId] = useState(serviceRequests[0]?.id ?? "");
  const selectedRequest = serviceRequests.find((request) => request.id === selectedRequestId) ?? serviceRequests[0];
  const [selectedCommercialCompanyId, setSelectedCommercialCompanyId] = useState(companies[0]?.id ?? "");
  const selectedCommercialCompany = companies.find((company) => company.id === selectedCommercialCompanyId) ?? companies[0];
  const selectedCommercialCompanySettings = selectedCommercialCompany?.settings ?? defaultCompanyOperationalSettings;
  const [companyCommercialForm, setCompanyCommercialForm] = useState({
    activePlanId: selectedCommercialCompanySettings.activePlanId ?? "",
    maxAssets: String(selectedCommercialCompanySettings.maxAssets ?? 0),
    maxUsers: String(selectedCommercialCompanySettings.maxUsers ?? 0),
    commercialAdjustmentType: selectedCommercialCompanySettings.commercialAdjustmentType ?? "none",
    commercialAdjustmentValue: formatCurrency(selectedCommercialCompanySettings.commercialAdjustmentValue ?? 0),
    trialUntil: selectedCommercialCompanySettings.trialUntil ?? "",
    commercialNotes: selectedCommercialCompanySettings.commercialNotes ?? "",
    trainingEnabled: selectedCommercialCompanySettings.trainingEnabled ?? false
  });
  const [quoteForm, setQuoteForm] = useState({
    quoteAmount: formatCurrency(selectedRequest?.quoteAmount || selectedRequest?.estimatedAmount || 0),
    quoteNotes: selectedRequest?.quoteNotes ?? "",
    quoteValidityDays: "7",
    adjustmentType: "none" as "none" | "discount" | "increase",
    adjustmentValue: formatCurrency(0)
  });
  const [paymentForm, setPaymentForm] = useState({
    acceptCreditCard: paymentSettings.acceptCreditCard,
    acceptPix: paymentSettings.acceptPix,
    acceptDebitCard: paymentSettings.acceptDebitCard,
    acceptBoleto: paymentSettings.acceptBoleto,
    maxInstallments: String(paymentSettings.maxInstallments),
    defaultInstallments: String(paymentSettings.defaultInstallments),
    statementDescriptor: paymentSettings.statementDescriptor
  });
  const totalEstimated = serviceRequests.reduce((sum, request) => sum + (request.estimatedAmount ?? 0), 0);
  const pendingRequests = serviceRequests.filter((request) => !["concluido", "cancelado"].includes(request.status)).length;
  const paidRequests = serviceRequests.filter((request) => request.commercialStatus === "pago" || request.status === "aprovado" || request.status === "liberado").length;
  const planById = new Map(planServices.map((plan) => [plan.id, plan]));
  const assetsByCompany = assets.reduce<Record<string, number>>((acc, asset) => {
    acc[asset.companyId] = (acc[asset.companyId] ?? 0) + 1;
    return acc;
  }, {});
  const usersByCompany = users.reduce<Record<string, number>>((acc, profile) => {
    if (profile.globalRole === "MASTER" || profile.globalRole === "ADMIN") {
      return acc;
    }

    profile.companyAccess
      .filter((access) => access.status === "ativo")
      .forEach((access) => {
        acc[access.companyId] = (acc[access.companyId] ?? 0) + 1;
      });
    return acc;
  }, {});
  const activeCompanyRows = companies
    .map((company) => {
      const settings = company.settings ?? defaultCompanyOperationalSettings;
      const plan = settings.activePlanId ? planById.get(settings.activePlanId) : undefined;
      const baseValue = plan?.baseValue || plan?.minimumValue || 0;
      const adjustedValue = settings.commercialAdjustmentType === "discount"
        ? Math.max(baseValue - (settings.commercialAdjustmentValue ?? 0), 0)
        : settings.commercialAdjustmentType === "increase"
          ? baseValue + (settings.commercialAdjustmentValue ?? 0)
          : baseValue;
      return {
        company,
        settings,
        plan,
        monthlyValue: adjustedValue,
        assetsCount: assetsByCompany[company.id] ?? 0,
        usersCount: usersByCompany[company.id] ?? 0
      };
    })
    .filter((row) => row.plan);
  const filteredCompanyRows = activeCompanyRows.filter((row) => companyPlanFilter === "all" || row.plan?.id === companyPlanFilter);
  const monthlyRevenue = activeCompanyRows.reduce((sum, row) => sum + row.monthlyValue, 0);
  const periodRequests = serviceRequests.filter((request) => {
    if (!request.createdAt) {
      return false;
    }
    const requestDate = request.createdAt.slice(0, 10);
    return (!periodStart || requestDate >= periodStart) && (!periodEnd || requestDate <= periodEnd);
  });
  const periodRevenue = periodRequests
    .filter((request) => request.commercialStatus === "pago" || request.status === "aprovado" || request.status === "liberado" || request.status === "concluido")
    .reduce((sum, request) => sum + (request.quoteAmount || request.estimatedAmount || 0), 0);
  const catalogItemsForModal = activeModal === "plans"
    ? planServices
    : serviceCatalog.filter((service) => !(service.serviceCategory === "licenca_mensal" || service.billingType === "mensal"));

  function openCatalogModal(kind: "plans" | "services") {
    const items = kind === "plans"
      ? planServices
      : serviceCatalog.filter((service) => !(service.serviceCategory === "licenca_mensal" || service.billingType === "mensal"));
    setEditingServiceId(items[0]?.id ?? "");
    setActiveModal(kind);
  }

  function resetServiceForm(kind: "plans" | "services") {
    setEditingServiceId("");
    setForm({
      name: "",
      description: "",
      billingType: kind === "plans" ? "mensal" : "por_bem",
      baseValue: formatCurrency(0),
      minimumValue: formatCurrency(0),
      minimumQuantity: "1",
      valuePerAsset: formatCurrency(0),
      maxDiscountPercent: "0",
      maxAssets: "0",
      maxUsers: "0",
      allowDirectCheckout: kind === "plans",
      hidePrice: false,
      serviceCategory: kind === "plans" ? "licenca_mensal" : "servico_avulso",
      suggestedRange: "",
      quoteValidityDays: "7",
      adjustmentType: "none",
      adjustmentValue: formatCurrency(0),
      requiresMasterApproval: kind !== "plans",
      releasesAutomatically: kind === "plans",
      status: "ativo",
      internalNotes: ""
    });
  }

  useEffect(() => {
    if (!editingService) {
      return;
    }

    setForm({
      name: editingService.name,
      description: editingService.description,
      billingType: editingService.billingType,
      baseValue: formatCurrency(editingService.baseValue ?? 0),
      minimumValue: formatCurrency(editingService.minimumValue ?? 0),
      minimumQuantity: String(editingService.minimumQuantity ?? 1),
      valuePerAsset: formatCurrency(editingService.valuePerAsset ?? 0),
      maxDiscountPercent: String(editingService.maxDiscountPercent ?? 0),
      maxAssets: String(editingService.maxAssets ?? 0),
      maxUsers: String(editingService.maxUsers ?? 0),
      allowDirectCheckout: editingService.allowDirectCheckout ?? editingService.id !== "acesso-corporate",
      hidePrice: editingService.hidePrice ?? editingService.id === "acesso-corporate",
      serviceCategory: editingService.serviceCategory ?? "servico_avulso",
      suggestedRange: editingService.suggestedRange ?? "",
      quoteValidityDays: String(editingService.quoteValidityDays ?? 7),
      adjustmentType: editingService.adjustmentType ?? "none",
      adjustmentValue: formatCurrency(editingService.adjustmentValue ?? 0),
      requiresMasterApproval: editingService.requiresMasterApproval,
      releasesAutomatically: editingService.releasesAutomatically,
      status: editingService.status,
      internalNotes: editingService.internalNotes ?? ""
    });
  }, [editingService]);

  useEffect(() => {
    if (!selectedCommercialCompany) {
      return;
    }
    const settings = selectedCommercialCompany.settings ?? defaultCompanyOperationalSettings;
    setCompanyCommercialForm({
      activePlanId: settings.activePlanId ?? "",
      maxAssets: String(settings.maxAssets ?? 0),
      maxUsers: String(settings.maxUsers ?? 0),
      commercialAdjustmentType: settings.commercialAdjustmentType ?? "none",
      commercialAdjustmentValue: formatCurrency(settings.commercialAdjustmentValue ?? 0),
      trialUntil: settings.trialUntil ?? "",
      commercialNotes: settings.commercialNotes ?? "",
      trainingEnabled: settings.trainingEnabled ?? false
    });
  }, [selectedCommercialCompany]);

  useEffect(() => {
    if (!selectedRequest) {
      return;
    }
    setQuoteForm({
      quoteAmount: formatCurrency(selectedRequest.quoteAmount || selectedRequest.estimatedAmount || 0),
      quoteNotes: selectedRequest.quoteNotes ?? "",
      quoteValidityDays: "7",
      adjustmentType: "none",
      adjustmentValue: formatCurrency(0)
    });
  }, [selectedRequest]);

  useEffect(() => {
    setPaymentForm({
      acceptCreditCard: paymentSettings.acceptCreditCard,
      acceptPix: paymentSettings.acceptPix,
      acceptDebitCard: paymentSettings.acceptDebitCard,
      acceptBoleto: paymentSettings.acceptBoleto,
      maxInstallments: String(paymentSettings.maxInstallments),
      defaultInstallments: String(paymentSettings.defaultInstallments),
      statementDescriptor: paymentSettings.statementDescriptor
    });
  }, [paymentSettings]);

  async function handleSaveService(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser) {
      setMessage("Usuário autenticado nao encontrado.");
      return;
    }

    try {
      setIsSaving(true);
      await saveServiceCatalogItem({
        id: editingServiceId || undefined,
        name: form.name,
        description: form.description,
        billingType: form.billingType as ServiceCatalogItem["billingType"],
        baseValue: parseCurrencyNumber(form.baseValue) ?? 0,
        minimumValue: parseCurrencyNumber(form.minimumValue) ?? 0,
        minimumQuantity: Math.max(1, Number(form.minimumQuantity) || 1),
        valuePerAsset: parseCurrencyNumber(form.valuePerAsset) ?? 0,
        maxDiscountPercent: Number(form.maxDiscountPercent) || 0,
        maxAssets: Number(form.maxAssets) || 0,
        maxUsers: Number(form.maxUsers) || 0,
        allowDirectCheckout: form.serviceCategory === "licenca_mensal" ? form.allowDirectCheckout : false,
        hidePrice: form.hidePrice,
        serviceCategory: form.serviceCategory,
        suggestedRange: form.suggestedRange,
        quoteValidityDays: Math.max(1, Number(form.quoteValidityDays) || 7),
        adjustmentType: form.adjustmentType as "none" | "discount" | "increase",
        adjustmentValue: parseCurrencyNumber(form.adjustmentValue) ?? 0,
        requiresMasterApproval: form.requiresMasterApproval,
        releasesAutomatically: form.releasesAutomatically,
        status: form.status as ServiceCatalogItem["status"],
        internalNotes: form.internalNotes
      }, currentUser);
      setMessage("Serviço salvo na tabela comercial.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o servico.");
    } finally {
      setIsSaving(false);
    }
  }

  async function handleSendQuote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser || !selectedRequest) {
      setQuoteMessage("Selecione uma solicitacao.");
      return;
    }

    try {
      setIsQuoteSaving(true);
      await sendServiceQuote(selectedRequest, {
        quoteAmount: parseCurrencyNumber(quoteForm.quoteAmount) ?? 0,
        quoteNotes: quoteForm.quoteNotes,
        quoteValidityDays: Number(quoteForm.quoteValidityDays) || 7,
        adjustmentType: quoteForm.adjustmentType,
        adjustmentValue: parseCurrencyNumber(quoteForm.adjustmentValue) ?? 0
      }, currentUser);
      setQuoteMessage("Orçamento enviado ao cliente.");
    } catch (error) {
      setQuoteMessage(error instanceof Error ? error.message : "Não foi possível enviar o orçamento.");
    } finally {
      setIsQuoteSaving(false);
    }
  }

  async function handleManualRelease() {
    if (!currentUser || !selectedRequest) {
      setQuoteMessage("Selecione uma solicitacao.");
      return;
    }
    try {
      setIsQuoteSaving(true);
      await releaseServiceManually(selectedRequest, currentUser);
      setQuoteMessage("Serviço liberado manualmente.");
    } catch (error) {
      setQuoteMessage(error instanceof Error ? error.message : "Não foi possível liberar o servico.");
    } finally {
      setIsQuoteSaving(false);
    }
  }

  async function handleSaveCompanyCommercialSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser || !selectedCommercialCompany) {
      setCompanyCommercialMessage("Selecione uma empresa e confirme o usuário autenticado.");
      return;
    }
    const selectedPlan = planServices.find((plan) => plan.id === companyCommercialForm.activePlanId);
    const nextSettings: CompanyOperationalSettings = {
      ...(selectedCommercialCompany.settings ?? defaultCompanyOperationalSettings),
      activePlanId: selectedPlan?.id ?? "",
      activePlanName: selectedPlan?.name ?? "",
      maxAssets: Number(companyCommercialForm.maxAssets) || selectedPlan?.maxAssets || 0,
      maxUsers: Number(companyCommercialForm.maxUsers) || selectedPlan?.maxUsers || 0,
      commercialAdjustmentType: companyCommercialForm.commercialAdjustmentType as CompanyOperationalSettings["commercialAdjustmentType"],
      commercialAdjustmentValue: parseCurrencyNumber(companyCommercialForm.commercialAdjustmentValue) ?? 0,
      trialUntil: companyCommercialForm.trialUntil,
      commercialNotes: companyCommercialForm.commercialNotes,
      trainingEnabled: companyCommercialForm.trainingEnabled
    };

    try {
      setIsCompanyCommercialSaving(true);
      await saveCompanyOperationalSettings(selectedCommercialCompany.id, nextSettings, currentUser);
      setCompanyCommercialMessage("Configurações comerciais da empresa salvas.");
    } catch (error) {
      setCompanyCommercialMessage(error instanceof Error ? error.message : "Não foi possível salvar as configuracoes comerciais.");
    } finally {
      setIsCompanyCommercialSaving(false);
    }
  }

  async function handleSavePaymentSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentUser) {
      setPaymentMessage("Usuário autenticado nao encontrado.");
      return;
    }

    const maxInstallments = Math.min(Math.max(Number(paymentForm.maxInstallments) || 1, 1), 24);
    const defaultInstallments = Math.min(Math.max(Number(paymentForm.defaultInstallments) || 1, 1), maxInstallments);

    try {
      setIsPaymentSaving(true);
      await savePaymentSettings({
        acceptCreditCard: paymentForm.acceptCreditCard,
        acceptPix: paymentForm.acceptPix,
        acceptDebitCard: paymentForm.acceptDebitCard,
        acceptBoleto: paymentForm.acceptBoleto,
        maxInstallments,
        defaultInstallments,
        statementDescriptor: paymentForm.statementDescriptor
      }, currentUser);
      setPaymentMessage("Configurações de pagamento salvas.");
    } catch (error) {
      setPaymentMessage(error instanceof Error ? error.message : "Não foi possível salvar as configuracoes de pagamento.");
    } finally {
      setIsPaymentSaving(false);
    }
  }

  const requestTable = (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
          <tr>
            <th className="px-3 py-2">Empresa</th>
            <th className="px-3 py-2">Serviço</th>
            <th className="px-3 py-2">Status</th>
            <th className="px-3 py-2 text-right">Valor</th>
            <th className="px-3 py-2">Data</th>
            <th className="px-3 py-2 text-right">Acoes</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {serviceRequests.map((request) => (
            <tr key={request.id}>
              <td className="px-3 py-3 font-medium text-ink">{request.companyName || request.companyId}</td>
              <td className="px-3 py-3 text-slate-600">{request.serviceName || request.serviceId}</td>
              <td className="px-3 py-3"><StatusPill tone={request.status === "concluido" ? "green" : request.status === "cancelado" ? "red" : "yellow"}>{formatServiceStatus(request.status)}</StatusPill></td>
              <td className="px-3 py-3 text-right font-semibold text-ink">{formatCurrency(request.quoteAmount || request.estimatedAmount || 0)}</td>
              <td className="px-3 py-3 text-slate-500">{formatDateOnly(request.createdAt)}</td>
              <td className="px-3 py-3 text-right">
                <SecondaryButton onClick={() => {
                  setSelectedRequestId(request.id);
                  setActiveModal("quote");
                }}>Abrir</SecondaryButton>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!serviceRequests.length ? <EmptyState title="Sem solicitacoes" description="Quando um cliente fizer pagamento ou pedido de servico, ele aparecera aqui." /> : null}
    </div>
  );

  const companyPlanTable = (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm font-medium text-slate-700">Filtrar plano
          <select className="focus-ring mt-1 min-w-56 rounded-md border border-slate-300 px-3 py-2" value={companyPlanFilter} onChange={(event) => setCompanyPlanFilter(event.target.value)}>
            <option value="all">Todos os planos ativos</option>
            {planServices.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
          </select>
        </label>
        <StatusPill tone="blue">{filteredCompanyRows.length} empresas</StatusPill>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Empresa</th>
              <th className="px-3 py-2">Plano</th>
              <th className="px-3 py-2">Validade/teste</th>
              <th className="px-3 py-2 text-right">Bens</th>
              <th className="px-3 py-2 text-right">Usuários</th>
              <th className="px-3 py-2 text-right">Mensalidade</th>
              <th className="px-3 py-2 text-right">Acoes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filteredCompanyRows.map((row) => (
              <tr key={row.company.id}>
                <td className="px-3 py-3 font-medium text-ink">{row.company.tradeName}</td>
                <td className="px-3 py-3 text-slate-600">{row.plan?.name}</td>
                <td className="px-3 py-3 text-slate-600">{row.settings.trialUntil ? `Teste ate ${formatDateOnly(row.settings.trialUntil)}` : "Mensal ativo"}</td>
                <td className="px-3 py-3 text-right">{row.assetsCount} / {row.settings.maxAssets || row.plan?.maxAssets || "sem limite"}</td>
                <td className="px-3 py-3 text-right">{row.usersCount} / {row.settings.maxUsers || row.plan?.maxUsers || "sem limite"}</td>
                <td className="px-3 py-3 text-right font-semibold text-ink">{formatCurrency(row.monthlyValue)}</td>
                <td className="px-3 py-3 text-right">
                  <SecondaryButton onClick={() => {
                    setSelectedCommercialCompanyId(row.company.id);
                    setActiveModal("companies");
                  }}>Ajustar</SecondaryButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!filteredCompanyRows.length ? <EmptyState title="Sem empresas com plano" description="Ative um plano para uma empresa ou ajuste os filtros." /> : null}
      </div>
    </div>
  );

  const companyCommercialFormView = (
    <form className="grid gap-3 lg:grid-cols-6" onSubmit={handleSaveCompanyCommercialSettings}>
      <label className="text-sm font-medium text-slate-700 lg:col-span-2">Empresa
        <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedCommercialCompany?.id ?? ""} onChange={(event) => setSelectedCommercialCompanyId(event.target.value)}>
          {companies.map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}
        </select>
      </label>
      <label className="text-sm font-medium text-slate-700 lg:col-span-2">Plano ativo
        <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={companyCommercialForm.activePlanId} onChange={(event) => {
          const plan = planServices.find((item) => item.id === event.target.value);
          setCompanyCommercialForm((current) => ({
            ...current,
            activePlanId: event.target.value,
            maxAssets: plan?.maxAssets ? String(plan.maxAssets) : current.maxAssets,
            maxUsers: plan?.maxUsers ? String(plan.maxUsers) : current.maxUsers
          }));
        }}>
          <option value="">Sem plano ativo</option>
          {planServices.map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}
        </select>
      </label>
      <label className="text-sm font-medium text-slate-700">Limite bens<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={companyCommercialForm.maxAssets} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, maxAssets: event.target.value.replace(/\D/g, "") }))} /></label>
      <label className="text-sm font-medium text-slate-700">Limite usuários<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={companyCommercialForm.maxUsers} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, maxUsers: event.target.value.replace(/\D/g, "") }))} /></label>
      <label className="text-sm font-medium text-slate-700">Ajuste
        <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={companyCommercialForm.commercialAdjustmentType} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, commercialAdjustmentType: event.target.value as "none" | "discount" | "increase" }))}>
          <option value="none">Sem ajuste</option><option value="discount">Desconto</option><option value="increase">Acrescimo</option>
        </select>
      </label>
      <label className="text-sm font-medium text-slate-700">Valor ajuste<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={companyCommercialForm.commercialAdjustmentValue} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, commercialAdjustmentValue: formatCurrencyMask(event.target.value) }))} /></label>
      <label className="text-sm font-medium text-slate-700">Teste ate<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="date" value={companyCommercialForm.trialUntil} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, trialUntil: event.target.value }))} /></label>
      <label className="text-sm font-medium text-slate-700 lg:col-span-3">Observacoes comerciais<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={companyCommercialForm.commercialNotes} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, commercialNotes: event.target.value }))} placeholder="Ex.: limite extra sem cobranca, desconto por contrato anual, cortesia." /></label>
      <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3 text-sm font-medium text-slate-700 lg:col-span-6">
        <input type="checkbox" checked={companyCommercialForm.trainingEnabled} onChange={(event) => setCompanyCommercialForm((current) => ({ ...current, trainingEnabled: event.target.checked }))} className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700" />
        <span>Liberar módulo de treinamento<span className="mt-1 block text-xs font-normal text-slate-500">Dá acesso ao manual online (Treinamento) para todos os usuários desta empresa.</span></span>
      </label>
      <div className="flex items-end lg:col-span-6">
        <PrimaryButton type="submit" disabled={isCompanyCommercialSaving}><Save size={16} /> {isCompanyCommercialSaving ? "Salvando..." : "Salvar limites da empresa"}</PrimaryButton>
      </div>
      {companyCommercialMessage ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800 lg:col-span-6">{companyCommercialMessage}</p> : null}
    </form>
  );

  const quoteFormView = (
    <form className="grid gap-3" onSubmit={handleSendQuote}>
      <label className="text-sm font-medium text-slate-700">Solicitacao
        <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedRequest?.id ?? ""} onChange={(event) => setSelectedRequestId(event.target.value)}>
          {serviceRequests.map((request) => <option key={request.id} value={request.id}>{request.companyName || request.companyId} - {request.serviceName || request.serviceId}</option>)}
        </select>
      </label>
      <div className="grid gap-3 md:grid-cols-4">
        <label className="text-sm font-medium text-slate-700">Valor do orçamento<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={quoteForm.quoteAmount} onChange={(event) => setQuoteForm((current) => ({ ...current, quoteAmount: formatCurrencyMask(event.target.value) }))} /></label>
        <label className="text-sm font-medium text-slate-700">Ajuste<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={quoteForm.adjustmentType} onChange={(event) => setQuoteForm((current) => ({ ...current, adjustmentType: event.target.value as "none" | "discount" | "increase" }))}><option value="none">Sem ajuste</option><option value="discount">Desconto</option><option value="increase">Acrescimo</option></select></label>
        <label className="text-sm font-medium text-slate-700">Valor ajuste<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={quoteForm.adjustmentValue} onChange={(event) => setQuoteForm((current) => ({ ...current, adjustmentValue: formatCurrencyMask(event.target.value) }))} /></label>
        <label className="text-sm font-medium text-slate-700">Validade dias<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={quoteForm.quoteValidityDays} onChange={(event) => setQuoteForm((current) => ({ ...current, quoteValidityDays: event.target.value.replace(/\D/g, "") }))} /></label>
      </div>
      <label className="text-sm font-medium text-slate-700">Resposta para o cliente<textarea className="focus-ring mt-1 min-h-20 w-full rounded-md border border-slate-300 px-3 py-2" value={quoteForm.quoteNotes} onChange={(event) => setQuoteForm((current) => ({ ...current, quoteNotes: event.target.value }))} placeholder="Inclua prazo, escopo, condicoes e proximos passos." /></label>
      <div className="flex flex-wrap gap-2">
        <PrimaryButton type="submit" disabled={isQuoteSaving || !selectedRequest}><DollarSign size={16} /> Enviar orçamento</PrimaryButton>
        <SecondaryButton type="button" disabled={isQuoteSaving || !selectedRequest} onClick={() => void handleManualRelease()}><ShieldCheck size={16} /> Liberar manualmente</SecondaryButton>
      </div>
      {quoteMessage ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{quoteMessage}</p> : null}
    </form>
  );

  const catalogEditorView = (
    <div className="grid gap-4 lg:grid-cols-[320px_minmax(0,1fr)]">
      <div className="space-y-2">
        <SecondaryButton onClick={() => resetServiceForm(activeModal === "plans" ? "plans" : "services")}><Plus size={16} /> Novo</SecondaryButton>
        {catalogItemsForModal.map((service) => (
          <button key={service.id} type="button" onClick={() => setEditingServiceId(service.id)} className={`w-full rounded-md border p-3 text-left ${editingServiceId === service.id ? "border-brand-300 bg-brand-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}>
            <span className="flex items-center justify-between gap-3">
              <span className="font-semibold text-ink">{service.name}</span>
              <StatusPill tone={service.status === "ativo" ? "green" : "neutral"}>{service.status}</StatusPill>
            </span>
            <span className="mt-1 block text-xs text-slate-500">{service.billingType} - {service.hidePrice || service.id.includes("corporate") ? "sob proposta" : formatCurrency(service.minimumValue ?? service.baseValue ?? 0)}</span>
          </button>
        ))}
      </div>
      <form className="grid gap-3" onSubmit={handleSaveService}>
        <label className="text-sm font-medium text-slate-700">Nome<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} /></label>
        <label className="text-sm font-medium text-slate-700">Descricao<textarea className="focus-ring mt-1 min-h-20 w-full rounded-md border border-slate-300 px-3 py-2" value={form.description} onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))} /></label>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm font-medium text-slate-700">Categoria<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.serviceCategory} onChange={(event) => setForm((current) => ({ ...current, serviceCategory: event.target.value as NonNullable<ServiceCatalogItem["serviceCategory"]> }))}><option value="licenca_mensal">Licença mensal</option><option value="etiquetas">Etiquetas</option><option value="levantamento">Levantamento</option><option value="auditoria">Auditoria</option><option value="balanco">Balanço</option><option value="implantacao">Implantação</option><option value="importacao">Importação</option><option value="treinamento">Treinamento</option><option value="servico_avulso">Serviço avulso</option></select></label>
          <label className="text-sm font-medium text-slate-700">Cobranca<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.billingType} onChange={(event) => setForm((current) => ({ ...current, billingType: event.target.value as ServiceCatalogItem["billingType"] }))}><option value="por_bem">Por bem</option><option value="unico">Unico</option><option value="mensal">Mensal</option><option value="por_relatorio">Por relatorio</option><option value="personalizado">Personalizado</option></select></label>
          <label className="text-sm font-medium text-slate-700">Status<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value as ServiceCatalogItem["status"] }))}><option value="ativo">Ativo</option><option value="inativo">Inativo</option></select></label>
          <label className="text-sm font-medium text-slate-700">Validade orçamento (dias)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={form.quoteValidityDays} onChange={(event) => setForm((current) => ({ ...current, quoteValidityDays: event.target.value.replace(/\D/g, "") }))} /></label>
          <label className="text-sm font-medium text-slate-700">Valor minimo<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.minimumValue} onChange={(event) => setForm((current) => ({ ...current, minimumValue: formatCurrencyMask(event.target.value) }))} /></label>
          <label className="text-sm font-medium text-slate-700">Qtd. minima<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={form.minimumQuantity} onChange={(event) => setForm((current) => ({ ...current, minimumQuantity: event.target.value.replace(/\D/g, "") }))} /></label>
          <label className="text-sm font-medium text-slate-700">Valor por bem<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.valuePerAsset} onChange={(event) => setForm((current) => ({ ...current, valuePerAsset: formatCurrencyMask(event.target.value) }))} /></label>
          <label className="text-sm font-medium text-slate-700">Valor base<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.baseValue} onChange={(event) => setForm((current) => ({ ...current, baseValue: formatCurrencyMask(event.target.value) }))} /></label>
          <label className="text-sm font-medium text-slate-700">Limite de bens<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={form.maxAssets} onChange={(event) => setForm((current) => ({ ...current, maxAssets: event.target.value.replace(/\D/g, "") }))} placeholder="0 para sob proposta" /></label>
          <label className="text-sm font-medium text-slate-700">Limite de usuários<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={form.maxUsers} onChange={(event) => setForm((current) => ({ ...current, maxUsers: event.target.value.replace(/\D/g, "") }))} placeholder="0 para sob proposta" /></label>
          <label className="text-sm font-medium text-slate-700">Ajuste padrão<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.adjustmentType} onChange={(event) => setForm((current) => ({ ...current, adjustmentType: event.target.value as "none" | "discount" | "increase" }))}><option value="none">Sem ajuste</option><option value="discount">Desconto</option><option value="increase">Acrescimo</option></select></label>
          <label className="text-sm font-medium text-slate-700">Valor ajuste<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.adjustmentValue} onChange={(event) => setForm((current) => ({ ...current, adjustmentValue: formatCurrencyMask(event.target.value) }))} /></label>
        </div>
        <label className="text-sm font-medium text-slate-700">Faixa sugerida<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={form.suggestedRange} onChange={(event) => setForm((current) => ({ ...current, suggestedRange: event.target.value }))} placeholder="Ex.: R$ 3.000 a R$ 20.000 por projeto" /></label>
        <label className="text-sm font-medium text-slate-700">Observacoes internas<textarea className="focus-ring mt-1 min-h-16 w-full rounded-md border border-slate-300 px-3 py-2" value={form.internalNotes} onChange={(event) => setForm((current) => ({ ...current, internalNotes: event.target.value }))} /></label>
        <div className="grid gap-2 text-sm text-slate-600">
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.requiresMasterApproval} onChange={(event) => setForm((current) => ({ ...current, requiresMasterApproval: event.target.checked }))} /> Exige aprovacao MASTER</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.releasesAutomatically} onChange={(event) => setForm((current) => ({ ...current, releasesAutomatically: event.target.checked }))} /> Libera automaticamente apos pagamento</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.allowDirectCheckout} onChange={(event) => setForm((current) => ({ ...current, allowDirectCheckout: event.target.checked }))} /> Permitir checkout direto para plano</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={form.hidePrice} onChange={(event) => setForm((current) => ({ ...current, hidePrice: event.target.checked }))} /> Não exibir valor na tela de Serviços</label>
        </div>
        <PrimaryButton type="submit" disabled={isSaving}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar"}</PrimaryButton>
        {message ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
      </form>
    </div>
  );

  const paymentSettingsView = (
    <form className="grid gap-4" onSubmit={handleSavePaymentSettings}>
      <div className="rounded-lg border border-brand-100 bg-brand-50 p-4">
        <h3 className="font-semibold text-ink">Parâmetros do checkout</h3>
        <p className="mt-1 text-sm text-slate-600">Essas opcoes controlam os meios de pagamento liberados no gateway. Tokens e segredos continuam protegidos nas Cloud Functions.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <input className="mt-1" type="checkbox" checked={paymentForm.acceptCreditCard} onChange={(event) => setPaymentForm((current) => ({ ...current, acceptCreditCard: event.target.checked }))} />
          <span><span className="block font-semibold text-ink">Cartao de credito</span><span className="text-slate-500">Permite pagamento com parcelamento configurado.</span></span>
        </label>
        <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <input className="mt-1" type="checkbox" checked={paymentForm.acceptPix} onChange={(event) => setPaymentForm((current) => ({ ...current, acceptPix: event.target.checked }))} />
          <span><span className="block font-semibold text-ink">Pix</span><span className="text-slate-500">Libera pagamento instantaneo quando disponivel no provedor.</span></span>
        </label>
        <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <input className="mt-1" type="checkbox" checked={paymentForm.acceptDebitCard} onChange={(event) => setPaymentForm((current) => ({ ...current, acceptDebitCard: event.target.checked }))} />
          <span><span className="block font-semibold text-ink">Cartao de debito</span><span className="text-slate-500">Opcional, conforme disponibilidade da conta.</span></span>
        </label>
        <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 text-sm text-slate-700">
          <input className="mt-1" type="checkbox" checked={paymentForm.acceptBoleto} onChange={(event) => setPaymentForm((current) => ({ ...current, acceptBoleto: event.target.checked }))} />
          <span><span className="block font-semibold text-ink">Boleto</span><span className="text-slate-500">Opcional para cobranças com compensacao posterior.</span></span>
        </label>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-sm font-medium text-slate-700">Parcelas maximas
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={paymentForm.maxInstallments} onChange={(event) => setPaymentForm((current) => ({ ...current, maxInstallments: event.target.value.replace(/\D/g, "") }))} />
        </label>
        <label className="text-sm font-medium text-slate-700">Parcela padrão
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" value={paymentForm.defaultInstallments} onChange={(event) => setPaymentForm((current) => ({ ...current, defaultInstallments: event.target.value.replace(/\D/g, "") }))} />
        </label>
        <label className="text-sm font-medium text-slate-700">Identificacao na fatura
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 uppercase" maxLength={22} value={paymentForm.statementDescriptor} onChange={(event) => setPaymentForm((current) => ({ ...current, statementDescriptor: event.target.value.toUpperCase() }))} />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <PrimaryButton type="submit" disabled={isPaymentSaving}><Save size={16} /> {isPaymentSaving ? "Salvando..." : "Salvar pagamentos"}</PrimaryButton>
        <StatusPill tone={paymentForm.acceptCreditCard || paymentForm.acceptPix || paymentForm.acceptDebitCard || paymentForm.acceptBoleto ? "green" : "red"}>
          {paymentForm.acceptCreditCard || paymentForm.acceptPix || paymentForm.acceptDebitCard || paymentForm.acceptBoleto ? "Checkout habilitado" : "Nenhum meio ativo"}
        </StatusPill>
      </div>
      {paymentMessage ? <p className="rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{paymentMessage}</p> : null}
    </form>
  );

  const modalContent = activeModal === "requests"
    ? { title: "Solicitacoes comerciais", body: requestTable }
    : activeModal === "quote"
      ? { title: "Orçamento e liberação", body: quoteFormView }
      : activeModal === "companies"
        ? { title: "Planos e limites por empresa", body: companyCommercialFormView }
        : activeModal === "plans"
          ? { title: "Planos", body: catalogEditorView }
          : activeModal === "services"
            ? { title: "Serviços", body: catalogEditorView }
            : activeModal === "payments"
              ? { title: "Pagamentos", body: paymentSettingsView }
              : null;

  return (
    <>
      <PageHeader
        title="Faturamento"
        subtitle="Resumo financeiro, planos ativos, solicitacoes e parametros comerciais."
        action={(
          <div className="flex flex-wrap gap-2">
            <SecondaryButton onClick={() => setActiveModal("payments")}><DollarSign size={16} /> Pagamentos</SecondaryButton>
            <SecondaryButton onClick={() => openCatalogModal("services")}><LayoutTemplate size={16} /> Serviços</SecondaryButton>
            <PrimaryButton onClick={() => openCatalogModal("plans")}><CreditCard size={16} /> Planos</PrimaryButton>
          </div>
        )}
      />
      <div className="space-y-4 p-4 lg:p-6">
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Painel financeiro</h2>
              <p className="mt-1 text-sm text-slate-500">Use o período para analisar solicitacoes, receita e volume comercial.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <label className="text-xs font-semibold uppercase text-slate-500">Inicio<input className="focus-ring mt-1 block rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-700" type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} /></label>
              <label className="text-xs font-semibold uppercase text-slate-500">Fim<input className="focus-ring mt-1 block rounded-md border border-slate-300 px-3 py-2 text-sm font-normal text-slate-700" type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} /></label>
            </div>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            <button type="button" onClick={() => setActiveModal("requests")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Solicitacoes</span>
              <span className="mt-2 block text-2xl font-bold text-ink">{serviceRequests.length}</span>
              <span className="text-xs text-slate-500">{pendingRequests} pendentes</span>
            </button>
            <button type="button" onClick={() => setActiveModal("companies")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Planos ativos</span>
              <span className="mt-2 block text-2xl font-bold text-ink">{activeCompanyRows.length}</span>
              <span className="text-xs text-slate-500">empresas ativas</span>
            </button>
            <button type="button" onClick={() => setActiveModal("companies")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Faturamento mensal</span>
              <span className="mt-2 block text-xl font-bold text-ink">{formatCurrency(monthlyRevenue)}</span>
              <span className="text-xs text-slate-500">recorrencia prevista</span>
            </button>
            <button type="button" onClick={() => setActiveModal("requests")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Receita período</span>
              <span className="mt-2 block text-xl font-bold text-ink">{formatCurrency(periodRevenue)}</span>
              <span className="text-xs text-slate-500">{periodRequests.length} solicitacoes</span>
            </button>
            <button type="button" onClick={() => openCatalogModal("services")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Serviços</span>
              <span className="mt-2 block text-2xl font-bold text-ink">{serviceCatalog.filter((service) => service.serviceCategory !== "licenca_mensal" && service.billingType !== "mensal").length}</span>
              <span className="text-xs text-slate-500">parametros avulsos</span>
            </button>
            <button type="button" onClick={() => openCatalogModal("plans")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Planos</span>
              <span className="mt-2 block text-2xl font-bold text-ink">{planServices.length}</span>
              <span className="text-xs text-slate-500">licencas mensais</span>
            </button>
            <button type="button" onClick={() => setActiveModal("payments")} className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-brand-300 hover:bg-brand-50">
              <span className="text-xs font-semibold uppercase text-slate-500">Pagamentos</span>
              <span className="mt-2 block text-2xl font-bold text-ink">{paymentSettings.maxInstallments}x</span>
              <span className="text-xs text-slate-500">{[paymentSettings.acceptCreditCard ? "credito" : "", paymentSettings.acceptPix ? "pix" : "", paymentSettings.acceptDebitCard ? "debito" : "", paymentSettings.acceptBoleto ? "boleto" : ""].filter(Boolean).join(", ") || "nenhum ativo"}</span>
            </button>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Empresas com plano ativo</h2>
              <p className="mt-1 text-sm text-slate-500">Acompanhe plano, validade, uso de bens e usuários. Ajustes extras podem ter cobranca ou cortesia.</p>
            </div>
            <SecondaryButton onClick={() => setActiveModal("companies")}><SlidersHorizontal size={16} /> Ajustar empresa</SecondaryButton>
          </div>
          <div className="mt-4">
            {companyPlanTable}
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-ink">Relatório geral do período</h2>
              <p className="mt-1 text-sm text-slate-500">Resumo rapido para acompanhamento financeiro interno.</p>
            </div>
            <StatusPill tone="blue">{formatDateOnly(periodStart)} - {formatDateOnly(periodEnd)}</StatusPill>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            <MetricCard label="Solicitacoes no período" value={String(periodRequests.length)} hint="Novas demandas comerciais" />
            <MetricCard label="Solicitacoes pagas/liberadas" value={String(periodRequests.filter((request) => request.commercialStatus === "pago" || request.status === "aprovado" || request.status === "liberado" || request.status === "concluido").length)} hint="Conversao operacional" />
            <MetricCard label="Valor estimado total" value={formatCurrency(totalEstimated)} hint="Base historica carregada" />
            <MetricCard label="Liberados/pagos" value={String(paidRequests)} hint="Total geral" />
          </div>
        </section>
      </div>

      {modalContent ? (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
          <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-lg bg-white p-4 shadow-panel">
            <div className="mb-4 flex items-center justify-between gap-3 border-b border-slate-200 pb-3">
              <div>
                <h2 className="text-lg font-semibold text-ink">{modalContent.title}</h2>
                <p className="text-sm text-slate-500">Faturamento IMOBI</p>
              </div>
              <button type="button" onClick={() => setActiveModal(null)} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
            </div>
            {modalContent.body}
          </div>
        </div>
      ) : null}
    </>
  );
}

function ServiceCatalogPage({ serviceCatalog }: { serviceCatalog: ServiceCatalogItem[] }) {
  return <SimpleList title="Tabela de valores e serviços" items={serviceCatalog.map((item) => `${item.name} - ${item.billingType}`)} action="Novo serviço" />;
}

function FeatureReleasesPage() {
  return <SimpleList title="Liberação manual por empresa" items={["Relatório com valores", "Exportação PDF", "Etiquetas QR Code", "Módulo offline", "Limite de bens e usuários"]} action="Liberar função" />;
}

function InventoryPage({ accessContext, onScan }: { accessContext: AccessContext; onScan: () => void }) {
  const inventory = seedInventoryChecks[0];
  const inventoryAssets = accessContext.scopedAssets.slice(0, 2);
  return (
    <>
      <PageHeader title="Balanço patrimonial" subtitle={inventory.name} action={<PrimaryButton onClick={onScan}><QrCode size={16} /> Escanear etiqueta</PrimaryButton>} />
      <div className="space-y-4 p-4 lg:p-6">
        <div className="grid gap-4 sm:grid-cols-5">
          <MetricCard label="Esperados" value={String(inventory.totals.expected)} />
          <MetricCard label="Verificados" value={String(inventory.totals.verified)} />
          <MetricCard label="Pendentes" value={String(inventory.totals.pending)} />
          <MetricCard label="Divergentes" value={String(inventory.totals.divergent)} />
          <MetricCard label="Não encontrados" value={String(inventory.totals.notFound)} />
        </div>
        <div className="grid gap-3">
          {inventoryAssets.map((asset, index) => <InventoryRow key={asset.id} asset={asset} status={index === 0 ? "verificado" : "pendente"} />)}
          {!inventoryAssets.length ? <EmptyState title="Sem bens no balanco" description="Cadastre bens reais para iniciar a conferencia da empresa." /> : null}
        </div>
      </div>
    </>
  );
}

function InventoryRow({ asset, status }: { asset: Asset; status: "verificado" | "pendente" | "divergente" | "nao_encontrado" }) {
  const config = {
    verificado: { icon: <CheckCircle2 size={18} />, label: "Verificado", className: "border-emerald-200 bg-emerald-50 text-emerald-700" },
    pendente: { icon: <CircleDot size={18} />, label: "Pendente", className: "border-slate-200 bg-slate-50 text-slate-600" },
    divergente: { icon: <AlertTriangle size={18} />, label: "Divergente", className: "border-amber-200 bg-amber-50 text-amber-700" },
    nao_encontrado: { icon: <CircleSlash size={18} />, label: "Não encontrado", className: "border-rose-200 bg-rose-50 text-rose-700" }
  }[status];

  return (
    <div className="grid items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-[1fr_190px]">
      <div>
        <p className="font-semibold text-ink">{asset.patrimonyNumber} - {asset.description}</p>
        <p className="text-sm text-slate-500">{asset.physicalLocation} · {asset.responsible}</p>
      </div>
      <div className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold ${config.className}`}>
        {config.icon}
        {config.label}
      </div>
    </div>
  );
}

function DynamicFieldsInput({
  fields,
  values,
  onChange
}: {
  fields: AssetDynamicField[];
  values: Record<string, string | number | boolean | null>;
  onChange: (values: Record<string, string | number | boolean | null>) => void;
}) {
  function updateValue(field: AssetDynamicField, value: string) {
    onChange({
      ...values,
      [field.id]: field.type === "number" ? (value === "" ? "" : Number(value)) : value
    });
  }

  return (
    <section className="rounded-lg border border-brand-100 bg-brand-50/50 p-4 lg:col-span-2">
      <div className="mb-3">
        <h3 className="font-semibold text-ink">Campos especificos do tipo</h3>
        <p className="mt-1 text-sm text-slate-600">Estes campos sao definidos pelo MASTER na tabela de depreciação.</p>
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {fields.map((field) => {
          const value = values[field.id] ?? "";
          const label = `${field.label}${field.unit ? ` (${field.unit})` : ""}${field.required ? " *" : ""}`;
          if (field.type === "textarea") {
            return (
              <label key={field.id} className="text-sm font-medium text-slate-700 md:col-span-2">{label}<textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={String(value)} required={field.required} placeholder={field.placeholder} onChange={(event) => updateValue(field, event.target.value)} /></label>
            );
          }

          if (field.type === "select") {
            return (
              <label key={field.id} className="text-sm font-medium text-slate-700">{label}<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={String(value)} required={field.required} onChange={(event) => updateValue(field, event.target.value)}><option value="">Selecionar</option>{(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}</select></label>
            );
          }

          return (
            <label key={field.id} className="text-sm font-medium text-slate-700">{label}<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"} inputMode={field.type === "number" ? "decimal" : undefined} value={String(value)} required={field.required} placeholder={field.placeholder} onChange={(event) => updateValue(field, event.target.value)} /></label>
          );
        })}
      </div>
    </section>
  );
}

function ReferenceSelectWithCreate({
  label,
  value,
  onChange,
  options,
  onCreate,
  allowCreate = true
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  onCreate?: (value: string) => void | Promise<void>;
  allowCreate?: boolean;
}) {
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [draft, setDraft] = useState("");

  async function handleCreate() {
    const value = draft.trim();
    if (!value || options.includes(value) || !onCreate) {
      return;
    }

    setIsSaving(true);
    try {
      await onCreate(value);
      onChange(value.toLocaleUpperCase("pt-BR"));
      setDraft("");
      setIsCreating(false);
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <label className="text-sm font-medium text-slate-700">
      {label}
      <div className="mt-1 flex gap-2">
        <select className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2" value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">Selecionar {label.toLowerCase()}</option>
          {options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
        {allowCreate ? <button
          type="button"
          onClick={() => setIsCreating((value) => !value)}
          className="focus-ring inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          title={`Cadastrar novo ${label.toLowerCase()}`}
        >
          <Plus size={16} />
        </button> : null}
      </div>
      {isCreating && allowCreate ? (
        <div className="mt-2 flex gap-2">
          <input
            className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={`Novo ${label.toLowerCase()}`}
          />
          <button type="button" onClick={() => void handleCreate()} disabled={isSaving} className="focus-ring rounded-md bg-ink px-3 py-2 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60">
            {isSaving ? "Salvando..." : "Adicionar"}
          </button>
        </div>
      ) : null}
    </label>
  );
}

function CodeInput({
  label,
  value,
  onChange,
  onScan,
  disabled = false,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onScan: () => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  return (
    <label className="text-sm font-medium text-slate-700">
      {label}
      <div className="mt-1 flex gap-2">
        <input className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 disabled:bg-slate-100 disabled:text-slate-500" value={value} onChange={(event) => onChange(event.target.value)} disabled={disabled} placeholder={placeholder} />
        <button
          type="button"
          onClick={onScan}
          disabled={disabled}
          className="focus-ring inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={`Ler ${label.toLowerCase()}`}
        >
          <Barcode size={16} />
        </button>
      </div>
    </label>
  );
}

function CodeScannerModal({ title, onScan, onClose }: { title: string; onScan: (value: string) => void; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end bg-ink/60 p-3 sm:items-center sm:justify-center">
      <div className="w-full rounded-lg bg-white p-4 shadow-panel sm:max-w-md">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-semibold text-ink">{title}</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600">
            <CircleSlash size={16} />
          </button>
        </div>
        <CodeScannerPanel onScan={onScan} />
      </div>
    </div>
  );
}

function CodeScannerPanel({ onScan }: { onScan: (value: string) => void }) {
  const elementId = useMemo(() => `asset-code-scanner-${Math.random().toString(36).slice(2)}`, []);
  const [status, setStatus] = useState("Solicitando acesso à câmera...");
  const [manualCode, setManualCode] = useState("");
  const [hasTorch, setHasTorch] = useState(false);
  const [isTorchOn, setIsTorchOn] = useState(false);
  const scannerRef = useRef<import("html5-qrcode").Html5Qrcode | null>(null);
  const hasReadRef = useRef(false);

  useEffect(() => {
    let scanner: import("html5-qrcode").Html5Qrcode | null = null;
    let isDisposed = false;

    async function stopScanner(activeScanner: import("html5-qrcode").Html5Qrcode | null) {
      if (!activeScanner) {
        return;
      }

      try {
        if (activeScanner.isScanning) {
          await activeScanner.stop();
        }
        activeScanner.clear();
      } catch {
        // O navegador pode encerrar a camera antes do stop ser concluido.
      }
    }

    async function startScanner() {
      try {
        const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
        if (isDisposed) {
          return;
        }

        scanner = new Html5Qrcode(elementId, {
          verbose: false,
          formatsToSupport: [
            Html5QrcodeSupportedFormats.QR_CODE,
            Html5QrcodeSupportedFormats.CODE_128,
            Html5QrcodeSupportedFormats.EAN_13,
            Html5QrcodeSupportedFormats.EAN_8,
            Html5QrcodeSupportedFormats.UPC_A,
            Html5QrcodeSupportedFormats.UPC_E
          ]
        });
        scannerRef.current = scanner;
        const activeScanner = scanner;

        const config = { fps: 10, qrbox: { width: 260, height: 180 }, aspectRatio: 1.777 };
        const handleSuccess = (decodedText: string) => {
          if (hasReadRef.current) {
            return;
          }

          hasReadRef.current = true;
          setStatus("Codigo lido. Fechando camera...");
          if ("vibrate" in navigator) {
            navigator.vibrate(80);
          }
          onScan(decodedText);
          void stopScanner(activeScanner);
        };

        try {
          await activeScanner.start({ facingMode: { exact: "environment" } }, config, handleSuccess, undefined);
        } catch {
          await activeScanner.start({ facingMode: "environment" }, config, handleSuccess, undefined);
        }

        const capabilities = (activeScanner as unknown as { getRunningTrackCapabilities?: () => { torch?: boolean } }).getRunningTrackCapabilities?.();
        setHasTorch(capabilities?.torch === true);
        setStatus("Aponte a câmera para o QR Code ou código de barras.");
      } catch (error) {
        console.warn("Falha ao abrir leitor de código.", error);
        setStatus("Não foi possível abrir a câmera. Verifique a permissão do navegador ou informe o código manualmente.");
      }
    }

    void startScanner();

    return () => {
      isDisposed = true;
      void stopScanner(scanner);
      scannerRef.current = null;
    };
  }, [elementId, onScan]);

  async function toggleTorch() {
    const scanner = scannerRef.current as unknown as { applyVideoConstraints?: (constraints: MediaTrackConstraints) => Promise<void> } | null;
    const nextValue = !isTorchOn;
    try {
      await scanner?.applyVideoConstraints?.({ advanced: [{ torch: nextValue } as MediaTrackConstraintSet] });
      setIsTorchOn(nextValue);
    } catch {
      setStatus("Este dispositivo ou navegador nao liberou o flash para leitura.");
    }
  }

  return (
    <div className="mt-4 space-y-3">
      <div id={elementId} className="min-h-56 overflow-hidden rounded-md border border-slate-200 bg-slate-950" />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-500">{status}</p>
        {hasTorch ? <SecondaryButton onClick={() => void toggleTorch()}><Camera size={16} /> {isTorchOn ? "Desligar flash" : "Ligar flash"}</SecondaryButton> : null}
      </div>
      <div className="flex gap-2">
        <input
          className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2"
          value={manualCode}
          onChange={(event) => setManualCode(event.target.value)}
          placeholder="Digitar código manualmente"
        />
        <button
          type="button"
          onClick={() => manualCode.trim() ? onScan(manualCode.trim()) : undefined}
          className="focus-ring rounded-md bg-ink px-3 py-2 text-sm font-semibold text-white"
        >
          Usar
        </button>
      </div>
    </div>
  );
}


function AuditStatus({ status }: { status: Asset["auditStatus"] }) {
  const config = {
    auditado: { label: "Auditado", icon: <ShieldCheck size={14} />, tone: "green" as const },
    divergente: { label: "Divergente", icon: <AlertTriangle size={14} />, tone: "yellow" as const },
    pendente_revisao: { label: "Revisão", icon: <History size={14} />, tone: "blue" as const },
    nao_auditado: { label: "Não auditado", icon: <CircleSlash size={14} />, tone: "neutral" as const }
  }[status ?? "nao_auditado"];

  return (
    <StatusPill tone={config.tone}>
      <span className="inline-flex items-center gap-1">{config.icon}{config.label}</span>
    </StatusPill>
  );
}

function normalizeAssetLookupCode(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return "";
  }

  try {
    const url = new URL(trimmed);
    const assetId = url.searchParams.get("assetId") || url.searchParams.get("patrimony") || url.searchParams.get("código");
    if (assetId) {
      return assetId.trim().toLocaleUpperCase("pt-BR");
    }
    const lastPath = url.pathname.split("/").filter(Boolean).pop();
    return (lastPath || trimmed).trim().toLocaleUpperCase("pt-BR");
  } catch {
    return trimmed.toLocaleUpperCase("pt-BR");
  }
}

function MobileScanPage({ accessContext }: { accessContext: AccessContext }) {
  const [selectedCompanyId, setSelectedCompanyId] = useState(accessContext.selectedCompany?.id ?? accessContext.scopedCompanies[0]?.id ?? "");
  const [manualCode, setManualCode] = useState("");
  const [lastScan, setLastScan] = useState("");
  const [foundAssetId, setFoundAssetId] = useState("");
  const [isScannerOpen, setIsScannerOpen] = useState(true);
  const [message, setMessage] = useState("");
  const [reviewStatus, setReviewStatus] = useState<"ok" | "divergente" | "">("");
  const [observation, setObservation] = useState("");
  const companies = accessContext.scopedCompanies.length ? accessContext.scopedCompanies : accessContext.accessibleCompanies;
  const selectedCompany = companies.find((company) => company.id === selectedCompanyId) ?? companies[0] ?? null;
  const companyAssets = accessContext.scopedAssets.filter((asset) => asset.companyId === selectedCompany?.id);
  const foundAsset = companyAssets.find((asset) => asset.id === foundAssetId) ?? null;

  useEffect(() => {
    if (!selectedCompanyId && companies[0]) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  function handleLookup(rawCode: string) {
    const normalizedCode = normalizeAssetLookupCode(rawCode);
    setLastScan(rawCode);
    setReviewStatus("");
    setObservation("");
    if (!selectedCompany) {
      setMessage("Selecione uma empresa antes de consultar.");
      return;
    }

    const asset = companyAssets.find((item) => {
      const candidates = [item.id, item.patrimonyNumber, item.internalCode, item.serialNumber].filter(Boolean).map((value) => normalizeAssetLookupCode(String(value)));
      return candidates.includes(normalizedCode);
    });

    if (!asset) {
      setFoundAssetId("");
      setMessage(`Nenhum bem encontrado para ${normalizedCode} em ${selectedCompany.tradeName}.`);
      return;
    }

    setFoundAssetId(asset.id);
    setIsScannerOpen(false);
    setMessage("");
  }

  function handleReview(status: "ok" | "divergente") {
    setReviewStatus(status);
    setMessage(status === "ok" ? "Consulta marcada como correta nesta sessão." : "Divergência marcada nesta sessão. O registro formal entra no fluxo de balanço/auditoria.");
  }

  return (
    <>
      <PageHeader title="Leitura avulsa" subtitle="Consulte um bem por etiqueta, QR Code, código de barras ou digitacao manual." />
      <div className="grid gap-4 p-4 lg:grid-cols-[420px_1fr] lg:p-6">
        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
          <h2 className="font-semibold text-ink">Consultar etiqueta</h2>
          <p className="mt-1 text-sm text-slate-500">Selecione a empresa e aponte a camera para o QR Code/código de barras.</p>
          <label className="mt-4 block text-sm font-medium text-slate-700">Empresa<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedCompany?.id ?? ""} onChange={(event) => { setSelectedCompanyId(event.target.value); setFoundAssetId(""); setMessage(""); setIsScannerOpen(true); }}><option value="">Selecionar empresa</option>{companies.map((company) => <option key={company.id} value={company.id}>{company.tradeName} - {company.cnpj}</option>)}</select></label>
          <div className="mt-4">
            {isScannerOpen ? (
              <CodeScannerPanel onScan={handleLookup} />
            ) : (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                <p className="font-semibold">Leitura concluida.</p>
                <p className="mt-1">A camera foi fechada para economizar bateria e evitar leituras duplicadas.</p>
                <PrimaryButton onClick={() => { setIsScannerOpen(true); setMessage(""); }}><QrCode size={16} /> Escanear novamente</PrimaryButton>
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <input className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2" value={manualCode} onChange={(event) => setManualCode(event.target.value)} placeholder="Patrimônio, série ou URL" />
            <PrimaryButton onClick={() => handleLookup(manualCode)}><Search size={16} /> Buscar</PrimaryButton>
          </div>
          {lastScan ? <p className="mt-3 rounded-md bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">Última leitura: {lastScan}</p> : null}
          {message ? <p className="mt-3 rounded-md border border-brand-100 bg-brand-50 p-3 text-sm font-medium text-brand-800">{message}</p> : null}
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          {foundAsset ? (
            <div className="grid gap-4 xl:grid-cols-[260px_1fr]">
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {foundAsset.mainPhotoUrl ? <img src={foundAsset.mainPhotoUrl} alt={foundAsset.description} className="h-64 w-full object-cover" /> : <div className="flex h-64 items-center justify-center text-sm text-slate-500">Sem foto principal</div>}
              </div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone="green">Bem encontrado</StatusPill>
                  <StatusPill tone={foundAsset.status === "ativo" ? "green" : "yellow"}>{foundAsset.status}</StatusPill>
                  <AuditStatus status={foundAsset.auditStatus} />
                </div>
                <h2 className="mt-3 text-xl font-semibold text-ink">{foundAsset.patrimonyNumber} - {foundAsset.description}</h2>
                <dl className="mt-4 grid gap-3 sm:grid-cols-2">
                  <Info label="Empresa" value={selectedCompany?.tradeName ?? "-"} />
                  <Info label="Local" value={foundAsset.physicalLocation || "-"} />
                  <Info label="Responsável" value={foundAsset.responsible || "-"} />
                  <Info label="Marca / modelo" value={[foundAsset.brand, foundAsset.model].filter(Boolean).join(" - ") || "-"} />
                  <Info label="Serie" value={foundAsset.serialNumber || "-"} />
                  <Info label="Conservação" value={foundAsset.conservationState} />
                </dl>
                <textarea className="focus-ring mt-4 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2" value={observation} onChange={(event) => setObservation(event.target.value)} placeholder="Observacao rapida da consulta" />
                <div className="mt-3 flex flex-wrap gap-2">
                  <PrimaryButton onClick={() => handleReview("ok")}><Check size={16} /> Tudo certo</PrimaryButton>
                  <SecondaryButton onClick={() => handleReview("divergente")}><AlertTriangle size={16} /> Dados divergentes</SecondaryButton>
                </div>
                {reviewStatus ? <p className={`mt-3 rounded-md border p-3 text-sm font-medium ${reviewStatus === "ok" ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-amber-200 bg-amber-50 text-amber-800"}`}>{reviewStatus === "ok" ? "Marcado como correto nesta consulta." : "Marcado com divergencia nesta consulta."}{observation ? ` Observacao: ${observation}` : ""}</p> : null}
              </div>
            </div>
          ) : (
            <EmptyState title="Aguardando leitura" description="Leia uma etiqueta ou informe manualmente o patrimonio, série ou URL segura do bem." />
          )}
        </section>
      </div>
    </>
  );
}

function InventoryReportPage() {
  return <SimpleList title="Relatório de balanço" items={["Resumo geral", "Bens conferidos", "Pendentes e não encontrados", "Divergências", "Participantes"]} action="Exportar PDF" />;
}

const labelFieldOptions: Array<{ id: LabelFieldId; label: string }> = [
  { id: "patrimonyNumber", label: "Patrimônio" },
  { id: "internalCode", label: "Código interno" },
  { id: "shortDescription", label: "Descricao" },
  { id: "companyName", label: "Empresa" },
  { id: "branchName", label: "Filial" },
  { id: "physicalLocation", label: "Local" },
  { id: "responsible", label: "Responsável" },
  { id: "createdAt", label: "Data cadastro" },
  { id: "issuedAt", label: "Data emissao" }
];

function labelFieldLabel(field: LabelFieldId | undefined) {
  return labelFieldOptions.find((item) => item.id === field)?.label ?? "Campo";
}

function clampNumber(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), Math.max(min, max));
}

function buildDefaultLabelElements(template: LabelTemplate): LabelElement[] {
  const margin = template.marginsMm ?? { top: template.marginMm, right: template.marginMm, bottom: template.marginMm, left: template.marginMm };
  const codeMode = template.codeMode ?? "qr";
  const hasCode = codeMode !== "none";
  const codeSize = hasCode ? Math.max(8, Math.min(template.heightMm - margin.top - margin.bottom, template.widthMm * 0.32)) : 0;
  const rightColumnX = hasCode ? Math.max(margin.left, template.widthMm - margin.right - codeSize) : template.widthMm - margin.right;
  const textWidth = hasCode ? Math.max(10, rightColumnX - margin.left - 2) : Math.max(10, template.widthMm - margin.left - margin.right);
  const logoSize = Math.min(8, template.heightMm * 0.22);

  const elements: LabelElement[] = [];
  let logoX = margin.left;
  if (template.showImobiLogo) {
    elements.push({ id: "logo-imobi", type: "logoImobi", xMm: logoX, yMm: margin.top, widthMm: logoSize, heightMm: logoSize });
    logoX += logoSize + 2;
  }
  if (template.showCompanyLogo) {
    elements.push({ id: "logo-company", type: "logoCompany", xMm: logoX, yMm: margin.top, widthMm: logoSize, heightMm: logoSize });
  }

  if (hasCode) {
    elements.push({
      id: "code-main",
      type: "code",
      xMm: rightColumnX,
      yMm: margin.top,
      widthMm: codeSize,
      heightMm: codeSize,
      showQrCode: codeMode === "qr" || codeMode === "both",
      showBarcode: codeMode === "barcode" || codeMode === "both",
      showNumber: false,
      codeSource: template.codeSource ?? "assetUrl"
    });
  }

  const fields: LabelFieldId[] = template.visibleFields.includes("patrimonyNumber") ? template.visibleFields : ["patrimonyNumber", ...template.visibleFields];
  let fieldY = margin.top + logoSize + 2;
  fields.forEach((field, index) => {
    const style = template.fieldStyles?.[field];
    const fontSize = style?.fontSize ?? Math.max(6, Math.round(template.fontSize * (index === 0 ? 1.4 : index === 1 ? 1.1 : 1)));
    const rowHeight = Math.max(4, fontSize * 0.5);
    elements.push({
      id: `field-${field}`,
      type: "field",
      field,
      xMm: margin.left,
      yMm: fieldY,
      widthMm: textWidth,
      heightMm: rowHeight,
      fontSize,
      color: style?.color ?? "#111827",
      fontWeight: style?.fontWeight ?? (index === 0 ? "700" : index === 1 ? "600" : "400"),
      textAlign: "left"
    });
    fieldY += rowHeight + 1;
  });

  return elements;
}

function normalizeLabelTemplate(template: LabelTemplate): LabelTemplate {
  const margin = template.marginMm || 2;
  const base: LabelTemplate = {
    ...template,
    marginsMm: template.marginsMm ?? { top: margin, right: margin, bottom: margin, left: margin },
    codeMode: template.codeMode ?? (template.showQrCode && template.showBarcode ? "both" : template.showQrCode ? "qr" : template.showBarcode ? "barcode" : "none"),
    codeSource: template.codeSource ?? "assetUrl",
    fontFamily: template.fontFamily ?? "Arial",
    fieldStyles: template.fieldStyles ?? {}
  };
  return {
    ...base,
    elements: base.elements && base.elements.length ? base.elements : buildDefaultLabelElements(base)
  };
}

function createBlankLabelTemplate(): LabelTemplate {
  const base = normalizeLabelTemplate(seedLabelTemplates[1]);
  return {
    ...base,
    id: "",
    name: "Novo modelo global",
    createdAt: new Date().toISOString(),
    createdBy: "system"
  };
}

function buildLabelSampleValues(asset: Asset, company: Company): Record<LabelFieldId, string> {
  return {
    patrimonyNumber: asset.patrimonyNumber,
    internalCode: asset.internalCode,
    shortDescription: asset.shortDescription || asset.description,
    companyName: company.tradeName,
    branchName: asset.branchId,
    issuedAt: new Date().toLocaleDateString("pt-BR"),
    physicalLocation: asset.physicalLocation,
    responsible: asset.responsible ?? company.tradeName,
    createdAt: asset.createdAt ? new Date(asset.createdAt).toLocaleDateString("pt-BR") : new Date().toLocaleDateString("pt-BR")
  };
}

function LabelElementPreviewContent({ element, sampleValues, template }: { element: LabelElement; sampleValues: Record<LabelFieldId, string>; template: LabelTemplate }) {
  if (element.type === "logoImobi") {
    return <div className="flex h-full w-full items-center justify-center rounded bg-brand-700 text-[8px] font-bold text-white">I</div>;
  }

  if (element.type === "logoCompany") {
    return <div className="flex h-full w-full items-center justify-center rounded border border-slate-300 text-[8px] font-bold text-slate-700">LOGO</div>;
  }

  if (element.type === "code") {
    const hasAny = element.showQrCode || element.showBarcode || element.showNumber;
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-0.5 text-slate-700">
        {element.showQrCode ? <QrCode className="h-1/2 w-1/2 max-h-8 max-w-8" /> : null}
        {element.showBarcode ? <Barcode className="h-1/4 w-4/5 max-h-4" /> : null}
        {element.showNumber ? <span className="truncate text-[8px] font-semibold">{sampleValues.patrimonyNumber}</span> : null}
        {!hasAny ? <span className="text-[8px] text-slate-400">vazio</span> : null}
      </div>
    );
  }

  const value = element.field ? sampleValues[element.field] : "";
  return (
    <div
      className="flex h-full w-full items-center truncate px-0.5"
      style={{
        fontSize: `${((element.fontSize ?? 8) / template.widthMm) * 100}cqw`,
        color: element.color ?? "#111827",
        fontWeight: element.fontWeight ?? "400",
        justifyContent: element.textAlign === "center" ? "center" : element.textAlign === "right" ? "flex-end" : "flex-start",
        fontFamily: template.fontFamily
      }}
    >
      {value || <span className="text-slate-400">{labelFieldLabel(element.field)}</span>}
    </div>
  );
}

function LabelTemplatePreview({ template, asset, company, compact = false }: { template: LabelTemplate; asset: Asset; company: Company; compact?: boolean }) {
  const normalizedTemplate = normalizeLabelTemplate(template);
  const sampleValues = buildLabelSampleValues(asset, company);
  const elements = (normalizedTemplate.elements ?? []).filter((element) =>
    (element.type !== "logoImobi" || normalizedTemplate.showImobiLogo) && (element.type !== "logoCompany" || normalizedTemplate.showCompanyLogo)
  );

  return (
    <div className={compact ? "mx-auto w-full" : "mx-auto w-full max-w-sm"}>
      <div
        className="relative mx-auto w-full overflow-hidden border-2 border-ink bg-white shadow-sm"
        style={{ aspectRatio: `${normalizedTemplate.widthMm} / ${normalizedTemplate.heightMm}`, containerType: "inline-size" }}
      >
        {elements.map((element) => (
          <div
            key={element.id}
            className="absolute overflow-hidden"
            style={{
              left: `${(element.xMm / normalizedTemplate.widthMm) * 100}%`,
              top: `${(element.yMm / normalizedTemplate.heightMm) * 100}%`,
              width: `${(element.widthMm / normalizedTemplate.widthMm) * 100}%`,
              height: `${(element.heightMm / normalizedTemplate.heightMm) * 100}%`
            }}
          >
            <LabelElementPreviewContent element={element} sampleValues={sampleValues} template={normalizedTemplate} />
          </div>
        ))}
      </div>
    </div>
  );
}

function LabelCanvasEditor({
  template,
  asset,
  company,
  selectedElementId,
  onSelectElement,
  onUpdateElement
}: {
  template: LabelTemplate;
  asset: Asset;
  company: Company;
  selectedElementId: string | null;
  onSelectElement: (id: string | null) => void;
  onUpdateElement: (elementId: string, patch: Partial<LabelElement>) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sampleValues = buildLabelSampleValues(asset, company);
  const elements = (template.elements ?? []).filter((element) =>
    (element.type !== "logoImobi" || template.showImobiLogo) && (element.type !== "logoCompany" || template.showCompanyLogo)
  );

  function handleDragStart(event: React.PointerEvent, element: LabelElement) {
    event.preventDefault();
    event.stopPropagation();
    onSelectElement(element.id);
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const pxPerMmX = rect.width / template.widthMm;
    const pxPerMmY = rect.height / template.heightMm;
    const startClientX = event.clientX;
    const startClientY = event.clientY;
    const startXMm = element.xMm;
    const startYMm = element.yMm;

    function handleMove(moveEvent: PointerEvent) {
      const deltaXMm = (moveEvent.clientX - startClientX) / pxPerMmX;
      const deltaYMm = (moveEvent.clientY - startClientY) / pxPerMmY;
      const nextXMm = clampNumber(startXMm + deltaXMm, 0, template.widthMm - element.widthMm);
      const nextYMm = clampNumber(startYMm + deltaYMm, 0, template.heightMm - element.heightMm);
      onUpdateElement(element.id, { xMm: Math.round(nextXMm * 10) / 10, yMm: Math.round(nextYMm * 10) / 10 });
    }

    function handleUp() {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    }

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }

  function handleResizeStart(event: React.PointerEvent, element: LabelElement) {
    event.preventDefault();
    event.stopPropagation();
    onSelectElement(element.id);
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const rect = container.getBoundingClientRect();
    const pxPerMmX = rect.width / template.widthMm;
    const pxPerMmY = rect.height / template.heightMm;
    const startClientX = event.clientX;
    const startClientY = event.clientY;
    const startWidthMm = element.widthMm;
    const startHeightMm = element.heightMm;

    function handleMove(moveEvent: PointerEvent) {
      const deltaWidthMm = (moveEvent.clientX - startClientX) / pxPerMmX;
      const deltaHeightMm = (moveEvent.clientY - startClientY) / pxPerMmY;
      const nextWidthMm = clampNumber(startWidthMm + deltaWidthMm, 4, template.widthMm - element.xMm);
      const nextHeightMm = clampNumber(startHeightMm + deltaHeightMm, 3, template.heightMm - element.yMm);
      onUpdateElement(element.id, { widthMm: Math.round(nextWidthMm * 10) / 10, heightMm: Math.round(nextHeightMm * 10) / 10 });
    }

    function handleUp() {
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
    }

    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
  }

  return (
    <div className="mx-auto w-full max-w-xl rounded-md bg-slate-100 p-4">
      <div
        ref={containerRef}
        onPointerDown={() => onSelectElement(null)}
        className="relative mx-auto w-full overflow-hidden border-2 border-ink bg-white shadow-sm"
        style={{ aspectRatio: `${template.widthMm} / ${template.heightMm}`, containerType: "inline-size" }}
      >
        {elements.map((element) => (
          <div
            key={element.id}
            onPointerDown={(event) => handleDragStart(event, element)}
            className={`absolute cursor-move select-none overflow-hidden border ${selectedElementId === element.id ? "border-brand-500 bg-brand-50/60" : "border-dashed border-slate-300 hover:border-brand-300"}`}
            style={{
              left: `${(element.xMm / template.widthMm) * 100}%`,
              top: `${(element.yMm / template.heightMm) * 100}%`,
              width: `${(element.widthMm / template.widthMm) * 100}%`,
              height: `${(element.heightMm / template.heightMm) * 100}%`
            }}
          >
            <LabelElementPreviewContent element={element} sampleValues={sampleValues} template={template} />
            {selectedElementId === element.id ? (
              <div
                onPointerDown={(event) => handleResizeStart(event, element)}
                className="absolute -bottom-1 -right-1 h-3 w-3 cursor-se-resize rounded-sm border border-white bg-brand-600"
              />
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function LabelTemplatesPage({ accessContext, currentUser }: { accessContext: AccessContext; currentUser: User | null }) {
  const [templates, setTemplates] = useState<LabelTemplate[]>(seedLabelTemplates.map(normalizeLabelTemplate));
  const [draft, setDraft] = useState<LabelTemplate>(createBlankLabelTemplate());
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const canManage = accessContext.globalRole === "MASTER";
  const previewCompany = accessContext.selectedCompany ?? accessContext.scopedCompanies[0] ?? seedCompany;
  const previewAsset = accessContext.scopedAssets[0] ?? seedAssets[0];

  useEffect(() => subscribeGlobalLabelTemplates(
    (items) => setTemplates(items.length ? items.map(normalizeLabelTemplate) : seedLabelTemplates.map(normalizeLabelTemplate)),
    (message) => console.warn("Não foi possível carregar modelos de etiquetas.", message)
  ), []);

  function updateDraft(patch: Partial<LabelTemplate>) {
    setDraft((current) => normalizeLabelTemplate({ ...current, ...patch }));
  }

  function updateMargins(field: keyof NonNullable<LabelTemplate["marginsMm"]>, value: number) {
    setDraft((current) => {
      const margins = current.marginsMm ?? { top: current.marginMm, right: current.marginMm, bottom: current.marginMm, left: current.marginMm };
      const nextMargins = { ...margins, [field]: value };
      return { ...current, marginsMm: nextMargins, marginMm: Math.min(nextMargins.top, nextMargins.right, nextMargins.bottom, nextMargins.left) };
    });
  }

  function updateLogoMode(mode: "both" | "company" | "imobi" | "none") {
    updateDraft({
      showCompanyLogo: mode === "both" || mode === "company",
      showImobiLogo: mode === "both" || mode === "imobi"
    });
  }

  function updateElement(elementId: string, patch: Partial<LabelElement>) {
    setDraft((current) => ({
      ...current,
      elements: (current.elements ?? []).map((element) => (element.id === elementId ? { ...element, ...patch } : element))
    }));
  }

  function addFieldElement(field: LabelFieldId) {
    const id = crypto.randomUUID();
    setDraft((current) => ({
      ...current,
      elements: [
        ...(current.elements ?? []),
        {
          id,
          type: "field",
          field,
          xMm: Math.max(2, current.marginsMm?.left ?? current.marginMm),
          yMm: Math.max(2, current.marginsMm?.top ?? current.marginMm),
          widthMm: Math.max(10, current.widthMm * 0.5),
          heightMm: 5,
          fontSize: current.fontSize,
          color: "#111827",
          fontWeight: "400",
          textAlign: "left"
        }
      ]
    }));
    setSelectedElementId(id);
  }

  function addCodeElement() {
    const id = crypto.randomUUID();
    setDraft((current) => ({
      ...current,
      elements: [
        ...(current.elements ?? []),
        {
          id,
          type: "code",
          xMm: Math.max(2, current.widthMm - 16),
          yMm: Math.max(2, current.marginsMm?.top ?? current.marginMm),
          widthMm: 14,
          heightMm: 14,
          showQrCode: true,
          showBarcode: false,
          showNumber: false,
          codeSource: "assetUrl"
        }
      ]
    }));
    setSelectedElementId(id);
  }

  function removeSelectedElement() {
    if (!selectedElementId) {
      return;
    }

    setDraft((current) => ({ ...current, elements: (current.elements ?? []).filter((element) => element.id !== selectedElementId) }));
    setSelectedElementId(null);
  }

  async function handleSave() {
    if (!canManage || !currentUser) {
      setMessage("Somente MASTER pode salvar modelos globais.");
      return;
    }

    setIsSaving(true);
    setMessage("");
    try {
      const id = await saveGlobalLabelTemplate({ ...draft, active: true }, currentUser);
      setMessage(`Modelo global salvo: ${id}.`);
      setDraft((current) => ({ ...current, id }));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o modelo.");
    } finally {
      setIsSaving(false);
    }
  }

  const selectedElement = draft.elements?.find((element) => element.id === selectedElementId) ?? null;
  const placedFields = new Set((draft.elements ?? []).filter((element) => element.type === "field").map((element) => element.field));
  const availableFields = labelFieldOptions.filter((field) => !placedFields.has(field.id));

  return (
    <>
      <PageHeader
        title="Modelos globais de etiquetas"
        subtitle="MASTER cria modelos globais; todas as empresas podem usar, mas cada empresa escolhe o modelo na emissao."
        action={<PrimaryButton onClick={() => { setDraft(createBlankLabelTemplate()); setSelectedElementId(null); }}><LayoutTemplate size={16} /> Novo modelo</PrimaryButton>}
      />
      <div className="grid gap-4 p-4 xl:grid-cols-[300px_1fr] lg:p-6">
        <div className="space-y-3">
          {templates.map((template) => (
            <button key={template.id} type="button" onClick={() => { setDraft(normalizeLabelTemplate(template)); setSelectedElementId(null); }} className="w-full rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm hover:border-brand-300">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-ink">{template.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">{template.widthMm}x{template.heightMm}mm</p>
                </div>
                <StatusPill tone={template.active ? "green" : "neutral"}>{template.active ? "ativo" : "inativo"}</StatusPill>
              </div>
              <div className="mt-3"><LabelTemplatePreview template={template} asset={previewAsset} company={previewCompany} compact /></div>
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          {!canManage ? <p className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">Seu usuário pode visualizar os modelos, mas somente MASTER salva alterações globais.</p> : null}
          <div className="grid gap-3 md:grid-cols-3">
            <label className="text-sm font-medium text-slate-700 md:col-span-3">Nome<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={draft.name} onChange={(event) => updateDraft({ name: event.target.value })} /></label>
            <label className="text-sm font-medium text-slate-700">Largura mm<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={draft.widthMm} onChange={(event) => updateDraft({ widthMm: Number(event.target.value) })} /></label>
            <label className="text-sm font-medium text-slate-700">Altura mm<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={draft.heightMm} onChange={(event) => updateDraft({ heightMm: Number(event.target.value) })} /></label>
            <label className="text-sm font-medium text-slate-700">Densidade<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={draft.printDensity} onChange={(event) => updateDraft({ printDensity: event.target.value as LabelTemplate["printDensity"] })}><option value="baixa">Baixa</option><option value="normal">Normal</option><option value="alta">Alta</option></select></label>
            <label className="text-sm font-medium text-slate-700">Fonte<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={draft.fontFamily ?? "Arial"} onChange={(event) => updateDraft({ fontFamily: event.target.value as LabelTemplate["fontFamily"] })}><option>Arial</option><option>Roboto</option><option>Courier New</option><option>Times New Roman</option></select></label>
            <label className="text-sm font-medium text-slate-700">
              Logos na etiqueta
              <select
                className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2"
                value={draft.showCompanyLogo && draft.showImobiLogo ? "both" : draft.showCompanyLogo ? "company" : draft.showImobiLogo ? "imobi" : "none"}
                onChange={(event) => updateLogoMode(event.target.value as "both" | "company" | "imobi" | "none")}
              >
                <option value="both">Logo da empresa + logo IMOBI</option>
                <option value="company">Somente logo da empresa</option>
                <option value="imobi">Somente logo IMOBI</option>
                <option value="none">Nenhuma logo</option>
              </select>
            </label>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-4">
            {(["top", "right", "bottom", "left"] as const).map((field) => (
              <label key={field} className="text-sm font-medium text-slate-700">Margem {field}<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={draft.marginsMm?.[field] ?? draft.marginMm} onChange={(event) => updateMargins(field, Number(event.target.value))} /></label>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-200 pt-4">
            <p className="mr-2 text-sm font-semibold text-ink">Adicionar elemento:</p>
            <SecondaryButton onClick={addCodeElement}><QrCode size={16} /> QR/código</SecondaryButton>
            {availableFields.length ? (
              <select
                className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm"
                value=""
                onChange={(event) => {
                  if (event.target.value) {
                    addFieldElement(event.target.value as LabelFieldId);
                  }
                  event.target.value = "";
                }}
              >
                <option value="">+ Campo...</option>
                {availableFields.map((field) => <option key={field.id} value={field.id}>{field.label}</option>)}
              </select>
            ) : null}
          </div>

          <div className="mt-4">
            <p className="mb-2 text-sm text-slate-500">Arraste os elementos para posicioná-los na etiqueta. Use a alça no canto para redimensionar. Clique em um elemento para ajustar a formatação.</p>
            <LabelCanvasEditor template={draft} asset={previewAsset} company={previewCompany} selectedElementId={selectedElementId} onSelectElement={setSelectedElementId} onUpdateElement={updateElement} />
          </div>

          {selectedElement ? (
            <div className="mt-4 rounded-md border border-brand-200 bg-brand-50 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-ink">
                  {selectedElement.type === "code" ? "Código" : selectedElement.type === "field" ? labelFieldLabel(selectedElement.field) : selectedElement.type === "logoImobi" ? "Logo IMOBI" : "Logo da empresa"}
                </p>
                {selectedElement.type === "field" || selectedElement.type === "code" ? (
                  <button type="button" onClick={removeSelectedElement} className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-rose-600 hover:bg-rose-100" title="Remover elemento"><Trash2 size={16} /></button>
                ) : null}
              </div>
              <div className="mt-3 grid gap-3 md:grid-cols-4">
                <label className="text-sm font-medium text-slate-700">X (mm)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={selectedElement.xMm} onChange={(event) => updateElement(selectedElement.id, { xMm: Number(event.target.value) })} /></label>
                <label className="text-sm font-medium text-slate-700">Y (mm)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={selectedElement.yMm} onChange={(event) => updateElement(selectedElement.id, { yMm: Number(event.target.value) })} /></label>
                <label className="text-sm font-medium text-slate-700">Largura (mm)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={selectedElement.widthMm} onChange={(event) => updateElement(selectedElement.id, { widthMm: Number(event.target.value) })} /></label>
                <label className="text-sm font-medium text-slate-700">Altura (mm)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={selectedElement.heightMm} onChange={(event) => updateElement(selectedElement.id, { heightMm: Number(event.target.value) })} /></label>
              </div>

              {selectedElement.type === "field" || selectedElement.type === "code" ? (
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                  <label className="text-sm font-medium text-slate-700">Tamanho fonte<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" type="number" value={selectedElement.fontSize ?? 8} onChange={(event) => updateElement(selectedElement.id, { fontSize: Number(event.target.value) })} /></label>
                  <label className="text-sm font-medium text-slate-700">Cor<input className="focus-ring mt-1 h-10 w-full rounded-md border border-slate-300 px-2 py-1" type="color" value={selectedElement.color ?? "#111827"} onChange={(event) => updateElement(selectedElement.id, { color: event.target.value })} /></label>
                  <label className="text-sm font-medium text-slate-700">Peso<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedElement.fontWeight ?? "400"} onChange={(event) => updateElement(selectedElement.id, { fontWeight: event.target.value as "400" | "600" | "700" })}><option value="400">Normal</option><option value="600">Semibold</option><option value="700">Bold</option></select></label>
                  <label className="text-sm font-medium text-slate-700">Alinhamento<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedElement.textAlign ?? "left"} onChange={(event) => updateElement(selectedElement.id, { textAlign: event.target.value as "left" | "center" | "right" })}><option value="left">Esquerda</option><option value="center">Centro</option><option value="right">Direita</option></select></label>
                </div>
              ) : null}

              {selectedElement.type === "field" ? (
                <label className="mt-3 block text-sm font-medium text-slate-700">Campo<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedElement.field} onChange={(event) => updateElement(selectedElement.id, { field: event.target.value as LabelFieldId })}>{labelFieldOptions.map((field) => <option key={field.id} value={field.id}>{field.label}</option>)}</select></label>
              ) : null}

              {selectedElement.type === "code" ? (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap gap-4 text-sm text-slate-700">
                    <label className="flex items-center gap-2"><input type="checkbox" checked={selectedElement.showQrCode ?? false} onChange={(event) => updateElement(selectedElement.id, { showQrCode: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-brand-700" /> QR Code</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={selectedElement.showBarcode ?? false} onChange={(event) => updateElement(selectedElement.id, { showBarcode: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-brand-700" /> Código de barras</label>
                    <label className="flex items-center gap-2"><input type="checkbox" checked={selectedElement.showNumber ?? false} onChange={(event) => updateElement(selectedElement.id, { showNumber: event.target.checked })} className="h-4 w-4 rounded border-slate-300 text-brand-700" /> Número do bem</label>
                  </div>
                  <label className="block text-sm font-medium text-slate-700">Origem do código<select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={selectedElement.codeSource ?? "assetUrl"} onChange={(event) => updateElement(selectedElement.id, { codeSource: event.target.value as LabelTemplate["codeSource"] })}><option value="assetUrl">URL segura do bem</option><option value="patrimonyNumber">Patrimônio</option><option value="internalCode">Código interno</option></select></label>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-4 flex flex-wrap gap-2">
            <PrimaryButton onClick={() => void handleSave()} disabled={isSaving || !canManage}><Save size={16} /> {isSaving ? "Salvando..." : "Salvar modelo global"}</PrimaryButton>
          </div>
          {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
        </div>
      </div>
    </>
  );
}

function matchesLabelSearch(asset: Asset, search: string) {
  if (!search.trim()) {
    return true;
  }

  const term = search.trim().toLocaleLowerCase("pt-BR");
  return [asset.patrimonyNumber, asset.internalCode, asset.description, asset.shortDescription, asset.physicalLocation]
    .some((value) => (value ?? "").toLocaleLowerCase("pt-BR").includes(term));
}

function assetNeedsNewLabel(asset: Asset) {
  if (!asset.labelPrintedAt) {
    return true;
  }

  if (asset.labelLostAt && asset.labelLostAt > asset.labelPrintedAt) {
    return true;
  }

  return false;
}

function LabelsManagementPage({ accessContext, currentUser, preselectedAsset }: { accessContext: AccessContext; currentUser: User | null; preselectedAsset?: Asset | null }) {
  const fallbackCompany = preselectedAsset
    ? accessContext.scopedCompanies.find((item) => item.id === preselectedAsset.companyId) ?? accessContext.selectedCompany ?? seedCompany
    : accessContext.selectedCompany ?? accessContext.scopedCompanies[0] ?? seedCompany;
  const companySettings: CompanyOperationalSettings | undefined = "settings" in fallbackCompany ? (fallbackCompany as CompanyRecord).settings : undefined;
  const [templates, setTemplates] = useState<LabelTemplate[]>(seedLabelTemplates.map(normalizeLabelTemplate));
  const [templateId, setTemplateId] = useState<string>(companySettings?.defaultLabelTemplateId || seedLabelTemplates[1].id);
  const [search, setSearch] = useState("");
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>(preselectedAsset ? [preselectedAsset.id] : []);
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const template = templates.find((item) => item.id === templateId) ?? templates[0] ?? normalizeLabelTemplate(seedLabelTemplates[0]);

  useEffect(() => subscribeGlobalLabelTemplates(
    (items) => {
      const nextTemplates = items.length ? items.map(normalizeLabelTemplate).filter((item) => item.active) : seedLabelTemplates.map(normalizeLabelTemplate);
      setTemplates(nextTemplates);
      const companyDefaultTemplateId = companySettings?.defaultLabelTemplateId;
      setTemplateId((current) => {
        if (companyDefaultTemplateId && nextTemplates.some((item) => item.id === companyDefaultTemplateId)) {
          return companyDefaultTemplateId;
        }

        return nextTemplates.some((item) => item.id === current) ? current : nextTemplates[0]?.id ?? current;
      });
    },
    (message) => console.warn("Não foi possível carregar modelos de etiquetas.", message)
  ), [companySettings?.defaultLabelTemplateId]);

  const relevantAssets = accessContext.scopedAssets.filter((item) => item.status === "ativo");
  const searchedAssets = relevantAssets.filter((item) => matchesLabelSearch(item, search));
  const pendingAssets = searchedAssets.filter(assetNeedsNewLabel);
  const issuedAssets = searchedAssets.filter((item) => !assetNeedsNewLabel(item));
  const selectedAssets = accessContext.scopedAssets.filter((item) => selectedAssetIds.includes(item.id));
  const previewAsset = selectedAssets[0] ?? searchedAssets[0] ?? seedAssets[0];
  const previewCompany = accessContext.scopedCompanies.find((item) => item.id === previewAsset.companyId) ?? fallbackCompany;

  function toggleAssetSelection(assetId: string) {
    setSelectedAssetIds((current) => (current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId]));
  }

  function selectAllPending() {
    setSelectedAssetIds(pendingAssets.map((item) => item.id));
  }

  async function runLabelAction(action: "print" | "pdf" | "png") {
    if (!selectedAssets.length) {
      setMessage("Selecione ao menos um bem.");
      return;
    }

    setIsProcessing(true);
    setMessage("Gerando etiqueta(s)...");
    try {
      const issuedByCompany = new Map<string, string[]>();
      for (const target of selectedAssets) {
        const targetCompany = accessContext.scopedCompanies.find((item) => item.id === target.companyId) ?? fallbackCompany;
        const options = { asset: target, company: targetCompany, template, issuedAt: new Date() };
        if (action === "print") {
          await printAssetLabel(options);
        }
        if (action === "pdf") {
          await downloadAssetLabelPdf(options);
        }
        if (action === "png") {
          await downloadAssetLabelPng(options);
        }
        const list = issuedByCompany.get(target.companyId) ?? [];
        list.push(target.id);
        issuedByCompany.set(target.companyId, list);
      }

      if (currentUser) {
        for (const [companyId, assetIds] of issuedByCompany) {
          await markAssetsLabelIssued(companyId, assetIds, currentUser);
        }
      }

      setMessage(`${selectedAssets.length} etiqueta(s) gerada(s).`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível gerar a etiqueta.");
    } finally {
      setIsProcessing(false);
    }
  }

  async function handleMarkLost(item: Asset) {
    if (!currentUser) {
      return;
    }

    try {
      await markAssetLabelLost(item.companyId, item.id, currentUser);
      setMessage(`Etiqueta de ${item.patrimonyNumber} marcada como perdida.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o status da etiqueta.");
    }
  }

  async function handleBluetoothConnect() {
    const bluetooth = (navigator as Navigator & { bluetooth?: { requestDevice: (options: { acceptAllDevices: boolean; optionalServices?: string[] }) => Promise<unknown> } }).bluetooth;
    if (!bluetooth) {
      setMessage("Web Bluetooth nao esta disponivel neste navegador. No iPhone, use PDF/PNG ou o app oficial da impressora.");
      return;
    }

    try {
      await bluetooth.requestDevice({ acceptAllDevices: true, optionalServices: [] });
      setMessage("Impressora selecionada. Envio direto sera implementado conforme protocolo/SDK do modelo.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Conexao Bluetooth cancelada.");
    }
  }

  return (
    <>
      <PageHeader title="Gestão de etiquetas" subtitle="Veja quais bens já têm etiqueta, gere novas para quem não tem ou perdeu, e reimprima quando precisar." />
      <div className="grid gap-4 p-4 lg:grid-cols-[340px_1fr] lg:p-6">
        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <LabelTemplatePreview template={template} asset={previewAsset} company={previewCompany} />
            <label className="mt-3 block text-sm font-medium text-slate-700">
              Modelo global
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={templateId} onChange={(event) => setTemplateId(event.target.value)}>
                {templates.map((item) => <option key={item.id} value={item.id}>{item.name} - {item.widthMm}x{item.heightMm}mm</option>)}
              </select>
            </label>
            <div className="mt-3 flex flex-wrap gap-2">
              <PrimaryButton onClick={() => void runLabelAction("print")} disabled={isProcessing || !selectedAssetIds.length}><Printer size={16} /> Imprimir</PrimaryButton>
              <SecondaryButton onClick={() => void runLabelAction("pdf")} disabled={isProcessing || !selectedAssetIds.length}><Download size={16} /> PDF</SecondaryButton>
              <SecondaryButton onClick={() => void runLabelAction("png")} disabled={isProcessing || !selectedAssetIds.length}><Download size={16} /> PNG</SecondaryButton>
              <SecondaryButton onClick={() => void handleBluetoothConnect()}><Bluetooth size={16} /> BT</SecondaryButton>
            </div>
            <p className="mt-2 text-xs text-slate-500">{selectedAssetIds.length} bem(ns) selecionado(s).</p>
            {message ? <p className="mt-3 text-sm text-slate-600">{message}</p> : null}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <input className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="Buscar por patrimônio, descrição ou local..." value={search} onChange={(event) => setSearch(event.target.value)} />
          </div>

          <div className="rounded-lg border border-amber-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-amber-100 bg-amber-50 px-4 py-3">
              <h2 className="font-semibold text-amber-900">Sem etiqueta ou perdida ({pendingAssets.length})</h2>
              {pendingAssets.length ? <button type="button" onClick={selectAllPending} className="text-xs font-medium text-amber-800 underline">Selecionar todos</button> : null}
            </div>
            <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
              {pendingAssets.length ? pendingAssets.map((item) => (
                <label key={item.id} className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={selectedAssetIds.includes(item.id)} onChange={() => toggleAssetSelection(item.id)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{item.patrimonyNumber} - {item.shortDescription || item.description}</p>
                    <p className="truncate text-xs text-slate-500">{item.physicalLocation}{item.labelLostAt ? " - etiqueta perdida" : ""}</p>
                  </div>
                </label>
              )) : <p className="px-4 py-6 text-center text-sm text-slate-500">Todos os bens ativos já têm etiqueta emitida.</p>}
            </div>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-100 bg-slate-50 px-4 py-3">
              <h2 className="font-semibold text-ink">Com etiqueta emitida ({issuedAssets.length})</h2>
            </div>
            <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
              {issuedAssets.length ? issuedAssets.map((item) => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-slate-50">
                  <input type="checkbox" checked={selectedAssetIds.includes(item.id)} onChange={() => toggleAssetSelection(item.id)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-ink">{item.patrimonyNumber} - {item.shortDescription || item.description}</p>
                    <p className="truncate text-xs text-slate-500">Emitida em {item.labelPrintedAt ? new Date(item.labelPrintedAt).toLocaleDateString("pt-BR") : "-"}</p>
                  </div>
                  <button type="button" onClick={() => void handleMarkLost(item)} className="shrink-0 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100">Marcar como perdida</button>
                </div>
              )) : <p className="px-4 py-6 text-center text-sm text-slate-500">Nenhum bem com etiqueta emitida ainda.</p>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function generatedContractStatusLabel(status: GeneratedContractStatus) {
  return status === "assinado" ? "Assinado" : "Gerado";
}

function ContractTemplatesPage({
  currentUser,
  companies,
  serviceCatalog,
  imobiLogoUrl
}: {
  currentUser: User | null;
  companies: CompanyRecord[];
  serviceCatalog: ServiceCatalogItem[];
  imobiLogoUrl: string;
}) {
  const [tab, setTab] = useState<"templates" | "generated">("templates");
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [generatedContracts, setGeneratedContracts] = useState<GeneratedContract[]>([]);
  const [editingTemplate, setEditingTemplate] = useState<ContractTemplate | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [generateTemplate, setGenerateTemplate] = useState<ContractTemplate | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => subscribeContractTemplates(
    setTemplates,
    (errorMessage) => console.warn("Não foi possível carregar modelos de contrato.", errorMessage)
  ), []);

  useEffect(() => subscribeGeneratedContracts(
    setGeneratedContracts,
    (errorMessage) => console.warn("Não foi possível carregar contratos gerados.", errorMessage)
  ), []);

  async function handleToggleActive(template: ContractTemplate) {
    if (!currentUser) {
      return;
    }

    try {
      await setContractTemplateActive(template.id, !template.active, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o modelo.");
    }
  }

  async function handleUploadSigned(contractId: string, file: File | null) {
    if (!currentUser || !file) {
      return;
    }

    try {
      await uploadSignedContract(contractId, file, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível enviar o contrato assinado.");
    }
  }

  return (
    <>
      <PageHeader
        title="Contratos"
        subtitle="Modelos de contrato e contratos já gerados para as empresas que usam o IMOBI."
        action={tab === "templates" ? <PrimaryButton onClick={() => { setEditingTemplate(null); setIsFormOpen(true); }}><Plus size={16} /> Novo modelo</PrimaryButton> : undefined}
      />
      <div className="space-y-4 p-4 lg:p-6">
        <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
          {[
            { id: "templates", label: `Modelos (${templates.length})` },
            { id: "generated", label: `Contratos gerados (${generatedContracts.length})` }
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id as "templates" | "generated")}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${tab === item.id ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {item.label}
            </button>
          ))}
        </div>
        {message ? <p className="text-sm text-slate-600">{message}</p> : null}

        {tab === "templates" ? (
          <div className="space-y-2">
            {templates.map((template) => (
              <div key={template.id} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3 shadow-sm">
                <div className="min-w-0">
                  <p className="font-semibold text-ink">{template.name}</p>
                  <p className="mt-1 truncate text-xs text-slate-500">{template.documentTitle}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusPill tone={template.active ? "green" : "neutral"}>{template.active ? "Ativo" : "Inativo"}</StatusPill>
                  <SecondaryButton onClick={() => setGenerateTemplate(template)}><FileDown size={16} /> Gerar contrato</SecondaryButton>
                  <SecondaryButton onClick={() => { setEditingTemplate(template); setIsFormOpen(true); }}><Edit3 size={16} /> Editar</SecondaryButton>
                  <SecondaryButton onClick={() => void handleToggleActive(template)}>{template.active ? "Desativar" : "Ativar"}</SecondaryButton>
                </div>
              </div>
            ))}
            {!templates.length ? <EmptyState title="Nenhum modelo de contrato" description="Crie um modelo padrão para gerar contratos em PDF para as empresas que usam o IMOBI." /> : null}
          </div>
        ) : (
          <div className="space-y-2">
            {generatedContracts.map((contract) => (
              <div key={contract.id} className="rounded-md border border-slate-200 bg-white p-3 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">{contract.companyName}</p>
                    <p className="mt-0.5 truncate text-xs text-slate-500">{contract.documentTitle} · {contract.templateName}</p>
                  </div>
                  <StatusPill tone={contract.status === "assinado" ? "green" : "neutral"}>{generatedContractStatusLabel(contract.status)}</StatusPill>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Gerado em {formatAuditDate(contract.createdAt)} por {contract.generatedByName}
                  {contract.signedAt ? ` · Assinado em ${formatAuditDate(contract.signedAt)}` : ""}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Representante: {contract.representativeName}{contract.representativeCpf ? ` (CPF ${contract.representativeCpf})` : ""}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <SecondaryButton onClick={() => window.open(contract.pdfUrl, "_blank", "noopener,noreferrer")}><ExternalLink size={16} /> Abrir PDF</SecondaryButton>
                  {contract.signedFileUrl ? (
                    <SecondaryButton onClick={() => window.open(contract.signedFileUrl, "_blank", "noopener,noreferrer")}><CheckCircle2 size={16} /> Ver assinado</SecondaryButton>
                  ) : null}
                  <label className="focus-ring inline-flex cursor-pointer items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                    <Upload size={16} /> {contract.status === "assinado" ? "Substituir assinado" : "Enviar assinado"}
                    <input type="file" accept="application/pdf,image/*" className="hidden" onChange={(event) => void handleUploadSigned(contract.id, event.target.files?.[0] ?? null)} />
                  </label>
                </div>
              </div>
            ))}
            {!generatedContracts.length ? <EmptyState title="Nenhum contrato gerado" description="Contratos gerados a partir dos modelos aparecem aqui, com data de geração e assinatura." /> : null}
          </div>
        )}
      </div>
      {isFormOpen ? (
        <ContractTemplateFormModal
          template={editingTemplate}
          currentUser={currentUser}
          onClose={() => setIsFormOpen(false)}
        />
      ) : null}
      {generateTemplate ? (
        <GenerateContractModal
          template={generateTemplate}
          companies={companies}
          serviceCatalog={serviceCatalog}
          currentUser={currentUser}
          imobiLogoUrl={imobiLogoUrl}
          onClose={() => setGenerateTemplate(null)}
        />
      ) : null}
    </>
  );
}

function ContractTemplateFormModal({
  template,
  currentUser,
  onClose
}: {
  template: ContractTemplate | null;
  currentUser: User | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(template?.name ?? "");
  const [documentTitle, setDocumentTitle] = useState(template?.documentTitle ?? "Contrato de Prestação de Serviços");
  const [showImobiLogo, setShowImobiLogo] = useState(template?.showImobiLogo ?? true);
  const [showCompanyLogo, setShowCompanyLogo] = useState(template?.showCompanyLogo ?? true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [selectionToolbar, setSelectionToolbar] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = template?.bodyHtml?.trim() || "<p></p>";
    }
    // Only ever set the initial content - contentEditable owns its DOM after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateSelectionToolbarPosition() {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      setSelectionToolbar(null);
      return;
    }
    const anchorNode = selection.anchorNode;
    if (!editorRef.current || !anchorNode || !editorRef.current.contains(anchorNode)) {
      setSelectionToolbar(null);
      return;
    }
    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      setSelectionToolbar(null);
      return;
    }
    savedRangeRef.current = range.cloneRange();
    setSelectionToolbar({ top: rect.top, left: rect.left + rect.width / 2 });
  }

  useEffect(() => {
    document.addEventListener("selectionchange", updateSelectionToolbarPosition);
    window.addEventListener("scroll", updateSelectionToolbarPosition, true);
    return () => {
      document.removeEventListener("selectionchange", updateSelectionToolbarPosition);
      window.removeEventListener("scroll", updateSelectionToolbarPosition, true);
    };
  }, []);

  function saveSelection() {
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
      savedRangeRef.current = selection.getRangeAt(0).cloneRange();
    }
  }

  function execEditorCommand(command: string, value?: string) {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (savedRangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
    document.execCommand(command, false, value);
    saveSelection();
  }

  function applyFontSize(pt: number) {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (savedRangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }
    const range = selection.getRangeAt(0);
    const span = document.createElement("span");
    span.style.fontSize = `${pt}pt`;
    span.appendChild(range.extractContents());
    range.insertNode(span);
    const newRange = document.createRange();
    newRange.selectNodeContents(span);
    selection.removeAllRanges();
    selection.addRange(newRange);
    saveSelection();
    updateSelectionToolbarPosition();
  }

  function handleEditorFocus() {
    document.execCommand("defaultParagraphSeparator", false, "p");
    // Forces bold/color to come out as inline styles on <span> instead of
    // browser-dependent tags (<b>, legacy <font color>), so the PDF export's
    // HTML parser only has one shape to understand.
    document.execCommand("styleWithCSS", false, "true");
  }

  function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain");
    document.execCommand("insertText", false, text);
  }

  async function handleSave() {
    if (!currentUser) {
      return;
    }

    if (!name.trim()) {
      setMessage("Informe o nome do modelo.");
      return;
    }

    const bodyHtml = editorRef.current?.innerHTML ?? "";
    if (!editorRef.current?.textContent?.trim()) {
      setMessage("Escreva o texto do contrato.");
      return;
    }

    setIsSaving(true);
    try {
      await saveContractTemplate({
        id: template?.id ?? "",
        name: name.trim(),
        documentTitle: documentTitle.trim() || "Contrato de Prestação de Serviços",
        bodyHtml,
        showImobiLogo,
        showCompanyLogo,
        active: template?.active ?? true,
        createdAt: template?.createdAt ?? "",
        createdBy: template?.createdBy ?? ""
      }, currentUser);
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o modelo.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <h2 className="font-semibold text-ink">{template ? "Editar modelo de contrato" : "Novo modelo de contrato"}</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700">
            Nome do modelo
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex.: Contrato padrão de adesão" />
          </label>
          <label className="text-sm text-slate-700">
            Título do documento (topo do PDF)
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={documentTitle} onChange={(event) => setDocumentTitle(event.target.value)} />
          </label>
        </div>
        <div className="mt-3 flex flex-wrap gap-4 text-sm text-slate-700">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={showImobiLogo} onChange={(event) => setShowImobiLogo(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
            Logo IMOBI no cabeçalho
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={showCompanyLogo} onChange={(event) => setShowCompanyLogo(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
            Logo da empresa no cabeçalho
          </label>
        </div>

        <div className="mt-4">
          <p className="text-sm font-semibold text-ink">Texto do contrato</p>
          <div className="mt-2 flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-slate-300 bg-slate-50 p-1.5">
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => execEditorCommand("bold")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200"
              title="Negrito (selecione o texto)"
            >
              <Bold size={16} />
            </button>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => execEditorCommand("underline")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200"
              title="Sublinhado (selecione o texto)"
            >
              <Underline size={16} />
            </button>
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => execEditorCommand("insertUnorderedList")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-600 hover:bg-slate-200"
              title="Lista com marcadores"
            >
              <List size={16} />
            </button>
            <input
              type="color"
              onMouseDown={saveSelection}
              onChange={(event) => execEditorCommand("foreColor", event.target.value)}
              className="h-8 w-9 cursor-pointer rounded-md border border-slate-300 bg-white"
              title="Cor do texto selecionado"
            />
            <button
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => execEditorCommand("removeFormat")}
              className="ml-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-200"
              title="Remover formatação do texto selecionado"
            >
              Limpar formatação
            </button>
          </div>
          <div className="relative">
            <div
              ref={editorRef}
              contentEditable
              suppressContentEditableWarning
              onFocus={handleEditorFocus}
              onPaste={handlePaste}
              onMouseUp={saveSelection}
              onKeyUp={saveSelection}
              className="focus-ring min-h-64 w-full rounded-b-md border border-slate-300 bg-white px-3 py-2 text-sm leading-relaxed [&_ul]:list-disc [&_ul]:pl-5"
            />
            {selectionToolbar ? (
              <div
                className="fixed z-[110] flex -translate-x-1/2 items-center gap-1 rounded-md border border-slate-300 bg-white p-1 shadow-panel"
                style={{ top: Math.max(8, selectionToolbar.top - 44), left: selectionToolbar.left }}
              >
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => execEditorCommand("bold")}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-200"
                  title="Negrito"
                >
                  <Bold size={14} />
                </button>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => execEditorCommand("underline")}
                  className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-600 hover:bg-slate-200"
                  title="Sublinhado"
                >
                  <Underline size={14} />
                </button>
                <input
                  type="color"
                  onMouseDown={saveSelection}
                  onChange={(event) => execEditorCommand("foreColor", event.target.value)}
                  className="h-7 w-8 cursor-pointer rounded border border-slate-300 bg-white"
                  title="Cor do texto"
                />
                <select
                  defaultValue=""
                  onMouseDown={saveSelection}
                  onChange={(event) => {
                    const pt = Number(event.target.value);
                    if (pt) {
                      applyFontSize(pt);
                    }
                    event.target.value = "";
                  }}
                  className="h-7 rounded border border-slate-300 bg-white text-xs text-slate-600"
                  title="Tamanho da fonte"
                >
                  <option value="" disabled>Tamanho</option>
                  <option value="9">9pt</option>
                  <option value="10.5">10.5pt</option>
                  <option value="12">12pt</option>
                  <option value="14">14pt</option>
                  <option value="16">16pt</option>
                  <option value="18">18pt</option>
                  <option value="24">24pt</option>
                </select>
              </div>
            ) : null}
          </div>
          <p className="mt-1 text-xs text-slate-500">Selecione um trecho de texto: use a barra fixa acima ou o atalho que aparece junto à seleção para aplicar negrito, sublinhado, cor ou tamanho.</p>
        </div>

        <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          <p className="font-semibold text-ink">Campos disponíveis (clique para inserir no texto)</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {contractMergeFieldHelp.map((field) => (
              <button
                key={field.token}
                type="button"
                title={field.description}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => execEditorCommand("insertText", field.token)}
                className="focus-ring rounded-md border border-slate-300 bg-white px-2 py-1 font-mono text-[11px] text-brand-700 hover:bg-brand-50"
              >
                {field.token}
              </button>
            ))}
          </div>
        </div>
        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={isSaving}>Salvar modelo</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function GenerateContractModal({
  template,
  companies,
  serviceCatalog,
  currentUser,
  imobiLogoUrl,
  onClose
}: {
  template: ContractTemplate;
  companies: CompanyRecord[];
  serviceCatalog: ServiceCatalogItem[];
  currentUser: User | null;
  imobiLogoUrl: string;
  onClose: () => void;
}) {
  const [companyId, setCompanyId] = useState("");
  const [search, setSearch] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [representativeCpf, setRepresentativeCpf] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState("");

  const filteredCompanies = companies.filter((company) => matchesCompanySearch(company, search));

  async function handleGenerate() {
    const company = companies.find((item) => item.id === companyId);
    if (!company || !currentUser) {
      setMessage("Selecione uma empresa.");
      return;
    }

    if (!representativeName.trim()) {
      setMessage("Informe o nome do representante que vai assinar pela contratante.");
      return;
    }

    setIsGenerating(true);
    try {
      await generateAndSaveContract({
        company,
        template,
        generatedBy: currentUser.displayName ?? currentUser.email ?? "Usuário",
        representativeName,
        representativeCpf,
        serviceCatalog,
        imobiLogoUrl
      }, currentUser);
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível gerar o contrato.");
    } finally {
      setIsGenerating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <div>
            <h2 className="font-semibold text-ink">Gerar contrato</h2>
            <p className="mt-1 text-sm text-slate-500">Modelo: {template.name}</p>
          </div>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <label className="mt-4 block text-sm text-slate-700">
          Empresa
          <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" placeholder="Buscar por nome ou CNPJ" value={search} onChange={(event) => setSearch(event.target.value)} />
        </label>
        <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
          {filteredCompanies.map((company) => (
            <button
              key={company.id}
              type="button"
              onClick={() => setCompanyId(company.id)}
              className={`w-full rounded-md border p-2 text-left text-sm ${companyId === company.id ? "border-brand-300 bg-brand-50" : "border-slate-200 hover:bg-slate-50"}`}
            >
              <p className="font-medium text-ink">{company.tradeName}</p>
              <p className="text-xs text-slate-500">{company.cnpj}</p>
            </button>
          ))}
          {!filteredCompanies.length ? <p className="text-sm text-slate-500">Nenhuma empresa encontrada.</p> : null}
        </div>

        <div className="mt-3 grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700">
            Nome do representante
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={representativeName} onChange={(event) => setRepresentativeName(event.target.value)} placeholder="Quem vai assinar pela contratante" />
          </label>
          <label className="text-sm text-slate-700">
            CPF do representante (opcional)
            <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" inputMode="numeric" maxLength={14} value={representativeCpf} onChange={(event) => setRepresentativeCpf(formatCpfInput(event.target.value))} placeholder="000.000.000-00" />
          </label>
        </div>

        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleGenerate()} disabled={!companyId || !representativeName.trim() || isGenerating}><FileDown size={16} /> {isGenerating ? "Gerando..." : "Gerar PDF"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function SimpleList({ title, items, action }: { title: string; items: string[]; action: string }) {
  return (
    <>
      <PageHeader title={title} action={<PrimaryButton>{action}</PrimaryButton>} />
      <div className="p-4 lg:p-6">
        {items.length ? (
          <div className="grid gap-3">
            {items.map((item) => (
              <div key={item} className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <span className="font-medium text-ink">{item}</span>
                <Bell className="text-slate-400" size={16} />
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="Nenhum registro" description="A estrutura da tela já está pronta para receber dados reais do Firebase." />
        )}
      </div>
    </>
  );
}

const trainingHelpViewOptions: Array<{ id: ViewId; label: string }> = [
  { id: "dashboard", label: "Dashboard" },
  { id: "assets", label: "Bens (lista)" },
  { id: "asset-form", label: "Cadastro/edição de bem" },
  { id: "asset-detail", label: "Detalhe do bem" },
  { id: "inventory", label: "Balanço patrimonial" },
  { id: "mobile-scan", label: "Leitura de QR/código" },
  { id: "label-print", label: "Gestão de etiquetas" },
  { id: "label-templates", label: "Modelos de etiqueta" },
  { id: "reports", label: "Relatórios" },
  { id: "audit", label: "Auditoria" },
  { id: "external-services", label: "Serviços externos" },
  { id: "responsibles", label: "Responsáveis" },
  { id: "company-transfers", label: "Transferências entre empresas" },
  { id: "depreciation", label: "Tipos e depreciação" },
  { id: "companies", label: "Empresas" },
  { id: "users", label: "Usuários" },
  { id: "settings", label: "Configurações" }
];

/** Screens that aren't in trainingHelpViewOptions, for the support ticket context. */
const supportViewLabels: Partial<Record<ViewId, string>> = {
  support: "Suporte",
  training: "Treinamento",
  "service-request": "Serviços",
  "commercial-panel": "Faturamento"
};

function trainingArticleStatusLabel(status: TrainingArticleStatus) {
  return status === "publicado" ? "Publicado" : "Rascunho";
}

function SortableRow({ id, children }: { id: string; children: (handle: { attributes: DraggableAttributes; listeners: DraggableSyntheticListeners }) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1
  };
  return <div ref={setNodeRef} style={style}>{children({ attributes, listeners })}</div>;
}

function findRelevantTrainingArticles(queryText: string, articles: TrainingArticle[]): TrainingArticle[] {
  const words = queryText.toLocaleLowerCase("pt-BR").split(/\W+/).filter((word) => word.length >= 3);
  if (!words.length) {
    return [];
  }

  return articles
    .map((article) => {
      const title = article.title.toLocaleLowerCase("pt-BR");
      const summary = (article.summary ?? "").toLocaleLowerCase("pt-BR");
      const body = article.body.toLocaleLowerCase("pt-BR");
      const score = words.reduce((total, word) => {
        let wordScore = 0;
        if (title.includes(word)) wordScore += 3;
        if (summary.includes(word)) wordScore += 2;
        if (body.includes(word)) wordScore += 1;
        return total + wordScore;
      }, 0);
      return { article, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 3)
    .map((entry) => entry.article);
}

function findRelevantKnowledgeEntries(queryText: string, entries: SupportKnowledgeEntry[]): SupportKnowledgeEntry[] {
  const words = queryText.toLocaleLowerCase("pt-BR").split(/\W+/).filter((word) => word.length >= 3);
  if (!words.length) {
    return [];
  }

  return entries
    .filter((entry) => entry.active)
    .map((entry) => {
      const question = entry.question.toLocaleLowerCase("pt-BR");
      const keywords = entry.keywords.join(" ").toLocaleLowerCase("pt-BR");
      const answer = entry.answer.toLocaleLowerCase("pt-BR");
      const score = words.reduce((total, word) => {
        let wordScore = 0;
        if (question.includes(word)) wordScore += 3;
        if (keywords.includes(word)) wordScore += 3;
        if (answer.includes(word)) wordScore += 1;
        return total + wordScore;
      }, 0);
      return { entry, score };
    })
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, 2)
    .map((item) => item.entry);
}

function supportTicketTypeLabel(type: SupportTicketType) {
  const labels: Record<SupportTicketType, string> = { bug: "Bug", duvida: "Dúvida", solicitacao: "Solicitação" };
  return labels[type] ?? type;
}

function supportTicketStatusLabel(status: SupportTicketStatus) {
  const labels: Record<SupportTicketStatus, string> = {
    aberto: "Aberto",
    em_atendimento: "Em atendimento",
    resolvido: "Resolvido",
    cancelado: "Cancelado"
  };
  return labels[status] ?? status;
}

function supportTicketStatusTone(status: SupportTicketStatus): "green" | "yellow" | "red" | "neutral" {
  if (status === "resolvido") return "green";
  if (status === "cancelado") return "red";
  if (status === "em_atendimento") return "yellow";
  return "neutral";
}

const supportRatingLabels = ["", "Muito ruim", "Ruim", "Regular", "Bom", "Excelente"];

function StarRatingDisplay({ rating, size = 14 }: { rating: number; size?: number }) {
  return (
    <span className="inline-flex items-center gap-0.5" title={`${rating} de 5 - ${supportRatingLabels[rating] ?? ""}`}>
      {[1, 2, 3, 4, 5].map((value) => (
        <Star key={value} size={size} className={value <= rating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
      ))}
    </span>
  );
}

/**
 * Opens a training article on top of whatever is on screen (the assistant
 * conversation or the support form), so closing it returns right to where the
 * user was and they can say whether it solved the problem.
 */
function TrainingArticleReaderModal({
  article,
  onClose,
  onFeedback
}: {
  article: TrainingArticle;
  onClose: () => void;
  onFeedback?: (resolved: boolean) => void;
}) {
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-stretch justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className="flex w-full max-w-3xl flex-col bg-white shadow-panel sm:max-h-[90vh] sm:rounded-lg sm:border sm:border-slate-200" onClick={(event) => event.stopPropagation()}>
        <div className="flex items-start gap-2 border-b border-slate-200 p-3">
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 shrink-0 items-center gap-1 rounded-md px-2 text-sm font-medium text-brand-700 hover:bg-brand-50">
            <ChevronLeft size={16} /> Voltar
          </button>
          <div className="min-w-0 flex-1 pt-1.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Treinamento</p>
            <h2 className="font-semibold text-ink">{article.title}</h2>
          </div>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {article.summary ? <p className="mb-4 text-sm text-slate-600">{article.summary}</p> : null}
          <TrainingArticleContent html={article.bodyHtml} />
        </div>
        {onFeedback ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200 bg-slate-50 p-3 sm:rounded-b-lg">
            <p className="text-sm font-medium text-ink">Isso resolveu sua dúvida?</p>
            <div className="flex flex-wrap gap-2">
              <SecondaryButton onClick={() => onFeedback(false)}>Não resolveu</SecondaryButton>
              <PrimaryButton onClick={() => onFeedback(true)}><Check size={16} /> Sim, resolveu</PrimaryButton>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SupportTicketChatModal({
  ticket,
  viewerRole,
  currentUser,
  onClose,
  onDelete
}: {
  ticket: SupportTicket;
  viewerRole: SupportMessageAuthorRole;
  currentUser: User | null;
  onClose: () => void;
  onDelete?: () => void;
}) {
  const [messages, setMessages] = useState<SupportTicketMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [ratingComment, setRatingComment] = useState("");
  const [isRating, setIsRating] = useState(false);
  const [error, setError] = useState("");
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isOpen = isSupportTicketOpen(ticket);
  const isUnread = isSupportTicketUnread(ticket, viewerRole);

  useEffect(() => subscribeSupportTicketMessages(ticket.id, setMessages, setError), [ticket.id]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages.length]);

  useEffect(() => {
    if (isUnread && currentUser) {
      markSupportTicketRead(ticket.id, viewerRole, currentUser).catch(() => undefined);
    }
  }, [currentUser, isUnread, ticket.id, viewerRole]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape" && viewerIndex === null) {
        onClose();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, viewerIndex]);

  async function handleSend() {
    if (!currentUser || !draft.trim()) {
      return;
    }
    setIsSending(true);
    setError("");
    try {
      await sendSupportTicketMessage(ticket, draft, viewerRole, currentUser);
      setDraft("");
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Não foi possível enviar a mensagem.");
    } finally {
      setIsSending(false);
    }
  }

  async function handleStatus(status: SupportTicketStatus) {
    if (!currentUser) {
      return;
    }
    if (status === "resolvido" && !window.confirm("Finalizar este chamado? A conversa será encerrada e o usuário poderá avaliar o atendimento.")) {
      return;
    }
    if (status === "cancelado" && !window.confirm("Cancelar este chamado? A conversa será encerrada.")) {
      return;
    }
    setIsUpdatingStatus(true);
    setError("");
    try {
      if (status === "em_atendimento" && ticket.status === "aberto") {
        await claimSupportTicket(ticket.id, currentUser);
      } else {
        await updateSupportTicketStatus(ticket.id, status, currentUser, ticket.resolutionNotes);
      }
    } catch (statusError) {
      setError(statusError instanceof Error ? statusError.message : "Não foi possível atualizar o chamado.");
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  async function handleRate() {
    if (!currentUser || !rating) {
      return;
    }
    setIsRating(true);
    setError("");
    try {
      await rateSupportTicket(ticket.id, rating, ratingComment, currentUser);
    } catch (rateError) {
      setError(rateError instanceof Error ? rateError.message : "Não foi possível enviar a avaliação.");
    } finally {
      setIsRating(false);
    }
  }

  const conversation: SupportTicketMessage[] = [
    {
      id: "__description",
      authorUserId: ticket.reporterUserId,
      authorName: ticket.reporterName,
      authorRole: "reporter",
      text: ticket.description,
      createdAt: ticket.createdAt
    },
    ...messages
  ];
  const shownRating = hoverRating || rating;

  return (
    <div className="fixed inset-0 z-[100] flex items-stretch justify-center bg-slate-950/50 backdrop-blur-sm sm:items-center sm:p-4" onClick={onClose}>
      <div className="flex h-full w-full max-w-2xl flex-col bg-white shadow-panel sm:h-[85vh] sm:rounded-lg sm:border sm:border-slate-200" onClick={(event) => event.stopPropagation()}>
        <div className="border-b border-slate-200 p-3">
          <div className="flex items-start gap-2">
            <button type="button" onClick={onClose} aria-label="Fechar" className="focus-ring inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"><ChevronLeft size={18} /></button>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-semibold text-ink">{ticket.subject}</h2>
                <StatusPill tone={supportTicketStatusTone(ticket.status)}>{supportTicketStatusLabel(ticket.status)}</StatusPill>
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                {viewerRole === "agent" ? `${ticket.reporterName}${ticket.companyName ? ` · ${ticket.companyName}` : ""} · ` : ""}
                {supportTicketTypeLabel(ticket.type)} · aberto em {formatAuditDate(ticket.createdAt)}
                {ticket.assignedToUserName ? ` · Atendente: ${ticket.assignedToUserName}` : ""}
              </p>
            </div>
          </div>
          {viewerRole === "agent" && (isOpen || onDelete) ? (
            <div className="mt-2 flex flex-wrap gap-2 pl-11">
              {isOpen && ticket.status === "aberto" ? <SecondaryButton onClick={() => void handleStatus("em_atendimento")} disabled={isUpdatingStatus}>Assumir</SecondaryButton> : null}
              {isOpen ? <PrimaryButton onClick={() => void handleStatus("resolvido")} disabled={isUpdatingStatus}><CheckCircle2 size={16} /> Finalizar chamado</PrimaryButton> : null}
              {isOpen ? <SecondaryButton onClick={() => void handleStatus("cancelado")} disabled={isUpdatingStatus}>Cancelar</SecondaryButton> : null}
              {onDelete ? (
                <button type="button" onClick={onDelete} className="focus-ring inline-flex items-center gap-2 rounded-md border border-rose-200 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50">
                  <Trash2 size={16} /> Apagar chamado
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div ref={listRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-3">
          {ticket.attachments.length || (viewerRole === "agent" && ticket.context) ? (
            <div className="rounded-md border border-slate-200 bg-white p-2">
              {ticket.attachments.length ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Print da tela enviado</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {ticket.attachments.map((attachment, index) => (
                      <button
                        key={attachment.url}
                        type="button"
                        onClick={() => setViewerIndex(index)}
                        className="focus-ring overflow-hidden rounded-md border border-slate-200"
                        title="Ampliar"
                      >
                        <img src={attachment.url} alt={attachment.name} className="h-24 w-36 object-cover object-top" />
                      </button>
                    ))}
                  </div>
                </>
              ) : null}
              {viewerRole === "agent" && ticket.context ? (
                <details className={`text-xs text-slate-500 ${ticket.attachments.length ? "mt-2" : ""}`}>
                  <summary className="cursor-pointer font-medium text-slate-600">
                    Aberto na tela {ticket.context.viewLabel ? `"${ticket.context.viewLabel}"` : "-"}{ticket.context.appVersion ? ` · v${ticket.context.appVersion}` : ""}
                  </summary>
                  <div className="mt-1 space-y-0.5 break-all">
                    {ticket.context.url ? <p>Endereço: {ticket.context.url}</p> : null}
                    {ticket.context.viewport ? <p>Tamanho da tela: {ticket.context.viewport}{ticket.context.theme ? ` · tema ${ticket.context.theme === "dark" ? "escuro" : "claro"}` : ""}</p> : null}
                    {ticket.context.userAgent ? <p>Navegador: {ticket.context.userAgent}</p> : null}
                  </div>
                </details>
              ) : null}
            </div>
          ) : null}
          {conversation.map((item) => {
            const isMine = item.authorRole === viewerRole;
            return (
              <div key={item.id} className={`flex ${isMine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm shadow-sm ${isMine ? "bg-brand-700 text-white" : "border border-slate-200 bg-white text-slate-800"}`}>
                  <p className={`text-xs font-semibold ${isMine ? "text-white/80" : "text-slate-500"}`}>
                    {item.authorRole === "agent" ? `${item.authorName} (suporte)` : item.authorName}
                  </p>
                  <p className="mt-0.5 whitespace-pre-wrap break-words">{item.text}</p>
                  <p className={`mt-1 text-right text-[11px] ${isMine ? "text-white/70" : "text-slate-400"}`}>{formatAuditDate(item.createdAt)}</p>
                </div>
              </div>
            );
          })}
          {isOpen && !messages.length ? (
            <p className="text-center text-xs text-slate-500">
              {viewerRole === "reporter" ? "Nossa equipe vai responder por aqui. Você também pode mandar mais detalhes." : "Responda o usuário por aqui. A primeira resposta assume o chamado."}
            </p>
          ) : null}
          {ticket.resolutionNotes ? (
            <p className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900"><span className="font-semibold">Solução registrada:</span> {ticket.resolutionNotes}</p>
          ) : null}
        </div>

        {error ? <p className="px-3 pt-2 text-sm text-rose-600">{error}</p> : null}

        {isOpen ? (
          <div className="flex items-end gap-2 border-t border-slate-200 p-3">
            <textarea
              className="focus-ring max-h-40 min-h-[2.75rem] min-w-0 flex-1 resize-y rounded-md border border-slate-300 px-3 py-2 text-sm"
              rows={2}
              placeholder="Escreva sua mensagem... (Enter envia, Shift+Enter quebra linha)"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  void handleSend();
                }
              }}
            />
            <PrimaryButton onClick={() => void handleSend()} disabled={isSending || !draft.trim()}>
              {isSending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </PrimaryButton>
          </div>
        ) : (
          <div className="border-t border-slate-200 p-3">
            <p className="text-center text-xs text-slate-500">Chamado {ticket.status === "cancelado" ? "cancelado" : "finalizado"}{ticket.resolvedAt ? ` em ${formatAuditDate(ticket.resolvedAt)}` : ""}. A conversa foi encerrada.</p>
            {ticket.status === "resolvido" ? (
              ticket.rating ? (
                <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-center">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{viewerRole === "reporter" ? "Sua avaliação" : "Avaliação do usuário"}</p>
                  <div className="mt-1 flex items-center justify-center gap-2">
                    <StarRatingDisplay rating={ticket.rating} size={20} />
                    <span className="text-sm font-medium text-ink">{supportRatingLabels[ticket.rating]}</span>
                  </div>
                  {ticket.ratingComment ? <p className="mt-1 text-sm text-slate-600">"{ticket.ratingComment}"</p> : null}
                </div>
              ) : viewerRole === "reporter" ? (
                <div className="mt-2 rounded-md border border-brand-200 bg-brand-50/40 p-3">
                  <p className="text-center text-sm font-semibold text-ink">Como foi o atendimento?</p>
                  <div className="mt-2 flex items-center justify-center gap-1" onMouseLeave={() => setHoverRating(0)}>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setRating(value)}
                        onMouseEnter={() => setHoverRating(value)}
                        aria-label={`${value} estrela(s) - ${supportRatingLabels[value]}`}
                        className="focus-ring rounded p-1"
                      >
                        <Star size={30} className={value <= shownRating ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
                      </button>
                    ))}
                  </div>
                  <p className="h-5 text-center text-sm text-slate-600">{shownRating ? supportRatingLabels[shownRating] : ""}</p>
                  {rating ? (
                    <>
                      <textarea
                        className="focus-ring mt-2 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                        rows={2}
                        placeholder="Quer deixar um comentário? (opcional)"
                        value={ratingComment}
                        onChange={(event) => setRatingComment(event.target.value)}
                      />
                      <div className="mt-2 flex justify-center">
                        <PrimaryButton onClick={() => void handleRate()} disabled={isRating}>{isRating ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Enviar avaliação</PrimaryButton>
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <p className="mt-1 text-center text-xs text-slate-500">O usuário ainda não avaliou o atendimento.</p>
              )
            ) : null}
          </div>
        )}
        {viewerIndex !== null ? (
          <TrainingImageViewer urls={ticket.attachments.map((attachment) => attachment.url)} initialIndex={viewerIndex} onClose={() => setViewerIndex(null)} />
        ) : null}
      </div>
    </div>
  );
}

function SupportFloatingWidget({
  currentUser,
  company,
  contextArticles,
  trainingArticles,
  knowledgeEntries,
  assistantSettings,
  unreadTicketCount,
  currentViewId,
  currentViewLabel,
  onClose,
  onOpenFullSupport
}: {
  currentUser: User | null;
  company: CompanyRecord | null;
  contextArticles: TrainingArticle[];
  trainingArticles: TrainingArticle[];
  knowledgeEntries: SupportKnowledgeEntry[];
  assistantSettings: SupportAssistantSettings;
  unreadTicketCount: number;
  currentViewId: string;
  currentViewLabel: string;
  onClose: () => void;
  onOpenFullSupport: (tab?: SupportPageTab) => void;
}) {
  const [question, setQuestion] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [hasAsked, setHasAsked] = useState(false);
  const [knowledgeMatches, setKnowledgeMatches] = useState<SupportKnowledgeEntry[]>([]);
  const [articleMatches, setArticleMatches] = useState<TrainingArticle[]>([]);
  const [escalated, setEscalated] = useState(false);
  const [message, setMessage] = useState("");
  const [readingArticle, setReadingArticle] = useState<TrainingArticle | null>(null);
  const [wasResolved, setWasResolved] = useState(false);
  const [articleNotResolved, setArticleNotResolved] = useState(false);
  const [mode, setMode] = useState<"assistant" | "ticket">("assistant");
  const [ticketType, setTicketType] = useState<SupportTicketType>("bug");
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketDescription, setTicketDescription] = useState("");
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [attachScreenshot, setAttachScreenshot] = useState(true);
  const [screenshot, setScreenshot] = useState<{ blob: Blob; url: string } | null>(null);
  const [captureState, setCaptureState] = useState<"idle" | "capturing" | "ready" | "error">("idle");
  const [isScreenshotViewerOpen, setIsScreenshotViewerOpen] = useState(false);

  useEffect(() => () => {
    if (screenshot) {
      URL.revokeObjectURL(screenshot.url);
    }
  }, [screenshot]);

  function resetConversation() {
    setHasAsked(false);
    setQuestion("");
    setKnowledgeMatches([]);
    setArticleMatches([]);
    setArticleNotResolved(false);
  }

  function handleArticleFeedback(resolved: boolean) {
    setReadingArticle(null);
    if (resolved) {
      resetConversation();
      setWasResolved(true);
      return;
    }
    setArticleNotResolved(true);
  }

  function handleAsk() {
    if (!question.trim()) {
      setMessage("Digite sua dúvida para eu tentar ajudar.");
      return;
    }
    setMessage("");
    setWasResolved(false);
    setArticleNotResolved(false);
    setIsThinking(true);
    window.setTimeout(() => {
      setKnowledgeMatches(findRelevantKnowledgeEntries(question, knowledgeEntries));
      setArticleMatches(findRelevantTrainingArticles(question, trainingArticles));
      setHasAsked(true);
      setIsThinking(false);
    }, 500);
  }

  async function captureScreen() {
    setCaptureState("capturing");
    try {
      // Let the "capturing" state paint before html2canvas blocks the main thread.
      await new Promise((resolve) => window.requestAnimationFrame(() => resolve(null)));
      const blob = await captureCurrentScreen();
      setScreenshot({ blob, url: URL.createObjectURL(blob) });
      setCaptureState("ready");
    } catch (error) {
      console.warn("Não foi possível capturar a tela.", error);
      setScreenshot(null);
      setCaptureState("error");
    }
  }

  function openTicketForm(prefill?: { type: SupportTicketType; description: string }) {
    setMode("ticket");
    setMessage("");
    if (prefill) {
      setTicketType(prefill.type);
      setTicketDescription((current) => current || prefill.description);
      setTicketSubject((current) => current || prefill.description.slice(0, 80));
    }
    if (attachScreenshot && !screenshot && captureState !== "capturing") {
      void captureScreen();
    }
  }

  function handleToggleScreenshot(checked: boolean) {
    setAttachScreenshot(checked);
    if (checked && !screenshot && captureState !== "capturing") {
      void captureScreen();
    }
  }

  async function handleSubmitTicket() {
    if (!currentUser) {
      return;
    }
    if (!ticketDescription.trim()) {
      setMessage("Descreva o que aconteceu.");
      return;
    }
    setIsCreatingTicket(true);
    setMessage("");
    try {
      await createSupportTicket({
        companyId: company?.id,
        companyName: company?.tradeName,
        type: ticketType,
        subject: ticketSubject.trim() || ticketDescription.trim().slice(0, 80),
        description: ticketDescription,
        suggestedArticleIds: articleMatches.map((article) => article.id),
        screenshot: attachScreenshot && screenshot ? screenshot.blob : undefined,
        context: {
          viewId: currentViewId,
          viewLabel: currentViewLabel,
          url: window.location.href,
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          appVersion: SERVICE_WORKER_VERSION,
          theme: getCurrentTheme()
        }
      }, currentUser);
      setEscalated(true);
      setMode("assistant");
      resetConversation();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível abrir o chamado.");
    } finally {
      setIsCreatingTicket(false);
    }
  }

  const foundSomething = knowledgeMatches.length > 0 || articleMatches.length > 0;

  return (
    <>
      <div className="fixed inset-0 z-40" data-screenshot-ignore onClick={() => { if (!readingArticle && !isScreenshotViewerOpen) onClose(); }} />
      <div
        data-screenshot-ignore
        className="fixed bottom-40 right-4 z-50 w-[calc(100vw-2rem)] max-w-sm rounded-lg border border-slate-200 bg-white p-4 shadow-panel lg:bottom-24"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 border-b border-slate-200 pb-2">
          {mode === "ticket" ? (
            <button type="button" onClick={() => { setMode("assistant"); setMessage(""); }} className="focus-ring -ml-1 inline-flex items-center gap-1 rounded-md px-1 text-sm font-semibold text-ink hover:text-brand-700">
              <ChevronLeft size={16} /> Abrir chamado
            </button>
          ) : (
            <h2 className="font-semibold text-ink">Suporte IMOBI</h2>
          )}
          <button type="button" onClick={onClose} aria-label="Fechar" className="focus-ring inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100"><CircleSlash size={16} /></button>
        </div>

        {mode === "ticket" ? (
          <div className="mt-3 max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            <label className="block text-sm text-slate-700">Tipo
              <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={ticketType} onChange={(event) => setTicketType(event.target.value as SupportTicketType)}>
                <option value="bug">Erro no sistema</option>
                <option value="duvida">Dúvida</option>
                <option value="solicitacao">Solicitação</option>
              </select>
            </label>
            <label className="block text-sm text-slate-700">Assunto
              <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={ticketSubject} onChange={(event) => setTicketSubject(event.target.value)} placeholder="Resuma em poucas palavras" />
            </label>
            <label className="block text-sm text-slate-700">O que aconteceu?
              <textarea className="focus-ring mt-1 min-h-24 w-full rounded-md border border-slate-300 px-3 py-2 text-sm" value={ticketDescription} onChange={(event) => setTicketDescription(event.target.value)} placeholder="Ex.: cliquei em salvar e apareceu uma mensagem de erro." />
            </label>

            <div className="rounded-md border border-slate-200 p-2">
              <label className="flex items-center gap-2 text-sm font-medium text-ink">
                <input type="checkbox" checked={attachScreenshot} onChange={(event) => handleToggleScreenshot(event.target.checked)} className="h-4 w-4 rounded border-slate-300 text-brand-700" />
                Anexar print da tela atual
              </label>
              {attachScreenshot ? (
                <div className="mt-2">
                  {captureState === "capturing" ? (
                    <p className="flex items-center gap-2 text-xs text-slate-500"><Loader2 size={14} className="animate-spin" /> Capturando a tela...</p>
                  ) : captureState === "error" ? (
                    <p className="text-xs text-rose-600">
                      Não foi possível capturar a tela.{" "}
                      <button type="button" onClick={() => void captureScreen()} className="font-semibold underline">Tentar de novo</button>
                    </p>
                  ) : screenshot ? (
                    <div className="flex items-start gap-2">
                      <button type="button" onClick={() => setIsScreenshotViewerOpen(true)} className="focus-ring shrink-0 overflow-hidden rounded-md border border-slate-200" title="Ver o print">
                        <img src={screenshot.url} alt="Print da tela atual" className="h-20 w-28 object-cover object-top" />
                      </button>
                      <div className="text-xs text-slate-500">
                        <p>Print da tela {currentViewLabel ? <span className="font-medium text-slate-700">"{currentViewLabel}"</span> : null} sem esta janela de suporte.</p>
                        <button type="button" onClick={() => void captureScreen()} className="mt-1 font-semibold text-brand-700 hover:underline">Capturar de novo</button>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>

            <PrimaryButton onClick={() => void handleSubmitTicket()} disabled={isCreatingTicket || captureState === "capturing"}>
              {isCreatingTicket ? <Loader2 size={16} className="animate-spin" /> : <LifeBuoy size={16} />} Enviar chamado
            </PrimaryButton>
          </div>
        ) : (
          <div className="mt-3 max-h-[55vh] space-y-4 overflow-y-auto pr-1">
            {unreadTicketCount > 0 ? (
              <button
                type="button"
                onClick={() => onOpenFullSupport("mine")}
                className="focus-ring flex w-full items-center gap-2 rounded-md border border-brand-200 bg-brand-50 px-3 py-2 text-left text-sm font-medium text-brand-800 hover:bg-brand-100"
              >
                <MessageCircle size={16} />
                {unreadTicketCount === 1 ? "Você tem 1 chamado com resposta nova" : `Você tem ${unreadTicketCount} chamados com resposta nova`}
                <ChevronRight size={16} className="ml-auto" />
              </button>
            ) : null}
            {contextArticles.length ? (
              <section>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sobre esta tela</p>
                <div className="mt-2 space-y-1">
                  {contextArticles.map((article) => (
                    <button
                      key={article.id}
                      type="button"
                      onClick={() => setReadingArticle(article)}
                      className="focus-ring block w-full rounded-md border border-slate-200 px-2 py-1.5 text-left text-sm text-ink hover:border-brand-300 hover:bg-brand-50/40"
                    >
                      {article.title}
                    </button>
                  ))}
                </div>
              </section>
            ) : null}

            <section>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pergunte para a IA do IMOBI</p>
              {wasResolved ? (
                <p className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-900">Que bom que resolveu! Se precisar de mais alguma coisa, é só perguntar.</p>
              ) : !hasAsked && !escalated ? <p className="mt-2 text-sm text-slate-600">{assistantSettings.greeting}</p> : null}
              {escalated ? (
                <div className="mt-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
                  <p className="font-medium">Chamado aberto!</p>
                  <p className="mt-1">Nossa equipe de suporte foi notificada. Você acompanha e conversa com o suporte em "Meus chamados".</p>
                  <button type="button" onClick={() => onOpenFullSupport("mine")} className="mt-2 text-sm font-semibold text-emerald-800 underline">Abrir meus chamados</button>
                </div>
              ) : (
                <>
                  <div className="mt-2 flex gap-2">
                    <input
                      className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Ex.: como eu cadastro um bem?"
                      value={question}
                      onChange={(event) => setQuestion(event.target.value)}
                      onKeyDown={(event) => { if (event.key === "Enter") handleAsk(); }}
                    />
                    <PrimaryButton onClick={handleAsk} disabled={isThinking}>
                      {isThinking ? <Loader2 size={16} className="animate-spin" /> : <MessageCircle size={16} />}
                    </PrimaryButton>
                  </div>
                  {hasAsked && !isThinking ? (
                    foundSomething ? (
                      <div className="mt-2 space-y-2">
                        {knowledgeMatches.map((entry) => (
                          <div key={entry.id} className="rounded-md border border-brand-200 bg-brand-50/40 p-2">
                            <p className="text-sm font-medium text-ink">{entry.question}</p>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{entry.answer}</p>
                          </div>
                        ))}
                        {articleMatches.length ? (
                          <>
                            <p className="text-sm text-slate-600">{knowledgeMatches.length ? "Veja também:" : "Encontrei isto:"}</p>
                            {articleMatches.map((article) => (
                              <button
                                key={article.id}
                                type="button"
                                onClick={() => setReadingArticle(article)}
                                className="focus-ring flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-left text-sm hover:border-brand-300"
                              >
                                <GraduationCap size={14} className="shrink-0 text-brand-700" />
                                <span className="min-w-0 flex-1">{article.title}</span>
                                <ChevronRight size={14} className="shrink-0 text-slate-400" />
                              </button>
                            ))}
                          </>
                        ) : null}
                        {articleNotResolved ? (
                          <p className="rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">Poxa, que pena. Veja outra sugestão acima ou fale com o nosso suporte.</p>
                        ) : (
                          <p className="text-sm text-slate-600">{assistantSettings.escalationPromptMessage}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <SecondaryButton onClick={() => { resetConversation(); setWasResolved(true); }}><Check size={14} /> Sim, resolveu</SecondaryButton>
                          {articleNotResolved ? (
                            <PrimaryButton onClick={() => openTicketForm({ type: "duvida", description: question.trim() })}><LifeBuoy size={14} /> Falar com o suporte</PrimaryButton>
                          ) : (
                            <SecondaryButton onClick={() => openTicketForm({ type: "duvida", description: question.trim() })}><LifeBuoy size={14} /> Ainda preciso de ajuda</SecondaryButton>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2">
                        <p className="text-sm text-slate-600">{assistantSettings.fallbackMessage}</p>
                        <div className="mt-2"><PrimaryButton onClick={() => openTicketForm({ type: "duvida", description: question.trim() })}><LifeBuoy size={14} /> Falar com o suporte</PrimaryButton></div>
                      </div>
                    )
                  ) : null}
                </>
              )}
            </section>
          </div>
        )}

        {message ? <p className="mt-2 text-xs text-rose-600">{message}</p> : null}

        {mode === "assistant" ? (
          <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-200 pt-2">
            <button type="button" onClick={() => openTicketForm()} className="focus-ring inline-flex items-center gap-1 text-xs font-semibold text-rose-600 hover:underline">
              <AlertTriangle size={14} /> Deu erro? Abrir chamado
            </button>
            <button type="button" onClick={() => onOpenFullSupport()} className="focus-ring text-xs font-medium text-brand-700 hover:underline">
              Central de suporte
            </button>
          </div>
        ) : null}

        {isScreenshotViewerOpen && screenshot ? (
          <TrainingImageViewer urls={[screenshot.url]} initialIndex={0} onClose={() => setIsScreenshotViewerOpen(false)} />
        ) : null}
      </div>
      {readingArticle ? (
        <TrainingArticleReaderModal
          article={readingArticle}
          onClose={() => setReadingArticle(null)}
          onFeedback={handleArticleFeedback}
        />
      ) : null}
    </>
  );
}

type SupportPageTab = "new" | "mine" | "queue" | "knowledge";

function SupportPage({
  currentUser,
  company,
  trainingArticles,
  knowledgeEntries,
  assistantSettings,
  myTickets,
  allTickets,
  isSupportAgent,
  isMaster,
  supportAgentUserIds,
  companyUsers,
  initialTab,
  onInitialTabConsumed
}: {
  currentUser: User | null;
  company: CompanyRecord | null;
  trainingArticles: TrainingArticle[];
  knowledgeEntries: SupportKnowledgeEntry[];
  assistantSettings: SupportAssistantSettings;
  myTickets: SupportTicket[];
  allTickets: SupportTicket[];
  isSupportAgent: boolean;
  isMaster: boolean;
  supportAgentUserIds: string[];
  companyUsers: ManagedUser[];
  initialTab?: SupportPageTab | null;
  onInitialTabConsumed?: () => void;
}) {
  const [tab, setTab] = useState<SupportPageTab>(initialTab ?? "new");
  const [readingArticle, setReadingArticle] = useState<TrainingArticle | null>(null);
  const [articleNotResolved, setArticleNotResolved] = useState(false);
  const [openChat, setOpenChat] = useState<{ ticketId: string; role: SupportMessageAuthorRole } | null>(null);
  const [type, setType] = useState<SupportTicketType>("duvida");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [knowledgeMatches, setKnowledgeMatches] = useState<SupportKnowledgeEntry[]>([]);
  const [articleMatches, setArticleMatches] = useState<TrainingArticle[]>([]);
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [confirmedTicketId, setConfirmedTicketId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [resolutionDraftByTicketId, setResolutionDraftByTicketId] = useState<Record<string, string>>({});
  const [isKnowledgeFormOpen, setIsKnowledgeFormOpen] = useState(false);
  const [editingKnowledgeEntry, setEditingKnowledgeEntry] = useState<SupportKnowledgeEntry | null>(null);
  const [assistantForm, setAssistantForm] = useState(assistantSettings);
  const [isSavingAssistant, setIsSavingAssistant] = useState(false);

  useEffect(() => {
    setAssistantForm(assistantSettings);
  }, [assistantSettings]);

  useEffect(() => {
    if (initialTab) {
      setTab(initialTab);
      onInitialTabConsumed?.();
    }
  }, [initialTab, onInitialTabConsumed]);

  function resetForm() {
    setSubject("");
    setDescription("");
    setHasSearched(false);
    setKnowledgeMatches([]);
    setArticleMatches([]);
    setConfirmedTicketId(null);
    setArticleNotResolved(false);
    setMessage("");
  }

  function handleArticleFeedback(resolved: boolean) {
    setReadingArticle(null);
    if (resolved) {
      resetForm();
      setMessage("");
      return;
    }
    setArticleNotResolved(true);
  }

  function handleSearchHelp() {
    if (!description.trim()) {
      setMessage("Descreva o que está acontecendo para eu poder ajudar.");
      return;
    }
    setMessage("");
    setArticleNotResolved(false);
    setIsThinking(true);
    window.setTimeout(() => {
      setKnowledgeMatches(findRelevantKnowledgeEntries(`${subject} ${description}`, knowledgeEntries));
      setArticleMatches(findRelevantTrainingArticles(`${subject} ${description}`, trainingArticles));
      setHasSearched(true);
      setIsThinking(false);
    }, 600);
  }

  async function handleEscalate() {
    if (!currentUser) {
      return;
    }
    setIsCreatingTicket(true);
    setMessage("");
    try {
      const ticketId = await createSupportTicket({
        companyId: company?.id,
        companyName: company?.tradeName,
        type,
        subject,
        description,
        suggestedArticleIds: articleMatches.map((article) => article.id)
      }, currentUser);
      setConfirmedTicketId(ticketId);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível abrir o chamado.");
    } finally {
      setIsCreatingTicket(false);
    }
  }

  async function handleSaveAssistantSettings() {
    if (!currentUser) {
      return;
    }
    setIsSavingAssistant(true);
    try {
      await saveSupportAssistantSettings(assistantForm, currentUser);
      setMessage("Configurações da IA salvas.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar as configurações da IA.");
    } finally {
      setIsSavingAssistant(false);
    }
  }

  async function handleToggleKnowledgeActive(entry: SupportKnowledgeEntry) {
    if (!currentUser) {
      return;
    }
    try {
      await setSupportKnowledgeEntryActive(entry.id, !entry.active, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o item.");
    }
  }

  async function handleUpdateStatus(ticket: SupportTicket, status: SupportTicketStatus) {
    if (!currentUser) {
      return;
    }
    try {
      await updateSupportTicketStatus(ticket.id, status, currentUser, resolutionDraftByTicketId[ticket.id] ?? ticket.resolutionNotes);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar o chamado.");
    }
  }

  async function handleDeleteTicket(ticket: SupportTicket) {
    if (!isMaster) {
      return;
    }
    if (!window.confirm(`Apagar o chamado "${ticket.subject}" de ${ticket.reporterName}?\n\nO chamado e toda a conversa serão excluídos definitivamente, sem deixar histórico. Não dá para desfazer.`)) {
      return;
    }
    try {
      if (openChat?.ticketId === ticket.id) {
        setOpenChat(null);
      }
      await deleteSupportTicket(ticket.id, ticket.attachments);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível apagar o chamado.");
    }
  }

  async function handleClaim(ticket: SupportTicket) {
    if (!currentUser) {
      return;
    }
    try {
      await claimSupportTicket(ticket.id, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível assumir o chamado.");
    }
  }

  async function handleToggleSupportAgent(userId: string) {
    if (!currentUser) {
      return;
    }
    const next = supportAgentUserIds.includes(userId)
      ? supportAgentUserIds.filter((id) => id !== userId)
      : [...supportAgentUserIds, userId];
    try {
      await saveSupportAgents(next, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar os agentes de suporte.");
    }
  }

  const openTicketsCount = allTickets.filter(isSupportTicketOpen).length;
  const myUnreadCount = myTickets.filter((ticket) => isSupportTicketUnread(ticket, "reporter")).length;
  const myPendingRatingCount = myTickets.filter((ticket) => ticket.status === "resolvido" && !ticket.rating).length;
  const queueUnreadCount = allTickets.filter((ticket) => isSupportTicketUnread(ticket, "agent")).length;
  const ratedTickets = allTickets.filter((ticket) => ticket.rating);
  const averageRating = ratedTickets.length ? ratedTickets.reduce((total, ticket) => total + (ticket.rating ?? 0), 0) / ratedTickets.length : 0;
  const chatTicket = openChat
    ? (openChat.role === "reporter" ? myTickets : allTickets).find((ticket) => ticket.id === openChat.ticketId) ?? null
    : null;

  return (
    <>
      <PageHeader title="Suporte" subtitle="Descreva o problema e receba ajuda na hora, ou chame nossa equipe." />
      <div className="space-y-4 p-4 lg:p-6">
        <div className="flex flex-wrap gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm">
          {[
            { id: "new", label: "Novo chamado" },
            { id: "mine", label: `Meus chamados (${myTickets.length})`, badge: myUnreadCount + myPendingRatingCount },
            ...(isSupportAgent ? [{ id: "queue", label: `Fila de atendimento (${openTicketsCount})`, badge: queueUnreadCount }] : []),
            ...(isMaster ? [{ id: "knowledge", label: `IA do IMOBI (${knowledgeEntries.length})` }] : [])
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setTab(item.id as SupportPageTab)}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-semibold ${tab === item.id ? "bg-brand-700 text-white" : "text-slate-600 hover:bg-slate-100"}`}
            >
              {item.label}
              {"badge" in item && item.badge ? <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 text-[11px] font-bold text-white">{item.badge}</span> : null}
            </button>
          ))}
        </div>

        {message ? <p className="text-sm text-rose-600">{message}</p> : null}

        {tab === "new" ? (
          <section className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            {confirmedTicketId ? (
              <div className="text-center">
                <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
                <h2 className="mt-2 font-semibold text-ink">Chamado aberto!</h2>
                <p className="mt-1 text-sm text-slate-600">Nossa equipe de suporte foi notificada. A conversa com o suporte acontece dentro do chamado.</p>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  <PrimaryButton onClick={() => { setOpenChat({ ticketId: confirmedTicketId, role: "reporter" }); setTab("mine"); resetForm(); }}><MessageCircle size={16} /> Abrir conversa</PrimaryButton>
                  <SecondaryButton onClick={resetForm}>Abrir outro chamado</SecondaryButton>
                </div>
              </div>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="text-sm text-slate-700">Tipo
                    <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={type} onChange={(event) => setType(event.target.value as SupportTicketType)}>
                      <option value="duvida">Dúvida</option>
                      <option value="bug">Bug / erro no sistema</option>
                      <option value="solicitacao">Solicitação</option>
                    </select>
                  </label>
                  <label className="text-sm text-slate-700">Assunto
                    <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={subject} onChange={(event) => setSubject(event.target.value)} placeholder="Resuma em poucas palavras" />
                  </label>
                  <label className="text-sm text-slate-700 md:col-span-2">Descreva o que está acontecendo
                    <textarea className="focus-ring mt-1 min-h-28 w-full rounded-md border border-slate-300 px-3 py-2" value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Quanto mais detalhes, mais fácil encontrar uma solução." />
                  </label>
                </div>
                <div className="mt-3">
                  <PrimaryButton onClick={handleSearchHelp} disabled={isThinking}>
                    {isThinking ? <><Loader2 size={16} className="animate-spin" /> Analisando...</> : <><GraduationCap size={16} /> Buscar ajuda</>}
                  </PrimaryButton>
                </div>

                {hasSearched && !isThinking ? (
                  <div className="mt-4 rounded-md border border-brand-200 bg-brand-50/40 p-3">
                    <p className="text-sm font-semibold text-ink">Assistente automático IMOBI</p>
                    {knowledgeMatches.length || articleMatches.length ? (
                      <>
                        <div className="mt-2 space-y-2">
                          {knowledgeMatches.map((entry) => (
                            <div key={entry.id} className="rounded-md border border-brand-200 bg-white p-3">
                              <p className="text-sm font-medium text-ink">{entry.question}</p>
                              <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{entry.answer}</p>
                            </div>
                          ))}
                          {articleMatches.map((article) => (
                            <button
                              key={article.id}
                              type="button"
                              onClick={() => setReadingArticle(article)}
                              className="focus-ring flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm hover:border-brand-300"
                            >
                              <GraduationCap size={16} className="shrink-0 text-brand-700" />
                              <span className="min-w-0 flex-1">
                                <span className="font-medium text-ink">{article.title}</span>
                                {article.summary ? <span className="mt-0.5 block text-xs text-slate-500">{article.summary}</span> : null}
                              </span>
                              <ChevronRight size={16} className="shrink-0 text-slate-400" />
                            </button>
                          ))}
                        </div>
                        {articleNotResolved ? (
                          <p className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">Que pena que não resolveu. Veja outra sugestão ou chame o suporte técnico.</p>
                        ) : (
                          <p className="mt-3 text-sm text-slate-600">{assistantSettings.escalationPromptMessage}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-2">
                          <SecondaryButton onClick={resetForm}>Sim, resolvido</SecondaryButton>
                          <PrimaryButton onClick={() => void handleEscalate()} disabled={isCreatingTicket}><LifeBuoy size={16} /> Não, chamar suporte técnico</PrimaryButton>
                        </div>
                      </>
                    ) : (
                      <>
                        <p className="mt-1 text-sm text-slate-600">{assistantSettings.fallbackMessage}</p>
                        <div className="mt-2">
                          <PrimaryButton onClick={() => void handleEscalate()} disabled={isCreatingTicket}><LifeBuoy size={16} /> Chamar suporte técnico</PrimaryButton>
                        </div>
                      </>
                    )}
                  </div>
                ) : null}
              </>
            )}
          </section>
        ) : null}

        {tab === "mine" ? (
          <div className="space-y-2">
            {myTickets.map((ticket) => {
              const hasUnread = isSupportTicketUnread(ticket, "reporter");
              const needsRating = ticket.status === "resolvido" && !ticket.rating;
              return (
                <div key={ticket.id} className={`rounded-lg border bg-white p-4 shadow-sm ${hasUnread || needsRating ? "border-brand-300" : "border-slate-200"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-medium text-ink">{ticket.subject}</p>
                    <div className="flex items-center gap-2">
                      {hasUnread ? <StatusPill tone="blue">Nova resposta</StatusPill> : null}
                      <StatusPill tone={supportTicketStatusTone(ticket.status)}>{supportTicketStatusLabel(ticket.status)}</StatusPill>
                    </div>
                  </div>
                  <p className="mt-1 flex flex-wrap items-center gap-1 text-xs text-slate-500">
                    {supportTicketTypeLabel(ticket.type)} · {formatAuditDate(ticket.createdAt)}{ticket.assignedToUserName ? ` · Atendente: ${ticket.assignedToUserName}` : ""}
                    {ticket.attachments.length ? <span className="inline-flex items-center gap-0.5 text-slate-600"> · <Image size={12} /> com print</span> : null}
                  </p>
                  <p className="mt-2 line-clamp-2 text-sm text-slate-600">{ticket.description}</p>
                  {ticket.lastMessagePreview ? (
                    <p className="mt-2 truncate rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600">
                      <span className="font-semibold">{ticket.lastMessageAuthorRole === "agent" ? "Suporte" : "Você"}:</span> {ticket.lastMessagePreview}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {needsRating ? (
                      <PrimaryButton onClick={() => setOpenChat({ ticketId: ticket.id, role: "reporter" })}><Star size={16} /> Avaliar atendimento</PrimaryButton>
                    ) : (
                      <SecondaryButton onClick={() => setOpenChat({ ticketId: ticket.id, role: "reporter" })}><MessageCircle size={16} /> {isSupportTicketOpen(ticket) ? "Abrir conversa" : "Ver conversa"}</SecondaryButton>
                    )}
                    {ticket.rating ? <StarRatingDisplay rating={ticket.rating} /> : null}
                  </div>
                </div>
              );
            })}
            {!myTickets.length ? <EmptyState title="Nenhum chamado" description="Seus chamados de suporte aparecem aqui." /> : null}
          </div>
        ) : null}

        {tab === "queue" && isSupportAgent ? (
          <div className="space-y-4">
            {isMaster ? (
              <details className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <summary className="cursor-pointer text-sm font-semibold text-ink">Agentes de suporte ({supportAgentUserIds.length})</summary>
                <p className="mt-2 text-xs text-slate-500">Selecione quem recebe notificação push quando um chamado é aberto.</p>
                <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
                  {companyUsers.map((profile) => (
                    <label key={profile.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <input type="checkbox" checked={supportAgentUserIds.includes(profile.id)} onChange={() => void handleToggleSupportAgent(profile.id)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-700" />
                      {userDisplayName(profile)}
                    </label>
                  ))}
                </div>
              </details>
            ) : null}

            {ratedTickets.length ? (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3 text-sm shadow-sm">
                <span className="font-semibold text-ink">Avaliação média do atendimento:</span>
                <StarRatingDisplay rating={Math.round(averageRating)} size={16} />
                <span className="text-slate-600">{averageRating.toFixed(1).replace(".", ",")} de 5 ({ratedTickets.length} {ratedTickets.length === 1 ? "avaliação" : "avaliações"})</span>
              </div>
            ) : null}

            <div className="space-y-2">
              {allTickets.map((ticket) => (
                <div key={ticket.id} className={`rounded-lg border bg-white p-4 shadow-sm ${isSupportTicketUnread(ticket, "agent") ? "border-brand-300" : "border-slate-200"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="font-medium text-ink">{ticket.subject}</p>
                      <p className="flex flex-wrap items-center gap-1 text-xs text-slate-500">
                        {ticket.reporterName}{ticket.companyName ? ` · ${ticket.companyName}` : ""} · {supportTicketTypeLabel(ticket.type)} · {formatAuditDate(ticket.createdAt)}
                        {ticket.attachments.length ? <span className="inline-flex items-center gap-0.5 text-slate-600"> · <Image size={12} /> com print</span> : null}
                        {ticket.context?.viewLabel ? <span> · tela "{ticket.context.viewLabel}"</span> : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {isSupportTicketUnread(ticket, "agent") ? <StatusPill tone="blue">Nova mensagem</StatusPill> : null}
                      <StatusPill tone={supportTicketStatusTone(ticket.status)}>{supportTicketStatusLabel(ticket.status)}</StatusPill>
                    </div>
                  </div>
                  <p className="mt-2 line-clamp-3 text-sm text-slate-600">{ticket.description}</p>
                  {ticket.lastMessagePreview ? (
                    <p className="mt-2 truncate rounded-md bg-slate-50 px-2 py-1 text-xs text-slate-600">
                      <span className="font-semibold">{ticket.lastMessageAuthorRole === "agent" ? "Suporte" : ticket.reporterName}:</span> {ticket.lastMessagePreview}
                    </p>
                  ) : null}
                  {ticket.assignedToUserName ? <p className="mt-1 text-xs text-slate-500">Responsável: {ticket.assignedToUserName}</p> : null}
                  {ticket.rating ? (
                    <p className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      Avaliação: <StarRatingDisplay rating={ticket.rating} />
                      {ticket.ratingComment ? <span className="italic">"{ticket.ratingComment}"</span> : null}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <PrimaryButton onClick={() => setOpenChat({ ticketId: ticket.id, role: "agent" })}><MessageCircle size={16} /> Conversa</PrimaryButton>
                    {ticket.status === "aberto" ? <SecondaryButton onClick={() => void handleClaim(ticket)}>Assumir</SecondaryButton> : null}
                    {isMaster ? (
                      <button type="button" onClick={() => void handleDeleteTicket(ticket)} title="Apagar chamado e conversa" aria-label="Apagar chamado" className="focus-ring inline-flex h-10 w-10 items-center justify-center rounded-md border border-rose-200 text-rose-600 hover:bg-rose-50">
                        <Trash2 size={16} />
                      </button>
                    ) : null}
                    <textarea
                      className="focus-ring min-w-0 flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm"
                      placeholder="Notas de resolução (opcional)"
                      value={resolutionDraftByTicketId[ticket.id] ?? ticket.resolutionNotes ?? ""}
                      onChange={(event) => setResolutionDraftByTicketId((current) => ({ ...current, [ticket.id]: event.target.value }))}
                    />
                    <select
                      className="focus-ring rounded-md border border-slate-300 px-3 py-2 text-sm"
                      value={ticket.status}
                      onChange={(event) => void handleUpdateStatus(ticket, event.target.value as SupportTicketStatus)}
                    >
                      <option value="aberto">Aberto</option>
                      <option value="em_atendimento">Em atendimento</option>
                      <option value="resolvido">Resolvido</option>
                      <option value="cancelado">Cancelado</option>
                    </select>
                  </div>
                </div>
              ))}
              {!allTickets.length ? <EmptyState title="Nenhum chamado" description="Os chamados de suporte de todas as empresas aparecem aqui." /> : null}
            </div>
          </div>
        ) : null}

        {tab === "knowledge" && isMaster ? (
          <div className="space-y-4">
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <h2 className="font-semibold text-ink">Como a IA responde</h2>
              <p className="mt-1 text-sm text-slate-500">Mensagens fixas usadas pelo assistente automático, no botão flutuante e na tela de suporte.</p>
              <div className="mt-3 grid gap-3">
                <label className="text-sm text-slate-700">Saudação inicial
                  <textarea className="focus-ring mt-1 min-h-16 w-full rounded-md border border-slate-300 px-3 py-2" value={assistantForm.greeting} onChange={(event) => setAssistantForm((current) => ({ ...current, greeting: event.target.value }))} />
                </label>
                <label className="text-sm text-slate-700">Mensagem quando nada é encontrado
                  <textarea className="focus-ring mt-1 min-h-16 w-full rounded-md border border-slate-300 px-3 py-2" value={assistantForm.fallbackMessage} onChange={(event) => setAssistantForm((current) => ({ ...current, fallbackMessage: event.target.value }))} />
                </label>
                <label className="text-sm text-slate-700">Pergunta feita após sugerir algo
                  <input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={assistantForm.escalationPromptMessage} onChange={(event) => setAssistantForm((current) => ({ ...current, escalationPromptMessage: event.target.value }))} />
                </label>
              </div>
              <div className="mt-3"><PrimaryButton onClick={() => void handleSaveAssistantSettings()} disabled={isSavingAssistant}><Save size={16} /> Salvar mensagens</PrimaryButton></div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-ink">Base de conhecimento</h2>
                  <p className="mt-1 text-sm text-slate-500">Perguntas e respostas próprias da IA, além dos treinamentos.</p>
                </div>
                <PrimaryButton onClick={() => { setEditingKnowledgeEntry(null); setIsKnowledgeFormOpen(true); }}><Plus size={16} /> Nova pergunta</PrimaryButton>
              </div>
              <div className="mt-3 space-y-2">
                {knowledgeEntries.map((entry) => (
                  <div key={entry.id} className="rounded-md border border-slate-200 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-ink">{entry.question}</p>
                      <div className="flex items-center gap-2">
                        <StatusPill tone={entry.active ? "green" : "neutral"}>{entry.active ? "Ativa" : "Inativa"}</StatusPill>
                        <SecondaryButton onClick={() => { setEditingKnowledgeEntry(entry); setIsKnowledgeFormOpen(true); }}><Edit3 size={16} /> Editar</SecondaryButton>
                        <SecondaryButton onClick={() => void handleToggleKnowledgeActive(entry)}>{entry.active ? "Desativar" : "Ativar"}</SecondaryButton>
                      </div>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{entry.answer}</p>
                    {entry.keywords.length ? <p className="mt-1 text-xs text-slate-400">Palavras-chave: {entry.keywords.join(", ")}</p> : null}
                  </div>
                ))}
                {!knowledgeEntries.length ? <EmptyState title="Nenhum item na base de conhecimento" description="Cadastre perguntas frequentes para a IA responder na hora, sem precisar de um treinamento completo." /> : null}
              </div>
            </section>
          </div>
        ) : null}
      </div>

      {isKnowledgeFormOpen ? (
        <SupportKnowledgeEntryFormModal
          entry={editingKnowledgeEntry}
          currentUser={currentUser}
          onClose={() => setIsKnowledgeFormOpen(false)}
        />
      ) : null}

      {readingArticle ? (
        <TrainingArticleReaderModal
          article={readingArticle}
          onClose={() => setReadingArticle(null)}
          onFeedback={handleArticleFeedback}
        />
      ) : null}

      {chatTicket && openChat ? (
        <SupportTicketChatModal
          ticket={chatTicket}
          viewerRole={openChat.role}
          currentUser={currentUser}
          onClose={() => setOpenChat(null)}
          onDelete={isMaster && openChat.role === "agent" ? () => void handleDeleteTicket(chatTicket) : undefined}
        />
      ) : null}
    </>
  );
}

function SupportKnowledgeEntryFormModal({
  entry,
  currentUser,
  onClose
}: {
  entry: SupportKnowledgeEntry | null;
  currentUser: User | null;
  onClose: () => void;
}) {
  const [question, setQuestion] = useState(entry?.question ?? "");
  const [answer, setAnswer] = useState(entry?.answer ?? "");
  const [keywords, setKeywords] = useState(entry?.keywords.join(", ") ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function handleSave() {
    if (!currentUser) {
      return;
    }
    setIsSaving(true);
    try {
      const input = {
        question,
        answer,
        keywords: keywords.split(",").map((keyword) => keyword.trim()).filter(Boolean)
      };
      if (entry) {
        await updateSupportKnowledgeEntry(entry.id, input, currentUser);
      } else {
        await createSupportKnowledgeEntry(input, currentUser);
      }
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <h2 className="font-semibold text-ink">{entry ? "Editar pergunta" : "Nova pergunta"}</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 grid gap-3">
          <label className="text-sm text-slate-700">Pergunta<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={question} onChange={(event) => setQuestion(event.target.value)} placeholder="Ex.: Como recupero minha senha?" /></label>
          <label className="text-sm text-slate-700">Resposta<textarea className="focus-ring mt-1 min-h-28 w-full rounded-md border border-slate-300 px-3 py-2" value={answer} onChange={(event) => setAnswer(event.target.value)} /></label>
          <label className="text-sm text-slate-700">Palavras-chave extras (separadas por vírgula)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={keywords} onChange={(event) => setKeywords(event.target.value)} placeholder="Ex.: senha, login, acesso" /></label>
        </div>
        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={isSaving || !question.trim() || !answer.trim()}>Salvar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function TrainingPage({
  categories,
  articles,
  isManager,
  currentUser,
  initialArticleId,
  onInitialArticleConsumed
}: {
  categories: TrainingCategory[];
  articles: TrainingArticle[];
  isManager: boolean;
  currentUser: User | null;
  initialArticleId?: string | null;
  onInitialArticleConsumed?: () => void;
}) {
  const [mode, setMode] = useState<"browse" | "manage">("browse");
  const [selectedArticleId, setSelectedArticleId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<TrainingCategory | null>(null);
  const [isArticleFormOpen, setIsArticleFormOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<TrainingArticle | null>(null);
  const [message, setMessage] = useState("");

  const activeCategories = categories.filter((category) => category.active).sort((left, right) => left.order - right.order);
  const visibleArticles = isManager ? articles : articles.filter((article) => article.status === "publicado");
  const normalizedQuery = searchQuery.trim().toLocaleLowerCase("pt-BR");
  const filteredArticles = normalizedQuery
    ? visibleArticles.filter((article) => [article.title, article.summary ?? "", article.body].join(" ").toLocaleLowerCase("pt-BR").includes(normalizedQuery))
    : visibleArticles;

  useEffect(() => {
    if (!initialArticleId) {
      return;
    }
    if (articles.some((article) => article.id === initialArticleId)) {
      setMode("browse");
      setSelectedArticleId(initialArticleId);
    }
    onInitialArticleConsumed?.();
  }, [articles, initialArticleId, onInitialArticleConsumed]);

  const selectedArticle = selectedArticleId ? articles.find((article) => article.id === selectedArticleId) ?? null : null;
  const dragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 8 } })
  );
  const sortedCategories = [...categories].sort((left, right) => left.order - right.order);

  async function handleToggleCategoryActive(category: TrainingCategory) {
    if (!currentUser) {
      return;
    }
    try {
      await setTrainingCategoryActive(category.id, !category.active, currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível atualizar a categoria.");
    }
  }

  async function handleCategoryDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!currentUser || !over || active.id === over.id) {
      return;
    }
    const oldIndex = sortedCategories.findIndex((category) => category.id === active.id);
    const newIndex = sortedCategories.findIndex((category) => category.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    const reordered = arrayMove(sortedCategories, oldIndex, newIndex);
    try {
      await reorderTrainingCategories(reordered.map((category) => category.id), currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível reordenar as categorias.");
    }
  }

  async function handleArticleDragEnd(categoryArticles: TrainingArticle[], event: DragEndEvent) {
    const { active, over } = event;
    if (!currentUser || !over || active.id === over.id) {
      return;
    }
    const oldIndex = categoryArticles.findIndex((article) => article.id === active.id);
    const newIndex = categoryArticles.findIndex((article) => article.id === over.id);
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }
    const reordered = arrayMove(categoryArticles, oldIndex, newIndex);
    try {
      await reorderTrainingArticles(reordered.map((article) => article.id), currentUser);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível reordenar os treinamentos.");
    }
  }

  if (selectedArticle) {
    const category = categories.find((item) => item.id === selectedArticle.categoryId);
    return (
      <>
        <PageHeader title={selectedArticle.title} subtitle={category?.name} />
        <div className="mx-auto max-w-3xl p-4 lg:p-6">
          <button type="button" onClick={() => setSelectedArticleId(null)} className="focus-ring mb-4 inline-flex items-center gap-1 text-sm font-medium text-brand-700 hover:underline">
            <ChevronLeft size={16} /> Voltar para o treinamento
          </button>
          {selectedArticle.status === "rascunho" ? (
            <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs font-medium text-amber-800">Este conteúdo ainda está em rascunho e só é visível para quem gerencia o treinamento.</p>
          ) : null}
          {selectedArticle.summary ? <p className="mb-4 text-sm text-slate-600">{selectedArticle.summary}</p> : null}
          <TrainingArticleContent html={selectedArticle.bodyHtml} />
        </div>
        {isManager ? (
          <div className="mx-auto max-w-3xl px-4 pb-6 lg:px-6">
            <SecondaryButton onClick={() => { setEditingArticle(selectedArticle); setIsArticleFormOpen(true); }}><Edit3 size={16} /> Editar este treinamento</SecondaryButton>
          </div>
        ) : null}
        {isArticleFormOpen ? (
          <TrainingArticleFormModal
            categories={categories}
            article={editingArticle}
            currentUser={currentUser}
            onClose={() => setIsArticleFormOpen(false)}
          />
        ) : null}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Treinamento"
        subtitle="Manual online do IMOBI: tire dúvidas sem precisar abrir um chamado."
        action={isManager ? (
          <SecondaryButton onClick={() => setMode((current) => current === "browse" ? "manage" : "browse")}>
            {mode === "browse" ? <><Edit3 size={16} /> Gerenciar conteúdo</> : <><GraduationCap size={16} /> Ver como usuário</>}
          </SecondaryButton>
        ) : undefined}
      />
      <div className="space-y-4 p-4 lg:p-6">
        {message ? <p className="text-sm text-rose-600">{message}</p> : null}

        {mode === "manage" && isManager ? (
          <>
            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-semibold text-ink">Categorias</h2>
                <PrimaryButton onClick={() => { setEditingCategory(null); setIsCategoryFormOpen(true); }}><Plus size={16} /> Nova categoria</PrimaryButton>
              </div>
              <p className="mt-1 text-xs text-slate-500">Arraste pelo ícone para reordenar.</p>
              <div className="mt-3 space-y-2">
                <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleCategoryDragEnd(event)}>
                  <SortableContext items={sortedCategories.map((category) => category.id)} strategy={verticalListSortingStrategy}>
                    {sortedCategories.map((category) => (
                      <SortableRow key={category.id} id={category.id}>
                        {(handle) => (
                          <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3">
                            <div className="flex items-center gap-2">
                              <button type="button" {...handle.attributes} {...handle.listeners} className="cursor-grab touch-none text-slate-400 hover:text-slate-600 active:cursor-grabbing"><GripVertical size={16} /></button>
                              <span className="font-medium text-ink">{category.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <StatusPill tone={category.active ? "green" : "neutral"}>{category.active ? "Ativa" : "Inativa"}</StatusPill>
                              <SecondaryButton onClick={() => { setEditingCategory(category); setIsCategoryFormOpen(true); }}><Edit3 size={16} /> Editar</SecondaryButton>
                              <SecondaryButton onClick={() => void handleToggleCategoryActive(category)}>{category.active ? "Desativar" : "Ativar"}</SecondaryButton>
                            </div>
                          </div>
                        )}
                      </SortableRow>
                    ))}
                  </SortableContext>
                </DndContext>
                {!categories.length ? <EmptyState title="Nenhuma categoria" description="Crie uma categoria para organizar os treinamentos." /> : null}
              </div>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-semibold text-ink">Treinamentos ({articles.length})</h2>
                <PrimaryButton onClick={() => { setEditingArticle(null); setIsArticleFormOpen(true); }} disabled={!categories.length}><Plus size={16} /> Novo treinamento</PrimaryButton>
              </div>
              {!categories.length ? <p className="mt-2 text-sm text-slate-500">Crie uma categoria antes de adicionar treinamentos.</p> : null}
              <div className="mt-3 space-y-5">
                {sortedCategories.map((category) => {
                  const categoryArticles = [...articles].filter((article) => article.categoryId === category.id).sort((left, right) => left.order - right.order);
                  if (!categoryArticles.length) {
                    return null;
                  }
                  return (
                    <div key={category.id}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{category.name}</p>
                      <div className="mt-2 space-y-2">
                        <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={(event) => void handleArticleDragEnd(categoryArticles, event)}>
                          <SortableContext items={categoryArticles.map((article) => article.id)} strategy={verticalListSortingStrategy}>
                            {categoryArticles.map((article) => (
                              <SortableRow key={article.id} id={article.id}>
                                {(handle) => (
                                  <div className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3">
                                    <div className="flex items-center gap-2">
                                      <button type="button" {...handle.attributes} {...handle.listeners} className="cursor-grab touch-none text-slate-400 hover:text-slate-600 active:cursor-grabbing"><GripVertical size={16} /></button>
                                      <div>
                                        <p className="font-medium text-ink">{article.title}</p>
                                        <p className="mt-1 text-xs text-slate-500">
                                          {article.videoUrl ? "com vídeo · " : ""}
                                          {article.imageUrls.length ? `${article.imageUrls.length} imagem(ns) · ` : ""}
                                          {article.helpViewIds.length ? `ajuda em ${article.helpViewIds.length} tela(s)` : "sem ajuda contextual"}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <StatusPill tone={article.status === "publicado" ? "green" : "neutral"}>{trainingArticleStatusLabel(article.status)}</StatusPill>
                                      <SecondaryButton onClick={() => setSelectedArticleId(article.id)}>Ver</SecondaryButton>
                                      <SecondaryButton onClick={() => { setEditingArticle(article); setIsArticleFormOpen(true); }}><Edit3 size={16} /> Editar</SecondaryButton>
                                    </div>
                                  </div>
                                )}
                              </SortableRow>
                            ))}
                          </SortableContext>
                        </DndContext>
                      </div>
                    </div>
                  );
                })}
                {!articles.length ? <EmptyState title="Nenhum treinamento cadastrado" description="Crie o primeiro conteúdo do manual online." /> : null}
              </div>
            </section>
          </>
        ) : (
          <>
            <input
              className="focus-ring w-full rounded-md border border-slate-300 px-3 py-2"
              placeholder="Buscar por palavra-chave..."
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {activeCategories.map((category) => {
              const categoryArticles = filteredArticles.filter((article) => article.categoryId === category.id).sort((left, right) => left.order - right.order);
              if (!categoryArticles.length) {
                return null;
              }
              return (
                <section key={category.id} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                  <h2 className="font-semibold text-ink">{category.name}</h2>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    {categoryArticles.map((article) => (
                      <button
                        key={article.id}
                        type="button"
                        onClick={() => setSelectedArticleId(article.id)}
                        className="focus-ring flex flex-col rounded-md border border-slate-200 p-3 text-left hover:border-brand-300 hover:bg-brand-50/40"
                      >
                        <span className="font-medium text-ink">{article.title}</span>
                        {article.summary ? <span className="mt-1 text-sm text-slate-500">{article.summary}</span> : null}
                        {article.status === "rascunho" ? <span className="mt-1 text-xs font-medium text-amber-700">Rascunho</span> : null}
                      </button>
                    ))}
                  </div>
                </section>
              );
            })}
            {!filteredArticles.length ? <EmptyState title="Nada encontrado" description={normalizedQuery ? "Tente outra palavra-chave." : "Ainda não há conteúdo publicado."} /> : null}
          </>
        )}
      </div>

      {isCategoryFormOpen ? (
        <TrainingCategoryFormModal
          category={editingCategory}
          currentUser={currentUser}
          onClose={() => setIsCategoryFormOpen(false)}
        />
      ) : null}

      {isArticleFormOpen ? (
        <TrainingArticleFormModal
          categories={categories}
          article={editingArticle}
          currentUser={currentUser}
          onClose={() => setIsArticleFormOpen(false)}
        />
      ) : null}
    </>
  );
}

function TrainingCategoryFormModal({
  category,
  currentUser,
  onClose
}: {
  category: TrainingCategory | null;
  currentUser: User | null;
  onClose: () => void;
}) {
  const [name, setName] = useState(category?.name ?? "");
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");

  async function handleSave() {
    if (!currentUser) {
      return;
    }
    setIsSaving(true);
    try {
      const input = { name };
      if (category) {
        await updateTrainingCategory(category.id, input, currentUser);
      } else {
        await createTrainingCategory(input, currentUser);
      }
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar a categoria.");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <h2 className="font-semibold text-ink">{category ? "Editar categoria" : "Nova categoria"}</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>
        <div className="mt-4 grid gap-3">
          <label className="text-sm text-slate-700">Nome<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex.: Bens patrimoniais" /></label>
        </div>
        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={isSaving || !name.trim()}>Salvar categoria</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

const TRAINING_CONTENT_CLASS =
  "text-sm leading-relaxed text-slate-700 [&_p]:my-2 [&_h3]:mb-2 [&_h3]:mt-5 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-ink [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_figure]:my-4 [&_img]:mx-auto [&_img]:max-h-[70vh] [&_img]:max-w-full [&_img]:rounded-md [&_img]:border [&_img]:border-slate-200 [&_video]:max-h-[70vh] [&_video]:w-full [&_video]:rounded-lg [&_video]:bg-black";

const SELECTED_MEDIA_CLASSES = ["ring-2", "ring-brand-500", "ring-offset-2", "rounded-md"];

function TrainingArticleContent({ html }: { html: string }) {
  const safeHtml = useMemo(() => sanitizeTrainingHtml(html), [html]);
  const [viewer, setViewer] = useState<{ urls: string[]; index: number } | null>(null);

  function handleClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target as HTMLElement;
    if (target.tagName !== "IMG") {
      return;
    }
    const images = Array.from(event.currentTarget.querySelectorAll("img"));
    setViewer({
      urls: images.map((image) => image.getAttribute("src") ?? ""),
      index: Math.max(0, images.indexOf(target as HTMLImageElement))
    });
  }

  return (
    <>
      <div className={`${TRAINING_CONTENT_CLASS} [&_img]:cursor-zoom-in`} onClick={handleClick} dangerouslySetInnerHTML={{ __html: safeHtml }} />
      {viewer ? <TrainingImageViewer urls={viewer.urls} initialIndex={viewer.index} onClose={() => setViewer(null)} /> : null}
    </>
  );
}

type ImageViewTransform = { scale: number; x: number; y: number };

const IMAGE_VIEWER_MIN_SCALE = 1;
const IMAGE_VIEWER_MAX_SCALE = 5;
const IMAGE_VIEWER_RESET: ImageViewTransform = { scale: 1, x: 0, y: 0 };

function TrainingImageViewer({ urls, initialIndex, onClose }: { urls: string[]; initialIndex: number; onClose: () => void }) {
  const [index, setIndex] = useState(initialIndex);
  const [view, setView] = useState<ImageViewTransform>(IMAGE_VIEWER_RESET);
  const [isInteracting, setIsInteracting] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const gestureRef = useRef<{ startDistance: number; startScale: number; startX: number; startY: number; lastX: number; lastY: number; moved: boolean; onBackdrop: boolean } | null>(null);
  const lastTapRef = useRef(0);
  const hasMany = urls.length > 1;

  function clampView(next: ImageViewTransform): ImageViewTransform {
    const scale = Math.min(IMAGE_VIEWER_MAX_SCALE, Math.max(IMAGE_VIEWER_MIN_SCALE, next.scale));
    if (scale === 1) {
      return IMAGE_VIEWER_RESET;
    }
    const rect = stageRef.current?.getBoundingClientRect();
    const maxX = rect ? (rect.width * (scale - 1)) / 2 : Infinity;
    const maxY = rect ? (rect.height * (scale - 1)) / 2 : Infinity;
    return { scale, x: Math.min(maxX, Math.max(-maxX, next.x)), y: Math.min(maxY, Math.max(-maxY, next.y)) };
  }

  /** Zooms keeping the given screen point fixed under the finger/cursor. */
  function zoomAround(clientX: number, clientY: number, nextScale: number) {
    setView((current) => {
      const rect = stageRef.current?.getBoundingClientRect();
      const pointX = rect ? clientX - (rect.left + rect.width / 2) : 0;
      const pointY = rect ? clientY - (rect.top + rect.height / 2) : 0;
      const scale = Math.min(IMAGE_VIEWER_MAX_SCALE, Math.max(IMAGE_VIEWER_MIN_SCALE, nextScale));
      const ratio = scale / current.scale;
      return clampView({ scale, x: pointX - (pointX - current.x) * ratio, y: pointY - (pointY - current.y) * ratio });
    });
  }

  function zoomBy(factor: number) {
    setView((current) => clampView({ ...current, scale: current.scale * factor }));
  }

  function goTo(delta: number) {
    if (!hasMany) {
      return;
    }
    setIndex((current) => (current + delta + urls.length) % urls.length);
    setView(IMAGE_VIEWER_RESET);
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "ArrowRight" && urls.length > 1) {
        setIndex((current) => (current + 1) % urls.length);
        setView(IMAGE_VIEWER_RESET);
      } else if (event.key === "ArrowLeft" && urls.length > 1) {
        setIndex((current) => (current - 1 + urls.length) % urls.length);
        setView(IMAGE_VIEWER_RESET);
      }
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, urls.length]);

  function pinchDistance() {
    const [first, second] = Array.from(pointersRef.current.values());
    return Math.hypot(first.x - second.x, first.y - second.y);
  }

  function pinchCenter() {
    const [first, second] = Array.from(pointersRef.current.values());
    return { x: (first.x + second.x) / 2, y: (first.y + second.y) / 2 };
  }

  function handlePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    setIsInteracting(true);
    if (pointersRef.current.size === 2) {
      gestureRef.current = { startDistance: pinchDistance(), startScale: view.scale, startX: event.clientX, startY: event.clientY, lastX: event.clientX, lastY: event.clientY, moved: true, onBackdrop: false };
    } else if (pointersRef.current.size === 1) {
      // Pointer capture retargets later events to the stage, so remember what was actually pressed.
      gestureRef.current = { startDistance: 0, startScale: view.scale, startX: event.clientX, startY: event.clientY, lastX: event.clientX, lastY: event.clientY, moved: false, onBackdrop: event.target === event.currentTarget };
    }
  }

  function handlePointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(event.pointerId) || !gestureRef.current) {
      return;
    }
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const gesture = gestureRef.current;

    if (pointersRef.current.size >= 2) {
      if (gesture.startDistance > 0) {
        const center = pinchCenter();
        zoomAround(center.x, center.y, gesture.startScale * (pinchDistance() / gesture.startDistance));
      }
      return;
    }

    const deltaX = event.clientX - gesture.lastX;
    const deltaY = event.clientY - gesture.lastY;
    if (Math.abs(event.clientX - gesture.startX) > 6 || Math.abs(event.clientY - gesture.startY) > 6) {
      gesture.moved = true;
    }
    gesture.lastX = event.clientX;
    gesture.lastY = event.clientY;
    if (view.scale > 1) {
      setView((current) => clampView({ ...current, x: current.x + deltaX, y: current.y + deltaY }));
    }
  }

  function handlePointerUp(event: React.PointerEvent<HTMLDivElement>) {
    if (!pointersRef.current.has(event.pointerId)) {
      return;
    }
    pointersRef.current.delete(event.pointerId);
    const gesture = gestureRef.current;

    if (pointersRef.current.size === 1) {
      // Pinch ended with one finger still down: continue as a pan from where it is.
      const [remaining] = Array.from(pointersRef.current.values());
      gestureRef.current = { startDistance: 0, startScale: view.scale, startX: remaining.x, startY: remaining.y, lastX: remaining.x, lastY: remaining.y, moved: true, onBackdrop: false };
      return;
    }
    if (pointersRef.current.size > 0) {
      return;
    }

    setIsInteracting(false);
    gestureRef.current = null;
    if (!gesture) {
      return;
    }

    const swipeX = event.clientX - gesture.startX;
    if (view.scale === 1 && gesture.moved && Math.abs(swipeX) > 60) {
      goTo(swipeX < 0 ? 1 : -1);
      return;
    }
    if (gesture.moved) {
      return;
    }

    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      lastTapRef.current = 0;
      if (view.scale > 1) {
        setView(IMAGE_VIEWER_RESET);
      } else {
        zoomAround(event.clientX, event.clientY, 2.5);
      }
      return;
    }
    lastTapRef.current = now;
    if (gesture.onBackdrop && view.scale === 1) {
      onClose();
    }
  }

  function handleWheel(event: React.WheelEvent<HTMLDivElement>) {
    zoomAround(event.clientX, event.clientY, view.scale * (event.deltaY < 0 ? 1.2 : 1 / 1.2));
  }

  const toolbarButtonClass = "inline-flex h-9 w-9 items-center justify-center rounded-md text-white/90 hover:bg-white/15 disabled:opacity-40";

  return (
    <div className="fixed inset-0 z-[130] flex flex-col bg-slate-950/95" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between gap-2 px-3 py-2 text-white">
        <span className="text-sm text-white/80">{hasMany ? `${index + 1} / ${urls.length}` : ""}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => zoomBy(1 / 1.5)} disabled={view.scale <= IMAGE_VIEWER_MIN_SCALE} className={toolbarButtonClass} title="Diminuir zoom"><ZoomOut size={18} /></button>
          <span className="w-12 text-center text-xs tabular-nums text-white/80">{Math.round(view.scale * 100)}%</span>
          <button type="button" onClick={() => zoomBy(1.5)} disabled={view.scale >= IMAGE_VIEWER_MAX_SCALE} className={toolbarButtonClass} title="Aumentar zoom"><ZoomIn size={18} /></button>
          <button type="button" onClick={() => setView(IMAGE_VIEWER_RESET)} disabled={view.scale === 1} className={toolbarButtonClass} title="Tamanho original"><RotateCcw size={18} /></button>
          <button type="button" onClick={onClose} className={`${toolbarButtonClass} ml-2`} title="Fechar (Esc)"><CloseIcon size={20} /></button>
        </div>
      </div>
      <div
        ref={stageRef}
        className="relative flex min-h-0 flex-1 touch-none select-none items-center justify-center overflow-hidden"
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onWheel={handleWheel}
      >
        <img
          key={urls[index]}
          src={urls[index]}
          alt=""
          draggable={false}
          className={`max-h-full max-w-full object-contain ${view.scale > 1 ? "cursor-grab" : "cursor-zoom-in"}`}
          style={{
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
            transition: isInteracting ? "none" : "transform 0.15s ease-out"
          }}
        />
        {hasMany ? (
          <>
            <button type="button" onPointerDown={(event) => event.stopPropagation()} onClick={() => goTo(-1)} className="absolute left-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60" title="Anterior"><ChevronLeft size={22} /></button>
            <button type="button" onPointerDown={(event) => event.stopPropagation()} onClick={() => goTo(1)} className="absolute right-2 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60" title="Próxima"><ChevronRight size={22} /></button>
          </>
        ) : null}
      </div>
      <p className="px-3 py-2 text-center text-xs text-white/60">Pinça, roda do mouse ou toque duplo para dar zoom. Arraste para mover.</p>
    </div>
  );
}

function TrainingArticleFormModal({
  categories,
  article,
  currentUser,
  onClose
}: {
  categories: TrainingCategory[];
  article: TrainingArticle | null;
  currentUser: User | null;
  onClose: () => void;
}) {
  const articleIdRef = useRef(article?.id ?? generateTrainingArticleId());
  const [title, setTitle] = useState(article?.title ?? "");
  const [categoryId, setCategoryId] = useState(article?.categoryId ?? categories[0]?.id ?? "");
  const [summary, setSummary] = useState(article?.summary ?? "");
  const [helpViewIds, setHelpViewIds] = useState<string[]>(article?.helpViewIds ?? []);
  const [status, setStatus] = useState<TrainingArticleStatus>(article?.status ?? "rascunho");
  const [imageUploadCount, setImageUploadCount] = useState(0);
  const [videoUploadProgress, setVideoUploadProgress] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [selectedMedia, setSelectedMedia] = useState<HTMLElement | null>(null);
  const cancelVideoUploadRef = useRef<(() => void) | null>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.innerHTML = sanitizeTrainingHtml(article?.bodyHtml ?? "").trim() || "<p><br></p>";
      editor.querySelectorAll("figure").forEach((figure) => figure.setAttribute("contenteditable", "false"));
    }
    // Only ever set the initial content - contentEditable owns its DOM after that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleSelectionChange() {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0 && editorRef.current?.contains(selection.anchorNode)) {
        savedRangeRef.current = selection.getRangeAt(0).cloneRange();
      }
    }
    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  useEffect(() => {
    if (!selectedMedia) {
      return;
    }
    selectedMedia.classList.add(...SELECTED_MEDIA_CLASSES);
    return () => selectedMedia.classList.remove(...SELECTED_MEDIA_CLASSES);
  }, [selectedMedia]);

  function toggleHelpView(viewId: string) {
    setHelpViewIds((current) => current.includes(viewId) ? current.filter((id) => id !== viewId) : [...current, viewId]);
  }

  function execEditorCommand(command: string, value?: string) {
    editorRef.current?.focus();
    const selection = window.getSelection();
    if (savedRangeRef.current && selection) {
      selection.removeAllRanges();
      selection.addRange(savedRangeRef.current);
    }
    document.execCommand(command, false, value);
  }

  function toggleHeading() {
    const current = String(document.queryCommandValue("formatBlock") ?? "").toLowerCase();
    execEditorCommand("formatBlock", current === "h3" ? "<p>" : "<h3>");
  }

  function handleEditorFocus() {
    document.execCommand("defaultParagraphSeparator", false, "p");
    document.execCommand("styleWithCSS", false, "true");
  }

  /** Captures where the media should go before an upload starts, so typing during the upload doesn't move it. */
  function captureInsertionPoint(): Range | null {
    const range = savedRangeRef.current;
    return range && editorRef.current?.contains(range.startContainer) ? range.cloneRange() : null;
  }

  /**
   * Inserts a media block at the given caret position. A caret in the middle of a
   * paragraph splits it, so the photo/video lands exactly between the two halves.
   */
  function insertMediaAt(html: string, range: Range | null) {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }
    const template = document.createElement("template");
    template.innerHTML = html;
    const figure = template.content.firstElementChild as HTMLElement | null;
    if (!figure) {
      return;
    }
    figure.setAttribute("contenteditable", "false");

    let block: Node | null = range && editor.contains(range.startContainer) ? range.startContainer : null;
    while (block && block.parentNode !== editor) {
      block = block.parentNode;
    }

    if (!range || !block || block === editor) {
      editor.appendChild(figure);
    } else if (block.nodeType === Node.ELEMENT_NODE && ["P", "DIV", "H3"].includes((block as HTMLElement).tagName)) {
      const element = block as HTMLElement;
      const tailRange = document.createRange();
      tailRange.setStart(range.startContainer, range.startOffset);
      tailRange.setEnd(element, element.childNodes.length);
      const tail = tailRange.extractContents();
      element.after(figure);
      if ((tail.textContent ?? "").trim()) {
        const next = document.createElement(element.tagName === "H3" ? "p" : element.tagName.toLowerCase());
        next.appendChild(tail);
        figure.after(next);
      }
      if (!(element.textContent ?? "").trim()) {
        element.remove();
      }
    } else {
      (block as ChildNode).after(figure);
    }

    let after = figure.nextSibling;
    if (!after || (after as HTMLElement).tagName === "FIGURE") {
      const paragraph = document.createElement("p");
      paragraph.innerHTML = "<br>";
      figure.after(paragraph);
      after = paragraph;
    }
    const caret = document.createRange();
    caret.setStart(after, 0);
    caret.collapse(true);
    savedRangeRef.current = caret;
    if (document.activeElement === editor) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(caret);
    }
  }

  async function handleImageFiles(files: File[], insertionPoint: Range | null) {
    const images = files.filter((file) => file.type.startsWith("image/")).slice(0, 10);
    if (!images.length) {
      return;
    }
    setMessage("");
    setImageUploadCount((current) => current + images.length);
    let range = insertionPoint;
    try {
      for (const file of images) {
        const url = await uploadTrainingImage(articleIdRef.current, file);
        insertMediaAt(trainingImageFigureHtml(url), range);
        // Following images go right after the one just inserted.
        range = savedRangeRef.current?.cloneRange() ?? null;
        setImageUploadCount((current) => Math.max(0, current - 1));
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível enviar a(s) imagem(ns).");
      setImageUploadCount(0);
    }
  }

  function handleVideoFile(file: File | null) {
    if (!file) {
      return;
    }
    const insertionPoint = captureInsertionPoint();
    setMessage("");
    setVideoUploadProgress(0);
    const { promise, cancel } = uploadTrainingVideo(articleIdRef.current, file, setVideoUploadProgress);
    cancelVideoUploadRef.current = cancel;
    promise
      .then((url) => {
        insertMediaAt(trainingVideoFigureHtml(url), insertionPoint);
        setVideoUploadProgress(null);
      })
      .catch((error) => {
        setMessage(error instanceof Error ? error.message : "Não foi possível enviar o vídeo.");
        setVideoUploadProgress(null);
      });
  }

  function handlePaste(event: React.ClipboardEvent<HTMLDivElement>) {
    event.preventDefault();
    const pastedImages = Array.from(event.clipboardData.files).filter((file) => file.type.startsWith("image/"));
    if (pastedImages.length) {
      void handleImageFiles(pastedImages, captureInsertionPoint());
      return;
    }
    document.execCommand("insertText", false, event.clipboardData.getData("text/plain"));
  }

  function handleDrop(event: React.DragEvent<HTMLDivElement>) {
    const droppedImages = Array.from(event.dataTransfer.files).filter((file) => file.type.startsWith("image/"));
    const droppedVideo = Array.from(event.dataTransfer.files).find((file) => file.type.startsWith("video/"));
    if (!droppedImages.length && !droppedVideo) {
      return;
    }
    event.preventDefault();
    const dropRange = document.caretRangeFromPoint?.(event.clientX, event.clientY) ?? null;
    if (dropRange && editorRef.current?.contains(dropRange.startContainer)) {
      savedRangeRef.current = dropRange;
    }
    if (droppedImages.length) {
      void handleImageFiles(droppedImages, captureInsertionPoint());
    } else if (droppedVideo && videoUploadProgress === null) {
      handleVideoFile(droppedVideo);
    }
  }

  function handleEditorClick(event: MouseEvent<HTMLDivElement>) {
    const figure = (event.target as HTMLElement).closest("figure");
    setSelectedMedia(figure && editorRef.current?.contains(figure) ? figure : null);
  }

  function moveSelectedMedia(direction: -1 | 1) {
    if (!selectedMedia) {
      return;
    }
    if (direction < 0 && selectedMedia.previousElementSibling) {
      selectedMedia.previousElementSibling.before(selectedMedia);
    } else if (direction > 0 && selectedMedia.nextElementSibling) {
      selectedMedia.nextElementSibling.after(selectedMedia);
    }
    selectedMedia.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }

  function removeSelectedMedia() {
    if (!selectedMedia) {
      return;
    }
    selectedMedia.remove();
    setSelectedMedia(null);
    if (editorRef.current && !editorRef.current.childNodes.length) {
      editorRef.current.innerHTML = "<p><br></p>";
    }
  }

  async function handleSave() {
    if (!currentUser) {
      return;
    }
    if (!title.trim()) {
      setMessage("Informe o título.");
      return;
    }
    if (!categoryId) {
      setMessage("Selecione uma categoria.");
      return;
    }

    setIsSaving(true);
    setSelectedMedia(null);
    try {
      const editor = editorRef.current;
      editor?.querySelectorAll("figure").forEach((figure) => figure.classList.remove(...SELECTED_MEDIA_CLASSES));
      const input = { categoryId, title, summary, bodyHtml: editor?.innerHTML ?? "", helpViewIds, status };
      if (article) {
        await updateTrainingArticle(article.id, input, currentUser);
      } else {
        await createTrainingArticle(articleIdRef.current, input, currentUser);
      }
      onClose();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Não foi possível salvar o treinamento.");
    } finally {
      setIsSaving(false);
    }
  }

  const isUploading = imageUploadCount > 0 || videoUploadProgress !== null;
  const toolbarButtonClass = "inline-flex h-8 items-center justify-center gap-1 rounded-md px-2 text-slate-600 hover:bg-slate-200 disabled:opacity-40";

  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-slate-950/50 p-4 backdrop-blur-sm">
      <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg border border-slate-200 bg-white p-4 shadow-panel">
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
          <h2 className="font-semibold text-ink">{article ? "Editar treinamento" : "Novo treinamento"}</h2>
          <button type="button" onClick={onClose} className="focus-ring inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 text-slate-600"><CircleSlash size={18} /></button>
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-sm text-slate-700 md:col-span-2">Título<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Ex.: Como cadastrar um bem" /></label>
          <label className="text-sm text-slate-700">Categoria
            <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
              <option value="">Selecione</option>
              {categories.map((category) => <option key={category.id} value={category.id}>{category.name}</option>)}
            </select>
          </label>
          <label className="text-sm text-slate-700">Status
            <select className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={status} onChange={(event) => setStatus(event.target.value as TrainingArticleStatus)}>
              <option value="rascunho">Rascunho</option>
              <option value="publicado">Publicado</option>
            </select>
          </label>
          <label className="text-sm text-slate-700 md:col-span-2">Resumo (aparece na listagem)<input className="focus-ring mt-1 w-full rounded-md border border-slate-300 px-3 py-2" value={summary} onChange={(event) => setSummary(event.target.value)} /></label>
        </div>

        <div className="mt-4">
          <p className="text-sm font-semibold text-ink">Conteúdo</p>
          <p className="mt-0.5 text-xs text-slate-500">Clique no ponto do texto onde a foto ou o vídeo deve aparecer e use os botões abaixo. Também dá para colar (Ctrl+V) ou arrastar imagens direto no texto.</p>
          <div className="sticky top-0 z-10 mt-2 flex flex-wrap items-center gap-1 rounded-t-md border border-b-0 border-slate-300 bg-slate-50 p-1.5">
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => execEditorCommand("bold")} className={toolbarButtonClass} title="Negrito"><Bold size={16} /></button>
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => execEditorCommand("underline")} className={toolbarButtonClass} title="Sublinhado"><Underline size={16} /></button>
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={toggleHeading} className={toolbarButtonClass} title="Subtítulo"><Heading size={16} /></button>
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => execEditorCommand("insertUnorderedList")} className={toolbarButtonClass} title="Lista com marcadores"><List size={16} /></button>
            <input type="color" onChange={(event) => execEditorCommand("foreColor", event.target.value)} className="h-8 w-9 cursor-pointer rounded-md border border-slate-300 bg-white" title="Cor do texto selecionado" />
            <span className="mx-1 h-6 w-px bg-slate-300" />
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => imageInputRef.current?.click()} className={`${toolbarButtonClass} text-sm`} title="Inserir foto na posição do cursor">
              {imageUploadCount > 0 ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Foto
            </button>
            <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => videoInputRef.current?.click()} disabled={videoUploadProgress !== null} className={`${toolbarButtonClass} text-sm`} title="Inserir vídeo na posição do cursor">
              <Film size={16} /> Vídeo
            </button>
            <input ref={imageInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(event) => { void handleImageFiles(Array.from(event.target.files ?? []), captureInsertionPoint()); event.target.value = ""; }} />
            <input ref={videoInputRef} type="file" accept="video/*" className="hidden" onChange={(event) => { handleVideoFile(event.target.files?.[0] ?? null); event.target.value = ""; }} />
            {selectedMedia ? (
              <div className="ml-auto flex items-center gap-1 rounded-md bg-brand-50 px-1">
                <span className="px-1 text-xs font-medium text-brand-700">{selectedMedia.dataset.media === "video" ? "Vídeo" : "Foto"} selecionado(a):</span>
                <button type="button" onClick={() => moveSelectedMedia(-1)} className={toolbarButtonClass} title="Mover para cima"><ArrowUp size={16} /></button>
                <button type="button" onClick={() => moveSelectedMedia(1)} className={toolbarButtonClass} title="Mover para baixo"><ArrowDown size={16} /></button>
                <button type="button" onClick={removeSelectedMedia} className={`${toolbarButtonClass} text-rose-600 hover:bg-rose-100`} title="Remover do texto"><Trash2 size={16} /></button>
              </div>
            ) : null}
          </div>
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            onFocus={handleEditorFocus}
            onPaste={handlePaste}
            onDrop={handleDrop}
            onClick={handleEditorClick}
            className={`${TRAINING_CONTENT_CLASS} focus-ring min-h-72 w-full rounded-b-md border border-slate-300 bg-white px-3 py-2 [&_figure]:cursor-pointer`}
          />
          {videoUploadProgress !== null ? (
            <div className="mt-2 flex items-center gap-3 text-sm text-slate-600">
              <Loader2 size={16} className="animate-spin" /> Enviando vídeo... {videoUploadProgress}% (ele entra no ponto do texto em que o cursor estava)
              <button type="button" onClick={() => { cancelVideoUploadRef.current?.(); setVideoUploadProgress(null); }} className="text-xs text-rose-600 hover:underline">Cancelar</button>
            </div>
          ) : null}
          {imageUploadCount > 0 ? (
            <p className="mt-2 flex items-center gap-2 text-sm text-slate-600"><Loader2 size={16} className="animate-spin" /> Enviando {imageUploadCount} foto(s)...</p>
          ) : null}
        </div>

        <div className="mt-4">
          <p className="text-sm font-semibold text-ink">Aparecer como ajuda ("?") nestas telas</p>
          <div className="mt-2 grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
            {trainingHelpViewOptions.map((option) => (
              <label key={option.id} className="flex items-center gap-2 text-xs text-slate-700">
                <input type="checkbox" checked={helpViewIds.includes(option.id)} onChange={() => toggleHelpView(option.id)} className="h-3.5 w-3.5 rounded border-slate-300 text-brand-700" />
                {option.label}
              </label>
            ))}
          </div>
        </div>

        {message ? <p className="mt-3 text-sm text-rose-600">{message}</p> : null}
        <div className="mt-4 flex justify-end gap-2 border-t border-slate-200 pt-3">
          <SecondaryButton onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton onClick={() => void handleSave()} disabled={isSaving || isUploading}>Salvar treinamento</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

export default App;
